import React, { useCallback, useEffect, useState } from 'react';
import { InfoCircleOutlined, DeleteOutlined, DatabaseOutlined, HistoryOutlined, CopyOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { Card, Form, Input, Select, Button, Space, message, InputNumber, Modal, Table, Tag, Upload, Switch, Radio, Checkbox, Tooltip, Pagination, Spin } from 'antd';
import type { InputRef } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { getAllWarehouses } from '../../services/warehouse';
import { getCurrentUserProjects, getCustomerList, getProjectList } from '../../services/project';
import { getProductDetail } from '../../services/product';
import { getShippingPackagings } from '../../services/logistics';
import { getInventoryUnitDetail } from '../../services/inventory';
import { useTableQuery } from '../../hooks/useTableQuery';
import request from '../../utils/request';
import MediaPreviewModal, { MediaPreviewTarget, previewKindOf } from '../nonstandard-ops/MediaPreviewModal';
import { auditWaybillFile } from '../../utils/waybillAudit';
import { HelpHintButton } from '../../components/HelpHintButton';
import { getAttachmentUrl } from '../../utils/attachmentUrl';
import { getTemplatePolicy } from '../../utils/instructionTemplatePolicy';
import {
  showOversizeDialog, splitOversizedFiles, uploadInstructionAttachment, UploadFailure,
} from '../../utils/uploadLimits';

// 超过这个大小的表格附件，在线预览前提醒用户（预览要把整个文件读进浏览器）
const BIG_SHEET_BYTES = 20 * 1024 * 1024;
// "添加操作对象"弹窗按批加载库存单元，每批的数量和同时请求产品详情的数量
const INVENTORY_UNITS_BATCH_SIZE = 200;
const PRODUCT_DETAIL_CONCURRENCY = 4;

interface CreateInstructionPageProps {
  /** 编辑模式：要修改的指令ID，由路由参数（/instructions/:id/edit）传入；创建/追加时不传 */
  editInstructionId?: number;
}

const CreateInstructionPage: React.FC<CreateInstructionPageProps> = ({ editInstructionId }) => {
  const [form] = Form.useForm();
  const tplVal = Form.useWatch('template', form);
  const postalCodeInputRef = React.useRef<InputRef | null>(null);
  const zipInvalidModalOpenRef = React.useRef(false);
  type TargetSourceMode = 'inventory' | 'history_ioc';
  // 编辑模式：指令ID由路由直接传入（首次渲染就可用）；URL 查询参数 edit_from 仅作兼容兜底
  const [editId, setEditId] = useState<number | undefined>(
    Number.isFinite(Number(editInstructionId)) && Number(editInstructionId) > 0 ? Number(editInstructionId) : undefined,
  );
  // 追加指令：通过 URL 查询参数 append_from 识别
  const [appendFromId, setAppendFromId] = useState<number | undefined>(undefined);
  // 操作对象来源：当前可用库存 vs 历史指令执行结果(IOC)
  const [targetSourceMode, setTargetSourceMode] = useState<TargetSourceMode>('inventory');
  useEffect(() => {
    try {
      const search = String((window as any)?.location?.search || '');
      const sp = new URLSearchParams(search);
      const idStr = sp.get('edit_from');
      const idNum = idStr ? Number((String(idStr).match(/^\d+/)?.[0] || '')) : NaN;
      if (!Number.isNaN(idNum) && idNum > 0) setEditId(prev => prev || idNum);
      let appendStr = sp.get('append_from');
      if (!appendStr) {
        // 兜底：部分开标签场景可能丢失 append_from，短时读取列表页落地的 hint
        try {
          const raw = sessionStorage.getItem('ci_append_from_hint');
          if (raw) {
            const parsed = JSON.parse(raw || '{}') as { id?: number; ts?: number };
            const ageMs = Date.now() - Number(parsed?.ts || 0);
            if (Number.isFinite(parsed?.id) && Number(parsed?.id) > 0 && ageMs >= 0 && ageMs < 2 * 60 * 1000) {
              appendStr = String(parsed.id);
            }
          }
        } catch {}
      }
      const appendNum = appendStr ? Number((String(appendStr).match(/^\d+/)?.[0] || '')) : NaN;
      if (!Number.isNaN(appendNum) && appendNum > 0) {
        setAppendFromId(appendNum);
        setTargetSourceMode('history_ioc');
        setHistoryInstrId(appendNum);
      }
      try { sessionStorage.removeItem('ci_append_from_hint'); } catch {}
    } catch {}
  }, []);
  // 依据是否为编辑模式决定文案/流程
  const isEditMode = !!editId;
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<Array<any>>([]);
  const [warehouses, setWarehouses] = useState<Array<any>>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  // 操作对象列表与弹窗
  const [targets, setTargets] = useState<Array<any>>([]);
  const [targetModalOpen, setTargetModalOpen] = useState(false);
  const [targetForm] = Form.useForm();
  // 历史指令执行结果(IOC) 模式：选择历史指令 + IOC 列表 + 选中 IOC
  const [historyInstrLoading, setHistoryInstrLoading] = useState(false);
  const [historyInstrOptions, setHistoryInstrOptions] = useState<Array<{ value: number; label: string; row?: any }>>([]);
  const [historyInstrId, setHistoryInstrId] = useState<number | undefined>(undefined);
  const [historyInstrRow, setHistoryInstrRow] = useState<any | null>(null);
  const [historyIocLoading, setHistoryIocLoading] = useState(false);
  const [historyIocRows, setHistoryIocRows] = useState<any[]>([]);
  const [historyTargetModalOpen, setHistoryTargetModalOpen] = useState(false);
  const [historyTargetRows, setHistoryTargetRows] = useState<any[]>([]);
  const [historyTargetSelectedRowKeys, setHistoryTargetSelectedRowKeys] = useState<React.Key[]>([]);
  const [historyTargetForm] = Form.useForm();
  const historyModalProjectId = Form.useWatch('modal_project_id', historyTargetForm);
  const [historyAvailableRows, setHistoryAvailableRows] = useState<any[]>([]);
  const [historyAvailableLoading, setHistoryAvailableLoading] = useState(false);
  const [historyInstructionBriefById, setHistoryInstructionBriefById] = useState<Record<number, any>>({});
  const [historyAvailableSelectedRowKeys, setHistoryAvailableSelectedRowKeys] = useState<React.Key[]>([]);
  const [historyAvailablePage, setHistoryAvailablePage] = useState(1);
  const [historyAvailablePageSize, setHistoryAvailablePageSize] = useState(10);
  const [historyTargetsPage, setHistoryTargetsPage] = useState(1);
  const [historyTargetsPageSize, setHistoryTargetsPageSize] = useState(10);
  const [historyIocFilterKeyword, setHistoryIocFilterKeyword] = useState('');
  const [historyIocHierarchyByInstrId, setHistoryIocHierarchyByInstrId] = useState<Record<number, {
    parentByCode: Record<string, string>;
    childByParentCode: Record<string, string[]>;
  }>>({});
  const appendAutoFilledFromRef = React.useRef<number | null>(null);
  const parseHistoryIocCodeSortMeta = React.useCallback((row:any) => {
    const code = String(row?.system_code || row?.display_code || '').trim();
    if (!code) {
      return { groupNum: -1, tailNum: Number.MAX_SAFE_INTEGER };
    }
    const parts = code.split('-');
    if (parts.length < 2) {
      return { groupNum: -1, tailNum: Number.MAX_SAFE_INTEGER };
    }
    const groupRaw = parts[parts.length - 2];
    const tailRaw = parts[parts.length - 1];
    const groupNum = Number(groupRaw);
    const tailNum = Number(tailRaw);
    return {
      groupNum: Number.isFinite(groupNum) ? groupNum : -1,
      tailNum: Number.isFinite(tailNum) ? tailNum : Number.MAX_SAFE_INTEGER,
    };
  }, []);
  const sortedFilteredHistoryAvailableRows = React.useMemo(() => {
    const kw = String(historyIocFilterKeyword || '').trim().toLowerCase();
    const rows = (Array.isArray(historyAvailableRows) ? historyAvailableRows : []).filter((r:any) => {
      if (!kw) return true;
      const code = String(r?.system_code || r?.display_code || '').toLowerCase();
      const content = String(r?.output_contents_detail || '').toLowerCase();
      return code.includes(kw) || content.includes(kw);
    });
    const list = [...rows];
    const getGroupSortMeta = (r:any) => {
      const instrId = Number(r?.instruction_id || r?.prev_instruction_id || 0);
      const code = String(r?.system_code || r?.display_code || '').trim();
      const hier = (Number.isFinite(instrId) && instrId > 0) ? (historyIocHierarchyByInstrId[instrId] || { parentByCode: {}, childByParentCode: {} }) : { parentByCode: {}, childByParentCode: {} };
      const parentCode = String(hier.parentByCode?.[code] || '').trim();
      const childCodes = Array.isArray(hier.childByParentCode?.[code]) ? hier.childByParentCode[code] : [];
      const role: 'parent' | 'child' | 'standalone' = parentCode
        ? 'child'
        : (childCodes.length > 0 ? 'parent' : ((r?.is_child_container ? 'child' : (r?.is_parent_container ? 'parent' : 'standalone'))));
      const rootCode = parentCode || code;
      const groupKey = role === 'standalone' ? `${instrId}::self::${code}` : `${instrId}::${rootCode}`;
      const rootMeta = parseHistoryIocCodeSortMeta({ system_code: rootCode });
      const selfMeta = parseHistoryIocCodeSortMeta({ system_code: code });
      const roleOrder = role === 'parent' ? 0 : (role === 'child' ? 1 : 0);
      return { instrId, code, role, roleOrder, groupKey, rootMeta, selfMeta };
    };
    list.sort((a:any, b:any) => {
      const ga = getGroupSortMeta(a);
      const gb = getGroupSortMeta(b);
      if (ga.rootMeta.groupNum !== gb.rootMeta.groupNum) return gb.rootMeta.groupNum - ga.rootMeta.groupNum;
      if (ga.rootMeta.tailNum !== gb.rootMeta.tailNum) return ga.rootMeta.tailNum - gb.rootMeta.tailNum;
      if (ga.groupKey !== gb.groupKey) return ga.groupKey.localeCompare(gb.groupKey);
      if (ga.roleOrder !== gb.roleOrder) return ga.roleOrder - gb.roleOrder; // 同组内：父在前，子在后
      if (ga.selfMeta.tailNum !== gb.selfMeta.tailNum) return ga.selfMeta.tailNum - gb.selfMeta.tailNum;
      return ga.code.localeCompare(gb.code);
    });
    return list;
  }, [historyAvailableRows, historyIocFilterKeyword, parseHistoryIocCodeSortMeta, historyIocHierarchyByInstrId]);
  const historyIocVisualMeta = React.useMemo(() => {
    const rowBgMap = new Map<string, string>();
    const rowVisualGroupMap = new Map<string, string>();
    const rowGroupBorderMap = new Map<string, { top: boolean; bottom: boolean; grouped: boolean }>();
    const groupCount = new Map<string, number>();
    let nextStripe = 0;
    let lastVisualGroup = '';
    (sortedFilteredHistoryAvailableRows || []).forEach((r:any) => {
      const rowKey = String(r?.id || '');
      if (!rowKey) return;
      const instrId = Number(r?.instruction_id || r?.prev_instruction_id || 0);
      const code = String(r?.system_code || r?.display_code || '').trim();
      const hier = (Number.isFinite(instrId) && instrId > 0) ? (historyIocHierarchyByInstrId[instrId] || { parentByCode: {}, childByParentCode: {} }) : { parentByCode: {}, childByParentCode: {} };
      const parentCode = String(hier.parentByCode?.[code] || '').trim();
      const childCodes = Array.isArray(hier.childByParentCode?.[code]) ? hier.childByParentCode[code] : [];
      const role: 'parent' | 'child' | 'standalone' = parentCode
        ? 'child'
        : (childCodes.length > 0 ? 'parent' : ((r?.is_child_container ? 'child' : (r?.is_parent_container ? 'parent' : 'standalone'))));
      const rootCode = parentCode || code;
      const visualGroupKey = role === 'standalone' ? `standalone:${instrId}:${code}` : `hier:${instrId}:${rootCode}`;
      rowVisualGroupMap.set(rowKey, visualGroupKey);
      groupCount.set(visualGroupKey, (groupCount.get(visualGroupKey) || 0) + 1);
      if (visualGroupKey !== lastVisualGroup) {
        nextStripe += 1;
      }
      lastVisualGroup = visualGroupKey;
      const idx = nextStripe % 2;
      rowBgMap.set(rowKey, idx === 0 ? '#e6f7ff' : '#fff7e6');
    });
    (sortedFilteredHistoryAvailableRows || []).forEach((r:any, idx:number) => {
      const rowKey = String(r?.id || '');
      if (!rowKey) return;
      const groupKey = String(rowVisualGroupMap.get(rowKey) || '');
      const prevKey = idx > 0 ? String((rowVisualGroupMap.get(String(sortedFilteredHistoryAvailableRows[idx - 1]?.id || '')) || '')) : '';
      const nextKey = idx < (sortedFilteredHistoryAvailableRows.length - 1)
        ? String((rowVisualGroupMap.get(String(sortedFilteredHistoryAvailableRows[idx + 1]?.id || '')) || ''))
        : '';
      const grouped = groupKey.startsWith('hier:') && Number(groupCount.get(groupKey) || 0) > 1;
      rowGroupBorderMap.set(rowKey, {
        top: grouped && prevKey !== groupKey,
        bottom: grouped && nextKey !== groupKey,
        grouped,
      });
    });
    return { rowBgMap, rowGroupBorderMap };
  }, [sortedFilteredHistoryAvailableRows, historyIocHierarchyByInstrId]);
  const historySelectedStats = React.useMemo(() => {
    const selected = new Set((historyAvailableSelectedRowKeys || []).map((k) => String(k)));
    const rows = (historyAvailableRows || []).filter((r:any) => selected.has(String(r?.id)));
    const containerCount = rows.length;
    const inventoryUnitQty = rows.reduce((sum:number, r:any) => {
      // 避免父容器“含子容器汇总”与子容器行重复计数：仅按每个容器直接装载数量统计
      const n = Number(
        r?.output_contents_direct_total_qty != null
          ? r?.output_contents_direct_total_qty
          : (r?.output_contents_total_qty || 0),
      );
      return sum + (Number.isFinite(n) && n > 0 ? n : 0);
    }, 0);
    return { containerCount, inventoryUnitQty };
  }, [historyAvailableRows, historyAvailableSelectedRowKeys]);
  const historyAddedStats = React.useMemo(() => {
    const rows = Array.isArray(historyTargetRows) ? historyTargetRows : [];
    const containerCount = rows.length;
    const inventoryUnitQty = rows.reduce((sum:number, r:any) => {
      const n = Number(
        r?.output_contents_direct_total_qty != null
          ? r?.output_contents_direct_total_qty
          : (r?.output_contents_total_qty || 0),
      );
      return sum + (Number.isFinite(n) && n > 0 ? n : 0);
    }, 0);
    return { containerCount, inventoryUnitQty };
  }, [historyTargetRows]);
  useEffect(() => {
    if (!historyTargetSelectedRowKeys || historyTargetSelectedRowKeys.length === 0) return;
    const valid = new Set((historyTargetRows || []).map((r:any) => String(r?.__key || '')));
    const next = historyTargetSelectedRowKeys.filter((k) => valid.has(String(k)));
    if (next.length !== historyTargetSelectedRowKeys.length) {
      setHistoryTargetSelectedRowKeys(next);
    }
  }, [historyTargetRows, historyTargetSelectedRowKeys]);
  useEffect(() => {
    const total = Array.isArray(historyTargetRows) ? historyTargetRows.length : 0;
    const pageSize = Math.max(1, Number(historyTargetsPageSize) || 10);
    const maxPage = Math.max(1, Math.ceil(total / pageSize));
    if (historyTargetsPage > maxPage) setHistoryTargetsPage(maxPage);
  }, [historyTargetRows, historyTargetsPage, historyTargetsPageSize]);
  const alreadyAddedHistoryIocIds = React.useMemo(() => {
    const s = new Set<string>();
    (historyTargetRows || []).forEach((r:any) => {
      const id = String(r?.output_container_id || '').trim();
      if (id) s.add(id);
    });
    return s;
  }, [historyTargetRows]);
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (!historyTargetModalOpen && (historyTargetRows || []).length === 0) return;
        const instrIds = Array.from(new Set(
          [...(historyAvailableRows || []), ...(historyTargetRows || [])]
            .map((r:any) => Number(r?.instruction_id || r?.prev_instruction_id || 0))
            .filter((id:number) => Number.isFinite(id) && id > 0),
        ));
        const missing = instrIds.filter((id) => !historyIocHierarchyByInstrId[id]);
        if (missing.length === 0) return;
        const patch: Record<number, { parentByCode: Record<string, string>; childByParentCode: Record<string, string[]> }> = {};
        await Promise.all(missing.map(async (instrId:number) => {
          try {
            const resp = await request.get('/api/v1/storage/output-containers/', { params: { instruction_id: instrId } });
            const rows = Array.isArray(resp?.data?.results) ? resp.data.results : [];
            const parentByCode: Record<string, string> = {};
            const childByParentCode: Record<string, string[]> = {};
            (rows || []).forEach((row:any) => {
              const code = String(row?.code || '').trim();
              const parentCode = String(row?.parent_code || '').trim();
              const childCodes = Array.isArray(row?.child_codes) ? row.child_codes : [];
              if (code && parentCode) parentByCode[code] = parentCode;
              if (code && childCodes.length > 0) {
                childByParentCode[code] = childCodes.map((x:any) => String(x || '').trim()).filter(Boolean);
              }
            });
            patch[instrId] = { parentByCode, childByParentCode };
          } catch {
            patch[instrId] = { parentByCode: {}, childByParentCode: {} };
          }
        }));
        if (cancelled) return;
        if (Object.keys(patch).length > 0) {
          setHistoryIocHierarchyByInstrId((prev) => ({ ...(prev || {}), ...patch }));
        }
      } catch {}
    };
    void run();
    return () => { cancelled = true; };
  }, [historyTargetModalOpen, historyAvailableRows, historyTargetRows, historyIocHierarchyByInstrId]);
  const historyIocGroupingMeta = React.useMemo(() => {
    const rowToGroup = new Map<string, string>();
    const groupToRows = new Map<string, string[]>();
    const roleByRow = new Map<string, 'parent' | 'child' | 'standalone'>();
    (historyAvailableRows || []).forEach((r:any) => {
      const rowId = String(r?.id || '');
      const instrId = Number(r?.instruction_id || r?.prev_instruction_id || 0);
      const code = String(r?.system_code || r?.display_code || '').trim();
      if (!rowId || !Number.isFinite(instrId) || instrId <= 0 || !code) return;
      const hier = historyIocHierarchyByInstrId[instrId] || { parentByCode: {}, childByParentCode: {} };
      const parentCode = String(hier.parentByCode?.[code] || '').trim();
      const childCodes = Array.isArray(hier.childByParentCode?.[code]) ? hier.childByParentCode[code] : [];
      let role: 'parent' | 'child' | 'standalone' = 'standalone';
      if (parentCode) role = 'child';
      else if (childCodes.length > 0) role = 'parent';
      else if (r?.is_child_container) role = 'child';
      else if (r?.is_parent_container) role = 'parent';
      roleByRow.set(rowId, role);
      const groupKey = parentCode ? `${instrId}::${parentCode}` : (role === 'parent' ? `${instrId}::${code}` : `${instrId}::self::${code}`);
      rowToGroup.set(rowId, groupKey);
      if (!groupToRows.has(groupKey)) groupToRows.set(groupKey, []);
      groupToRows.get(groupKey)!.push(rowId);
    });
    return { rowToGroup, groupToRows, roleByRow };
  }, [historyAvailableRows, historyIocHierarchyByInstrId]);
  const historyDisabledRowKeySet = React.useMemo(() => {
    const out = new Set<string>();
    const disabledGroups = new Set<string>();
    alreadyAddedHistoryIocIds.forEach((id) => {
      const gk = historyIocGroupingMeta.rowToGroup.get(String(id));
      if (gk) disabledGroups.add(gk);
    });
    disabledGroups.forEach((gk) => {
      (historyIocGroupingMeta.groupToRows.get(gk) || []).forEach((rowId) => out.add(String(rowId)));
    });
    alreadyAddedHistoryIocIds.forEach((id) => out.add(String(id)));
    return out;
  }, [alreadyAddedHistoryIocIds, historyIocGroupingMeta]);
  const expandHistorySelectedKeysByGroup = React.useCallback((keys: React.Key[]) => {
    const out = new Set<string>();
    (keys || []).forEach((k) => {
      const rowId = String(k);
      if (!rowId || historyDisabledRowKeySet.has(rowId)) return;
      const gk = historyIocGroupingMeta.rowToGroup.get(rowId);
      if (gk) {
        (historyIocGroupingMeta.groupToRows.get(gk) || []).forEach((sid) => {
          if (!historyDisabledRowKeySet.has(String(sid))) out.add(String(sid));
        });
      } else {
        out.add(rowId);
      }
    });
    return Array.from(out);
  }, [historyIocGroupingMeta, historyDisabledRowKeySet]);
  const historyTargetGroupingMeta = React.useMemo(() => {
    const rowToGroup = new Map<string, string>();
    const groupToRows = new Map<string, string[]>();
    (historyTargetRows || []).forEach((r:any) => {
      const rowId = String(r?.__key || '');
      const instrId = Number(r?.instruction_id || r?.prev_instruction_id || 0);
      const code = String(r?.output_code || r?.display_code || '').trim();
      if (!rowId || !Number.isFinite(instrId) || instrId <= 0 || !code) return;
      const hier = historyIocHierarchyByInstrId[instrId] || { parentByCode: {}, childByParentCode: {} };
      const parentCode = String(hier.parentByCode?.[code] || '').trim();
      const childCodes = Array.isArray(hier.childByParentCode?.[code]) ? hier.childByParentCode[code] : [];
      const isStandalone = !parentCode && childCodes.length === 0;
      const groupKey = parentCode ? `${instrId}::${parentCode}` : (isStandalone ? `${instrId}::self::${code}` : `${instrId}::${code}`);
      rowToGroup.set(rowId, groupKey);
      if (!groupToRows.has(groupKey)) groupToRows.set(groupKey, []);
      groupToRows.get(groupKey)!.push(rowId);
    });
    return { rowToGroup, groupToRows };
  }, [historyTargetRows, historyIocHierarchyByInstrId]);
  const expandHistoryTargetKeysByGroup = React.useCallback((keys: React.Key[]) => {
    const out = new Set<string>();
    (keys || []).forEach((k) => {
      const rowId = String(k || '');
      if (!rowId) return;
      const gk = historyTargetGroupingMeta.rowToGroup.get(rowId);
      if (gk) {
        (historyTargetGroupingMeta.groupToRows.get(gk) || []).forEach((sid) => out.add(String(sid)));
      } else {
        out.add(rowId);
      }
    });
    return Array.from(out);
  }, [historyTargetGroupingMeta]);
  const sortedHistoryTargetRows = React.useMemo(() => {
    const list = [...(historyTargetRows || [])];
    const getSortMeta = (r:any) => {
      const instrId = Number(r?.instruction_id || r?.prev_instruction_id || 0);
      const code = String(r?.output_code || r?.display_code || '').trim();
      const hier = (Number.isFinite(instrId) && instrId > 0) ? (historyIocHierarchyByInstrId[instrId] || { parentByCode: {}, childByParentCode: {} }) : { parentByCode: {}, childByParentCode: {} };
      const parentCode = String(hier.parentByCode?.[code] || '').trim();
      const childCodes = Array.isArray(hier.childByParentCode?.[code]) ? hier.childByParentCode[code] : [];
      const role: 'parent' | 'child' | 'standalone' = parentCode ? 'child' : (childCodes.length > 0 ? 'parent' : 'standalone');
      const rootCode = parentCode || code;
      const rootMeta = parseHistoryIocCodeSortMeta({ system_code: rootCode });
      const selfMeta = parseHistoryIocCodeSortMeta({ system_code: code });
      const roleOrder = role === 'parent' ? 0 : (role === 'child' ? 1 : 0);
      const groupKey = role === 'standalone' ? `${instrId}::self::${code}` : `${instrId}::${rootCode}`;
      return { code, roleOrder, rootMeta, selfMeta, groupKey };
    };
    list.sort((a:any, b:any) => {
      const ma = getSortMeta(a);
      const mb = getSortMeta(b);
      if (ma.rootMeta.groupNum !== mb.rootMeta.groupNum) return mb.rootMeta.groupNum - ma.rootMeta.groupNum;
      if (ma.rootMeta.tailNum !== mb.rootMeta.tailNum) return ma.rootMeta.tailNum - mb.rootMeta.tailNum;
      if (ma.groupKey !== mb.groupKey) return ma.groupKey.localeCompare(mb.groupKey);
      if (ma.roleOrder !== mb.roleOrder) return ma.roleOrder - mb.roleOrder;
      if (ma.selfMeta.tailNum !== mb.selfMeta.tailNum) return ma.selfMeta.tailNum - mb.selfMeta.tailNum;
      return ma.code.localeCompare(mb.code);
    });
    return list;
  }, [historyTargetRows, historyIocHierarchyByInstrId, parseHistoryIocCodeSortMeta]);
  const historyTargetVisualMeta = React.useMemo(() => {
    const rowBgMap = new Map<string, string>();
    const rowGroupBorderMap = new Map<string, { grouped: boolean; top: boolean; bottom: boolean }>();
    const rowVisualGroupMap = new Map<string, string>();
    const groupCount = new Map<string, number>();
    let stripe = 0;
    let lastGroup = '';
    (sortedHistoryTargetRows || []).forEach((r:any) => {
      const rowKey = String(r?.__key || '');
      const instrId = Number(r?.instruction_id || r?.prev_instruction_id || 0);
      const code = String(r?.output_code || r?.display_code || '').trim();
      if (!rowKey || !code || !Number.isFinite(instrId) || instrId <= 0) return;
      const hier = historyIocHierarchyByInstrId[instrId] || { parentByCode: {}, childByParentCode: {} };
      const parentCode = String(hier.parentByCode?.[code] || '').trim();
      const childCodes = Array.isArray(hier.childByParentCode?.[code]) ? hier.childByParentCode[code] : [];
      const visualGroup = parentCode ? `hier:${instrId}:${parentCode}` : (childCodes.length > 0 ? `hier:${instrId}:${code}` : `standalone:${instrId}:${code}`);
      rowVisualGroupMap.set(rowKey, visualGroup);
      groupCount.set(visualGroup, (groupCount.get(visualGroup) || 0) + 1);
      if (visualGroup !== lastGroup) stripe += 1;
      lastGroup = visualGroup;
      rowBgMap.set(rowKey, stripe % 2 === 0 ? '#e6f7ff' : '#fff7e6');
    });
    (sortedHistoryTargetRows || []).forEach((r:any, idx:number) => {
      const rowKey = String(r?.__key || '');
      const gk = String(rowVisualGroupMap.get(rowKey) || '');
      const prev = idx > 0 ? String(rowVisualGroupMap.get(String(sortedHistoryTargetRows[idx - 1]?.__key || '')) || '') : '';
      const next = idx < sortedHistoryTargetRows.length - 1 ? String(rowVisualGroupMap.get(String(sortedHistoryTargetRows[idx + 1]?.__key || '')) || '') : '';
      const grouped = gk.startsWith('hier:') && Number(groupCount.get(gk) || 0) > 1;
      rowGroupBorderMap.set(rowKey, { grouped, top: grouped && prev !== gk, bottom: grouped && next !== gk });
    });
    return { rowBgMap, rowGroupBorderMap };
  }, [sortedHistoryTargetRows, historyIocHierarchyByInstrId]);
  useEffect(() => {
    if (!historyAvailableSelectedRowKeys || historyAvailableSelectedRowKeys.length === 0) return;
    const next = expandHistorySelectedKeysByGroup(
      historyAvailableSelectedRowKeys.filter((k) => !historyDisabledRowKeySet.has(String(k))),
    );
    if (next.length !== historyAvailableSelectedRowKeys.length) {
      setHistoryAvailableSelectedRowKeys(next);
    }
  }, [historyDisabledRowKeySet, historyAvailableSelectedRowKeys, expandHistorySelectedKeysByGroup]);
  // 选项加载：分拣组/库存单元/历史指令
  const [sortingGroups, setSortingGroups] = useState<Array<any>>([]);
  const [inventoryUnits, setInventoryUnits] = useState<Array<any>>([]);
  const [loadingSortingGroups, setLoadingSortingGroups] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  // 库存单元分批加载：进度、加载不完整的错误、最新一次加载的标记（切换项目后丢弃旧结果）、用于重试的参数
  const [unitsLoadProgress, setUnitsLoadProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [unitsLoadError, setUnitsLoadError] = useState<string | null>(null);
  const unitsLoadTokenRef = React.useRef(0);
  const unitsLoadParamsRef = React.useRef<any>(undefined);
  // "添加操作对象-当前可用库存"列表的分页
  const [unitsPage, setUnitsPage] = useState(1);
  const [unitsPageSize, setUnitsPageSize] = useState(10);
  // 表头"全选"默认只作用于当前页；打开"跨页全选"后作用于当前筛选结果的全部页
  const [selectAllAcrossPages, setSelectAllAcrossPages] = useState(false);
  // 分拣组ID -> 信息 映射（system_code, name），供列表"所属分拣组"渲染回退使用
  const sgIdToInfo = React.useMemo(() => {
    const map: Record<number, { system_code?: string; name?: string }> = {};
    try {
      (sortingGroups || []).forEach((g:any) => {
        const id = Number(g?.id);
        if (!Number.isFinite(id)) return;
        map[id] = { system_code: g?.system_code || g?.systemCode || g?.code, name: g?.name };
      });
    } catch {}
    return map;
  }, [sortingGroups]);
  const [projectSgCount, setProjectSgCount] = useState(0);
  const [projectUnitCount, setProjectUnitCount] = useState(0);
  const [upstreamOptions, setUpstreamOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [loadingUpstream, setLoadingUpstream] = useState(false);
  const [productInfo, setProductInfo] = useState<Record<number, any>>({});
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [unitFilterKeyword, setUnitFilterKeyword] = useState('');
  // 双列表选择状态（当前可用库存）
  const [invSel, setInvSel] = useState<Record<number, { checked: boolean; mode: 'all' | 'number'; qty?: number }>>({});
  // 注意：历史指令结果不再混入“添加操作对象”弹窗，改用 IOC 模式独立选择
  const [upstreamRows, setUpstreamRows] = useState<any[]>([]);
  const [tplDetailState, setTplDetailState] = useState<any | null>(null);
  // 问卷答案
  const [answers, setAnswers] = useState<Record<string, any>>({});
  // 编辑模式标题中的系统编码
  const [editSystemCode, setEditSystemCode] = useState<string | undefined>(undefined);
  const [editDetail, setEditDetail] = useState<any | null>(null);
  const [editTargetsReady, setEditTargetsReady] = useState(false);
  // 编辑模式：现有附件恢复
  const [existingAttachments, setExistingAttachments] = useState<any[]>([]);
  const attachmentsRestoredRef = React.useRef(false);
  const fetchedUnitDetailRef = React.useRef<Set<number>>(new Set());
const zipLookupTimerRef = React.useRef<number | null>(null);
  const zipMismatchModalRef = React.useRef<{ zip?: string; ts?: number }>({});
  const zipIgnoreWarningRef = React.useRef<Set<string>>(new Set());
  // 编辑模式：保存原始标准化后的 targets，用于提交前差异判断，避免无变化也提交导致后端重建
  const originalTargetsRef = React.useRef<any[]>([]);
  // 选择统计（基于 displayUnits 统一口径）
  const selectedUnitIds = React.useMemo(() => Object.entries(invSel).filter(([_, s]) => !!s?.checked).map(([id]) => Number(id)), [invSel]);
  // 统一口径：弹窗/编辑场景“可用数量展示” = 后端可用 + 本指令在该IU的活跃锁定量
  const getDisplayAvailableQtyForUnit = React.useCallback((u: any) => {
    try {
      const baseAvail = (u?.available_qty != null ? Number(u.available_qty)
        : (u?.warehouse_total_quantity != null ? Number(u.warehouse_total_quantity) : 0));
      const reservedSelf = Number((u as any)?.reserved_qty_for_instruction || 0) || 0;
      const v = (Number.isFinite(baseAvail) ? baseAvail : 0) + reservedSelf;
      return Number.isFinite(v) ? v : 0;
    } catch {
      return 0;
    }
  }, [historyTargetForm]);
  // 仅展示有可用库存的单元
  const displayUnits = React.useMemo(() => {
    try {
      const filtered = (inventoryUnits || []).filter((u:any) => {
        const v = getDisplayAvailableQtyForUnit(u);
        return Number.isFinite(v) && v > 0;
      });
      // 按分拣组分组排序，确保同组连续，便于背景色分割
      filtered.sort((a:any, b:any) => {
        const ga = Number(a.sorting_group || (a.sorting_group_info && a.sorting_group_info.id) || 0);
        const gb = Number(b.sorting_group || (b.sorting_group_info && b.sorting_group_info.id) || 0);
        if (ga !== gb) return ga - gb;
        const sa = String(a.system_barcode || '');
        const sb = String(b.system_barcode || '');
        return sa.localeCompare(sb);
      });
      return filtered;
    } catch { return inventoryUnits || []; }
  }, [inventoryUnits, getDisplayAvailableQtyForUnit]);
  const filteredUnits = React.useMemo(() => {
    try {
      const q = String(unitFilterKeyword || '').trim().toLowerCase();
      if (!q) return displayUnits || [];
      const hit = (val:any) => String(val ?? '').toLowerCase().includes(q);
      const matched = (displayUnits || []).filter((u:any) => {
        if (hit(u?.system_barcode) || hit(u?.system_code) || hit(u?.systemCode) || hit(u?.id) || hit(u?.remarks)) return true;
        const items = Array.isArray(u?.content?.items) ? u.content.items : [];
        for (const it of items) {
          if (hit(it?.product_name) || hit(it?.custom_code)) return true;
          const pid = Number(it?.product);
          const info = productInfo[pid] || {};
          if (hit(info?.name) || hit(info?.custom_code)) return true;
          const codes = Array.isArray(info?.sorting_codes)
            ? info.sorting_codes
            : (Array.isArray(it?.sorting_codes) ? it.sorting_codes : []);
          const codeList = codes.map((c:any)=> (typeof c === 'string' ? c : c?.code)).filter(Boolean);
          if (codeList.some((c:any)=> hit(c))) return true;
        }
        return false;
      });
      const groupIds = new Set<number>();
      matched.forEach((u:any) => {
        const gid = Number(u?.sorting_group || (u?.sorting_group_info && u.sorting_group_info.id));
        if (Number.isFinite(gid)) groupIds.add(gid);
      });
      if (groupIds.size === 0) return [];
      return (displayUnits || []).filter((u:any) => {
        const gid = Number(u?.sorting_group || (u?.sorting_group_info && u.sorting_group_info.id));
        return Number.isFinite(gid) && groupIds.has(gid);
      });
    } catch { return displayUnits || []; }
  }, [displayUnits, unitFilterKeyword, productInfo]);
  useEffect(() => { setUnitsPage(1); }, [inventoryUnits, unitFilterKeyword]);
  // 打开弹窗、换项目、重新加载时"跨页全选"恢复关闭
  useEffect(() => { setSelectAllAcrossPages(false); }, [inventoryUnits]);
  // 当 displayUnits 变化时，清理已选中但不在可用列表中的条目
  useEffect(() => {
    try {
      const present = new Set((displayUnits || []).map((u:any)=>Number(u.id)));
      setInvSel(prev => {
        const next: typeof prev = {} as any;
        Object.keys(prev).forEach(k => {
          const id = Number(k);
          if (present.has(id)) next[id] = prev[id];
        });
        return next;
      });
    } catch {}
  }, [displayUnits]);
  // 分拣组交替背景：计算分拣组顺序索引
  const groupIndexById = React.useMemo(() => {
    const order: number[] = [];
    const map: Record<number, number> = {};
    (displayUnits || []).forEach((u:any) => {
      const gid = Number(u.sorting_group || (u.sorting_group_info && u.sorting_group_info.id));
      if (!Number.isNaN(gid) && map[gid] == null) {
        map[gid] = order.length; order.push(gid);
      }
    });
    return map;
  }, [displayUnits]);
  // 分拣组 → 行数量（用于判定多单元分拣组）
  const groupUnitCountById = React.useMemo(() => {
    const countMap: Record<number, number> = {};
    (displayUnits || []).forEach((u:any) => {
      const gid = Number(u.sorting_group || (u.sorting_group_info && u.sorting_group_info.id));
      if (!Number.isNaN(gid)) countMap[gid] = (countMap[gid] || 0) + 1;
    });
    return countMap;
  }, [displayUnits]);
  
  // 选择统计（依赖 displayUnits，放在其后定义）
  const selectedUnits = React.useMemo(() => (displayUnits || []).filter((u:any) => selectedUnitIds.includes(Number(u.id))), [displayUnits, selectedUnitIds]);
  const selectedGroupCount = React.useMemo(() => {
    const setIds = new Set<number>();
    selectedUnits.forEach((u:any) => {
      const gid = Number(u.sorting_group || (u.sorting_group_info && u.sorting_group_info.id));
      if (!Number.isNaN(gid)) setIds.add(gid);
    });
    return setIds.size;
  }, [selectedUnits]);
  const selectedUnitCount = selectedUnits.length;
  const selectedUnitQtyTotal = React.useMemo(() => {
    try {
      return selectedUnits.reduce((acc:number, u:any) => {
        const id = Number(u.id);
        const sel = invSel[id];
        if (sel && sel.checked) {
          if (sel.mode === 'number' && Number(sel.qty) > 0) {
            return acc + Number(sel.qty);
          }
        }
        const v = getDisplayAvailableQtyForUnit(u);
        return acc + (Number.isFinite(v) ? v : 0);
      }, 0);
    } catch { return 0; }
  }, [selectedUnits, invSel, getDisplayAvailableQtyForUnit]);
  const hasAnySelection = React.useMemo(() => {
    const present = new Set((displayUnits || []).map((u:any)=>Number(u.id)));
    const anyUnits = selectedUnitIds.some(id => present.has(id));
    return anyUnits;
  }, [displayUnits, selectedUnitIds]);
  // 已添加的项目集合（用于禁用同项目的再次添加）
  const usedProjectIds = React.useMemo(() => {
    const s = new Set<number>();
    try {
      (targets || []).forEach((t:any) => { const pid = Number(t.project_id); if (!Number.isNaN(pid)) s.add(pid); });
    } catch {}
    return s;
  }, [targets]);
  // 统一分节标题样式
  const sectionTitleStyle: React.CSSProperties = { fontWeight: 600, fontSize: 14 };
  // 操作对象列表统计：项目数 / 库存单元种类 / 库存单元数量
  const targetsProjectCount = React.useMemo(() => {
    const s = new Set<number>();
    try { (targets || []).forEach((t:any) => { const pid = Number(t.project_id); if (!Number.isNaN(pid)) s.add(pid); }); } catch {}
    return s.size;
  }, [targets]);

  // 视图数据：为 targets 添加稳定的原始索引，并按 sorting_group_id 分组排序（同组相邻）
  const targetsView = React.useMemo(() => {
    try {
      const rows = (targets || []).map((t:any, i:number) => ({ ...t, __idx: i })) as any[];
      rows.sort((a:any, b:any) => {
        const ga = Number(a?.sorting_group_id ?? 0);
        const gb = Number(b?.sorting_group_id ?? 0);
        if (ga !== gb) return ga - gb;
        const ua = Number(a?.inventory_unit_id ?? 0);
        const ub = Number(b?.inventory_unit_id ?? 0);
        return ua - ub;
      });
      return rows;
    } catch { return (targets || []).map((t:any, i:number)=> ({ ...t, __idx: i })); }
  }, [targets]);

  // 目标列表：分拣组交替底色（同组同色）
  const targetsGroupIndexByGroup = React.useMemo(() => {
    const order: number[] = [];
    const map: Record<number, number> = {};
    try {
      (targetsView || []).forEach((r:any) => {
        const gid = Number(r?.sorting_group_id);
        if (Number.isFinite(gid) && map[gid] == null) { map[gid] = order.length; order.push(gid); }
      });
    } catch {}
    return map;
  }, [targetsView]);
  // 操作对象列表分页
  const [targetsPage, setTargetsPage] = useState(1);
  const [targetsPageSize, setTargetsPageSize] = useState(10);
  useEffect(() => { setTargetsPage(1); }, [targets]);

  // URL 轻量状态：仓库/模板/优先级（单一数据源：useTableQuery 同步 URL）
  const { state: urlState, actions: urlActions } = useTableQuery({
    initialState: {
      pagination: { current: 1, pageSize: 10, total: 0 },
      filters: {
        ci_warehouse_id: '',
        ci_template: '',
        ci_priority: 'normal',
        ci_remarks: ''
      },
      sorting: { field: 'created_at', order: 'desc' }
    },
    urlConfig: {
      syncToUrl: true,
      urlKeys: ['filters']
    },
    // 创建页不取表格数据，这里返回空数据即可
    fetchData: async () => ({ results: [], count: 0 })
  } as any);
  const targetsUnitKindCount = React.useMemo(() => {
    const s = new Set<number>();
    try { (targets || []).forEach((t:any) => { if (t?.target_type === 'inventory_unit') { const id = Number(t.inventory_unit_id); if (!Number.isNaN(id)) s.add(id); } }); } catch {}
    return s.size;
  }, [targets]);
  const targetsUnitQtyTotal = React.useMemo(() => {
    try {
      return (targets || []).reduce((acc:number, t:any) => {
        if (t?.target_type !== 'inventory_unit') return acc;
        if (t?.use_all_available) {
          const v = (t?.available_qty != null ? Number(t.available_qty) : (t?.warehouse_total_quantity != null ? Number(t.warehouse_total_quantity) : 0));
          return acc + (Number.isFinite(v) ? v : 0);
        }
        const q = Number(t?.expected_qty);
        return acc + (Number.isFinite(q) && q > 0 ? q : 0);
      }, 0);
    } catch { return 0; }
  }, [targets]);
  // 弹窗：已经添加到“操作对象列表”的库存单元（禁止重复添加）
  const alreadyAddedUnitIds = React.useMemo(() => {
    const s = new Set<number>();
    try {
      (targets || []).forEach((t: any) => {
        if (t?.target_type !== 'inventory_unit') return;
        const id = Number(t?.inventory_unit_id);
        if (Number.isFinite(id)) s.add(id);
      });
    } catch {}
    return s;
  }, [targets]);
  // 计算"多单元分拣组"：同一分拣组内去重后的库存单元数量 > 1
  const targetsGroupUnitIdsByGroup = React.useMemo(() => {
    const map: Record<number, Set<number>> = {};
    try {
      (targets || []).forEach((t:any) => {
        if (t?.target_type !== 'inventory_unit') return;
        const gid = Number(t?.sorting_group_id);
        const iu = Number(t?.inventory_unit_id);
        if (!Number.isFinite(gid) || !Number.isFinite(iu)) return;
        if (!map[gid]) map[gid] = new Set<number>();
        map[gid].add(iu);
      });
    } catch {}
    return map;
  }, [targets]);

  const multiGroupIds = React.useMemo(() => {
    const s = new Set<number>();
    try {
      Object.entries(targetsGroupUnitIdsByGroup).forEach(([gid, set]) => {
        const cnt = (set as Set<number>).size;
        if (cnt > 1) s.add(Number(gid));
      });
    } catch {}
    return s;
  }, [targetsGroupUnitIdsByGroup]);
  // 当前页（页码超出时按最后一页算）
  const unitsCurrentPage = Math.min(unitsPage, Math.max(1, Math.ceil((filteredUnits || []).length / unitsPageSize)));
  // 头部"全选/取消全选"的范围：当前页或当前筛选结果的全部页；多单元分拣组必须同时处理，范围内出现的分拣组带上整组
  const headerScopeUnits = React.useMemo(() => {
    try {
      const base = selectAllAcrossPages
        ? (filteredUnits || [])
        : (filteredUnits || []).slice((unitsCurrentPage - 1) * unitsPageSize, unitsCurrentPage * unitsPageSize);
      const gidOf = (u:any) => Number(u.sorting_group || (u.sorting_group_info && u.sorting_group_info.id));
      const multiGroupIds = new Set(base.map(gidOf).filter((gid:number) => (groupUnitCountById[gid] || 0) > 1));
      const ids = new Set(base.map((u:any) => Number(u.id)));
      (displayUnits || []).forEach((u:any) => { if (multiGroupIds.has(gidOf(u))) ids.add(Number(u.id)); });
      return (displayUnits || []).filter((u:any) => ids.has(Number(u.id)));
    } catch { return []; }
  }, [selectAllAcrossPages, filteredUnits, unitsCurrentPage, unitsPageSize, displayUnits, groupUnitCountById]);
  const headerCheckedCount = React.useMemo(() => {
    try {
      return (headerScopeUnits || []).reduce((acc:number, u:any) => {
        const s = invSel[Number(u.id)];
        // 已添加的库存单元不计入可选范围
        if (alreadyAddedUnitIds.has(Number(u.id))) return acc;
        return acc + (s && s.checked ? 1 : 0);
      }, 0);
    } catch { return 0; }
  }, [headerScopeUnits, invSel, alreadyAddedUnitIds]);
  const headerEnabledCount = React.useMemo(() => {
    try {
      return (headerScopeUnits || []).reduce((acc:number, u:any) => acc + (alreadyAddedUnitIds.has(Number(u.id)) ? 0 : 1), 0);
    } catch { return 0; }
  }, [headerScopeUnits, alreadyAddedUnitIds]);
  // 已选中但不在当前筛选结果里的单元数（筛选词变化后仍保留之前的选择，这里提示一下）
  const selectedOutsideFilterCount = React.useMemo(() => {
    const visibleIds = new Set((filteredUnits || []).map((u:any) => Number(u.id)));
    return (displayUnits || []).filter((u:any) => invSel[Number(u.id)]?.checked && !visibleIds.has(Number(u.id))).length;
  }, [filteredUnits, displayUnits, invSel]);
  // 附件（按规则逐行上传）
  const [attachFilesMap, setAttachFilesMap] = useState<Record<string, UploadFile<any>[]>>({});
  const [attachTplMetaById, setAttachTplMetaById] = useState<Record<number, { file_name?: string; name?: string; version?: string }>>({});
  // 需打印附件的每文件打印配置：
  // attachPrintConfigMap[ruleKey][fileUid] = { print, copies, paper_size, placement, explicit_no_print }
  const [attachPrintConfigMap, setAttachPrintConfigMap] = useState<Record<string, Record<string, { print?: boolean; copies?: number; paper_size?: string; placement?: string; explicit_no_print?: boolean }>>>({});
  // 切换模板时清空已选择的附件与打印配置
  useEffect(() => { setAttachFilesMap({}); setAttachPrintConfigMap({}); }, [tplVal]);
  // 切换模板时拉取模板详情（确保拿到 questionnaire）并清空答案
  useEffect(() => {
    (async () => {
      try {
        // 创建模式才在切换模板时清空答案；编辑模式保持后端回填的答案
        if (!isEditMode) setAnswers({});
        if (!tplVal || tplVal === '__NONE__') { setTplDetailState(null); return; }
        const inList = (templates || []).find(t => String(t.id) === String(tplVal));
        // 先用列表项做一次快速展示（如有），随后始终拉取详情，避免列表数据不完整导致 default/字段缺失
        if (inList) { setTplDetailState(inList); }
        const res = await request.get(`/api/v1/instruction/templates/${tplVal}/`);
        setTplDetailState(res?.data || inList || null);
      } catch { setTplDetailState(null); }
    })();
  }, [tplVal, templates, isEditMode]);
  // 规则行（从模板 attachment_requirements 拉平，优先用 tplDetailState 以获取完整 printable）
  const ruleRows = React.useMemo(() => {
    try {
      if (!tplVal || tplVal === '__NONE__') return [] as any[];
      const tpl = (tplDetailState && String((tplDetailState as any)?.id) === String(tplVal))
        ? tplDetailState
        : templates.find((t: any) => String(t.id) === String(tplVal));
      const req = (tpl?.attachment_requirements || {}) as any;
    const rows: Array<{ key: string; scope: 'order'|'actions'; action_key?: string; label?: string; types?: string[]; min?: number; max?: number; tips?: string; template_id?: number; template_ids?: number[]; printable?: boolean; show_if?: any }>=[];
      const normalizeTplIds = (node:any) => {
        if (Array.isArray(node?.template_ids)) {
          return node.template_ids.map((id:any)=>Number(id)).filter((id:number)=>Number.isFinite(id));
        }
        if (node?.template_id != null) {
          const n = Number(node.template_id);
          return Number.isFinite(n) ? [n] : [];
        }
        return [];
      };
      const orderArr = Array.isArray(req.order) ? req.order : [];
    orderArr.forEach((n:any, i:number) => rows.push({ key: `order:${i}`, scope: 'order', label: n.label, types: n.types, min: n.min, max: n.max, tips: n.tips, template_ids: normalizeTplIds(n), template_id: n.template_id, printable: !!n.printable, show_if: n.show_if }));
      if (req.actions && typeof req.actions === 'object') {
        Object.keys(req.actions).forEach((k:string) => {
          const arr = Array.isArray(req.actions[k]) ? req.actions[k] : [];
        arr.forEach((n:any, i:number) => rows.push({ key: `actions:${k}:${i}`, scope: 'actions', action_key: k, label: n.label, types: n.types, min: n.min, max: n.max, tips: n.tips, template_ids: normalizeTplIds(n), template_id: n.template_id, printable: !!n.printable, show_if: n.show_if }));
        });
      }
      return rows;
    } catch { return []; }
  }, [templates, tplVal, tplDetailState]);
  useEffect(() => {
    try {
      const ids = new Set<number>();
      (ruleRows || []).forEach((r:any) => {
        const list = Array.isArray(r?.template_ids)
          ? r.template_ids
          : (r?.template_id != null ? [Number(r.template_id)] : []);
        list.forEach((id:any) => {
          const n = Number(id);
          if (Number.isFinite(n)) ids.add(n);
        });
      });
      const missing = Array.from(ids).filter((id) => !(attachTplMetaById || {})[id]);
      if (missing.length === 0) return;
      (async () => {
        try {
          const resp = await request.get('/api/v1/instruction/attachment-templates/', { params: { is_active: 1, page_size: 500 } });
          const rows = Array.isArray(resp.data?.results) ? resp.data.results : (Array.isArray(resp.data) ? resp.data : []);
          const next: Record<number, { file_name?: string; name?: string; version?: string }> = {};
          rows.forEach((r:any) => {
            const id = Number(r?.id);
            if (!Number.isFinite(id)) return;
            next[id] = { file_name: r?.file_name || undefined, name: r?.name || undefined, version: r?.version || undefined };
          });
          setAttachTplMetaById((prev) => ({ ...(prev || {}), ...next }));
        } catch {}
      })();
    } catch {}
  }, [ruleRows, attachTplMetaById]);
  // 问卷（从模板详情）
  const qRows = React.useMemo(() => {
    try {
      const d: any = tplDetailState as any;
      const qn = (d && d.questionnaire) ? d.questionnaire : ({} as any);
      const rows = Array.isArray((qn as any).questions) ? (qn as any).questions : [];
      return rows as any[];
    } catch { return []; }
  }, [tplDetailState]);
  const [questionOptionsBySource, setQuestionOptionsBySource] = useState<Record<string, Array<{ value: any; label: any }>>>({});
  const [questionOptionsLoading, setQuestionOptionsLoading] = useState<Record<string, boolean>>({});
  const getOptionSourceKey = (source: any) => {
    const model = String(source?.model || '').trim();
    const valueField = String(source?.value_field || 'code').trim();
    const labelField = String(source?.label_field || 'code').trim();
    return `${model}|${valueField}|${labelField}`;
  };
  const fetchOptionSource = React.useCallback(async (source: any) => {
    const model = String(source?.model || '').trim();
    const valueField = String(source?.value_field || 'code').trim();
    const labelField = String(source?.label_field || 'code').trim();
    const key = getOptionSourceKey(source);
    if (!model) return;
    setQuestionOptionsLoading(prev => ({ ...prev, [key]: true }));
    try {
      if (model === 'logistics_provider') {
        const resp = await request.get('/api/v1/logistics/providers/', { params: { is_active: 1, page_size: 1000 } });
        const rows = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
        const normalizeCode = (v: any) => String(v || '').trim().toUpperCase();
        const firstFixed = ['USPS', 'FEDEX', 'UPS', 'DHL'];
        const lastFixed = ['LTL/FTL CARRIER', 'OTHERS'];
        const options = (rows || []).map((r:any) => {
          const val = r?.[valueField];
          const codeRaw = String(r?.code ?? val ?? '').trim();
          const nameRaw = String(r?.name ?? '').trim();
          const labelByField = String(r?.[labelField] ?? '').trim();
          const label = codeRaw && nameRaw
            ? `${codeRaw} - ${nameRaw}`
            : (labelByField || codeRaw || nameRaw || String(val ?? '').trim());
          return {
            value: val,
            label,
            __sortCode: normalizeCode(codeRaw || val),
            __sortCodeRaw: codeRaw || String(val ?? '').trim(),
          };
        }).filter((o:any) => o.value !== undefined && o.value !== null && String(o.value).trim() !== '');
        options.sort((a: any, b: any) => {
          const ca = String(a?.__sortCode || '');
          const cb = String(b?.__sortCode || '');
          const iaFirst = firstFixed.indexOf(ca);
          const ibFirst = firstFixed.indexOf(cb);
          if (iaFirst >= 0 || ibFirst >= 0) {
            if (iaFirst < 0) return 1;
            if (ibFirst < 0) return -1;
            return iaFirst - ibFirst;
          }
          const iaLast = lastFixed.indexOf(ca);
          const ibLast = lastFixed.indexOf(cb);
          if (iaLast >= 0 || ibLast >= 0) {
            if (iaLast < 0) return -1;
            if (ibLast < 0) return 1;
            return iaLast - ibLast;
          }
          return String(a?.__sortCodeRaw || '').localeCompare(String(b?.__sortCodeRaw || ''), undefined, { numeric: true, sensitivity: 'base' });
        });
        setQuestionOptionsBySource(prev => ({ ...prev, [key]: options }));
      }
    } catch {
      setQuestionOptionsBySource(prev => ({ ...prev, [key]: [] }));
    } finally {
      setQuestionOptionsLoading(prev => ({ ...prev, [key]: false }));
    }
  }, []);
  useEffect(() => {
    try {
      const sources: Record<string, any> = {};
      (qRows || []).forEach((q:any) => {
        const source = q?.options_source || q?.optionsSource;
        if (source && source.type === 'model') {
          const key = getOptionSourceKey(source);
          sources[key] = source;
        }
      });
      Object.keys(sources).forEach((key) => {
        if (questionOptionsBySource[key] || questionOptionsLoading[key]) return;
        fetchOptionSource(sources[key]);
      });
    } catch {}
  }, [qRows, questionOptionsBySource, questionOptionsLoading, fetchOptionSource]);
  const parseShowIfValue = (raw: any) => {
    if (raw === undefined || raw === null) return undefined;
    if (typeof raw !== 'string') return raw;
    const s = raw.trim();
    if (s === '') return undefined;
    if (s === 'true') return true;
    if (s === 'false') return false;
    if (s === 'null') return null;
    if (/^-?\d+(\.\d+)?$/.test(s)) return Number(s);
    if ((s.startsWith('[') && s.endsWith(']')) || (s.startsWith('{') && s.endsWith('}'))) {
      try { return JSON.parse(s); } catch {}
    }
    return raw;
  };
  const isEmptyAnswer = (val: any) => (
    val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)
  );
  const isConditionMatched = React.useCallback((cond: any) => {
    const questionByKey = new Map(
      (Array.isArray(qRows) ? qRows : [])
        .map((q:any) => [String(q?.key || '').trim(), q] as const)
        .filter(([k]) => !!k)
    );
    const norm = (v: any) => String(v ?? '').trim();
    const getOptionsForKey = (depKey: string) => {
      const q: any = questionByKey.get(depKey);
      if (!q) return [] as Array<{ value: any; label: any }>;
      const source = q?.options_source || q?.optionsSource;
      if (source && source.type === 'model') {
        const sourceKey = getOptionSourceKey(source);
        return Array.isArray(questionOptionsBySource[sourceKey]) ? questionOptionsBySource[sourceKey] : [];
      }
      return Array.isArray(q?.options) ? q.options : [];
    };
    const getCandidateValues = (depKey: string, raw: any) => {
      const out = new Set<string>();
      const opts = getOptionsForKey(depKey);
      const addOne = (item: any) => {
        const s = norm(item);
        if (!s) return;
        out.add(s);
        opts.forEach((o:any) => {
          const vs = norm(o?.value);
          const ls = norm(o?.label);
          if (!vs && !ls) return;
          if (s === vs && ls) out.add(ls);
          if (s === ls && vs) out.add(vs);
        });
      };
      if (Array.isArray(raw)) raw.forEach(addOne);
      else addOne(raw);
      return out;
    };
    const hasMatchByCandidates = (depKey: string, left: any, right: any) => {
      const lset = getCandidateValues(depKey, left);
      const rset = getCandidateValues(depKey, right);
      return Array.from(lset).some((v) => rset.has(v));
    };
    const evalNode = (node: any): boolean => {
      try {
        if (!node || typeof node !== 'object') return true;
        // 新结构：must_conditions(AND) + any_conditions(OR)
        const mustConditions = Array.isArray((node as any)?.must_conditions) ? (node as any).must_conditions.filter(Boolean) : [];
        const anyConditions = Array.isArray((node as any)?.any_conditions) ? (node as any).any_conditions.filter(Boolean) : [];
        if (mustConditions.length > 0 || anyConditions.length > 0) {
          const mustOk = mustConditions.every((c: any) => evalNode(c));
          const anyOk = anyConditions.length === 0 ? true : anyConditions.some((c: any) => evalNode(c));
          return mustOk && anyOk;
        }
        // 复合条件：{ logic: 'and'|'or', conditions: [...] }（支持递归）
        const conditions = Array.isArray((node as any)?.conditions) ? (node as any).conditions.filter(Boolean) : [];
        if (conditions.length > 0) {
          const logic = String((node as any)?.logic || (node as any)?.mode || 'and').toLowerCase();
          if (logic === 'or' || logic === 'any') return conditions.some((c: any) => evalNode(c));
          return conditions.every((c: any) => evalNode(c));
        }
        // 单条件叶子节点
        const depKey = String((node as any)?.key || '').trim();
        if (!depKey) return true;
        const op = String((node as any)?.op || 'eq');
        const ans = answers[depKey];
        if (op === 'is_empty') return isEmptyAnswer(ans);
        if (op === 'not_empty') return !isEmptyAnswer(ans);
        if (op === 'is_true') return ans === true;
        if (op === 'is_false') return ans === false;
        const expectedRaw = (node as any)?.value ?? (node as any)?.values ?? (node as any)?.value_text;
        const expected = parseShowIfValue(expectedRaw);
        if (op === 'in') {
          if (Array.isArray(expected)) {
            const expSet = new Set<string>();
            (expected || []).forEach((x:any) => {
              getCandidateValues(depKey, x).forEach((s) => expSet.add(s));
            });
            if (Array.isArray(ans)) {
              return ans.some((v:any) => {
                const cands = getCandidateValues(depKey, v);
                return Array.from(cands).some((s) => expSet.has(s));
              });
            }
            const cands = getCandidateValues(depKey, ans);
            return Array.from(cands).some((s) => expSet.has(s));
          }
          return false;
        }
        if (op === 'contains') {
          if (Array.isArray(ans)) {
            if (Array.isArray(expected)) return ans.some((v:any)=> expected.includes(v));
            return ans.includes(expected);
          }
          if (typeof ans === 'string' && expected != null) return ans.includes(String(expected));
          return false;
        }
        if (['gt', 'gte', 'lt', 'lte'].includes(op)) {
          const n1 = Number(ans);
          const n2 = Number(expected);
          if (!Number.isFinite(n1) || !Number.isFinite(n2)) return false;
          if (op === 'gt') return n1 > n2;
          if (op === 'gte') return n1 >= n2;
          if (op === 'lt') return n1 < n2;
          if (op === 'lte') return n1 <= n2;
        }
        if (Array.isArray(ans) || Array.isArray(expected)) {
          return op === 'neq'
            ? JSON.stringify(ans) !== JSON.stringify(expected)
            : JSON.stringify(ans) === JSON.stringify(expected);
        }
        const directEqual = (ans === expected || String(ans) === String(expected));
        const mappedEqual = hasMatchByCandidates(depKey, ans, expected);
        return op === 'neq' ? !(directEqual || mappedEqual) : (directEqual || mappedEqual);
      } catch {
        return true;
      }
    };
    return evalNode(cond || {});
  }, [answers, qRows, questionOptionsBySource]);
  const isQuestionVisible = React.useCallback((q: any) => {
    try {
      const showIf = q?.show_if || q?.showIf;
      return isConditionMatched(showIf);
    } catch { return true; }
  }, [isConditionMatched]);
  const isQuestionRequired = React.useCallback((q: any) => {
    try {
      if (!isQuestionVisible(q)) return false;
      if (q?.required) return true;
      const reqIf = q?.required_if || q?.requiredIf;
      if (!reqIf) return false;
      return isConditionMatched(reqIf);
    } catch {
      return false;
    }
  }, [isConditionMatched, isQuestionVisible]);
  const normalizeQuestionValue = React.useCallback((q: any, val: any) => {
    const type = String(q?.type || 'text').trim().toLowerCase();
    if (type === 'boolean') {
      // 业务约定：布尔开关未触发交互时，默认按“否(false)”处理
      if (val === undefined || val === null || val === '') return false;
      if (typeof val === 'boolean') return val;
      const s = String(val).trim().toLowerCase();
      if (s === 'true' || s === '1' || s === 'yes' || s === 'y' || s === 'on') return true;
      if (s === 'false' || s === '0' || s === 'no' || s === 'n' || s === 'off') return false;
      return !!val;
    }
    return val;
  }, []);
  const isQuestionAnswered = React.useCallback((q: any, rawVal: any) => {
    const type = String(q?.type || 'text').trim().toLowerCase();
    const val = normalizeQuestionValue(q, rawVal);
    if (type === 'boolean') return typeof val === 'boolean';
    if (type === 'multiselect') return Array.isArray(val) && val.length > 0;
    if (type === 'number') return !(val === undefined || val === null || val === '');
    return !(val === undefined || val === null || val === '');
  }, [normalizeQuestionValue]);
  const resolveWaybillSubmitMode = React.useCallback(() => {
    try {
      const candidates = [
        'waybill_submit_mode',
        'waybill_upload_mode',
        'waybill_submit_timing',
        '运单上传时机',
        '运单提交方式',
      ];
      let raw: any;
      for (const k of candidates) {
        if (answers && Object.prototype.hasOwnProperty.call(answers, k)) {
          raw = (answers as any)[k];
          break;
        }
      }
      if (raw === undefined || raw === null || raw === '') return undefined;
      const s = String(raw).trim();
      if (!s) return undefined;
      if (s === 'immediate' || s === 'after_pack') return s;
      if (/先|后|包装|打包|仓库/.test(s)) return 'after_pack';
      if (/立即|立刻|马上|即时/.test(s)) return 'immediate';
      return undefined;
    } catch { return undefined; }
  }, [answers]);
  const isImmediateWaybillValue = React.useCallback((raw: any) => {
    try {
      if (raw === undefined || raw === null || raw === '') return false;
      const s = String(raw).trim();
      if (!s) return false;
      const lower = s.toLowerCase();
      if (lower === 'immediate') return true;
      return /立即|立刻|马上|即时/.test(s);
    } catch { return false; }
  }, []);
  const isWaybillSubmitTimingQuestion = React.useCallback((q: any) => {
    try {
      const key = String(q?.key || '').trim().toLowerCase();
      const label = String(q?.label || '').trim();
      return (
        key === 'waybill_submit_mode'
        || key === 'waybill_upload_mode'
        || key === 'waybill_submit_timing'
        || label === '运单上传时机'
        || label === '运单提交方式'
      );
    } catch { return false; }
  }, []);
  const resolveRepackRequired = React.useCallback(() => {
    try {
      const candidates = [
        'is_repack_required',
        'repack_required',
        '是否需重新包装',
        'is_pack_required',
      ];
      let raw: any;
      for (const k of candidates) {
        if (answers && Object.prototype.hasOwnProperty.call(answers, k)) {
          raw = (answers as any)[k];
          break;
        }
      }
      if (raw === undefined || raw === null || raw === '') return undefined;
      if (typeof raw === 'boolean') return raw;
      const s = String(raw).trim().toLowerCase();
      if (!s) return undefined;
      if (s === 'true' || s === '1' || s === 'yes' || s === 'y' || s === 'on') return true;
      if (s === 'false' || s === '0' || s === 'no' || s === 'n' || s === 'off') return false;
      if (/无需|不需|无须|否/.test(s)) return false;
      if (/需|是/.test(s)) return true;
      return undefined;
    } catch { return undefined; }
  }, [answers]);
  const lockImmediateWaybillSubmitTiming = React.useMemo(() => {
    try {
      if (!tplVal || tplVal === '__NONE__') return false;
      const tpl = (templates || []).find((t:any) => String(t.id) === String(tplVal));
      const name = String(tpl?.name || '').trim();
      const isRegularOutbound = name.includes('常规出库');
      return isRegularOutbound && resolveRepackRequired() === false;
    } catch { return false; }
  }, [tplVal, templates, resolveRepackRequired]);
  useEffect(() => {
    try {
      if (!lockImmediateWaybillSubmitTiming) return;
      const rows = (Array.isArray(qRows) ? qRows : []).filter((q: any) => isQuestionVisible(q));
      const timingRows = rows.filter((q: any) => isWaybillSubmitTimingQuestion(q));
      if (timingRows.length === 0) return;
      setAnswers((prev: any) => {
        const next: any = { ...(prev || {}) };
        let changed = false;
        timingRows.forEach((q: any) => {
          const key = String(q?.key || '').trim();
          if (!key) return;
          const current = next[key];
          if (isImmediateWaybillValue(current)) return;
          const options = Array.isArray(q?.options) ? q.options : [];
          const immediateOpt = options.find((o: any) => isImmediateWaybillValue(o?.value) || isImmediateWaybillValue(o?.label));
          const forced = immediateOpt ? immediateOpt.value : 'immediate';
          next[key] = forced;
          changed = true;
        });
        return changed ? next : prev;
      });
    } catch {}
  }, [lockImmediateWaybillSubmitTiming, qRows, isQuestionVisible, isWaybillSubmitTimingQuestion, isImmediateWaybillValue]);
  const isAttachmentVisible = React.useCallback((r: any) => {
    try {
      const showIf = r?.show_if || r?.showIf;
      return isConditionMatched(showIf);
    } catch { return true; }
  }, [isConditionMatched]);
  const visibleQRows = React.useMemo(() => {
    try { return (Array.isArray(qRows) ? qRows : []).filter(isQuestionVisible); } catch { return []; }
  }, [qRows, isQuestionVisible]);
  useEffect(() => {
    try {
      const visibleKeys = new Set(
        (visibleQRows || [])
          .map((q:any) => String(q?.key || '').trim())
          .filter((k:string) => k)
      );
      const hiddenKeys = (Array.isArray(qRows) ? qRows : [])
        .map((q:any) => String(q?.key || '').trim())
        .filter((k:string) => k && !visibleKeys.has(k));
      if (hiddenKeys.length === 0) return;
      setAnswers((prev:any) => {
        const next:any = { ...(prev || {}) };
        let changed = false;
        hiddenKeys.forEach((k:string) => {
          if (k in next) { delete next[k]; changed = true; }
        });
        return changed ? next : prev;
      });
    } catch {}
  }, [visibleQRows, qRows]);
  useEffect(() => {
    try {
      if (isEditMode) return;
      if (!tplVal || tplVal === '__NONE__') return;
      const rows: any[] = Array.isArray(visibleQRows) ? visibleQRows : [];
      if (rows.length === 0) return;
      setAnswers((prev: any) => {
        const next: any = { ...(prev || {}) };
        let changed = false;
        rows.forEach((q: any) => {
          const key = String(q?.key || '').trim();
          if (!key) return;
          const type = String(q?.type || 'text');
          const def = (q as any)?.default;
          const hasDef = !(def === undefined || def === null || def === '');
          if (!hasDef) return;
          const cur = next[key];
          const isEmpty = (() => {
            if (cur === undefined || cur === null || cur === '') return true;
            if (type === 'multiselect' && Array.isArray(cur) && cur.length === 0) return true;
            return false;
          })();
          if (!isEmpty) return;
          if (type === 'multiselect') {
            const arr = Array.isArray(def) ? def : [def];
            const vals = arr.map((x: any) => String(x ?? '').trim()).filter(Boolean);
            if (vals.length > 0) { next[key] = vals; changed = true; }
          } else if (type === 'number') {
            const n = Number(def);
            if (Number.isFinite(n)) { next[key] = n; changed = true; }
          } else if (type === 'boolean') {
            if (typeof def === 'boolean') { next[key] = def; changed = true; }
          } else if (type === 'select') {
            const s = String(def ?? '').trim();
            if (s) { next[key] = s; changed = true; }
          } else {
            // text / textarea
            next[key] = String(def ?? '');
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    } catch {}
  }, [visibleQRows, tplVal, isEditMode]);

  // 创建页：当选择模板后，将问卷默认值注入到 answers（仅填充“未填写”的项，且不覆盖用户已输入）
  useEffect(() => {
    try {
      if (isEditMode) return;
      if (!tplVal || tplVal === '__NONE__') return;
      const rows: any[] = Array.isArray(qRows) ? qRows : [];
      if (rows.length === 0) return;
      setAnswers((prev: any) => {
        const next: any = { ...(prev || {}) };
        let changed = false;
        rows.forEach((q: any) => {
          const key = String(q?.key || '').trim();
          if (!key) return;
          const type = String(q?.type || 'text');
          const def = (q as any)?.default;
          const hasDef = !(def === undefined || def === null || def === '');
          if (!hasDef) return;
          const cur = next[key];
          const isEmpty = (() => {
            if (cur === undefined || cur === null || cur === '') return true;
            if (type === 'multiselect' && Array.isArray(cur) && cur.length === 0) return true;
            return false;
          })();
          if (!isEmpty) return;
          if (type === 'multiselect') {
            const arr = Array.isArray(def) ? def : [def];
            const vals = arr.map((x: any) => String(x ?? '').trim()).filter(Boolean);
            if (vals.length > 0) { next[key] = vals; changed = true; }
          } else if (type === 'number') {
            const n = Number(def);
            if (Number.isFinite(n)) { next[key] = n; changed = true; }
          } else if (type === 'boolean') {
            if (typeof def === 'boolean') { next[key] = def; changed = true; }
          } else if (type === 'select') {
            const s = String(def ?? '').trim();
            if (s) { next[key] = s; changed = true; }
          } else {
            // text / textarea
            next[key] = String(def ?? '');
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    } catch {}
  }, [tplVal, qRows, isEditMode]);

  // —— 附件角色绑定（方式A）辅助 —— //
  const findBindingByRuleKey = React.useCallback((ruleKey: string) => {
    try {
      const arr = Array.isArray((tplDetailState as any)?.attachment_role_bindings)
        ? (tplDetailState as any).attachment_role_bindings : [];
      return arr.find((b:any) => String(b?.rule_key) === String(ruleKey) && b?.enabled !== false);
    } catch { return undefined; }
  }, [tplDetailState]);

  const isBarcodeMappingBinding = (binding: any) => {
    try { return String(binding?.role_code || '') === 'BARCODE_MAPPING'; } catch { return false; }
  };
  const isShippingLabelTargetMappingBinding = (binding: any) => {
    try { return String(binding?.role_code || '') === 'SHIPPING_LABEL_TARGET_MAPPING'; } catch { return false; }
  };
  const isGroupListBinding = (binding: any) => {
    try { return String(binding?.role_code || '').trim().toUpperCase() === 'GROUP_LIST'; } catch { return false; }
  };

  // 创建页：新标签 PDF（或压缩包内 PDF）质量预审（A1：上传即预审，不依赖 attachment_id）
  const isNewBarcodeLabelBinding = (binding: any) => {
    try {
      const rc = String(binding?.role_code || '').trim().toUpperCase();
      return rc === 'NEW_BARCODE' || rc.includes('NEW_BARCODE');
    } catch { return false; }
  };
  const getExtLower = (name?: string) => {
    try {
      const s = String(name || '');
      if (!s.includes('.')) return '';
      return String(s.split('.').pop() || '').trim().toLowerCase();
    } catch { return ''; }
  };
  const isPdfAuditCandidateFile = (name?: string) => {
    const ext = getExtLower(name);
    return ext === 'pdf' || ext === 'zip' || ext === 'rar';
  };
  const [pdfQaLoadingByUid, setPdfQaLoadingByUid] = useState<Record<string, boolean>>({});
  const [pdfQaResultByUid, setPdfQaResultByUid] = useState<Record<string, any>>({});
  const [pdfQaModalOpen, setPdfQaModalOpen] = useState(false);
  const [pdfQaModalRuleKey, setPdfQaModalRuleKey] = useState<string | undefined>(undefined);
  const [pdfQaModalTitle, setPdfQaModalTitle] = useState<string | undefined>(undefined);
  const [waybillAuditModalOpen, setWaybillAuditModalOpen] = useState(false);
  const [waybillAuditFileName, setWaybillAuditFileName] = useState<string>('');
  const [waybillAuditIssues, setWaybillAuditIssues] = useState<Array<{ code: string; label: string; message: string }>>([]);
  const [waybillAuditRunning, setWaybillAuditRunning] = useState(false);
  const waybillAuditRunningRef = React.useRef(0);
  const [waybillAuditModalRuleKey, setWaybillAuditModalRuleKey] = useState<string | undefined>(undefined);
  const [waybillAuditModalTitle, setWaybillAuditModalTitle] = useState<string | undefined>(undefined);
  const copyWaybillAuditWarnText = useCallback(async () => {
    try {
      const title = '物流运单解析审核 风险项提示';
      const ts = new Date().toLocaleString();
      const fileName = waybillAuditFileName || '-';
      const issues = Array.isArray(waybillAuditIssues) ? waybillAuditIssues : [];
      if (issues.length === 0) { message.warning('暂无可复制的风险项'); return; }
      const header = [
        title,
        `生成时间：${ts}`,
        waybillAuditModalTitle ? `类型：${waybillAuditModalTitle}` : null,
        `文件：${fileName}`,
        `风险项数量：${issues.length}`,
      ].filter(Boolean).join('\n');
      const blocks = issues.map((it:any, i:number) => {
        const label = String(it?.label || '-');
        const msg = String(it?.message || '-');
        return `序号：${i + 1}\n规则类别：${label}\n原因：${msg}`;
      });
      const text = `${header}\n\n${blocks.join('\n--------------------\n')}`;
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const el = document.createElement('textarea');
        el.value = text;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      message.success('已复制风险项文本');
    } catch {
      message.warning('复制失败');
    }
  }, [waybillAuditFileName, waybillAuditIssues, waybillAuditModalTitle]);
  const copyPdfQaWarnText = useCallback(async () => {
    try {
      const title = '新标签pdf文件解析审核 风险项提示';
      const ruleKey = String(pdfQaModalRuleKey || '');
      if (!ruleKey) { message.warning('未选择风险项'); return; }
      const files = (attachFilesMap[ruleKey] || []) as any[];
      const uids = files
        .filter((f:any) => !!f?.originFileObj && isPdfAuditCandidateFile(String(f?.name || '')))
        .map((f:any)=> String(f?.uid || ''))
        .filter(Boolean);
      const raws = uids.map((uid)=> ({ uid, raw: pdfQaResultByUid[uid] })).filter((x:any)=> !!x?.raw);
      if (raws.length === 0) { message.warning('暂无可复制的风险项'); return; }

      const issueList: Array<{ loc: string; decoded: string; reason: string }> = [];
      raws.forEach((x:any) => {
        const raw = x.raw;
        const sourceName = String(raw?.source_name || '');
        const items = Array.isArray(raw?.items) ? raw.items : [];
        items.forEach((it:any) => {
          const issues = Array.isArray(it?.issues) ? it.issues : [];
          if (!issues || issues.length === 0) return;
          const fn = String(it?.file_name || '');
          const kind = String(it?.source?.kind || '');
          const arc = String(it?.source?.archive_name || '');
          const ep = String(it?.source?.entry_path || '');
          const loc = (kind === 'zip' || kind === 'rar')
            ? `${arc || '-'} :: ${ep || fn || '-'}`
            : (fn || sourceName || '-');
          const decodedArr = Array.isArray(it?.decoded) ? it.decoded : [];
          const decodedText = decodedArr.length > 0 ? decodedArr.join('，') : '-';
          const codes = issues.map((iss:any)=> String(iss?.code || '').trim()).filter(Boolean);
          const hasMismatch = codes.includes('pdf.decoded_filename_mismatch');
          const reason = hasMismatch ? '条码解析结果与文件名不一致' : '整页模拟打印后未识别到可解析条码';
          issueList.push({ loc, decoded: decodedText, reason });
        });
      });

      if (issueList.length === 0) { message.warning('暂无可复制的风险项'); return; }

      const ts = new Date().toLocaleString();
      const header = [
        title,
        `生成时间：${ts}`,
        pdfQaModalTitle ? `类型：${pdfQaModalTitle}` : null,
        `风险项数量：${issueList.length}`,
      ].filter(Boolean).join('\n');
      const blocks = issueList.map((it:any, i:number) => (
        `序号：${i + 1}\n文件/条目：${it.loc}\n解码结果：${it.decoded}\n原因：${it.reason}`
      ));
      const text = `${header}\n\n${blocks.join('\n--------------------\n')}`;
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const el = document.createElement('textarea');
        el.value = text;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      message.success('已复制风险项文本');
    } catch {
      message.warning('复制失败');
    }
  }, [pdfQaModalRuleKey, pdfQaModalTitle, attachFilesMap, pdfQaResultByUid]);
  const pdfQaRunning = React.useMemo(() => {
    try {
      return Object.values(pdfQaLoadingByUid || {}).some(Boolean);
    } catch { return false; }
  }, [pdfQaLoadingByUid]);

  // NEW_BARCODE：已上传附件列表的搜索 + 分页（仅该类启用）
  const [nbFileQueryByRuleKey, setNbFileQueryByRuleKey] = useState<Record<string, string>>({});
  const [nbFilePageByRuleKey, setNbFilePageByRuleKey] = useState<Record<string, number>>({});
  const [nbFilePageSizeByRuleKey, setNbFilePageSizeByRuleKey] = useState<Record<string, number>>({});

  const runPdfLabelQualityAudit = useCallback(async (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await request.post('/api/v1/instruction/attachments/pdf-label-quality-audit/', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return (res as any)?.data;
  }, []);

  const getPdfQaSummaryForUid = useCallback((uid?: string) => {
    try {
      if (!uid) return null;
      const r = pdfQaResultByUid[uid];
      if (!r) return null;
      const sum = r?.summary || {};
      const total = Number(sum?.total ?? 0) || 0;
      const ok = Number(sum?.ok ?? 0) || 0;
      const err = Number(sum?.error ?? 0) || 0;
      const hasRisk = err > 0;
      return { total, ok, err, hasRisk, raw: r };
    } catch { return null; }
  }, [pdfQaResultByUid]);

  const parseFileAsText = (file: File): Promise<string> => new Promise((resolve, reject) => {
    try {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result || ''));
      r.onerror = () => reject(r.error);
      r.readAsText(file, 'utf-8');
    } catch (e) { reject(e); }
  });

  const parseFileAsArrayBuffer = (file: File): Promise<ArrayBuffer> => new Promise((resolve, reject) => {
    try {
      const r = new FileReader();
      r.onload = () => resolve(r.result as ArrayBuffer);
      r.onerror = () => reject(r.error);
      r.readAsArrayBuffer(file);
    } catch (e) { reject(e); }
  });

  const normalizeBarcode = (s: any) => {
    try {
      return String(s ?? '')
        .trim()
        .replace(/\s+/g, '')
        .replace(/\u3000/g, '')
        .toUpperCase();
    } catch { return ''; }
  };

  const chooseOldColumn = (headers: string[], binding?: any) => {
    const cols = Array.isArray(binding?.schema_override?.columns?.old)
      ? binding.schema_override.columns.old : undefined;
    const preferred: string[] = Array.isArray(cols) && cols.length > 0
      ? cols : ['旧条码','旧码','原条码','old_barcode','old','source_barcode'];
    const norm = (s:string) => s.trim().toLowerCase();
    const low = headers.map(h => norm(h));
    for (const k of preferred) {
      const idx = low.indexOf(norm(String(k)));
      if (idx >= 0) return headers[idx];
    }
    return null;
  };

  const checkBarcodeMappingDuplicates = async (file: File, binding?: any) => {
    try {
      const name = (file?.name || '').toLowerCase();
      // 统一转为 AOA（Array of Arrays），第 0 行视为表头，数据从第 1 行开始
      let aoaRows: any[][] = [];
      // 用于定位真实起始列（当 A 列整列为空时，XLSX 会把 UsedRange 起始列定位到 B，从而 AOA 的 0 索引即代表 B 列）
      let startColOffset = 0; // 0 表示从 A 开始；1 表示从 B 开始
      if (name.endsWith('.csv')) {
        const text = await parseFileAsText(file);
        const Papa = (await import('papaparse')).default;
        // 使用 AOA（不以首行为键名），保持列位次精确对应 A/B/C/D/E
        const parsed = Papa.parse(text, { header: false, skipEmptyLines: 'greedy' });
        const arr = Array.isArray(parsed?.data) ? parsed.data : [];
        aoaRows = (arr as any[]).filter((r:any) => Array.isArray(r) && r.length > 0);
        // CSV 无法直接获知工作表 UsedRange；按常见导出习惯做经验性推断：
        // - 若最大列数 >= 5，则视为从 A 开始（A..E 至少 5 列）
        // - 若最大列数 == 4，则大概率整列 A 为空，视为从 B 开始（B..E 共 4 列）
        // - 其他情况后续会给出列位次不匹配提示
        try {
          const maxLen = Math.max(...aoaRows.map(r => (Array.isArray(r) ? r.length : 0)));
          if (Number.isFinite(maxLen)) {
            if (maxLen >= 5) startColOffset = 0;
            else if (maxLen === 4) startColOffset = 1;
          }
        } catch {}
      } else if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
        const XLSX = await import('xlsx');
        const buf = await parseFileAsArrayBuffer(file);
        const wb = XLSX.read(buf, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        // AOA，严格按列位次（A=0,B=1,...）
        const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        aoaRows = Array.isArray(aoa) ? (aoa as any[]).filter((r:any)=> Array.isArray(r) && r.length>0) : [];
        // 基于工作表引用范围推断起始列（UsedRange）
        try {
          const ref = (sheet as any)['!ref'] || 'A1:A1';
          const range = (XLSX as any).utils.decode_range(ref);
          const s = range?.s?.c;
          if (Number.isFinite(s)) startColOffset = Number(s);
        } catch {}
      } else {
        // 不支持的扩展名：仅提示
        message.info('已上传文件，但当前仅对 CSV/XLSX 执行重复校验');
        return;
      }
      if (!aoaRows || aoaRows.length === 0) {
        message.info('文件为空，未进行校验');
        return;
      }
      // 新模板：仅使用前 5 列（A-E）
      // - A 列：旧条码
      // - B 列：新条码
      // - C 列：新条码文本
      // - D 列：数量分配（需为数字）
      // - E 列：备注
      // 严格按列字母定位（考虑 UsedRange 起始偏移）：A/B/C/D/E => 绝对列 0/1/2/3/4
      const aAbs = 0, bAbs = 1, cAbs = 2, dAbs = 3, eAbs = 4;
      const aIdx = aAbs - startColOffset;
      const bIdx = bAbs - startColOffset;
      const cIdx = cAbs - startColOffset;
      const dIdx = dAbs - startColOffset;
      const eIdx = eAbs - startColOffset;
      // 校验可用列数：以最大有效列数扣除起始偏移，至少需要 A..E 共 5 列
      const maxLen = Math.max(...aoaRows.map(r => (Array.isArray(r) ? r.length : 0)));
      const availableCols = Number.isFinite(maxLen) ? (maxLen - startColOffset) : 0;
      if (aIdx < 0 || dIdx < 0 || availableCols < 5) {
        Modal.info({ title: '列位次不匹配', content: '未检测到 A/D 列，请确认文件结构与模板一致（A=旧条码，D=数量分配，显示A-E五列）。' });
        return;
      }
      const dataRows = aoaRows.slice(1); // 跳过表头行
      // 1) 以 A 列为键（trim，忽略大小写）分组，仅关注重复组（size>1）
      const toKey = (s: any) => String(s ?? '').trim().toUpperCase();
      const groups: Map<string, Array<{ idx: number; row: any }>> = new Map();
      dataRows.forEach((r, i) => {
        const key = toKey(r?.[aIdx]);
        if (!key) return;
        const arr = groups.get(key) || [];
        arr.push({ idx: i + 2, row: r }); // 行号：含表头
        groups.set(key, arr);
      });
      const dupGroups = Array.from(groups.entries()).filter(([, arr]) => (arr?.length || 0) > 1);

      // ===== 聚合内容审核：统一收集三类问题，合并为一个弹窗 =====
      // 1) 新旧条码相同（A==B）
      // 2) 新旧条码组合重复（A+B 相同出现>=2）
      // 3) 一个旧条码对应多个新条码，且 D 列数量分配无效（原有逻辑，提示信息保持不变）

      // (3) 原有逻辑：收集 badRows（不直接弹窗）
      let badRows: Array<{ rowNo: number; a: any; b: any; c: any; d: any; e: any; groupKey: string }> = [];
      let groupOrder: string[] = [];
      let groupMap: Record<string, Array<{ rowNo: number; a: any; b: any; c: any; d: any; e: any; groupKey: string }>> = {};
      if (dupGroups.length > 0) {
        const isNumeric = (v: any) => {
          if (v === null || v === undefined) return false;
          const s = String(v).trim();
          if (s === '') return false;
          return !isNaN(Number(s));
        };
        badRows = [];
        dupGroups.forEach(([key, arr]) => {
          const hasBad = arr.some(({ row }) => !isNumeric(row?.[dIdx]));
          if (hasBad) {
            arr.forEach(({ idx, row }) => {
              const aVal = (row as any)?.[aIdx];
              const bVal = (row as any)?.[bIdx];
              const cVal = (row as any)?.[cIdx];
              const dVal = (row as any)?.[dIdx];
              const eVal = (row as any)?.[eIdx];
              badRows.push({
                rowNo: idx,
                a: aVal,
                b: bVal,
                c: cVal,
                d: dVal,
                e: eVal,
                groupKey: key
              } as any);
            });
          }
        });
        if (badRows.length > 0) {
          // 分组（保持遇到的顺序），用于交替底色
          groupOrder = [];
          groupMap = {};
          badRows.forEach((r) => {
            const k = r.groupKey || '';
            if (!groupMap[k]) { groupMap[k] = []; groupOrder.push(k); }
            groupMap[k].push(r);
          });
        }
      }

      // (1)(2) 新增审核：去除首尾空格 + 忽略大小写后进行判定
      const normCode = (v:any) => String(v ?? '').trim().toUpperCase();
      const sameRows: Array<{ rowNo: number; old: string; newer: string }> = [];
      const pairMap: Map<string, Array<{ rowNo: number; old: string; newer: string }>> = new Map();
      dataRows.forEach((r, i) => {
        const rowNo = i + 2; // 含表头
        const oldRaw = (r as any)?.[aIdx];
        const newRaw = (r as any)?.[bIdx];
        const oldN = normCode(oldRaw);
        const newN = normCode(newRaw);
        if (!oldN || !newN) return;
        if (oldN === newN) {
          sameRows.push({ rowNo, old: oldN, newer: newN });
        }
        const key = `${oldN}→${newN}`;
        const arr = pairMap.get(key) || [];
        arr.push({ rowNo, old: oldN, newer: newN });
        pairMap.set(key, arr);
      });
      const dupPairs = Array.from(pairMap.entries()).filter(([, arr]) => (arr?.length || 0) >= 2);
      const hasThird = badRows.length > 0;
      const hasFirst = sameRows.length > 0;
      const hasSecond = dupPairs.length > 0;
      if (hasFirst || hasSecond || hasThird) {
        const dupPairsForUi = dupPairs.map(([k, arr]) => ({
          key: String(k),
          count: Array.isArray(arr) ? arr.length : 0,
          rowNos: Array.isArray(arr) ? arr.map((x:any)=> Number(x?.rowNo)).filter((n:number)=>Number.isFinite(n)) : [],
        }));
        setBmAuditData({
          file_name: String(file?.name || ''),
          sameRows,
          dupPairs: dupPairsForUi,
          third: hasThird ? { groupOrder, groupMap } : null,
        });
        setBmAuditOpen(true);
      }
    } catch (e:any) {
      try { console.error('[BarcodeMapping][Check] failed:', e); } catch {}
      message.error('校验失败，请检查文件格式');
    }
  };
  const checkShippingLabelTargetMappingWarnings = async (file: File, binding?: any) => {
    try {
      const name = (file?.name || '').toLowerCase();
      let aoaRows: any[][] = [];
      let startColOffset = 0;
      if (name.endsWith('.csv')) {
        const text = await parseFileAsText(file);
        const Papa = (await import('papaparse')).default;
        const parsed = Papa.parse(text, { header: false, skipEmptyLines: 'greedy' });
        const arr = Array.isArray(parsed?.data) ? parsed.data : [];
        aoaRows = (arr as any[]).filter((r:any) => Array.isArray(r) && r.length > 0);
        try {
          const maxLen = Math.max(...aoaRows.map(r => (Array.isArray(r) ? r.length : 0)));
          if (Number.isFinite(maxLen)) {
            if (maxLen >= 4) startColOffset = 0;
            else if (maxLen === 3) startColOffset = 1;
          }
        } catch {}
      } else if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
        const XLSX = await import('xlsx');
        const buf = await parseFileAsArrayBuffer(file);
        const wb = XLSX.read(buf, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        aoaRows = Array.isArray(aoa) ? (aoa as any[]).filter((r:any)=> Array.isArray(r) && r.length > 0) : [];
        try {
          const ref = (sheet as any)['!ref'] || 'A1:A1';
          const range = (XLSX as any).utils.decode_range(ref);
          const s = range?.s?.c;
          if (Number.isFinite(s)) startColOffset = Number(s);
        } catch {}
      } else {
        return;
      }
      if (!aoaRows || aoaRows.length === 0) return;
      const aAbs = 0, bAbs = 1, cAbs = 2, dAbs = 3;
      const aIdx = aAbs - startColOffset;
      const bIdx = bAbs - startColOffset;
      const cIdx = cAbs - startColOffset;
      const dIdx = dAbs - startColOffset;
      const maxLen = Math.max(...aoaRows.map(r => (Array.isArray(r) ? r.length : 0)));
      const availableCols = Number.isFinite(maxLen) ? (maxLen - startColOffset) : 0;
      if (aIdx < 0 || dIdx < 0 || availableCols < 4) return;
      const toNorm = (v:any) => String(v ?? '').trim();
      const toDupKey = (v:any) => String(v ?? '').trim().toUpperCase();
      const dataRows = aoaRows.slice(1);
      const incompleteRows: Array<{ rowNo: number; a: string; b: string; c: string; d: string }> = [];
      const invalidBRows: Array<{ rowNo: number; b: string }> = [];
      const cMap = new Map<string, number[]>();
      const dMap = new Map<string, number[]>();
      dataRows.forEach((r:any, i:number) => {
        const rowNo = i + 2;
        const a = toNorm(r?.[aIdx]);
        const b = toNorm(r?.[bIdx]);
        const c = toNorm(r?.[cIdx]);
        const d = toNorm(r?.[dIdx]);
        if (!a && !b && !c && !d) return;
        if (!a || !b || (!c && !d)) {
          incompleteRows.push({ rowNo, a, b, c, d });
        }
        if (b && b !== '1') {
          invalidBRows.push({ rowNo, b });
        }
        if (c) {
          const key = toDupKey(c);
          const arr = cMap.get(key) || [];
          arr.push(rowNo);
          cMap.set(key, arr);
        }
        if (d) {
          const key = toDupKey(d);
          const arr = dMap.get(key) || [];
          arr.push(rowNo);
          dMap.set(key, arr);
        }
      });
      const dupC = Array.from(cMap.entries())
        .filter(([, rows]) => (rows?.length || 0) >= 2)
        .map(([value, rowNos]) => ({ value, count: rowNos.length, rowNos }));
      const dupD = Array.from(dMap.entries())
        .filter(([, rows]) => (rows?.length || 0) >= 2)
        .map(([value, rowNos]) => ({ value, count: rowNos.length, rowNos }));
      if (incompleteRows.length > 0 || invalidBRows.length > 0 || dupC.length > 0 || dupD.length > 0) {
        setSltmAuditData({
          file_name: String(file?.name || ''),
          incompleteRows,
          invalidBRows,
          dupC,
          dupD,
        });
        setSltmAuditOpen(true);
      }
    } catch (e:any) {
      try { console.error('[SLTM][Check] failed:', e); } catch {}
      message.warning('运输标签与出库对象对应表格校验失败，请检查文件格式');
    }
  };
  const checkGroupListWarnings = async (file: File, _binding?: any) => {
    try {
      const name = (file?.name || '').toLowerCase();
      const toNorm = (v:any) => String(v ?? '').trim();
      const toCmp = (v:any) => String(v ?? '').trim().toUpperCase();
      if (name.endsWith('.csv')) {
        const text = await parseFileAsText(file);
        const Papa = (await import('papaparse')).default;
        const parsed = Papa.parse(text, { header: false, skipEmptyLines: false });
        const arr = Array.isArray(parsed?.data) ? parsed.data : [];
        const aoaRows = (arr as any[]).filter((r:any) => Array.isArray(r));
        if (!aoaRows || aoaRows.length <= 1) return; // 仅表头或空
        const dataRows = aoaRows.slice(1); // 第一行是表头
        const missingABRows: Array<{ rowNo: number; a: string; b: string }> = [];
        const invalidARows: Array<{ rowNo: number; a: string }> = [];
        const comboRowsByKey = new Map<string, number[]>();
        const bRowsByKey = new Map<string, number[]>();
        dataRows.forEach((r:any, i:number) => {
          const rowNo = i + 2; // 真实表格行号（CSV 第一行为表头）
          const rowArr = Array.isArray(r) ? r : [];
          const hasAnyData = rowArr.some((cell:any) => toNorm(cell) !== '');
          if (!hasAnyData) return;
          const a = toNorm(rowArr?.[0]); // 真正 A 列
          const b = toNorm(rowArr?.[1]); // 真正 B 列
          const aCmp = toCmp(rowArr?.[0]); // 比较值：去空格+不区分大小写
          const bCmp = toCmp(rowArr?.[1]); // 比较值：去空格+不区分大小写
          // 规则1：有数据行必须 A/B 都有值
          if (!a || !b) {
            missingABRows.push({ rowNo, a, b });
          }
          // 规则2：仅在 A 有值时，A 列必须是非0整数
          if (a !== '') {
            const aNum = Number(a);
            const isInt = Number.isInteger(aNum);
            if (!isInt || aNum === 0) {
              invalidARows.push({ rowNo, a });
            }
          }
          // 规则3：分组编号+分拣标识组合不能重复（比较时去空格+不区分大小写）
          if (aCmp && bCmp) {
            const comboKey = `${aCmp}__${bCmp}`;
            const rowsHit = comboRowsByKey.get(comboKey) || [];
            rowsHit.push(rowNo);
            comboRowsByKey.set(comboKey, rowsHit);
          }
          // 规则4：分拣标识不能重复（比较时去空格+不区分大小写）
          if (bCmp) {
            const rowsHit = bRowsByKey.get(bCmp) || [];
            rowsHit.push(rowNo);
            bRowsByKey.set(bCmp, rowsHit);
          }
        });
        const duplicateABGroups = Array.from(comboRowsByKey.values()).filter((x) => x.length > 1).map((rowNos) => ({ rowNos }));
        const duplicateBGroups = Array.from(bRowsByKey.values()).filter((x) => x.length > 1).map((rowNos) => ({ rowNos }));
        if (missingABRows.length > 0 || invalidARows.length > 0 || duplicateABGroups.length > 0 || duplicateBGroups.length > 0) {
          setGroupListAuditData({
            file_name: String(file?.name || ''),
            missingABRows,
            invalidARows,
            duplicateABGroups,
            duplicateBGroups,
          });
          setGroupListAuditOpen(true);
        }
      } else if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
        const XLSX = await import('xlsx');
        const buf = await parseFileAsArrayBuffer(file);
        const wb = XLSX.read(buf, { type: 'array' });
        const firstSheetName = Array.isArray(wb?.SheetNames) && wb.SheetNames.length > 0 ? wb.SheetNames[0] : '';
        if (!firstSheetName) return;
        const sheet = wb.Sheets[firstSheetName];
        const ref = String((sheet as any)?.['!ref'] || '').trim();
        if (!ref) return;
        const range = XLSX.utils.decode_range(ref);
        if (!range || !Number.isFinite(range?.e?.r)) return;
        // 固定从“真实 A2 单元格”开始校验：r=1（0-based），无视 UsedRange 起始列/行
        const startRow = 1;
        const endRow = Number(range.e.r);
        if (endRow < startRow) return;
        const endCol = Math.max(1, Number(range.e.c || 0));
        const getCellText = (r:number, c:number) => {
          try {
            const addr = XLSX.utils.encode_cell({ r, c });
            const cell: any = (sheet as any)?.[addr];
            return toNorm(cell?.w ?? cell?.v ?? '');
          } catch {
            return '';
          }
        };
        const missingABRows: Array<{ rowNo: number; a: string; b: string }> = [];
        const invalidARows: Array<{ rowNo: number; a: string }> = [];
        const comboRowsByKey = new Map<string, number[]>();
        const bRowsByKey = new Map<string, number[]>();
        for (let rr = startRow; rr <= endRow; rr += 1) {
          const rowNo = rr + 1; // 转成 1-based 行号
          let hasAnyData = false;
          for (let cc = 0; cc <= endCol; cc += 1) {
            if (getCellText(rr, cc) !== '') { hasAnyData = true; break; }
          }
          if (!hasAnyData) continue;
          const a = getCellText(rr, 0); // 真正 A 列
          const b = getCellText(rr, 1); // 真正 B 列
          const aCmp = toCmp(a); // 比较值：去空格+不区分大小写
          const bCmp = toCmp(b); // 比较值：去空格+不区分大小写
          // 规则1：有数据行必须 A/B 都有值
          if (!a || !b) {
            missingABRows.push({ rowNo, a, b });
          }
          // 规则2：仅在 A 有值时，A 列必须是非0整数
          if (a !== '') {
            const aNum = Number(a);
            const isInt = Number.isInteger(aNum);
            if (!isInt || aNum === 0) {
              invalidARows.push({ rowNo, a });
            }
          }
          // 规则3：分组编号+分拣标识组合不能重复（比较时去空格+不区分大小写）
          if (aCmp && bCmp) {
            const comboKey = `${aCmp}__${bCmp}`;
            const rowsHit = comboRowsByKey.get(comboKey) || [];
            rowsHit.push(rowNo);
            comboRowsByKey.set(comboKey, rowsHit);
          }
          // 规则4：分拣标识不能重复（比较时去空格+不区分大小写）
          if (bCmp) {
            const rowsHit = bRowsByKey.get(bCmp) || [];
            rowsHit.push(rowNo);
            bRowsByKey.set(bCmp, rowsHit);
          }
        }
        const duplicateABGroups = Array.from(comboRowsByKey.values()).filter((x) => x.length > 1).map((rowNos) => ({ rowNos }));
        const duplicateBGroups = Array.from(bRowsByKey.values()).filter((x) => x.length > 1).map((rowNos) => ({ rowNos }));
        if (missingABRows.length > 0 || invalidARows.length > 0 || duplicateABGroups.length > 0 || duplicateBGroups.length > 0) {
          setGroupListAuditData({
            file_name: String(file?.name || ''),
            missingABRows,
            invalidARows,
            duplicateABGroups,
            duplicateBGroups,
          });
          setGroupListAuditOpen(true);
        }
      } else {
        return;
      }
    } catch (e:any) {
      try { console.error('[GroupList][Check] failed:', e); } catch {}
      message.warning('分组列表附件校验失败，请检查文件格式');
    }
  };

  const hasRequiredQuestions = React.useMemo(() => {
    try { return Array.isArray(visibleQRows) && visibleQRows.some((q:any)=> isQuestionRequired(q)); } catch { return false; }
  }, [visibleQRows, isQuestionRequired]);
  const visibleRuleRows = React.useMemo(() => {
    try {
      return (ruleRows || []).filter(isAttachmentVisible);
    } catch { return []; }
  }, [ruleRows, isAttachmentVisible]);
  const hasRequiredAttachments = React.useMemo(() => (visibleRuleRows || []).some((r:any) => Number(r.min ?? 0) >= 1), [visibleRuleRows]);
  // 页面挂载后：当 URL（模板）已就绪且模板详情已回填后，恢复草稿
  const restoredRef = React.useRef(false);
  const draftReadyRef = React.useRef(false);
  // 编辑模式不使用草稿恢复，直接从后端回填
  useEffect(() => {
    if (isEditMode) {
      try { restoredRef.current = true; draftReadyRef.current = true; } catch {}
    }
  }, [isEditMode]);
  useEffect(() => {
    if (restoredRef.current) return;
    const tpl = form.getFieldValue('template');
    if (!tpl) return; // 等待 URL → 表单完成
    if (tpl && !tplDetailState && tpl !== '__NONE__') return; // 若需要模板详情，等加载完成
    restoredRef.current = true;
    (async () => { await restoreAllDraft(); draftReadyRef.current = true; })();
  }, [form, tplDetailState]);
  // 任一关键数据变化时，触发节流保存
  useEffect(() => { scheduleSaveDraft(); }, [targets]);
  useEffect(() => { scheduleSaveDraft(); }, [answers]);
  useEffect(() => { scheduleSaveDraft(); }, [attachFilesMap]);
  useEffect(() => { scheduleSaveDraft(); }, [attachPrintConfigMap]);
  // 标签切换与可见性变化时，强制保存
  useEffect(() => {
    const handler = () => { saveAllDraft(); };
    (window as any).addEventListener('tabs:beforeSwitch', handler);
    (window as any).document.addEventListener('visibilitychange', handler);
    return () => {
      (window as any).removeEventListener('tabs:beforeSwitch', handler);
      (window as any).document.removeEventListener('visibilitychange', handler);
    };
  }, []);
  const downloadTemplate = async (id: number, name?: string) => {
    try {
      const resp = await request.get(`/api/v1/instruction/attachment-templates/${id}/download/`, { responseType: 'blob' as any });
      const blob: Blob = resp.data as any;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const disposition = String(resp?.headers?.['content-disposition'] || '');
      const fileNameMatch = disposition.match(/filename\*?=(?:UTF-8''|")?([^\";]+)(?:\"|;|$)/i);
      const headerName = fileNameMatch ? decodeURIComponent(fileNameMatch[1]) : '';
      a.href = url;
      a.download = (headerName && headerName.trim()) ? headerName.trim() : ((name && name.trim()) ? name.trim() : `附件模板-${id}`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error('下载附件模板失败');
    }
  };
  // 解析后端错误信息（兼容 DRF 常见结构）
  const extractErrorMessage = (err: any): string => {
    try {
      const resp = err?.response;
      const data = resp?.data;
      if (!data) return err?.message || '请求失败';
      if (typeof data === 'string') return data;
      if (Array.isArray(data)) return data.map((x:any)=>String(x)).join('\n');
      if (typeof data === 'object') {
        const lines: string[] = [];
        Object.keys(data).forEach((k) => {
          const v: any = (data as any)[k];
          if (Array.isArray(v)) {
            v.forEach((m:any) => lines.push(`${k}: ${String(m)}`));
          } else if (typeof v === 'string') {
            lines.push(`${k}: ${v}`);
          } else if (v && typeof v === 'object') {
            lines.push(`${k}: ${JSON.stringify(v)}`);
          }
        });
        return lines.join('\n') || (err?.message || '请求失败');
      }
      return err?.message || '请求失败';
    } catch {
      return err?.message || '请求失败';
    }
  };
  // 弹窗展示详细错误
  const showCreateErrorModal = (err: any, payload: any) => {
    try {
      const status = err?.response?.status;
      const method = String(err?.config?.method || '').toUpperCase();
      const url = String(err?.config?.url || '');
      const data = err?.response?.data;
      const prettyData = data ? JSON.stringify(data, null, 2) : '';
      Modal.error({
        title: '创建失败',
        width: 760,
        maskClosable: true,
        content: (
          <div style={{ maxHeight: 420, overflow: 'auto' }}>
            <div style={{ marginBottom: 8, color: '#595959' }}>{`HTTP ${status || '-'} · ${method || 'POST'} ${url || ''}`}</div>
            <div style={{ whiteSpace: 'pre-wrap', background: '#fffbe6', border: '1px solid #ffe58f', padding: 8, borderRadius: 4, color: '#ad6800' }}>
              {extractErrorMessage(err)}
            </div>
            {prettyData ? (
              <>
                <div style={{ marginTop: 12, fontWeight: 600 }}>响应数据</div>
                <pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 4, overflow: 'auto' }}>{prettyData}</pre>
              </>
            ) : null}
            <div style={{ marginTop: 12, fontWeight: 600 }}>请求载荷（关键字段）</div>
            <pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 4, overflow: 'auto' }}>{JSON.stringify({
              project_id: payload?.project_id,
              warehouse_id: payload?.warehouse_id,
              template: payload?.template,
              targets_len: Array.isArray(payload?.targets) ? payload.targets.length : 0,
              has_customer_answers: !!payload?.customer_answers && Object.keys(payload?.customer_answers || {}).length > 0,
            }, null, 2)}</pre>
          </div>
        ),
      });
    } catch {}
  };
  const setFilesForRule = (key:string, files: UploadFile<any>[]) => {
    // 服务器入口限制单次上传 50MB：超过的文件在这里就拒绝并说明原因，不能等上传时静默失败
    const { accepted, rejected } = splitOversizedFiles(files || []);
    if (rejected.length > 0) {
      showOversizeDialog(rejected as any[]);
    }
    setAttachFilesMap(prev => ({ ...prev, [key]: accepted }));
  };
  const CUSTOM_ATTACH_KEY = 'custom';
  const PRINT_PAPER_OPTIONS = [{ value: '4x6_thermal', label: '4×6英寸热敏纸打印' }, { value: 'A4', label: 'A4激光打印' }];
  const PRINT_PLACEMENT_OPTIONS = [{ value: 'inside', label: '放入包裹内' }, { value: 'outside', label: '贴在包裹外' }];
  const setAttachPrintConfig = (ruleKey: string, fileUid: string, partial: Partial<{ print?: boolean; copies?: number; paper_size?: string; placement?: string; explicit_no_print?: boolean }>) => {
    setAttachPrintConfigMap(prev => {
      const cur = prev[ruleKey] || {};
      const existing = cur[fileUid] || {};
      return { ...prev, [ruleKey]: { ...cur, [fileUid]: { ...existing, ...partial } } };
    });
  };
  // 动作级附件不在创建阶段上传，移除动作级文件列表
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  // 附件在线预览：还没提交的文件在浏览器内存里，用临时地址（blob:）预览；编辑已有指令时已上传的附件直接用服务器地址。
  // 复用非标指令的预览弹窗：图片、视频、PDF、Excel/CSV 表格（每页数量可调，视频关闭即停）
  const [localPreview, setLocalPreview] = useState<MediaPreviewTarget | null>(null);
  const localPreviewBlobRef = React.useRef<string>('');
  const closeLocalPreview = React.useCallback(() => {
    const url = localPreviewBlobRef.current;
    localPreviewBlobRef.current = '';
    setLocalPreview(null);
    // 稍后再释放临时地址，给弹窗停止播放/收尾留出时间
    if (url) window.setTimeout(() => { try { URL.revokeObjectURL(url); } catch { /* 已释放 */ } }, 600);
  }, []);
  React.useEffect(() => () => {
    const url = localPreviewBlobRef.current;
    localPreviewBlobRef.current = '';
    if (url) { try { URL.revokeObjectURL(url); } catch { /* 已释放 */ } }
  }, []);
  const openLocalPreview = React.useCallback((f: any) => {
    const name = String(f?.name || f?.originFileObj?.name || '附件');
    const file: File | undefined = f?.originFileObj || (typeof File !== 'undefined' && f instanceof File ? f : undefined);
    const open = () => {
      closeLocalPreview();
      if (file) {
        const url = URL.createObjectURL(file);
        localPreviewBlobRef.current = url;
        setLocalPreview({ url, name });
      } else if (f?.url) {
        setLocalPreview({ url: String(f.url), name });
      } else {
        message.warning('该附件暂时无法预览');
      }
    };
    // 大表格：预览要把整个文件读进浏览器，可能卡顿；提醒后由用户决定
    if (file && previewKindOf(name) === 'sheet' && file.size > BIG_SHEET_BYTES) {
      Modal.confirm({
        title: '表格文件较大',
        content: `${name} 约 ${(file.size / 1024 / 1024).toFixed(1)} MB，在线预览要把整个文件读进浏览器，可能会卡顿，而且只显示前 2000 行。提交后也可以在指令详情页查看。`,
        okText: '仍要预览',
        cancelText: '取消',
        onOk: open,
      });
      return;
    }
    open();
  }, [closeLocalPreview]);
  // 文件名后面的“预览”：图片、视频、PDF、Excel/CSV 才有；Word、压缩包等不支持在线预览，不显示
  const renderLocalPreviewLink = (f: any) => (
    previewKindOf(String(f?.name || f?.originFileObj?.name || ''))
      ? <a className="attach-preview-link" style={{ fontSize: 12 }} onClick={() => openLocalPreview(f)}>预览</a>
      : null
  );
  // BARCODE_MAPPING 内容审核弹窗（聚合展示 + 支持下载PDF）
  const [bmAuditOpen, setBmAuditOpen] = useState<boolean>(false);
  const [bmAuditData, setBmAuditData] = useState<any>(null);
  const [sltmAuditOpen, setSltmAuditOpen] = useState<boolean>(false);
  const [sltmAuditData, setSltmAuditData] = useState<any>(null);
  const [groupListAuditOpen, setGroupListAuditOpen] = useState<boolean>(false);
  const [groupListAuditData, setGroupListAuditData] = useState<any>(null);
  const copySltmAuditWarnText = useCallback(async () => {
    try {
      const d = sltmAuditData || {};
      const fileName = String(d?.file_name || '-');
      const incompleteRows: any[] = Array.isArray(d?.incompleteRows) ? d.incompleteRows : [];
      const invalidBRows: any[] = Array.isArray(d?.invalidBRows) ? d.invalidBRows : [];
      const dupC: any[] = Array.isArray(d?.dupC) ? d.dupC : [];
      const dupD: any[] = Array.isArray(d?.dupD) ? d.dupD : [];
      const hasAny = incompleteRows.length > 0 || invalidBRows.length > 0 || dupC.length > 0 || dupD.length > 0;
      if (!hasAny) { message.warning('暂无可复制的警告信息'); return; }
      const title = '运输标签与出库对象对应表格附件内容审核 警告信息';
      const ts = new Date().toLocaleString();
      const blocks: string[] = [];
      if (incompleteRows.length > 0) {
        blocks.push(
          `审核内容项1：记录不完整（A列/B列必须有值，且C列/D列至少一列有值）\n` +
          incompleteRows.map((r:any) => `行号:${r.rowNo} A:${r.a || '-'} B:${r.b || '-'} C:${r.c || '-'} D:${r.d || '-'}`).join('\n')
        );
      }
      if (invalidBRows.length > 0) {
        blocks.push(
          `审核内容项2：B列值必须为 1\n` +
          invalidBRows.map((r:any) => `行号:${r.rowNo} B:${r.b || '-'}`).join('\n')
        );
      }
      if (dupC.length > 0) {
        blocks.push(
          `审核内容项3：C列值重复（已移除首尾空格，忽略大小写）\n` +
          dupC.map((it:any) => `值:${String(it?.value || '-')} 次数:${Number(it?.count || 0)} 行号:${Array.isArray(it?.rowNos) ? it.rowNos.join('、') : ''}`).join('\n')
        );
      }
      if (dupD.length > 0) {
        blocks.push(
          `审核内容项4：D列值重复（已移除首尾空格，忽略大小写）\n` +
          dupD.map((it:any) => `值:${String(it?.value || '-')} 次数:${Number(it?.count || 0)} 行号:${Array.isArray(it?.rowNos) ? it.rowNos.join('、') : ''}`).join('\n')
        );
      }
      const text = [
        title,
        `生成时间：${ts}`,
        `文件：${fileName}`,
        '',
        ...blocks,
      ].join('\n\n');
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const el = document.createElement('textarea');
        el.value = text;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      message.success('已复制警告信息');
    } catch {
      message.error('复制失败');
    }
  }, [sltmAuditData]);
  const copyGroupListAuditWarnText = useCallback(async () => {
    try {
      const d = groupListAuditData || {};
      const fileName = String(d?.file_name || '-');
      const missingABRows: any[] = Array.isArray(d?.missingABRows) ? d.missingABRows : [];
      const invalidARows: any[] = Array.isArray(d?.invalidARows) ? d.invalidARows : [];
      const duplicateABGroups: any[] = Array.isArray(d?.duplicateABGroups) ? d.duplicateABGroups : [];
      const duplicateBGroups: any[] = Array.isArray(d?.duplicateBGroups) ? d.duplicateBGroups : [];
      const hasAny = missingABRows.length > 0 || invalidARows.length > 0 || duplicateABGroups.length > 0 || duplicateBGroups.length > 0;
      if (!hasAny) { message.warning('暂无可复制的警告信息'); return; }
      const title = '分组列表附件内容审核 警告信息';
      const ts = new Date().toLocaleString();
      const blocks: string[] = [];
      if (missingABRows.length > 0) {
        blocks.push(
          '审核内容项1：有数据的行必须 A 列和 B 列都有值\n' +
          missingABRows.map((r:any) => `行号:${r.rowNo} A:${r.a || '-'} B:${r.b || '-'}`).join('\n')
        );
      }
      if (invalidARows.length > 0) {
        blocks.push(
          '审核内容项2：有效行 A 列必须是非0整数（从第2行开始）\n' +
          invalidARows.map((r:any) => `行号:${r.rowNo} A:${r.a || '-'}`).join('\n')
        );
      }
      if (duplicateABGroups.length > 0) {
        blocks.push(
          '审核内容项3：分组编号+分拣标识组合不能重复（比较时已去除首尾空格且不区分大小写）\n' +
          duplicateABGroups.map((it:any) => `重复行号:${Array.isArray(it?.rowNos) ? it.rowNos.join('、') : ''}`).join('\n')
        );
      }
      if (duplicateBGroups.length > 0) {
        blocks.push(
          '审核内容项4：分拣标识不能重复（一个分拣标识只能属于一个分组编号；比较时已去除首尾空格且不区分大小写）\n' +
          duplicateBGroups.map((it:any) => `重复行号:${Array.isArray(it?.rowNos) ? it.rowNos.join('、') : ''}`).join('\n')
        );
      }
      const text = [
        title,
        `生成时间：${ts}`,
        `文件：${fileName}`,
        '',
        ...blocks,
      ].join('\n\n');
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const el = document.createElement('textarea');
        el.value = text;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      message.success('已复制警告信息');
    } catch {
      message.error('复制失败');
    }
  }, [groupListAuditData]);
  const downloadBmAuditPdf = useCallback(() => {
    try {
      const d = bmAuditData || {};
      if (!d || (typeof d === 'object' && Object.keys(d).length === 0)) {
        message.warning('暂无错误信息可下载');
        return;
      }
      const title = '新旧条码对应表附件内容审核 错误提示';
      const fileName = String(d?.file_name || '').trim();
      const sameRows: any[] = Array.isArray(d?.sameRows) ? d.sameRows : [];
      const dupPairs: any[] = Array.isArray(d?.dupPairs) ? d.dupPairs : [];
      const third = d?.third || null;
      const groupOrder: string[] = Array.isArray(third?.groupOrder) ? third.groupOrder : [];
      const groupMap: Record<string, any[]> = (third?.groupMap && typeof third.groupMap === 'object') ? third.groupMap : {};

      const esc = (s:any) => String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

      const now = new Date();
      const ts = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;

      const sectionTitleStyle = 'font-weight:700;color:#ff7875;margin:10px 0 6px 0;';
      const tableStyle = 'width:100%;border-collapse:collapse;';
      const thStyle = 'border:1px solid #ddd;padding:4px;text-align:center;background:#fafafa;';
      const tdStyle = 'border:1px solid #ddd;padding:4px;text-align:center;';
      const hr = '<div style="border-top:2px solid #f0f0f0;margin:12px 0;"></div>';

      const renderSame = () => {
        if (sameRows.length === 0) return '';
        const rows = sameRows.map((r:any) => (
          `<tr>
            <td style="${tdStyle}">${esc(r.rowNo)}</td>
            <td style="${tdStyle}">${esc(r.old)}</td>
            <td style="${tdStyle}">${esc(r.newer)}</td>
          </tr>`
        )).join('');
        return `
          <div style="${sectionTitleStyle}">1 新旧条码相同（已移除首尾空格，忽略大小写）</div>
          <table style="${tableStyle}">
            <thead><tr>
              <th style="${thStyle}">行号</th>
              <th style="${thStyle}">旧条码(A列)</th>
              <th style="${thStyle}">新条码(B列)</th>
            </tr></thead>
            <tbody>${rows}</tbody>
          </table>
        `;
      };
      const renderDupPairs = () => {
        if (dupPairs.length === 0) return '';
        const rows = dupPairs.map((it:any, i:number) => {
          const bg = i % 2 === 0 ? '#e6f4ff' : '#fff7e6';
          return `<tr style="background:${bg}">
            <td style="${tdStyle}">${esc(it.key)}</td>
            <td style="${tdStyle}">${esc(it.count)}</td>
            <td style="${tdStyle}">${esc((it.rowNos || []).join('、'))}</td>
          </tr>`;
        }).join('');
        return `
          <div style="${sectionTitleStyle}">2 新旧条码组合相同（已移除首尾空格，忽略大小写）</div>
          <table style="${tableStyle}">
            <thead><tr>
              <th style="${thStyle}">组合</th>
              <th style="${thStyle}">出现次数</th>
              <th style="${thStyle}">行号</th>
            </tr></thead>
            <tbody>${rows}</tbody>
          </table>
        `;
      };
      const renderThird = () => {
        if (!third || groupOrder.length === 0) return '';
        const body = groupOrder.map((gk:string, gi:number) => {
          const rowsInGroup = groupMap[gk] || [];
          const bg = gi % 2 === 0 ? '#e6f7ff' : '#fff7e6';
          return rowsInGroup.map((r:any, i:number) => (
            `<tr style="background:${bg}">
              <td style="${tdStyle}">${esc(r.rowNo)}</td>
              <td style="${tdStyle}">${esc(r.a)}</td>
              <td style="${tdStyle}">${esc(r.b)}</td>
              <td style="${tdStyle}">${esc(r.c)}</td>
              <td style="${tdStyle}">${esc(r.d)}</td>
              <td style="${tdStyle}">${esc(r.e)}</td>
            </tr>`
          )).join('');
        }).join('');
        return `
          <div style="${sectionTitleStyle}">3 一个旧条码对应多个新条码（且数量列无效）</div>
          <div style="margin-bottom:8px;color:#8c8c8c">
            若一个旧条码需要更换为多个新条码，请在D列“数量分配”中指定每个新条码的数量，请参考模板说明第8条。
          </div>
          <table style="${tableStyle}">
            <thead><tr>
              <th style="${thStyle}">行号</th>
              <th style="${thStyle}">A列-旧条码</th>
              <th style="${thStyle}">B列-新条码</th>
              <th style="${thStyle}">C列-新条码文本</th>
              <th style="${thStyle}">D列-数量分配</th>
              <th style="${thStyle}">E列-备注</th>
            </tr></thead>
            <tbody>${body}</tbody>
          </table>
        `;
      };

      const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${esc(title)}</title>
    <style>
      body{font-family:Arial,Helvetica,sans-serif;padding:16px;color:#000;}
      .hdr{margin-bottom:12px;}
      .hdr h2{margin:0 0 6px 0;}
      .meta{color:#595959;font-size:12px;}
      @media print { a{display:none;} }
    </style>
  </head>
  <body>
    <div class="hdr">
      <h2>${esc(title)}</h2>
      <div class="meta">生成时间：${esc(ts)}${fileName ? ` · 文件：${esc(fileName)}` : ''}</div>
    </div>
    ${renderSame()}
    ${(sameRows.length && (dupPairs.length || (third && groupOrder.length))) ? hr : ''}
    ${renderDupPairs()}
    ${(dupPairs.length && (third && groupOrder.length)) ? hr : ''}
    ${renderThird()}
  </body>
</html>`;

      // 注意：带 noreferrer 的 window.open 在部分浏览器会导致无法写入内容（出现空白页）
      const w = window.open('about:blank', '_blank');
      if (!w) { message.error('浏览器阻止了弹窗，请允许弹窗后重试'); return; }
      try {
        try { (w as any).opener = null; } catch {}
        w.document.open();
        w.document.write(html);
        w.document.close();
        // 方案1：调用打印对话框，用户可直接选择“另存为 PDF”
        const doPrint = () => {
          try { w.focus(); } catch {}
          try { w.onafterprint = () => { try { w.close(); } catch {} }; } catch {}
          try { w.print(); } catch {}
        };
        // 等待渲染完成再打印（避免空白页）
        try { (w as any).onload = () => setTimeout(doPrint, 50); } catch {}
        setTimeout(doPrint, 300);
      } catch {
        try { w.close(); } catch {}
        message.error('生成PDF失败');
      }
    } catch {
      message.error('生成PDF失败');
    }
  }, [bmAuditData]);

  // 编辑目标弹窗
  const [editOpen, setEditOpen] = useState(false);
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [editMode, setEditMode] = useState<'all' | 'number'>('all');
  const [editQty, setEditQty] = useState<number | undefined>(undefined);
  // 批量操作
  const [selectedTargetKeys, setSelectedTargetKeys] = useState<React.Key[]>([]);
  const hasSelectedTargets = React.useMemo(() => (selectedTargetKeys || []).length > 0, [selectedTargetKeys]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState<'all' | 'number'>('all');
  const [bulkQty, setBulkQty] = useState<number | undefined>(undefined);
  const [tplDescOpen, setTplDescOpen] = useState(false);
  const [packingInfoOpen, setPackingInfoOpen] = useState(false);
  // 管理员容器检测与"添加操作对象"弹窗专用客户/项目联动
  const isAdminContainer = React.useMemo(() => {
    try { return String((window as any)?.location?.pathname || '').startsWith('/dashboard/admin'); } catch { return false; }
  }, []);
  const [adminCustomerOptions, setAdminCustomerOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [adminCustomerLoading, setAdminCustomerLoading] = useState(false);
  const [adminProjectOptions, setAdminProjectOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [adminProjectLoading, setAdminProjectLoading] = useState(false);
  const ensureAdminCustomers = async () => {
    if (!isAdminContainer) return;
    if (adminCustomerOptions.length > 0) return;
    setAdminCustomerLoading(true);
    try {
      const list = await getCustomerList({ onlyMain: true } as any);
      const opts = (list || []).map((c: any) => {
        const code = c.customer_code || c.profile?.customer_code || '';
        const name = c.name || c.username || '';
        const label = code ? `${name} · ${code}` : name;
        return { value: Number(c.id), label };
      });
      setAdminCustomerOptions(opts);
    } catch { setAdminCustomerOptions([]); } finally { setAdminCustomerLoading(false); }
  };
  const loadAdminProjects = async (customerId?: number) => {
    if (!isAdminContainer) return;
    if (!customerId) { setAdminProjectOptions([]); return; }
    setAdminProjectLoading(true);
    try {
      const resp: any = await getProjectList({ page: 1, page_size: 200, search: '', customer: customerId } as any);
      const results = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []));
      const opts = (results || []).map((p: any) => ({ value: Number(p.id), label: `${p.project_code || p.id} - ${p.name || ''}` }));
      setAdminProjectOptions(opts);
    } catch { setAdminProjectOptions([]); } finally { setAdminProjectLoading(false); }
  };
  // tabId 用于草稿隔离
  const getTabId = (): string => {
    try {
      const sp = new URLSearchParams((window as any).location.search || '');
      return sp.get('tabId') || 'tab_default';
    } catch { return 'tab_default'; }
  };
  const tabIdRef = React.useRef<string>(getTabId());
  // 草稿存储键
  const DRAFT_TARGETS_KEY = React.useMemo(() => `ci_targets_${tabIdRef.current}`, []);
  const DRAFT_ANSWERS_KEY = React.useMemo(() => `ci_answers_${tabIdRef.current}`, []);
  const DRAFT_ATTACH_META_KEY = React.useMemo(() => `ci_attach_meta_${tabIdRef.current}`, []);
  const DRAFT_PRINT_CONFIG_KEY = React.useMemo(() => `ci_print_config_${tabIdRef.current}`, []);
  const DRAFT_META_KEY = React.useMemo(() => `ci_draft_meta_${tabIdRef.current}`, []);
  // IndexedDB 基础方法（files 存 Blob）
  const openDraftDB = (): Promise<IDBDatabase> => new Promise((resolve, reject) => {
    try {
      const req = (window as any).indexedDB.open('instruction_draft_db_v1', 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains('files')) {
          db.createObjectStore('files', { keyPath: 'key' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    } catch (e) { reject(e); }
  });
  const idbPut = async (key: string, data: { blob: Blob; name: string; type?: string; size?: number }) => {
    const db = await openDraftDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction('files', 'readwrite');
      const store = tx.objectStore('files');
      store.put({ key, ...data });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  };
  const idbGet = async (key: string): Promise<{ blob: Blob; name: string; type?: string; size?: number } | undefined> => {
    const db = await openDraftDB();
    return await new Promise((resolve) => {
      const tx = db.transaction('files', 'readonly');
      const store = tx.objectStore('files');
      const req = store.get(key);
      req.onsuccess = () => resolve(req.result as any);
      req.onerror = () => resolve(undefined);
    });
  };
  const idbDeleteByPrefix = async (prefix: string) => {
    const db = await openDraftDB();
    await new Promise<void>((resolve) => {
      const tx = db.transaction('files', 'readwrite');
      const store = tx.objectStore('files');
      const req = store.openCursor();
      req.onsuccess = (ev: any) => {
        const cursor = ev.target.result as IDBCursorWithValue | null;
        if (!cursor) return;
        const key = String(cursor.key || '');
        if (key.startsWith(prefix)) {
          cursor.delete();
        }
        cursor.continue();
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  };
  // 保存/恢复草稿（targets/answers/attachments）
  const draftTimer = React.useRef<number | null>(null);
  const scheduleSaveDraft = () => {
    try { if (draftTimer.current) { (window as any).clearTimeout(draftTimer.current); } } catch {}
    draftTimer.current = (window as any).setTimeout(async () => {
      await saveAllDraft();
    }, 500);
  };
  const saveAllDraft = async () => {
    try {
      // 1) targets
      try { (window as any).sessionStorage.setItem(DRAFT_TARGETS_KEY, JSON.stringify(targets || [])); } catch {}
      // 2) answers
      try { (window as any).sessionStorage.setItem(DRAFT_ANSWERS_KEY, JSON.stringify(answers || {})); } catch {}
      // 3) attachments 元信息 + Blob 入库
      const meta: Record<string, Array<{ uid: string; name: string; type?: string; size?: number }>> = {};
      const entries = Object.entries(attachFilesMap || {});
      for (const [ruleKey, files] of entries) {
        const arr: Array<{ uid: string; name: string; type?: string; size?: number }> = [];
        for (const f of (files || [])) {
          const name = String((f as any).name || '附件');
          const uid = String((f as any).uid || name);
          const size = Number((f as any).size || 0) || undefined;
          const type = String((f as any).type || '') || undefined;
          arr.push({ uid, name, type, size });
          const blob = (f as any).originFileObj as Blob | undefined;
          if (blob) {
            const key = `${tabIdRef.current}:${ruleKey}:${uid}`;
            await idbPut(key, { blob, name, type, size });
          }
        }
        meta[ruleKey] = arr;
      }
      try { (window as any).sessionStorage.setItem(DRAFT_ATTACH_META_KEY, JSON.stringify(meta)); } catch {}
      try { (window as any).sessionStorage.setItem(DRAFT_PRINT_CONFIG_KEY, JSON.stringify(attachPrintConfigMap || {})); } catch {}
      // 4) meta（模板绑定，避免串模板）
      try {
        const tpl = form.getFieldValue('template');
        (window as any).sessionStorage.setItem(DRAFT_META_KEY, JSON.stringify({ template: tpl || '' }));
      } catch {}
    } catch {}
  };
  const restoreAllDraft = async () => {
    try {
      const metaRaw = (window as any).sessionStorage.getItem(DRAFT_META_KEY) || '';
      let meta: any = {};
      try { meta = metaRaw ? JSON.parse(metaRaw) : {}; } catch { meta = {}; }
      const currentTpl = form.getFieldValue('template');
      if (meta && meta.template && currentTpl && String(meta.template) !== String(currentTpl)) {
        // 模板不同，不恢复附件/答案（避免串模板），只恢复 targets
      }
      // 1) targets
      try {
        const raw = (window as any).sessionStorage.getItem(DRAFT_TARGETS_KEY) || '[]';
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setTargets(parsed);
      } catch {}
      // 2) answers（仅当模板一致时）
      if (!meta.template || !currentTpl || String(meta.template) === String(currentTpl)) {
        try {
          const raw = (window as any).sessionStorage.getItem(DRAFT_ANSWERS_KEY) || '{}';
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') setAnswers(parsed);
        } catch {}
      }
      // 3) attachments（元信息 + Blob）仅当模板一致时
      if (!meta.template || !currentTpl || String(meta.template) === String(currentTpl)) {
        try {
          const raw = (window as any).sessionStorage.getItem(DRAFT_ATTACH_META_KEY) || '{}';
          const parsed: Record<string, Array<{ uid: string; name: string }>> = JSON.parse(raw);
          const newMap: Record<string, UploadFile<any>[]> = {};
          const promises: Array<Promise<void>> = [];
          Object.entries(parsed || {}).forEach(([ruleKey, arr]) => {
            const list: UploadFile<any>[] = [];
            (arr || []).forEach((item) => {
              const p = (async () => {
                const storeKey = `${tabIdRef.current}:${ruleKey}:${item.uid}`;
                const got = await idbGet(storeKey);
                if (got && got.blob) {
                  const fileLike = new File([got.blob], item.name || '附件', { type: got.type || got.blob.type || 'application/octet-stream' });
                  const up: UploadFile<any> = {
                    uid: item.uid,
                    name: item.name,
                    status: 'done',
                    size: got.size || got.blob.size,
                    type: got.type || got.blob.type,
                    originFileObj: fileLike
                  } as any;
                  list.push(up);
                }
              })();
              promises.push(p);
            });
            newMap[ruleKey] = list;
          });
          await Promise.all(promises);
          setAttachFilesMap(newMap);
          try {
            const rawPc = (window as any).sessionStorage.getItem(DRAFT_PRINT_CONFIG_KEY) || '{}';
            const pcParsed = JSON.parse(rawPc || '{}');
            if (pcParsed && typeof pcParsed === 'object') setAttachPrintConfigMap(pcParsed);
          } catch {}
        } catch {}
      }
    } catch {}
  };
  const clearDraft = async () => {
    try {
      (window as any).sessionStorage.removeItem(DRAFT_TARGETS_KEY);
      (window as any).sessionStorage.removeItem(DRAFT_ANSWERS_KEY);
      (window as any).sessionStorage.removeItem(DRAFT_ATTACH_META_KEY);
      (window as any).sessionStorage.removeItem(DRAFT_PRINT_CONFIG_KEY);
      (window as any).sessionStorage.removeItem(DRAFT_META_KEY);
      await idbDeleteByPrefix(`${tabIdRef.current}:`);
    } catch {}
  };
  // 项目映射
  const projectIdToLabel = React.useMemo(() => {
    const m = new Map<number, string>();
    try {
      (projects || []).forEach((p:any) => {
        const id = Number(p.id);
        if (!Number.isNaN(id)) m.set(id, `${p.project_code || id} - ${p.name || ''}`);
      });
    } catch {}
    return m;
  }, [projects]);
  // 项目 -> 客户 映射（用于模板过滤）
  const projectIdToCustomerId = React.useMemo(() => {
    const m = new Map<number, number>();
    try {
      (projects || []).forEach((p:any) => {
        const id = Number(p.id);
        const cid = Number((p as any)?.customer_id ?? (p as any)?.customer?.id);
        if (!Number.isNaN(id) && !Number.isNaN(cid)) m.set(id, cid);
      });
    } catch {}
    return m;
  }, [projects]);
  // 基础客户ID：用于在未选择操作对象前，先展示系统级 + 客户级模板
  const [baseCustomerId, setBaseCustomerId] = useState<number | undefined>(undefined);
  // 基于可访问项目，若只归属一个客户，则预设该客户ID
  useEffect(() => {
    try {
      const setIds = new Set<number>();
      (projects || []).forEach((p:any) => {
        const cid = Number((p as any)?.customer_id ?? (p as any)?.customer?.id);
        if (!Number.isNaN(cid)) setIds.add(cid);
      });
      if (setIds.size === 1) {
        const only = Array.from(setIds)[0];
        if (Number.isFinite(only)) setBaseCustomerId(only);
      }
    } catch {}
  }, [projects]);
  // 依据"已选操作对象的项目/客户"过滤模板：只显示 system 级，或属于这些项目/客户的 customer/project 级
  const filteredTemplates = React.useMemo(() => {
    try {
      const projIds = new Set<number>();
      (targets || []).forEach((t:any) => { const pid = Number(t?.project_id); if (!Number.isNaN(pid)) projIds.add(pid); });
      const custIds = new Set<number>();
      projIds.forEach(pid => { const cid = projectIdToCustomerId.get(pid); if (typeof cid === 'number' && !Number.isNaN(cid)) custIds.add(cid!); });
      if (Number.isFinite(baseCustomerId as any)) { custIds.add(Number(baseCustomerId)); }
      const rows = Array.isArray(templates) ? templates : [];
      return rows.filter((tpl:any) => {
        const lvl = String(tpl?.level || '');
        if (lvl === 'system') return true;
        if (lvl === 'project') {
          const pid = Number(tpl?.project_id);
          return projIds.size > 0 && projIds.has(pid);
        }
        if (lvl === 'customer') {
          const cid = Number(tpl?.customer_id);
          return custIds.size > 0 && custIds.has(cid);
        }
        return false;
      });
    } catch { return templates || []; }
  }, [templates, targets, projectIdToCustomerId, baseCustomerId]);
  // 所选模板的模板族和操作对象策略：以模板 meta 里的标记为准，模板改名不影响
  const templatePolicy = React.useMemo(() => {
    try {
      if (!tplVal || tplVal === '__NONE__') return getTemplatePolicy(null);
      return getTemplatePolicy((templates || []).find((t:any) => String(t.id) === String(tplVal)));
    } catch { return getTemplatePolicy(null); }
  }, [tplVal, templates]);
  const showPackingAssessment = templatePolicy.family === 'single_dropship';
  const isRegularOutboundTemplate = templatePolicy.family === 'regular_outbound';
  const isRelabelPackTemplate = templatePolicy.family === 'relabel_pack';
  const isSortAndCountTemplate = templatePolicy.family === 'sort_count';
  // 不支持以"历史指令执行结果"为操作对象的模板（换标+装箱、一件代发、库存分拣与清点、库存非标准操作）
  const historyIocUnsupported = !templatePolicy.allowHistoryIoc;
  const historyIocUnsupportedText = `${templatePolicy.label}仅支持“当前可用库存”操作对象，不可使用“历史指令执行结果”。`;
  const sortAndCountTargetProjectIds = React.useMemo(() => {
    const ids = new Set<number>();
    try {
      (targets || []).forEach((t: any) => {
        const pid = Number(t?.project_id);
        if (Number.isFinite(pid) && pid > 0) ids.add(pid);
      });
    } catch {}
    return ids;
  }, [targets]);
  const sortAndCountDemandAnswer = React.useMemo(() => {
    try {
      const rows = Array.isArray(qRows) ? qRows : [];
      const q = rows.find((it: any) => {
        const key = String(it?.key || '').trim().toLowerCase();
        const label = String(it?.label || '').trim();
        return key === 'sorting_requirement' || label === '分拣需求';
      });
      if (!q) return '';
      const key = String(q?.key || '').trim();
      if (!key) return '';
      return String((answers as any)?.[key] || '').trim();
    } catch {
      return '';
    }
  }, [qRows, answers]);
  const sortAndCountDemandMode = React.useMemo<'need1' | 'need2' | 'need3' | ''>(() => {
    const raw = String(sortAndCountDemandAnswer || '').trim().toLowerCase();
    if (!raw) return '';
    if (raw.includes('需求1') || raw.includes('need1') || raw.includes('混装')) return 'need1';
    if (raw.includes('需求2') || raw.includes('need2') || raw.includes('箱库存单元') || raw.includes('托盘库存单元')) return 'need2';
    if (raw.includes('需求3') || raw.includes('need3') || raw.includes('再次细分') || raw.includes('按照规则')) return 'need3';
    return '';
  }, [sortAndCountDemandAnswer]);
  const sortAndCountDemandDetailText = React.useMemo(() => {
    if (sortAndCountDemandMode === 'need1') {
      return '需求1：将多款入库时混合在一起的散货库存单元(LIU)或箱库存单元(BIU)分拣开，将按照指令选定的分拣策略(标准模式/分组模式)重新为每种库存单元设定入库分拣组，以后这些库存单元再有入库时将按照新的入库分拣组设定执行入库；此场景仅支持条码分拣。';
    }
    if (sortAndCountDemandMode === 'need2') {
      return '需求2：将某款箱库存单元(BIU)或者托盘库存单元(PIU)拆开后执行分拣，分拣出的不同产品将转换为散货库存单元(LIU)，将按照指令选定的分拣策略(标准模式/分组模式)为每种散货库存单元设定入库分拣组；此场景仅支持条码分拣；此场景限每个指令只能处理一款箱库存单元或者托盘库存单元，处理数量不限。';
    }
    if (sortAndCountDemandMode === 'need3') {
      return '需求3：将某款散货库存单元(LIU)再按照特定规则分拣开，如同一款产品按照不同的分拣条码分开(每个分拣条码可能适用于不同的平台店铺)，或者按照产品不同特征分开等(如包装方式/产品颜色/型号等)；将生成全新的变体产品和变体散货库存单元，并将按照指令选定的分拣策略(标准模式/分组模式)为每种变体散货库存单元设定入库分拣组；此场景支持条码分拣/目视分拣/其他分拣方式；此场景限每个指令只能处理一款散货库存单元，处理数量不限。';
    }
    return '';
  }, [sortAndCountDemandMode]);
  React.useEffect(() => {
    try {
      if (!isSortAndCountTemplate) return;
      if (sortAndCountDemandMode !== 'need1' && sortAndCountDemandMode !== 'need2') return;
      const rows = Array.isArray(qRows) ? qRows : [];
      const methodQ = rows.find((q: any) => {
        const k = String(q?.key || '').trim().toLowerCase();
        const lbl = String(q?.label || '').trim();
        return k === 'sorting_method' || k === 'sort_method' || lbl === '分拣方式';
      });
      if (!methodQ) return;
      const methodKey = String(methodQ?.key || '').trim();
      if (!methodKey) return;
      const options = Array.isArray(methodQ?.options) ? methodQ.options : [];
      const scanOpt = options.find((o: any) => {
        const label = String(o?.label || o?.value || '').trim();
        const value = String(o?.value || '').trim();
        return label.includes('扫码分拣') || label.includes('条码分拣') || value.toLowerCase().includes('scan') || value.toLowerCase().includes('barcode');
      });
      if (!scanOpt) return;
      const expected = scanOpt?.value;
      const current = (answers as any)?.[methodKey];
      if (current !== expected) {
        setAnswers((prev: any) => ({ ...(prev || {}), [methodKey]: expected }));
      }
    } catch {}
  }, [isSortAndCountTemplate, sortAndCountDemandMode, qRows, answers]);
  const renderSortAndCountValidationWarningContent = React.useCallback((reason: string) => {
    const detail = String(sortAndCountDemandDetailText || '').trim();
    const finalReason = String(reason || '库存分拣与清点指令操作对象校验未通过').trim();
    return (
      <div style={{ whiteSpace: 'pre-line', color: '#141414', lineHeight: 1.8 }}>
        {detail ? (
          <div style={{ marginBottom: 10 }}>
            <span style={{ fontWeight: 700 }}>已选分拣需求说明：</span>
            <div style={{ marginTop: 4 }}>{detail}</div>
          </div>
        ) : null}
        <div>
          <span style={{ fontWeight: 700 }}>校验未通过原因：</span>
          <div style={{ marginTop: 4, color: '#cf1322' }}>{finalReason}</div>
        </div>
      </div>
    );
  }, [sortAndCountDemandDetailText]);
  const getSortAndCountTargetUnitType = React.useCallback((t: any): string => {
    const direct = String(t?.unit_type || t?.snapshot?.unit_type || '').trim().toUpperCase();
    if (direct) return direct;
    const raw = String(
      t?.inventory_unit_system_barcode
      || t?.snapshot?.system_barcode
      || t?.snapshot?.system_code
      || t?.display
      || '',
    ).trim().toUpperCase();
    if (raw.startsWith('LIU')) return 'LIU';
    if (raw.startsWith('BIU')) return 'BIU';
    if (raw.startsWith('PIU')) return 'PIU';
    if (raw.startsWith('AIU')) return 'AIU';
    return '';
  }, []);
  const getSortAndCountTargetSortingGroupId = React.useCallback((t: any): number | null => {
    const raw = t?.sorting_group_id
      ?? t?.sorting_group
      ?? t?.sorting_group_info?.id
      ?? t?.snapshot?.sorting_group_id
      ?? t?.snapshot?.sorting_group_info?.id;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, []);
  const sortAndCountAIUWarningText = '"分拣清点指令"不支持"异常库存单元AIU",异常库存单元必须先检测翻新后才能进行分拣';
  const isAIUInventoryTarget = React.useCallback((t: any) => {
    if (!t) return false;
    if (String(t?.target_type) !== 'inventory_unit') return false;
    const unitType = String(t?.unit_type || t?.snapshot?.unit_type || '').trim().toUpperCase();
    if (unitType === 'AIU') return true;
    const unitTypeDisplay = String(t?.unit_type_display || t?.snapshot?.unit_type_display || '').trim();
    if (unitTypeDisplay.includes('异常库存单元') || unitTypeDisplay.toUpperCase().includes('AIU')) return true;
    const raw = String(
      t?.inventory_unit_system_barcode
      || t?.snapshot?.system_barcode
      || t?.snapshot?.system_code
      || t?.display
      || '',
    ).trim().toUpperCase();
    return raw.startsWith('AIU');
  }, []);
  const sortAndCountTargetValidation = React.useMemo(() => {
    if (!isSortAndCountTemplate) return { blocked: false, reason: '' };
    if (targetSourceMode === 'history_ioc') {
      return { blocked: true, reason: '库存分拣与清点指令仅支持“当前可用库存”操作对象，不可使用“历史指令执行结果”。' };
    }
    if (!Array.isArray(targets) || targets.length === 0) {
      return { blocked: true, reason: '请至少添加一个操作对象' };
    }
    if ((targets || []).some((t: any) => String(t?.target_type) !== 'inventory_unit')) {
      return { blocked: true, reason: '库存分拣与清点指令仅支持“库存单元”类型操作对象。' };
    }
    if ((targets || []).some((t: any) => isAIUInventoryTarget(t))) {
      return { blocked: true, reason: sortAndCountAIUWarningText };
    }
    if (sortAndCountDemandMode === 'need1') {
      const hasInvalidUnitType = (targets || []).some((t: any) => {
        const ut = getSortAndCountTargetUnitType(t);
        return !(ut === 'LIU' || ut === 'BIU');
      });
      if (hasInvalidUnitType) {
        return { blocked: true, reason: '当分拣需求为“需求1”时，操作对象必须全部为散货库存单元(LIU)或箱库存单元(BIU)。' };
      }
      const groupSet = new Set<string>();
      (targets || []).forEach((t: any) => {
        const gid = getSortAndCountTargetSortingGroupId(t);
        if (gid != null) groupSet.add(String(gid));
      });
      if (groupSet.size !== 1) {
        return { blocked: true, reason: '当分拣需求为“需求1”时，操作对象必须属于同一个分拣组。' };
      }
    } else if (sortAndCountDemandMode === 'need2') {
      if ((targets || []).length !== 1) {
        return { blocked: true, reason: '当分拣需求为“需求2”时，操作对象只能包含一款库存单元。' };
      }
      const only = (targets || [])[0];
      const ut = getSortAndCountTargetUnitType(only);
      if (!(ut === 'BIU' || ut === 'PIU')) {
        return { blocked: true, reason: '当分拣需求为“需求2”时，操作对象必须是箱库存单元(BIU)或托盘库存单元(PIU)。' };
      }
    } else if (sortAndCountDemandMode === 'need3') {
      if ((targets || []).length !== 1) {
        return { blocked: true, reason: '当分拣需求为“需求3”时，操作对象只能包含一款散货库存单元(LIU)。' };
      }
      const only = (targets || [])[0];
      const ut = getSortAndCountTargetUnitType(only);
      if (ut !== 'LIU') {
        return { blocked: true, reason: '当分拣需求为“需求3”时，操作对象必须是散货库存单元(LIU)。' };
      }
    }
    if (sortAndCountTargetProjectIds.size > 1) {
      return { blocked: true, reason: '库存分拣与清点指令的操作对象必须归属于同一个项目。' };
    }
    return { blocked: false, reason: '' };
  }, [
    isSortAndCountTemplate,
    targetSourceMode,
    targets,
    isAIUInventoryTarget,
    sortAndCountAIUWarningText,
    sortAndCountTargetProjectIds,
    sortAndCountDemandMode,
    getSortAndCountTargetUnitType,
    getSortAndCountTargetSortingGroupId,
  ]);
  const lastSortAndCountWarnKeyRef = React.useRef<string>('');
  const requiresInventoryLiuTargets = showPackingAssessment || isRelabelPackTemplate;
  const waybillValidateCacheRef = React.useRef<Record<string, boolean>>({});
  const WAYBILL_VALIDATE_DEBUG = true;
  const normalizeWaybillValue = (v: any) => String(v ?? '').replace(/\s+/g, '').trim().toUpperCase();
  const isWaybillAttachmentRow = (r: any) => /物流运单/.test(String(r?.label || '').trim());
  const shouldValidateWaybillUpload = React.useCallback(() => {
    try {
      return showPackingAssessment === true && resolveWaybillSubmitMode() === 'immediate';
    } catch { return false; }
  }, [showPackingAssessment, resolveWaybillSubmitMode]);
  const getTrackingNoFromAnswers = () => {
    try {
      const raw = (answers as any)?.tracking_number ?? (answers as any)?.waybill_tracking_no ?? '';
      return normalizeWaybillValue(raw);
    } catch { return ''; }
  };
  const validateWaybillFile = React.useCallback(async (file: UploadFile<any>) => {
    const beginRun = () => {
      waybillAuditRunningRef.current += 1;
      setWaybillAuditRunning(true);
    };
    const endRun = () => {
      waybillAuditRunningRef.current = Math.max(0, waybillAuditRunningRef.current - 1);
      if (waybillAuditRunningRef.current === 0) {
        setWaybillAuditRunning(false);
      }
    };
    beginRun();
    try {
      const origin = (file as any)?.originFileObj as File | undefined;
      if (WAYBILL_VALIDATE_DEBUG) {
        console.info('[WaybillValidate] start', {
          name: (origin as any)?.name || file?.name,
          size: (origin as any)?.size || file?.size,
        });
      }
      const trackingNo = getTrackingNoFromAnswers();
      if (WAYBILL_VALIDATE_DEBUG) {
        console.info('[WaybillValidate] request parse', { trackingNo });
      }
      const result = await auditWaybillFile(file, trackingNo);
      if (WAYBILL_VALIDATE_DEBUG && result?.parsed) {
        console.info('[WaybillValidate] parsed', {
          pageCount: result?.parsed?.page_count,
          ratio: result?.parsed?.pages?.[0]?.effective_area_ratio,
          barcodeReadable: result?.parsed?.pages?.[0]?.print_simulation?.barcode_readable,
          barcodeNumbers: result?.parsed?.pages?.[0]?.print_simulation?.barcode_numbers,
        });
      }
      if (!result.ok) {
        setWaybillAuditFileName(result.fileName || String((file as any)?.name || '附件'));
        setWaybillAuditIssues(result.issues || []);
        setWaybillAuditModalTitle('物流运单');
        setWaybillAuditModalOpen(true);
        return false;
      }
      return true;
    } finally {
      endRun();
    }
  }, [getTrackingNoFromAnswers]);
  const guardWaybillBeforeUpload = React.useCallback((file: File) => {
    if (!shouldValidateWaybillUpload()) return false;
    const trackingNo = getTrackingNoFromAnswers();
    if (!trackingNo) {
      Modal.warning({
        title: '请先填写物流跟踪号后再上传运单',
        centered: true,
      });
    }
    return false;
  }, [shouldValidateWaybillUpload, getTrackingNoFromAnswers]);
  const guardWaybillUploadClick = React.useCallback((r: any, e: React.MouseEvent) => {
    if (!isWaybillAttachmentRow(r) || !shouldValidateWaybillUpload()) return;
    const trackingNo = getTrackingNoFromAnswers();
    if (!trackingNo) {
      e.preventDefault();
      e.stopPropagation();
      Modal.warning({
        title: '请先填写物流跟踪号后再上传运单',
        centered: true,
      });
    }
  }, [isWaybillAttachmentRow, shouldValidateWaybillUpload, getTrackingNoFromAnswers]);
  const applyWaybillValidationIfNeeded = React.useCallback(async (r: any, files: UploadFile<any>[]) => {
    if (WAYBILL_VALIDATE_DEBUG) {
      console.info('[WaybillValidate] check', {
        label: r?.label,
        shouldValidate: shouldValidateWaybillUpload(),
        isWaybill: isWaybillAttachmentRow(r),
      });
    }
    if (!shouldValidateWaybillUpload() || !isWaybillAttachmentRow(r)) return files;
    for (const f of (files || [])) {
      const uid = String(f?.uid || f?.name || '');
      if (!uid) {
        continue;
      }
      if (waybillValidateCacheRef.current[uid]) {
        continue;
      }
      await validateWaybillFile(f);
      waybillValidateCacheRef.current[uid] = true;
    }
    return files;
  }, [shouldValidateWaybillUpload, isWaybillAttachmentRow, validateWaybillFile]);
  const [packingOptionId, setPackingOptionId] = useState<string | null>(null);
  const [copiedPackingOptionId, setCopiedPackingOptionId] = useState<string | null>(null);
  const [packingEstimateLoading, setPackingEstimateLoading] = useState(false);
  const [packingAvailability, setPackingAvailability] = useState<{
    warehouse_free?: boolean;
    warehouse_paid?: boolean;
    customer_carton?: boolean;
    customer_soft?: boolean;
  }>({});
  const [packingCodes, setPackingCodes] = useState<{
    warehouse_free?: string;
    warehouse_paid?: string;
    customer_carton?: string;
    customer_soft?: string;
  }>({});
  const [addressParseText, setAddressParseText] = useState('');
  const [packingPackagingIds, setPackingPackagingIds] = useState<{
    warehouse_free?: number;
    warehouse_paid?: number;
    customer_carton?: number;
  }>({});
  const [packingEstimates, setPackingEstimates] = useState<{
    warehouse_free?: { l: number; w: number; h: number; weight_g: number };
    warehouse_paid?: { l: number; w: number; h: number; weight_g: number };
    customer_carton?: { l: number; w: number; h: number; weight_g: number };
  }>({});
  const formatMeasure = (val?: number) => {
    if (val == null || !Number.isFinite(Number(val))) return '-';
    return (Math.ceil(Number(val) * 10) / 10).toFixed(1);
  };
  const toInch = (cm?: number) => (cm != null && Number.isFinite(cm) ? cm / 2.54 : undefined);
  const toLb = (g?: number) => (g != null && Number.isFinite(g) ? g / 453.59237 : undefined);
  const copyText = useCallback(async (text: string) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        message.success('已复制');
        return true;
      }
    } catch (err) {
      console.warn('clipboard write failed:', err);
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      message.success('已复制');
      return true;
    } catch {
      message.error('复制失败');
      return false;
    }
  }, []);
  const buildPackingOptionCopyText = useCallback((opt: any, availability: boolean | undefined, sizeValue: string, weightValue: string) => {
    const availabilityText = availability === true ? '可用' : availability === false ? '不可用' : '未知';
    const codeValue = opt?.codeKey ? (packingCodes as any)?.[opt.codeKey] || '-' : '-';
    const lines = [
      `包材名称: ${opt?.title || '-'}`,
      `包材说明: ${opt?.desc || '-'}`,
      `可用状态: ${availabilityText}`,
      `包材编码: ${codeValue}`,
      `包材费用: ${opt?.feeText || '-'}`,
    ];
    if (String(opt?.id) !== 'customer_soft') {
      lines.push(`预估包裹尺寸: ${sizeValue || '-'}`);
      lines.push(`预估包裹重量: ${weightValue || '-'}`);
    }
    return lines.join('\n');
  }, [packingCodes]);
  const [hasCustomerPackaging, setHasCustomerPackaging] = useState(false);
  const [customerPackagingLoading, setCustomerPackagingLoading] = useState(false);
  const currentCustomerId = React.useMemo(() => {
    try {
      const ids = new Set<number>();
      (targets || []).forEach((t:any) => {
        const pid = Number(t?.project_id);
        if (!Number.isNaN(pid)) {
          const cid = projectIdToCustomerId.get(pid);
          if (typeof cid === 'number' && !Number.isNaN(cid)) ids.add(cid);
        }
      });
      if (ids.size === 1) return Array.from(ids)[0];
      if (ids.size === 0 && Number.isFinite(baseCustomerId as any)) return Number(baseCustomerId);
      return undefined;
    } catch { return undefined; }
  }, [targets, projectIdToCustomerId, baseCustomerId]);
  const getActualDimsForTarget = (t:any) => {
    const l = Number(t?.actual_length);
    const w = Number(t?.actual_width);
    const h = Number(t?.actual_height);
    const weight = Number(t?.actual_weight);
    const ok = Number.isFinite(l) && Number.isFinite(w) && Number.isFinite(h) && l > 0 && w > 0 && h > 0;
    if (!ok) return null;
    return {
      length_cm: l,
      width_cm: w,
      height_cm: h,
      weight_g: Number.isFinite(weight) && weight > 0 ? weight : 0,
    };
  };
  const buildPackingItems = () => {
    const list: Array<{ length_cm: number; width_cm: number; height_cm: number; weight_g: number; quantity: number }> = [];
    const rows = (targets || []).filter((t:any) => String(t?.target_type) === 'inventory_unit');
    for (const t of rows) {
      const dims = getActualDimsForTarget(t);
      if (!dims) return null;
      const exp = Number(t?.expected_qty);
      const qty = (!t?.use_all_available && Number.isFinite(exp)) ? exp
        : (t?.available_qty != null ? Number(t.available_qty) : (t?.warehouse_total_quantity != null ? Number(t.warehouse_total_quantity) : 0));
      if (!Number.isFinite(qty) || qty <= 0) continue;
      list.push({ ...dims, quantity: Math.floor(qty) });
    }
    return list.length > 0 ? list : null;
  };
  const isLIUTarget = React.useCallback((t:any) => {
    if (!t) return false;
    if (String(t?.target_type) !== 'inventory_unit') return false;
    const raw = String(
      t?.inventory_unit_system_barcode
      || t?.snapshot?.system_barcode
      || t?.snapshot?.system_code
      || t?.display
      || '',
    ).trim().toUpperCase();
    return raw.startsWith('LIU');
  }, []);
  const nonLIUTargets = React.useMemo(() => {
    if (!Array.isArray(targets)) return [];
    return targets
      .map((t:any, i:number) => ({ t, i }))
      .filter(({ t }) => !isLIUTarget(t))
      .map(({ t, i }) => ({
        idx: i + 1,
        inventory_unit_id: t?.inventory_unit_id,
        target_type: t?.target_type,
        unit_type: t?.unit_type_display || t?.unit_type,
        display: t?.display || (t?.inventory_unit_id ? `库存单元#${t.inventory_unit_id}` : undefined),
      }));
  }, [targets, isLIUTarget]);
  const dropshipTargetWarningText = '代发指令的操作对象必须是"当前可用库存"且必须是散货库存单元(LIU),不可将"历史指令执行结果"或其他类型可用库存作为代发操作对象.';
  const relabelPackTargetWarningText = '换标+装箱指令的操作对象必须是"当前可用库存"且必须是散货库存单元(LIU),不可将"历史指令执行结果"或其他类型可用库存作为操作对象.';
  const templateTargetWarningText = showPackingAssessment ? dropshipTargetWarningText : relabelPackTargetWarningText;
  const packingAssessmentStatus = React.useMemo(() => {
    if (!requiresInventoryLiuTargets) return { blocked: false, reason: '', packingText: '待计算', totalQty: 0, needPacking: false, hasLogisticsPackage: false };
    if (isEditMode && !editTargetsReady) return { blocked: false, reason: '', packingText: '待计算', totalQty: 0, needPacking: false, hasLogisticsPackage: false };
    if (targetSourceMode === 'history_ioc') {
      return { blocked: true, reason: templateTargetWarningText, packingText: '待计算', totalQty: 0, needPacking: false, hasLogisticsPackage: false };
    }
    if (!Array.isArray(targets) || targets.length === 0) {
      return { blocked: true, reason: '请先添加操作对象', packingText: '待计算', totalQty: 0, needPacking: false, hasLogisticsPackage: false };
    }
    if (nonLIUTargets.length > 0) {
      return { blocked: true, reason: templateTargetWarningText, packingText: '待计算', totalQty: 0, needPacking: false, hasLogisticsPackage: false };
    }
    const getTargetQty = (t:any) => {
      const exp = Number(t?.expected_qty);
      if (!t?.use_all_available && Number.isFinite(exp)) return exp;
      const avail = (t?.available_qty != null) ? Number(t.available_qty) : (t?.warehouse_total_quantity != null ? Number(t.warehouse_total_quantity) : NaN);
      if (Number.isFinite(avail)) return avail;
      return 0;
    };
    const totalQty = (targets || []).reduce((acc:number, t:any) => acc + getTargetQty(t), 0);
    if (totalQty > 1) {
      return { blocked: false, reason: '', packingText: '一单多件,需打包', totalQty, needPacking: true, hasLogisticsPackage: false };
    }
    if (totalQty === 1) {
      const first = (targets || [])[0] || {};
      const items = Array.isArray(first?.content?.items) ? first.content.items : [];
      const it = items[0];
      const pid = Number(it?.product);
      const info = Number.isFinite(pid) ? (productInfo[pid] || {}) : {};
      const hasPack = (() => {
        if (typeof it?.product_has_logistics_package === 'boolean') return it.product_has_logistics_package;
        if (typeof it?.has_logistics_package === 'boolean') return it.has_logistics_package;
        if (typeof info?.has_logistics_package === 'boolean') return info.has_logistics_package;
        return false;
      })();
      return {
        blocked: false,
        reason: '',
        packingText: hasPack ? '一单一件 自带物流包装 无需打包' : '一单一件 无物流包装 需打包',
        totalQty,
        needPacking: !hasPack,
        hasLogisticsPackage: hasPack,
      };
    }
    return { blocked: false, reason: '', packingText: '待计算', totalQty, needPacking: false, hasLogisticsPackage: false };
  }, [requiresInventoryLiuTargets, isEditMode, editTargetsReady, targetSourceMode, targets, productInfo, nonLIUTargets.length, templateTargetWarningText]);
  useEffect(() => {
    let active = true;
    if (!showPackingAssessment || !currentCustomerId) {
      setHasCustomerPackaging(false);
      return;
    }
    (async () => {
      try {
        setCustomerPackagingLoading(true);
        const resp = await getShippingPackagings({ ownership_type: 'customer', customer: currentCustomerId, page_size: 1 });
        const rows = Array.isArray((resp as any)?.data?.results) ? (resp as any).data.results : (Array.isArray((resp as any)?.data) ? (resp as any).data : []);
        if (active) setHasCustomerPackaging(Array.isArray(rows) && rows.length > 0);
      } catch {
        if (active) setHasCustomerPackaging(false);
      } finally {
        if (active) setCustomerPackagingLoading(false);
      }
    })();
    return () => { active = false; };
  }, [showPackingAssessment, currentCustomerId]);
  useEffect(() => {
    let active = true;
    if (!showPackingAssessment || !packingAssessmentStatus.needPacking) {
      setPackingAvailability({});
      setPackingCodes({});
      setPackingEstimates({});
      setPackingPackagingIds({});
      setPackingEstimateLoading(false);
      return;
    }
    const items = buildPackingItems();
    if (!items) {
      setPackingAvailability({
        warehouse_free: false,
        warehouse_paid: false,
        customer_carton: false,
        customer_soft: false,
      });
      setPackingCodes({});
      setPackingEstimates({});
      setPackingPackagingIds({});
      setPackingEstimateLoading(false);
      return;
    }
    (async () => {
      try {
        if (active) setPackingEstimateLoading(true);
        const basePayload: any = {
          items,
          customer_id: currentCustomerId,
          warehouse_id: form.getFieldValue('warehouse_id'),
        };
        const softTypes = ['shipping_bag', 'bubble_mailer', 'other'];
        const [freeResp, paidResp, cusResp, ...softResps] = await Promise.all([
          request.post('/api/v1/logistics/packaging-recommendation/', { ...basePayload, category: 'warehouse_free' }),
          request.post('/api/v1/logistics/packaging-recommendation/', { ...basePayload, category: 'warehouse_paid' }),
          request.post('/api/v1/logistics/packaging-recommendation/', { ...basePayload, category: 'customer_carton' }),
          ...softTypes.map((t) => (
            currentCustomerId
              ? getShippingPackagings({ ownership_type: 'customer', customer: currentCustomerId, packaging_type: t, page_size: 1 })
              : Promise.resolve({ data: { results: [] } })
          )),
        ]);
        const softRows = softResps.flatMap((resp:any) => (
          Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : [])
        ));
        if (!active) return;
        setPackingAvailability({
          warehouse_free: !!freeResp?.data?.recommended_box,
          warehouse_paid: !!paidResp?.data?.recommended_box,
          customer_carton: !!cusResp?.data?.recommended_box,
          customer_soft: Array.isArray(softRows) && softRows.length > 0,
        });
        setPackingCodes({
          warehouse_free: String(freeResp?.data?.recommended_box?.system_code || ''),
          warehouse_paid: String(paidResp?.data?.recommended_box?.system_code || ''),
          customer_carton: String(cusResp?.data?.recommended_box?.system_code || ''),
          customer_soft: '',
        });
        setPackingPackagingIds({
          warehouse_free: Number(freeResp?.data?.recommended_box?.id) || undefined,
          warehouse_paid: Number(paidResp?.data?.recommended_box?.id) || undefined,
          customer_carton: Number(cusResp?.data?.recommended_box?.id) || undefined,
        });
        const mkEst = (resp:any) => {
          const l = Number(resp?.data?.estimated_length_cm);
          const w = Number(resp?.data?.estimated_width_cm);
          const h = Number(resp?.data?.estimated_height_cm);
          const wt = Number(resp?.data?.estimated_weight_g);
          if (!Number.isFinite(l) || !Number.isFinite(w) || !Number.isFinite(h) || l <= 0 || w <= 0 || h <= 0) return undefined;
          return { l, w, h, weight_g: (Number.isFinite(wt) && wt > 0) ? wt : 0 };
        };
        setPackingEstimates({
          warehouse_free: mkEst(freeResp),
          warehouse_paid: mkEst(paidResp),
          customer_carton: mkEst(cusResp),
        });
      } catch {
        if (!active) return;
        setPackingAvailability({
          warehouse_free: false,
          warehouse_paid: false,
          customer_carton: false,
          customer_soft: false,
        });
        setPackingCodes({});
        setPackingEstimates({});
        setPackingPackagingIds({});
      } finally {
        if (active) setPackingEstimateLoading(false);
      }
    })();
    return () => { active = false; };
  }, [showPackingAssessment, packingAssessmentStatus.needPacking, targets, currentCustomerId, form]);
  const packingOptions = React.useMemo(() => {
    if (!showPackingAssessment || !packingAssessmentStatus.needPacking) return [] as Array<any>;
    return [
      {
        id: 'free_packaging',
        title: '仓库免费包材(旧纸箱 | 全新塑胶袋)',
        desc: '旧纸箱可能包含Amazon等标识,当无合适旧纸箱时,可能使用全新塑胶袋发货(免包材费)',
        availabilityKey: 'warehouse_free',
        codeKey: 'warehouse_free',
        feeText: '免费',
        sizeCm: null,
        sizeIn: null,
        weightKg: null,
        weightLb: null,
      },
      {
        id: 'paid_carton',
        title: '仓库收费包材(全新纸箱)',
        desc: '全新纸箱,无特定标识',
        availabilityKey: 'warehouse_paid',
        codeKey: 'warehouse_paid',
        feeText: '待计算',
        sizeCm: null,
        sizeIn: null,
        weightKg: null,
        weightLb: null,
      },
      {
        id: 'customer_carton',
        title: '客户自购包材(纸箱)',
        desc: '仅客户自购的纸箱',
        availabilityKey: 'customer_carton',
        codeKey: 'customer_carton',
        feeText: '免费',
        sizeCm: null,
        sizeIn: null,
        weightKg: null,
        weightLb: null,
      },
      {
        id: 'customer_soft',
        title: '客户自购包材(非纸箱)',
        desc: '如塑胶袋/气泡信封等软包装,无法提供预估重量和尺寸,仓库会优先使用最小包装,如无可用包材,仓库会改用其他方式包装,避免影响发货时效,可能产生额外操作费用.',
        availabilityKey: 'customer_soft',
        codeKey: 'customer_soft',
        feeText: '免费',
        sizeCm: null,
        sizeIn: null,
        weightKg: null,
        weightLb: null,
      },
      {
        id: 'special_packing',
        title: '特殊包装操作',
        desc: '当无可用标准包装方式或不希望使用标准包装方式时,可选择特殊包装操作,比如改箱(将多个空箱合并成一个大箱)/特殊加固(如需使用伸缩膜防水袋等),请在下方指令备注栏标明包装要求,特殊包装操作可能产生额外费用,请联系本司客服确认.',
        feeText: '待确认',
        sizeCm: null,
        sizeIn: null,
        weightKg: null,
        weightLb: null,
      },
    ];
  }, [showPackingAssessment, packingAssessmentStatus.needPacking]);
  const selectedPacking = React.useMemo(() => {
    return packingOptions.find(o => String(o.id) === String(packingOptionId));
  }, [packingOptions, packingOptionId]);
  const lastPackingWarnKeyRef = React.useRef<string>('');
  useEffect(() => {
    setCopiedPackingOptionId(null);
  }, [packingOptionId]);
  useEffect(() => {
    try {
      if (!showPackingAssessment) { setPackingOptionId(null); return; }
      if (!packingOptions || packingOptions.length === 0) { setPackingOptionId(null); return; }
      if (packingOptionId && !packingOptions.some(o => String(o.id) === String(packingOptionId))) {
        setPackingOptionId(null);
      }
    } catch {}
  }, [showPackingAssessment, packingOptions, packingOptionId]);
  const disableAfterTemplate = requiresInventoryLiuTargets && !!packingAssessmentStatus.reason;
  useEffect(() => {
    try {
      if (!requiresInventoryLiuTargets) { lastPackingWarnKeyRef.current = ''; return; }
      if (isEditMode && !editTargetsReady) return;
      const reason = packingAssessmentStatus.reason || '';
      if (!reason) { lastPackingWarnKeyRef.current = ''; return; }
      const isHistoryModeBlocked = targetSourceMode === 'history_ioc';
      const idsKey = isHistoryModeBlocked
        ? (historyTargetRows || []).map((r:any) => String(r?.__key || r?.output_container_id || r?.output_code || '')).join(',')
        : nonLIUTargets.map((r:any) => String(r.inventory_unit_id || r.display || r.idx)).join(',');
      const warnKey = `${reason}|${targetSourceMode}|${idsKey}|${(targets || []).length}|${(historyTargetRows || []).length}`;
      if (warnKey === lastPackingWarnKeyRef.current) return;
      lastPackingWarnKeyRef.current = warnKey;
      if (isHistoryModeBlocked) {
        // 不支持历史指令执行结果的模板会自动切回"当前可用库存"并给出提示（见 historyIocUnsupported 的处理）
        return;
      }
      if (nonLIUTargets.length > 0) {
        const lines = nonLIUTargets.map((r:any) => {
          const head = r.display || (r.inventory_unit_id ? `库存单元#${r.inventory_unit_id}` : '未知对象');
          const type = r.unit_type || r.target_type || '';
          return `#${r.idx} ${head}${type ? ` · ${type}` : ''}`;
        });
        Modal.warning({
          title: '提示',
          icon: null,
          centered: true,
          width: 860,
          okText: '知道了',
          content: (
            <div style={{ whiteSpace: 'pre-line', color: '#141414' }}>
              {`${templateTargetWarningText}\n\n错误操作对象：\n${lines.join('\n')}`}
            </div>
          ),
        });
        return;
      }
      Modal.warning({
        title: '提示',
        icon: null,
        centered: true,
        width: 860,
        okText: '知道了',
        content: <div style={{ whiteSpace: 'pre-line', color: '#141414' }}>{reason}</div>,
      });
    } catch {}
  }, [requiresInventoryLiuTargets, isEditMode, editTargetsReady, packingAssessmentStatus.reason, nonLIUTargets, targetSourceMode, targets, historyTargetRows, templateTargetWarningText]);
  // 若当前所选模板不再可用，则自动清空
  useEffect(() => {
    try {
      if (!draftReadyRef.current) return; // 草稿未完全恢复前，不做模板有效性校验，避免打断恢复
      const tpl = form.getFieldValue('template');
      if (!tpl || tpl === '__NONE__') return;
      const ok = Array.isArray(filteredTemplates) && filteredTemplates.some(t => String(t.id) === String(tpl));
      if (!ok) {
        form.setFieldsValue({ template: undefined });
        try { urlActions.updateFilters({ ci_template: '' }); } catch {}
        setTplDetailState(null);
        setAnswers({});
        setAttachFilesMap({});
      }
    } catch {}
  }, [filteredTemplates, form, urlActions]);

  const fetchTemplates = async () => {
    try {
      // 客户后台：直接调用后端聚合接口，仅返回可用模板集合（系统级 + 客户级 [+ 项目级]）
      const params: any = {};
      if (Number.isFinite(Number(baseCustomerId))) params.customer_id = Number(baseCustomerId);
      // 初次加载未选项目：仅按客户返回系统级+客户级；选定项目后可按需补充调用（此处先不依赖项目）
      const res = await request.get('/api/v1/instruction/templates/available/', { params });
      const rows = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.results) ? res.data.results : []);
      setTemplates(rows);
    } catch {
      setTemplates([]);
    }
  };

  useEffect(() => { fetchTemplates(); }, [baseCustomerId]);

  // 从 URL → 表单：严格按 useTableQuery 的 filters 恢复
  useEffect(() => {
    try {
      const f: any = (urlState && (urlState as any).filters) || {};
      const wh = f.ci_warehouse_id ? Number(f.ci_warehouse_id) : undefined;
      const tpl = f.ci_template ? String(f.ci_template) : undefined;
      const pr = f.ci_priority ? String(f.ci_priority) : 'normal';
      const rk = f.ci_remarks ? String(f.ci_remarks) : '';
      const cur = form.getFieldsValue(['warehouse_id', 'template', 'priority']);
      const next: any = {};
      if (wh != null && cur.warehouse_id !== wh) next.warehouse_id = wh;
      if (tpl != null && cur.template !== tpl) next.template = tpl;
      if (cur.priority !== pr) next.priority = pr;
      // remarks 单独读取，避免一次性拿所有字段影响性能
      const curRemarks = form.getFieldValue('remarks');
      if (curRemarks !== rk) next.remarks = rk;
      if (Object.keys(next).length > 0) form.setFieldsValue(next);
    } catch {}
  }, [urlState, form]);

  useEffect(() => {
    (async () => {
      setLoadingProjects(true);
      try {
        const plist = await getCurrentUserProjects();
        setProjects(Array.isArray(plist) ? plist : []);
      } catch (e) {
        setProjects([]);
      } finally {
        setLoadingProjects(false);
      }
    })();
    (async () => {
      setLoadingWarehouses(true);
      try {
        const wlist = await getAllWarehouses();
        setWarehouses(Array.isArray(wlist) ? wlist : []);
        // 默认选择 US-1-NH 安飞物流美东仓库（若未手动选择）
        try {
          const list = Array.isArray(wlist) ? wlist : [];
          const preferred = list.find((w:any) => (String(w.code) === 'US-1-NH') || /安飞物流美东仓库|美东仓库/.test(String(w.name || '')));
          if (preferred && !form.getFieldValue('warehouse_id')) {
            const wid = Number(preferred.id);
            form.setFieldsValue({ warehouse_id: wid });
            try { urlActions.updateFilters({ ci_warehouse_id: String(wid) }); } catch {}
          }
        } catch {}
      } catch (e) {
        setWarehouses([]);
      } finally {
        setLoadingWarehouses(false);
      }
    })();
  }, []);

  // 管理员/仓库员工代客户创建：自动填入备注
  useEffect(() => {
    try {
      const path = String((window as any)?.location?.pathname || '');
      const isAdmin = path.startsWith('/dashboard/admin');
      const isStaff = path.startsWith('/dashboard/staff');
      if (isAdmin || isStaff) {
        const cur = form.getFieldValue('remarks');
        if (!cur || String(cur).trim() === '') {
          const text = '由仓库代为创建';
          form.setFieldsValue({ remarks: text });
          try { urlActions.updateFilters({ ci_remarks: String(text) }); } catch {}
        }
      }
    } catch {}
  }, []);

  const loadSortingGroups = async (projectId?: number) => {
    const pid = projectId ?? form.getFieldValue('project_id');
    if (!pid) { setSortingGroups([]); return; }
    setLoadingSortingGroups(true);
    try {
      // 1) 先按项目查询项目基础配置（inventory-configs 列表）
      const cfgListResp = await request.get('/api/v1/inventory/inventory-configs/', { params: { project: pid, page_size: 1 } });
      const cfgList = Array.isArray(cfgListResp?.data?.results) ? cfgListResp.data.results : (Array.isArray(cfgListResp?.data) ? cfgListResp.data : []);
      const cfgId = cfgList?.[0]?.id;
      if (!cfgId) { setSortingGroups([]); return; }
      // 2) 拉取配置详情，直接取 sorting_groups 列表（与项目基础配置详情页一致）
      const cfgDetailResp = await request.get(`/api/v1/inventory/inventory-configs/${cfgId}/`);
      const groups = Array.isArray(cfgDetailResp?.data?.sorting_groups) ? cfgDetailResp.data.sorting_groups : [];
      setSortingGroups(groups);
      setProjectSgCount(Array.isArray(groups) ? groups.length : 0);
    } catch { setSortingGroups([]); } finally { setLoadingSortingGroups(false); }
  };

  const loadInventoryUnits = async (params?: any) => {
    const pid = params?.project_id ?? form.getFieldValue('project_id');
    const token = ++unitsLoadTokenRef.current;
    unitsLoadParamsRef.current = params;
    setUnitsLoadError(null);
    setUnitsLoadProgress(null);
    if (!pid) { setInventoryUnits([]); setLoadingUnits(false); return; }
    setLoadingUnits(true);
    try {
      // 按批加载项目的全部库存单元：一次取太多会超时。全部取完才写入列表，否则下面"清理不在列表中的已选条目"会误清编辑指令时的已选单元
      const byId = new Map<number, any>();
      let total: number | null = null;
      let loadFailed = false;
      for (let page = 1; ; page += 1) {
        const fetchPage = () => request.get('/api/v1/inventory/inventory-units/', { params: {
          sorting_group__project_inventory_config__project: pid,
          sorting_group: params?.sorting_group_id,
          warehouse: form.getFieldValue('warehouse_id'),
          warehouse_id: form.getFieldValue('warehouse_id'),
          include_available: 1,
          for_modal: 1,
          // 编辑模式：用于返回 reserved_qty_for_instruction，以便按“可用+本指令锁定”同口径展示
          instruction_id: (isEditMode && editId) ? editId : undefined,
          page,
          page_size: INVENTORY_UNITS_BATCH_SIZE
        } });
        let payload: any;
        try {
          payload = (await fetchPage()).data;
        } catch {
          if (token !== unitsLoadTokenRef.current) return;
          try {
            payload = (await fetchPage()).data; // 失败重试一次
          } catch {
            loadFailed = true;
          }
        }
        if (token !== unitsLoadTokenRef.current) return;
        if (loadFailed) break;
        const pageRows = Array.isArray(payload?.results) ? payload.results : (Array.isArray(payload) ? payload : []);
        // 加载期间有新入库时，后面的页会出现重复单元，按 ID 去重
        pageRows.forEach((u: any) => byId.set(Number(u?.id), u));
        total = Number.isFinite(Number(payload?.count)) ? Number(payload.count) : byId.size;
        setUnitsLoadProgress({ loaded: byId.size, total });
        if (!payload?.next || pageRows.length === 0) break;
      }
      const rows = Array.from(byId.values());
      setInventoryUnits(rows);
      if (loadFailed || (total !== null && rows.length < total)) {
        setUnitsLoadError(`库存单元加载不完整：已加载 ${rows.length}${total !== null ? ` / ${total}` : ''} 个，部分库存单元可能不显示`);
      }
      // 不做默认全选
      if (!params || !params.sorting_group_id) {
        setProjectUnitCount(Array.isArray(rows) ? rows.length : 0);
      }
      // 预取内容项的产品信息映射（名称/自定义编码/分拣条码）
      try {
        const ids: number[] = [];
        rows.forEach((u: any) => {
          const items = Array.isArray(u?.content?.items) ? u.content.items : [];
          items.forEach((it: any) => {
            const pid2 = Number(it?.product);
            if (!Number.isNaN(pid2)) ids.push(pid2);
          });
        });
        const uniq = Array.from(new Set(ids));
        if (uniq.length > 0) {
          setLoadingProducts(true);
          const map: Record<number, any> = {};
          // 限制同时请求的数量，产品种类多时不会占满服务器，也不会排队超时导致产品名称和分拣条码缺失
          let nextIndex = 0;
          await Promise.all(Array.from({ length: Math.min(PRODUCT_DETAIL_CONCURRENCY, uniq.length) }, async () => {
            while (nextIndex < uniq.length) {
              const pid3 = uniq[nextIndex];
              nextIndex += 1;
              try {
                const resp = await getProductDetail(pid3);
                map[pid3] = (resp as any)?.data || {};
              } catch {}
            }
          }));
          if (token !== unitsLoadTokenRef.current) return;
          setProductInfo(prev => ({ ...prev, ...map }));
        }
      } catch {}
    } catch {
      if (token === unitsLoadTokenRef.current) setInventoryUnits([]);
    } finally {
      if (token === unitsLoadTokenRef.current) {
        setLoadingUnits(false);
        setLoadingProducts(false);
        setUnitsLoadProgress(null);
      }
    }
  };

  const loadUpstream = async (projectId?: number) => {
    const pid = projectId ?? form.getFieldValue('project_id');
    if (!pid) { setUpstreamOptions([]); return; }
    setLoadingUpstream(true);
    try {
      // 简化策略：不再维护 is_appendable，所有“已完成”指令结果均可被用于追加
      const res = await request.get('/api/v1/instruction/instructions/', { params: { project_id: pid, status: 'completed', page_size: 200 } });
      const payload = res.data;
      const rows = Array.isArray(payload?.results) ? payload.results : (Array.isArray(payload) ? payload : []);
      setUpstreamRows(rows);
      const opts = rows.map((r: any) => ({ value: Number(r.id), label: `${r.system_code || r.id} · 已完成` }));
      setUpstreamOptions(opts);
    } catch { setUpstreamOptions([]); } finally { setLoadingUpstream(false); }
  };

  const loadHistoryInstrById = useCallback(async (id?: number) => {
    if (!id || id <= 0) {
      setHistoryInstrRow(null);
      setHistoryIocRows([]);
      return;
    }
    setHistoryInstrLoading(true);
    try {
      const r = await request.get(`/api/v1/instruction/instructions/${id}/`);
      const row = r?.data || {};
      setHistoryInstrRow(row);
      // 便捷：若当前未选择仓库，则默认使用历史指令的仓库
      try {
        const wid = row?.warehouse_id ?? row?.warehouse_detail?.id;
        if (wid && !form.getFieldValue('warehouse_id')) {
          form.setFieldsValue({ warehouse_id: Number(wid) });
        }
      } catch {}
    } catch (e:any) {
      setHistoryInstrRow(null);
      message.error(e?.response?.data?.detail || '加载历史指令失败');
    } finally {
      setHistoryInstrLoading(false);
    }
  }, [form]);

  const searchHistoryInstr = useCallback(async (keyword?: string) => {
    setHistoryInstrLoading(true);
    try {
      const kw = String(keyword || '').trim();
      const params: any = { status: 'completed', page_size: 50 };
      if (kw) params.keyword = kw;
      const res = await request.get('/api/v1/instruction/instructions/', { params });
      const payload = res?.data;
      const rows = Array.isArray(payload?.results) ? payload.results : (Array.isArray(payload) ? payload : []);
      const opts = (rows || []).map((r:any) => ({
        value: Number(r.id),
        label: `${r.system_code || r.id} · ${r.status || ''}${r.remarks ? ` · ${String(r.remarks).slice(0, 20)}` : ''}`,
        row: r,
      }));
      setHistoryInstrOptions(opts);
    } catch {
      setHistoryInstrOptions([]);
    } finally {
      setHistoryInstrLoading(false);
    }
  }, []);

  const loadHistoryIoc = useCallback(async (instrId?: number) => {
    if (!instrId || instrId <= 0) {
      setHistoryIocRows([]);
      return;
    }
    setHistoryIocLoading(true);
    try {
      const r = await request.get(`/api/v1/instruction/instructions/${instrId}/ioc/with-contents/`);
      const list = Array.isArray(r?.data?.results) ? r.data.results : [];
      setHistoryIocRows(list);
    } catch (e:any) {
      message.error(e?.response?.data?.detail || '加载IOC装箱数据失败');
      setHistoryIocRows([]);
    } finally {
      setHistoryIocLoading(false);
    }
  }, []);

  const loadHistoryProjectOutputs = useCallback(async (projectId?: number) => {
    const pid = Number(projectId || 0);
    if (!Number.isFinite(pid) || pid <= 0) {
      setHistoryAvailableRows([]);
      setHistoryAvailableSelectedRowKeys([]);
      setHistoryAvailablePage(1);
      setHistoryIocFilterKeyword('');
      return;
    }
    const wid = Number(form.getFieldValue('warehouse_id') || 0);
    const w = (warehouses || []).find((x:any) => Number(x?.id) === wid);
    const warehouseCode = String(w?.code || '').trim();
    setHistoryAvailableLoading(true);
    try {
      const params: any = {
        page: 1,
        page_size: 200,
        container_kind: 'output',
        project: String(pid),
        status: 'in_use',
        ordering: '-created_at',
      };
      if (warehouseCode) params.warehouse_code = warehouseCode;
      const resp = await request.get('/api/v1/storage/containers/unified/', { params });
      const rows = Array.isArray(resp?.data?.results) ? resp.data.results : [];
      const normalRows = rows.filter((r: any) => String(r?.output_content_kind || '').trim().toLowerCase() !== 'exception');
      setHistoryAvailableRows(normalRows);
      setHistoryAvailableSelectedRowKeys([]);
      setHistoryAvailablePage(1);
      setHistoryIocFilterKeyword('');
    } catch (e:any) {
      setHistoryAvailableRows([]);
      setHistoryAvailableSelectedRowKeys([]);
      setHistoryAvailablePage(1);
      setHistoryIocFilterKeyword('');
      message.error(e?.response?.data?.detail || '加载项目历史指令执行结果失败');
    } finally {
      setHistoryAvailableLoading(false);
    }
  }, [form, warehouses]);

  useEffect(() => {
    if (!historyTargetModalOpen) return;
    const pid = Number(historyModalProjectId || 0);
    if (!Number.isFinite(pid) || pid <= 0) {
      setHistoryAvailableRows([]);
      setHistoryAvailableSelectedRowKeys([]);
      return;
    }
    void loadHistoryProjectOutputs(pid);
  }, [historyTargetModalOpen, historyModalProjectId, loadHistoryProjectOutputs]);

  // 为“来源历史指令摘要”补齐详情（模板名/指令名/备注等）
  useEffect(() => {
    let cancelled = false;
    const loadInstructionBriefs = async () => {
      try {
        const ids = Array.from(new Set(
          (historyAvailableRows || [])
            .map((r:any) => Number(r?.instruction_id || r?.prev_instruction_id || 0))
            .filter((id:number) => Number.isFinite(id) && id > 0)
        )).filter((id:number) => !historyInstructionBriefById[id]);
        if (ids.length === 0) return;
        const next: Record<number, any> = {};
        await Promise.all(ids.map(async (id:number) => {
          try {
            const resp = await request.get(`/api/v1/instruction/instructions/${id}/`);
            const d = resp?.data || {};
            next[id] = {
              system_code: String(d?.system_code || d?.systemCode || d?.code || '').trim(),
              template_name: String(d?.template_snapshot?.name || d?.template_detail?.name || '').trim(),
              name: String(d?.name || '').trim(),
              remarks: String(d?.remarks || '').trim(),
              created_at: d?.created_at || '',
            };
          } catch {}
        }));
        if (cancelled) return;
        if (Object.keys(next).length > 0) {
          setHistoryInstructionBriefById((prev) => ({ ...prev, ...next }));
        }
      } catch {}
    };
    void loadInstructionBriefs();
    return () => { cancelled = true; };
  }, [historyAvailableRows, historyInstructionBriefById]);

  // 当 URL 直接带 append_from 时，自动加载指令与 IOC
  useEffect(() => {
    if (!historyInstrId) return;
    void loadHistoryInstrById(historyInstrId);
    void loadHistoryIoc(historyInstrId);
  }, [historyInstrId, loadHistoryInstrById, loadHistoryIoc]);

  // 追加指令：自动将来源指令“可用 IOC”全部加入操作对象（口径由后端统一接口保证）
  useEffect(() => {
    if (!appendFromId || appendFromId <= 0) return;
    if (isEditMode) return;
    if (appendAutoFilledFromRef.current === appendFromId) return;
    let cancelled = false;
    const run = async () => {
      try {
        const resp = await request.get(`/api/v1/instruction/instructions/${appendFromId}/appendable-ioc/`);
        if (cancelled) return;
        const rows = Array.isArray(resp?.data?.results) ? resp.data.results : [];
        const mapped = (rows || []).map((r: any) => {
          const pid = Number(r?.project_id);
          const projectLabel = String(r?.project_label || '').trim();
          return {
            __key: `ioc-${String(r?.id || '')}`,
            project_id: Number.isFinite(pid) && pid > 0 ? pid : undefined,
            project_label: projectLabel,
            output_container_id: r?.id,
            instruction_id: Number(r?.instruction_id || appendFromId) || appendFromId,
            instruction_code: r?.instruction_code || undefined,
            output_code: r?.system_code || r?.display_code || '',
            display_code: r?.display_code || r?.system_code || '',
            warehouse_label: r?.warehouse_code || '-',
            container_type_display: r?.container_type_display || '-',
            output_content_kind: r?.output_content_kind || '',
            output_content_kind_display: r?.output_content_kind_display || '-',
            output_contents_summary: r?.output_contents_summary || '-',
            output_contents_detail: r?.output_contents_detail || '',
            output_contents_direct_total_qty: r?.output_contents_direct_total_qty,
            output_contents_total_qty: r?.output_contents_total_qty,
            length_cm: r?.length_cm,
            width_cm: r?.width_cm,
            height_cm: r?.height_cm,
            weight_g: r?.weight_g,
            created_at: r?.created_at,
          } as any;
        }).filter((x: any) => String(x?.output_code || '').trim());
        setTargetSourceMode('history_ioc');
        setHistoryInstrId(appendFromId);
        setHistoryTargetRows(mapped);
        setHistoryTargetSelectedRowKeys([]);
        appendAutoFilledFromRef.current = appendFromId;
        if (mapped.length === 0) {
          message.warning('该指令无可用的历史指令产出结果容器，无法自动追加');
        }
      } catch (e: any) {
        if (!cancelled) {
          message.error(e?.response?.data?.detail || '自动加载可追加IOC失败');
        }
      }
    };
    void run();
    return () => { cancelled = true; };
  }, [appendFromId, isEditMode]);

  // 追加指令便捷预设：来源指令为“一件代发 + 客户提供运单(upload) + 先申请包装数据(after_pack)”时，
  // 预选“常规出库”模板，并预设“不需重新包装”“运单提供方式=上传”“运单提交时机=立即”“运输标签对应关系=无对应关系”。
  // 仅在首次进入时预设一次，用户之后可自行修改；其他来源指令不受影响。
  const appendDropshipPresetActive = React.useMemo(() => {
    try {
      if (!appendFromId || appendFromId <= 0 || isEditMode) return false;
      const row: any = historyInstrRow;
      if (!row || Number(row?.id) !== Number(appendFromId)) return false;
      const tplName = String(row?.template_snapshot?.name || row?.template_detail?.name || '').trim();
      if (!tplName.includes('一件代发')) return false;
      if (String(row?.waybill_source || '').trim().toLowerCase() !== 'upload') return false;
      return String(row?.waybill_submit_mode || '').trim().toLowerCase() === 'after_pack';
    } catch { return false; }
  }, [appendFromId, isEditMode, historyInstrRow]);
  const appendPresetTemplateDoneRef = React.useRef(false);
  // 记录已预设的模板详情对象：切换模板/模板列表刷新会清空答案并换入新的详情对象，此时需要重新预设
  const appendPresetAnswersTplRef = React.useRef<any>(null);
  useEffect(() => {
    try {
      if (!appendDropshipPresetActive) return;
      if (appendPresetTemplateDoneRef.current) return;
      if (historyTargetRows.length === 0) return;
      const cur = form.getFieldValue('template');
      if (cur !== undefined && cur !== null && cur !== '') { appendPresetTemplateDoneRef.current = true; return; }
      const cands = (Array.isArray(filteredTemplates) ? filteredTemplates : [])
        .filter((t: any) => String(t?.name || '').includes('常规出库'));
      const pick = cands.find((t: any) => String(t?.level) === 'system') || cands[0];
      if (!pick) return;
      form.setFieldsValue({ template: String(pick.id) });
      appendPresetTemplateDoneRef.current = true;
    } catch {}
  }, [appendDropshipPresetActive, historyTargetRows, filteredTemplates, form]);
  useEffect(() => {
    try {
      if (!appendDropshipPresetActive) return;
      if (!tplVal || tplVal === '__NONE__') return;
      if (!isRegularOutboundTemplate) return;
      if (!tplDetailState || String((tplDetailState as any)?.id) !== String(tplVal)) return;
      if (appendPresetAnswersTplRef.current === tplDetailState) return;
      const rows: any[] = Array.isArray(qRows) ? qRows : [];
      if (rows.length === 0) return;
      const pickOpt = (q: any, test: (s: string) => boolean) => {
        const opts = Array.isArray(q?.options) ? q.options : [];
        const o = opts.find((x: any) => test(String(x?.value ?? '')) || test(String(x?.label ?? '')));
        return o ? o.value : undefined;
      };
      const patch: Record<string, any> = {};
      rows.forEach((q: any) => {
        const key = String(q?.key || '').trim();
        if (!key) return;
        const keyLower = key.toLowerCase();
        const label = String(q?.label || '').trim();
        if (['is_repack_required', 'repack_required', 'is_pack_required'].includes(keyLower) || label === '是否需重新包装') {
          if (String(q?.type || '').toLowerCase() === 'boolean') { patch[key] = false; return; }
          const v = pickOpt(q, (s) => /^(false|0|no|n)$/i.test(s.trim()) || /无需|不需|无须|否/.test(s));
          if (v !== undefined) patch[key] = v;
        } else if (keyLower === 'waybill_source' || label === '运单提供方式') {
          const v = pickOpt(q, (s) => /^upload$/i.test(s.trim()) || /上传/.test(s));
          if (v !== undefined) patch[key] = v;
        } else if (isWaybillSubmitTimingQuestion(q)) {
          const v = pickOpt(q, (s) => isImmediateWaybillValue(s));
          patch[key] = v !== undefined ? v : 'immediate';
        } else if (keyLower === 'shipping_label_to_target_mapping') {
          const v = pickOpt(q, (s) => s.trim() === '无对应关系');
          if (v !== undefined) patch[key] = v;
        }
      });
      appendPresetAnswersTplRef.current = tplDetailState;
      if (Object.keys(patch).length === 0) return;
      setAnswers((prev: any) => ({ ...(prev || {}), ...patch }));
    } catch {}
  }, [appendDropshipPresetActive, tplVal, isRegularOutboundTemplate, tplDetailState, qRows, isWaybillSubmitTimingQuestion, isImmediateWaybillValue]);

  const onTemplateChange = (_tplId: number) => {};

  // 编辑模式：预填充所有可用信息
  useEffect(() => {
    if (!editId) return;
    setEditTargetsReady(false);
    (async () => {
      try {
        const res = await request.get(`/api/v1/instruction/instructions/${editId}/`);
        const d = res?.data || {};
        try { setEditDetail(d); } catch {}
        try { setEditSystemCode(String(d?.system_code || d?.systemCode || d?.code || editId)); } catch {}
        // 表单字段
        try {
          const tplId = d?.template ?? d?.template_detail?.id;
          form.setFieldsValue({
            warehouse_id: d?.warehouse_id ?? d?.warehouse_detail?.id,
            template: tplId ? String(tplId) : '__NONE__',
            priority: d?.priority || 'normal',
            name: d?.name || undefined,
            remarks: d?.remarks || undefined,
          });
        } catch {}
        // URL 状态同步（以便与创建页保持一致行为）
        try {
          const wid = d?.warehouse_id ?? d?.warehouse_detail?.id;
          const tpl = d?.template ?? d?.template_detail?.id;
          const pr = d?.priority || 'normal';
          const rk = d?.remarks ? String(d.remarks) : '';
          urlActions.updateFilters({
            ci_warehouse_id: wid ? String(wid) : '',
            ci_template: tpl ? String(tpl) : '',
            ci_priority: String(pr),
            ci_remarks: rk,
          } as any);
        } catch {}
        // 问卷答案
        try { setAnswers(d?.customer_answers || {}); } catch {}
        // 拉取已存在附件
        try {
          const att = await request.get('/api/v1/instruction/attachments/', { params: { instruction: editId, page_size: 500 } });
          const rows = Array.isArray(att?.data?.results) ? att.data.results : (Array.isArray(att?.data) ? att.data : []);
          setExistingAttachments(rows);
        } catch { setExistingAttachments([]); }
        // 预填操作对象（编辑模式按原始 target_type 自动识别来源）
        try {
          const list = Array.isArray(d?.targets_detail) ? d.targets_detail : [];
          const compact = (arr:any[]) => {
            const norm = (x:any) => ({
              target_type: x?.target_type ?? null,
              project_id: Number.isFinite(Number(x?.project_id)) ? Number(x.project_id) : null,
              sorting_group_id: Number.isFinite(Number(x?.sorting_group_id)) ? Number(x.sorting_group_id) : null,
              inventory_unit_id: Number.isFinite(Number(x?.inventory_unit_id)) ? Number(x.inventory_unit_id) : null,
              prev_instruction_id: Number.isFinite(Number(x?.prev_instruction_id)) ? Number(x.prev_instruction_id) : null,
              output_selector: x?.output_selector ?? null,
              expected_qty: x?.use_all_available ? null : (x?.expected_qty != null ? Number(x.expected_qty) : null),
              use_all_available: !!x?.use_all_available,
              fingerprint: x?.fingerprint ?? null,
            });
            const out = (arr || []).map(norm);
            out.sort((a:any,b:any)=> {
              const ks = ['target_type','project_id','sorting_group_id','inventory_unit_id','prev_instruction_id','output_selector','expected_qty','use_all_available','fingerprint'];
              for (const k of ks) {
                const av = (a as any)[k]; const bv = (b as any)[k];
                if (av === bv) continue;
                return String(av).localeCompare(String(bv));
              }
              return 0;
            });
            return out;
          };
          const parseIocCodes = (selector: any): string[] => {
            try {
              if (selector && typeof selector === 'object') {
                const arr = Array.isArray(selector?.ioc_codes) ? selector.ioc_codes : [];
                return arr.map((x:any) => String(x || '').trim()).filter(Boolean);
              }
              const s = String(selector || '').trim();
              if (!s) return [];
              try {
                const j = JSON.parse(s);
                const arr = Array.isArray(j?.ioc_codes) ? j.ioc_codes : [];
                return arr.map((x:any) => String(x || '').trim()).filter(Boolean);
              } catch {}
              try {
                const normalized = s.replace(/'/g, '"');
                const j2 = JSON.parse(normalized);
                const arr = Array.isArray(j2?.ioc_codes) ? j2.ioc_codes : [];
                return arr.map((x:any) => String(x || '').trim()).filter(Boolean);
              } catch {}
              const m = s.match(/ioc_codes\s*[:=]\s*\[([^\]]*)\]/i);
              if (!m) return [];
              return String(m[1] || '')
                .split(',')
                .map((x) => String(x || '').replace(/['"]/g, '').trim())
                .filter(Boolean);
            } catch {
              return [];
            }
          };
          const hasHistoryTargets = (list || []).some((t:any) => {
            const tt = String(t?.target_type || '').trim();
            if (tt === 'instruction_output') return true;
            const selector = t?.output_selector_obj ?? t?.output_selector;
            return parseIocCodes(selector).length > 0;
          });
          if (hasHistoryTargets) {
            setTargetSourceMode('history_ioc');
            setTargets([]);
            setInvSel({});
            const projectLabelMap = new Map<number, string>();
            try {
              const rows = Array.isArray(d?.projects_detail) ? d.projects_detail : [];
              rows.forEach((p:any) => {
                const pid = Number(p?.id);
                if (!Number.isFinite(pid)) return;
                projectLabelMap.set(pid, `${p?.project_code || pid} - ${p?.name || ''}`);
              });
            } catch {}
            const historyRows = (list || [])
              .filter((t:any) => {
                const tt = String(t?.target_type || '').trim();
                const selector = t?.output_selector_obj ?? t?.output_selector;
                return tt === 'instruction_output' || parseIocCodes(selector).length > 0;
              })
              .map((t:any, idx:number) => {
                const snap = t?.snapshot || {};
                const selector = t?.output_selector_obj ?? t?.output_selector;
                const codes = parseIocCodes(selector);
                const code = String(
                  codes?.[0]
                  || snap?.output_code
                  || snap?.system_code
                  || snap?.code
                  || ''
                ).trim();
                const pid = Number(t?.project_id);
                const prevId = Number(t?.prev_instruction_id || snap?.prev_instruction_id || snap?.instruction_id);
                const outputContainerId = snap?.output_container_id ?? snap?.container_id ?? undefined;
                const keyPart = String(outputContainerId || code || t?.id || idx);
                const projectLabel = Number.isFinite(pid) ? (projectLabelMap.get(pid) || projectIdToLabel.get(pid) || `项目#${pid}`) : '-';
                return {
                  __key: `ioc-${keyPart}`,
                  project_id: Number.isFinite(pid) ? pid : undefined,
                  project_label: projectLabel,
                  output_container_id: outputContainerId,
                  instruction_id: Number.isFinite(prevId) ? prevId : undefined,
                  instruction_code: String(snap?.prev_instruction_code || snap?.instruction_code || '').trim() || undefined,
                  output_code: code,
                  display_code: code,
                  container_type_display: snap?.container_type_display || snap?.container_type || '-',
                  output_content_kind: snap?.output_content_kind || '',
                  output_content_kind_display: snap?.output_content_kind_display || '-',
                  output_contents_summary: snap?.output_contents_summary || '-',
                  output_contents_detail: snap?.output_contents_detail || snap?.contents_detail || '',
                  output_contents_direct_total_qty: Number(snap?.output_contents_direct_total_qty || snap?.direct_total_qty || 0) || 0,
                  output_contents_total_qty: Number(snap?.output_contents_total_qty || snap?.total_qty || 0) || 0,
                  length_cm: snap?.length_cm,
                  width_cm: snap?.width_cm,
                  height_cm: snap?.height_cm,
                  weight_g: snap?.weight_g,
                  created_at: snap?.created_at,
                } as any;
              });
            let mergedHistoryRows = historyRows;
            try {
              const normalizeCode = (v:any) => String(v || '').trim().toUpperCase();
              const containerTypeDisplayMap: Record<string, string> = {
                box: '纸箱',
                carton: '纸箱',
                bag: '袋',
                pallet: '托盘',
                crate: '木箱',
              };
              const contentKindDisplay = (k:any) => {
                const key = String(k || '').trim().toLowerCase();
                if (key === 'exception') return '异常内容容器';
                return '正常内容容器';
              };
              const buildDetail = (contents:any[]) => {
                return (contents || []).map((c:any) => {
                  const code = String(c?.inventory_unit_system_barcode || c?.inventory_unit_id || '').trim();
                  const qty = Number(c?.quantity || 0) || 0;
                  if (!code) return '';
                  return `${code}×${qty}`;
                }).filter(Boolean).join('；');
              };
              const prevIds: number[] = Array.from(new Set(
                (historyRows || [])
                  .map((r:any) => Number(r?.instruction_id))
                  .filter((v:number) => Number.isFinite(v) && v > 0)
              ));
              if (prevIds.length > 0) {
                const detailByCode = new Map<string, any>();
                const detailById = new Map<string, any>();
                await Promise.all(prevIds.map(async (pid) => {
                  try {
                    const rr = await request.get(`/api/v1/instruction/instructions/${pid}/ioc/with-contents/`);
                    const arr = Array.isArray(rr?.data?.results) ? rr.data.results : [];
                    (arr || []).forEach((oc:any) => {
                      const idKey = `${pid}::id::${String(oc?.id || '')}`;
                      const codeKey = `${pid}::code::${normalizeCode(oc?.code)}`;
                      if (String(oc?.id || '').trim()) detailById.set(idKey, oc);
                      if (normalizeCode(oc?.code)) detailByCode.set(codeKey, oc);
                    });
                  } catch {}
                }));
                mergedHistoryRows = (historyRows || []).map((row:any) => {
                  const pid = Number(row?.instruction_id);
                  if (!Number.isFinite(pid) || pid <= 0) return row;
                  const byId = row?.output_container_id ? detailById.get(`${pid}::id::${String(row.output_container_id)}`) : undefined;
                  const byCode = detailByCode.get(`${pid}::code::${normalizeCode(row?.output_code || row?.display_code)}`);
                  const hit = byId || byCode;
                  if (!hit) return row;
                  const contents = Array.isArray(hit?.contents) ? hit.contents : [];
                  const totalQty = contents.reduce((acc:number, c:any) => acc + (Number(c?.quantity || 0) || 0), 0);
                  const detailText = buildDetail(contents);
                  const containerTypeCode = String(hit?.container_type || '').trim().toLowerCase();
                  const kind = String(hit?.content_kind || row?.output_content_kind || '').trim().toLowerCase();
                  const fallbackSummary = contents.length > 0 ? `${contents.length}项` : '-';
                  const existingSummary = String(row?.output_contents_summary || '').trim();
                  const existingDetail = String(row?.output_contents_detail || '').trim();
                  return {
                    ...row,
                    output_container_id: hit?.id ?? row?.output_container_id,
                    output_code: String(hit?.code || row?.output_code || row?.display_code || '').trim(),
                    display_code: String(hit?.code || row?.display_code || row?.output_code || '').trim(),
                    container_type_display: row?.container_type_display && String(row.container_type_display).trim() !== '-'
                      ? row.container_type_display
                      : (containerTypeDisplayMap[containerTypeCode] || hit?.container_type || '-'),
                    output_content_kind: kind || row?.output_content_kind || '',
                    output_content_kind_display: row?.output_content_kind_display && String(row.output_content_kind_display).trim() !== '-'
                      ? row.output_content_kind_display
                      : contentKindDisplay(kind),
                    output_contents_summary: existingSummary && existingSummary !== '-' ? existingSummary : fallbackSummary,
                    output_contents_detail: existingDetail || detailText,
                    output_contents_direct_total_qty: Number(row?.output_contents_direct_total_qty || 0) > 0
                      ? Number(row.output_contents_direct_total_qty || 0)
                      : totalQty,
                    output_contents_total_qty: Number(row?.output_contents_total_qty || 0) > 0
                      ? Number(row.output_contents_total_qty || 0)
                      : totalQty,
                    length_cm: hit?.length_cm ?? row?.length_cm,
                    width_cm: hit?.width_cm ?? row?.width_cm,
                    height_cm: hit?.height_cm ?? row?.height_cm,
                    weight_g: hit?.weight_g ?? row?.weight_g,
                    created_at: hit?.created_at ?? row?.created_at,
                  };
                });
              }
              // 再用 unified 接口口径补齐 IOC 内容展示（与“添加操作对象”弹窗保持一致）
              try {
                const projectIds: number[] = Array.from(new Set(
                  (mergedHistoryRows || [])
                    .map((r:any) => Number(r?.project_id))
                    .filter((v:number) => Number.isFinite(v) && v > 0)
                ));
                if (projectIds.length > 0) {
                  const wid = Number(d?.warehouse_id || d?.warehouse_detail?.id || 0);
                  const w = (warehouses || []).find((x:any) => Number(x?.id) === wid);
                  const warehouseCode = String(w?.code || '').trim();
                  const unifiedByCode = new Map<string, any>();
                  await Promise.all(projectIds.map(async (pid:number) => {
                    try {
                      const params: any = {
                        page: 1,
                        page_size: 500,
                        container_kind: 'output',
                        project: String(pid),
                        ordering: '-created_at',
                        instruction_id: Number(d?.id || editId || 0) || undefined,
                      };
                      if (warehouseCode) params.warehouse_code = warehouseCode;
                      const resp = await request.get('/api/v1/storage/containers/unified/', { params });
                      const rows = Array.isArray(resp?.data?.results) ? resp.data.results : [];
                      (rows || []).forEach((r:any) => {
                        const code = normalizeCode(r?.system_code || r?.display_code || '');
                        if (code) unifiedByCode.set(`${pid}::${code}`, r);
                      });
                    } catch {}
                  }));
                  mergedHistoryRows = (mergedHistoryRows || []).map((row:any) => {
                    const pid = Number(row?.project_id);
                    const code = normalizeCode(row?.output_code || row?.display_code || '');
                    if (!Number.isFinite(pid) || pid <= 0 || !code) return row;
                    const hit = unifiedByCode.get(`${pid}::${code}`);
                    if (!hit) return row;
                    return {
                      ...row,
                      output_code: String(hit?.system_code || hit?.display_code || row?.output_code || row?.display_code || '').trim(),
                      display_code: String(hit?.display_code || hit?.system_code || row?.display_code || row?.output_code || '').trim(),
                      container_type_display: hit?.container_type_display || row?.container_type_display || '-',
                      output_content_kind: hit?.output_content_kind || row?.output_content_kind || '',
                      output_content_kind_display: hit?.output_content_kind_display || row?.output_content_kind_display || '-',
                      output_contents_summary: hit?.output_contents_summary || row?.output_contents_summary || '-',
                      output_contents_detail: hit?.output_contents_detail || row?.output_contents_detail || '',
                      output_contents_direct_total_qty: Number(hit?.output_contents_direct_total_qty || row?.output_contents_direct_total_qty || 0) || 0,
                      output_contents_total_qty: Number(hit?.output_contents_total_qty || row?.output_contents_total_qty || 0) || 0,
                    };
                  });
                }
              } catch {}
            } catch {}
            setHistoryTargetRows(mergedHistoryRows);
            setHistoryTargetSelectedRowKeys([]);
            setHistoryAvailableRows([]);
            setHistoryAvailableSelectedRowKeys([]);
            setHistoryIocFilterKeyword('');
            setHistoryInstrRow(null);
            try {
              const ids = Array.from(new Set(
                mergedHistoryRows
                  .map((r:any) => Number(r?.instruction_id))
                  .filter((v:number) => Number.isFinite(v) && v > 0)
              ));
              setHistoryInstrId(ids.length === 1 ? Number(ids[0]) : undefined);
            } catch {
              setHistoryInstrId(undefined);
            }
            const mappedForSubmit = (mergedHistoryRows || []).map((row:any) => ({
              target_type: 'instruction_output',
              project_id: row?.project_id,
              prev_instruction_id: row?.instruction_id,
              output_selector: { ioc_codes: row?.output_code ? [String(row.output_code)] : [] },
              use_all_available: true,
              expected_qty: undefined,
              fingerprint: row?.fingerprint,
            })).filter((x:any) => Array.isArray(x?.output_selector?.ioc_codes) && x.output_selector.ioc_codes.length > 0);
            originalTargetsRef.current = compact(mappedForSubmit);
          } else {
            setTargetSourceMode('inventory');
            setHistoryTargetRows([]);
            setHistoryTargetSelectedRowKeys([]);
            setHistoryInstrId(undefined);
            setHistoryInstrRow(null);
            const mapped = list.map((t:any) => {
            const snap = t?.snapshot || {};
            const sysBarcode = t?.inventory_unit_system_barcode || snap.system_barcode || snap.system_code || '';
            const unitType = snap.unit_type_display || snap.unit_type || '';
            // 可用数量按仓库过滤：这里不使用快照总量，留待下方按仓库查询补全
            const available = undefined as unknown as number | undefined;
            const content = snap?.content;
            return {
              target_type: t?.target_type,
              project_id: t?.project_id,
              sorting_group_id: t?.sorting_group_id,
              inventory_unit_id: t?.inventory_unit_id,
              inventory_unit_system_barcode: sysBarcode,
              prev_instruction_id: t?.prev_instruction_id,
              output_selector: t?.output_selector,
              expected_qty: t?.use_all_available ? undefined : (t?.expected_qty != null ? Number(t.expected_qty) : undefined),
              use_all_available: !!t?.use_all_available,
              snapshot: t?.snapshot,
              fingerprint: t?.fingerprint,
              // 编辑页：本指令对该IU的活跃锁定量（用于展示“可操作上限”= 可用 + 本指令锁定）
              reserved_qty_for_instruction: (t as any)?.reserved_qty_for_instruction ?? 0,
              unit_type: snap.unit_type,
              unit_type_display: snap.unit_type_display,
              actual_length: snap?.actual_length,
              actual_width: snap?.actual_width,
              actual_height: snap?.actual_height,
              actual_weight: snap?.actual_weight,
              // 供表格显示
              display: t?.target_type === 'inventory_unit' ? (
                sysBarcode ? `${sysBarcode} · ${unitType || ''}`.trim() : `库存单元#${t?.inventory_unit_id}`
              ) : undefined,
              content,
              available_qty: available,
            } as any;
            });
            setTargets(mapped);
            originalTargetsRef.current = compact(mapped);
          }
        } catch {}
      } catch {
        message.error('加载指令数据失败，进入编辑模式失败');
      } finally {
        setEditTargetsReady(true);
      }
    })();
  }, [editId]);

  // 编辑模式：为缺少内容/数量的库存单元目标补全细节
  useEffect(() => {
    if (!isEditMode) return;
    try {
      const pending = (targets || []).map((t:any, idx:number) => ({ t, idx }))
        .filter(x => x.t?.target_type === 'inventory_unit' && (!x.t?.content || !Array.isArray(x.t?.content?.items) || x.t.content.items.length === 0))
        .filter(x => Number.isFinite(Number(x.t?.inventory_unit_id)))
        .map(x => ({ id: Number(x.t.inventory_unit_id), idx: x.idx }));
      const unique = Array.from(new Set(pending.map(p => p.id)))
        .filter(id => !fetchedUnitDetailRef.current.has(id));
      if (unique.length === 0) return;
      (async () => {
        try {
          const wid = form.getFieldValue('warehouse_id');
          const details = await Promise.all(unique.map(async (id) => {
            try {
              const res = await getInventoryUnitDetail(id, wid ? { warehouse_id: wid } : undefined);
              return { id, data: res?.data || {} };
            } catch { return { id, data: {} }; }
          }));
          const idToUnit: Record<number, any> = {};
          details.forEach(({ id, data }) => { idToUnit[id] = data; fetchedUnitDetailRef.current.add(id); });
          // 更新targets
          setTargets(prev => prev.map((row:any) => {
            if (row?.target_type === 'inventory_unit' && Number.isFinite(Number(row?.inventory_unit_id))) {
              const id = Number(row.inventory_unit_id);
              const unit = idToUnit[id];
              if (unit && (!row.content || !Array.isArray(row?.content?.items) || row.content.items.length === 0)) {
                const content = unit?.content;
                const system_barcode = unit?.system_barcode || unit?.system_code;
                const unitType = unit?.unit_type_display || unit?.unit_type || '';
                const baseAvail = (unit?.available_qty != null ? Number(unit.available_qty)
                  : (unit?.warehouse_total_quantity != null ? Number(unit.warehouse_total_quantity) : row.available_qty));
                const reservedSelf = Number((row as any)?.reserved_qty_for_instruction || 0) || 0;
                // 编辑页口径：可操作上限 = 当前可用 + 本指令锁定
                const available = (Number.isFinite(baseAvail as any) ? baseAvail : 0) + reservedSelf;
                return {
                  ...row,
                  content: content || row.content,
                  available_qty: Number.isFinite(available as any) ? available : row.available_qty,
                  inventory_unit_system_barcode: system_barcode || row.inventory_unit_system_barcode,
                  unit_type: unit?.unit_type || row.unit_type,
                  unit_type_display: unit?.unit_type_display || row.unit_type_display,
                  actual_length: unit?.actual_length ?? row.actual_length,
                  actual_width: unit?.actual_width ?? row.actual_width,
                  actual_height: unit?.actual_height ?? row.actual_height,
                  actual_weight: unit?.actual_weight ?? row.actual_weight,
                  display: system_barcode ? `${system_barcode} · ${unitType || ''}`.trim() : row.display,
                };
              }
            }
            return row;
          }));
          // 收集产品ID，补充产品详情（用于内容列名称与编码）
          const prodIds: number[] = [];
          Object.values(idToUnit).forEach((u:any) => {
            const items = Array.isArray(u?.content?.items) ? u.content.items : [];
            items.forEach((it:any) => { const pid = Number(it?.product); if (Number.isFinite(pid)) prodIds.push(pid); });
          });
          const uniqPids = Array.from(new Set(prodIds)).filter((pid:number) => !productInfo[pid]);
          if (uniqPids.length > 0) {
            const map: Record<number, any> = {};
            await Promise.all(uniqPids.map(async (pid:number) => {
              try { const resp = await getProductDetail(pid); map[pid] = (resp as any)?.data || {}; } catch {}
            }));
            setProductInfo(prev => ({ ...prev, ...map }));
          }
        } catch {}
      })();
    } catch {}
  }, [isEditMode, targets]);

  // 将现有附件映射到规则键（或自定义分组）以恢复"已提交"数量显示
  useEffect(() => {
    if (!isEditMode) return;
    if (attachmentsRestoredRef.current) return;
    const rows = ruleRows || [];
    if (!tplVal || tplVal === '__NONE__') {
      // 无模板：全部归入自定义
      const files = (existingAttachments || []).map((a:any) => ({
        uid: `existing-${a.id}`,
        name: String(a.file_name || a.file_path || a.label || `附件${a.id}`),
        status: 'done' as const,
        url: getAttachmentUrl(a),
      }));
      if (files.length > 0) {
        setAttachFilesMap(prev => ({ ...prev, [CUSTOM_ATTACH_KEY]: files as any }));
        attachmentsRestoredRef.current = true;
      }
      return;
    }
    // 有模板：按规则项匹配（优先 label 近似匹配，其次扩展名匹配，其次 scope 匹配）
    const norm = (s:any) => String(s || '').trim().toLowerCase();
    const ruleByLabel: Record<string, string[]> = {};
    rows.forEach((r:any) => {
      const lbl = norm(r.label);
      if (!lbl) return;
      if (!ruleByLabel[lbl]) ruleByLabel[lbl] = [];
      ruleByLabel[lbl].push(r.key);
    });
    const resultMap: Record<string, any[]> = {};
    const resultPrintConfig: Record<string, Record<string, any>> = {};
    const ensure = (k:string) => { if (!resultMap[k]) resultMap[k] = []; return resultMap[k]; };
    const getExt = (name: string) => {
      const n = String(name || '');
      if (!n.includes('.')) return '';
      const e = n.split('.').pop() || '';
      return e.toLowerCase();
    };
    (existingAttachments || []).forEach((a:any) => {
      const fileName = String(a.file_name || a.file_path || '');
      const label = norm(a.label);
      const ext = getExt(fileName);
      let matchedKey: string | undefined;
      // 1) label 精确或包含匹配
      if (label) {
        const keys = ruleByLabel[label];
        if (keys && keys.length > 0) matchedKey = keys[0];
        if (!matchedKey) {
          const lblKeys = Object.keys(ruleByLabel);
          const found = lblKeys.find(k => k && (k.includes(label) || label.includes(k)));
          if (found) matchedKey = ruleByLabel[found][0];
        }
      }
      // 2) 扩展名匹配
      if (!matchedKey && ext) {
        const foundByExt = rows.find((r:any) => Array.isArray(r.types) && r.types.some((t:any) => norm(t).replace(/^\./,'') === ext));
        if (foundByExt) matchedKey = foundByExt.key;
      }
      // 3) scope 粗匹配（order/actions）
      if (!matchedKey && a.scope) {
        const firstScope = rows.find((r:any) => String(r.scope) === String(a.scope));
        if (firstScope) matchedKey = firstScope.key;
      }
      // 4) 兜底自定义
      const bucket = matchedKey || CUSTOM_ATTACH_KEY;
      const uid = `existing-${a.id}`;
      ensure(bucket).push({
        uid,
        name: fileName || (a.label ? String(a.label) : `附件${a.id}`),
        status: 'done',
        url: getAttachmentUrl(a),
      } as any);
      const pc = a.meta?.print_config;
      if (pc && matchedKey) {
        if (!resultPrintConfig[matchedKey]) resultPrintConfig[matchedKey] = {};
        resultPrintConfig[matchedKey][uid] = {
          print: !!pc.print,
          copies: Number(pc.copies) ?? 0,
          paper_size: String(pc.paper_size || 'A4'),
          placement: String(pc.placement || 'inside'),
          explicit_no_print: !!pc.explicit_no_print,
        };
      }
    });
    if (Object.keys(resultMap).length > 0) {
      setAttachFilesMap(prev => ({ ...prev, ...resultMap }));
      attachmentsRestoredRef.current = true;
    }
    if (Object.keys(resultPrintConfig).length > 0) {
      setAttachPrintConfigMap(prev => ({ ...prev, ...resultPrintConfig }));
    }
  }, [isEditMode, existingAttachments, ruleRows, tplVal]);

  const submit = async () => {
    // 表单必填项校验：拦截抛错并提示
    let v: any;
    try {
      v = await form.validateFields();
    } catch (vf: any) {
      try {
        const errs = Array.isArray(vf?.errorFields)
          ? vf.errorFields.map((f: any) => (Array.isArray(f?.errors) && f.errors[0]) || '校验失败').filter(Boolean)
          : [];
        message.error(`请完善必填项${errs.length ? `：${errs.slice(0, 3).join('、')}${errs.length > 3 ? ' 等' : ''}` : ''}`);
      } catch {
        message.error('请完善必填项');
      }
      return;
    }
    // 操作对象校验：
    // - 当前可用库存：必须选择 targets
    // - 历史指令执行结果：必须至少选择一个 IOC
    if (targetSourceMode === 'inventory') {
      if (!targets || targets.length === 0) {
        message.error('请至少添加一个操作对象');
        return;
      }
    } else {
      const hasSelectedIoc = Array.isArray(historyTargetRows) && historyTargetRows.length > 0;
      if (!hasSelectedIoc) {
        message.error('请选择至少一个IOC操作对象');
        return;
      }
    }
    if (isSortAndCountTemplate && sortAndCountTargetValidation.blocked) {
      Modal.warning({
        title: '提示',
        icon: null,
        centered: true,
        width: 900,
        okText: '知道了',
        content: renderSortAndCountValidationWarningContent(sortAndCountTargetValidation.reason || '库存分拣与清点指令操作对象校验未通过'),
      });
      return;
    }
    // 问题集必填项前端校验
    try {
      const missing = (Array.isArray(visibleQRows) ? visibleQRows : [])
        .filter((q: any) => isQuestionRequired(q))
        .filter((q: any) => {
          const key = String(q?.key || '').trim();
          const val = answers[key];
          if (key === '') return true;
          return !isQuestionAnswered(q, val);
        })
        .map((q: any) => q?.label || q?.key)
        .filter(Boolean);
      if (missing.length > 0) {
        message.error(`请先填写必答问题：${missing.slice(0, 3).join('、')}${missing.length > 3 ? ' 等' : ''}`);
        return;
      }
    } catch {}
    // 一件代发：包装预估结果校验与写入
    const packPayload = (() => {
      if (!showPackingAssessment) return {};
      const needPacking = !!packingAssessmentStatus.needPacking;
      if (!needPacking) {
        return {
          is_pack_required: false,
          estimated_packing_method: undefined,
          estimated_packaging: undefined,
        };
      }
      if (!packingOptionId) {
        message.error('请先选择打包方式');
        return null;
      }
      const optId = String(packingOptionId);
      const methodMap: Record<string, string> = {
        free_packaging: 'warehouse_free',
        paid_carton: 'warehouse_paid',
        customer_carton: 'customer_carton',
        customer_soft: 'customer_non_carton',
        special_packing: 'special_packing',
      };
      const method = methodMap[optId];
      if (!method) {
        message.error('打包方式无效，请重新选择');
        return null;
      }
      const pkgId = (optId === 'free_packaging')
        ? packingPackagingIds.warehouse_free
        : (optId === 'paid_carton')
          ? packingPackagingIds.warehouse_paid
          : (optId === 'customer_carton')
            ? packingPackagingIds.customer_carton
            : undefined;
      const estWeight = (optId === 'free_packaging')
        ? packingEstimates?.warehouse_free?.weight_g
        : (optId === 'paid_carton')
          ? packingEstimates?.warehouse_paid?.weight_g
          : (optId === 'customer_carton')
            ? packingEstimates?.customer_carton?.weight_g
            : undefined;
      const estSize = (optId === 'free_packaging')
        ? packingEstimates?.warehouse_free
        : (optId === 'paid_carton')
          ? packingEstimates?.warehouse_paid
          : (optId === 'customer_carton')
            ? packingEstimates?.customer_carton
            : undefined;
      if (['warehouse_free','warehouse_paid','customer_carton'].includes(method) && !pkgId) {
        message.error('缺少推荐包材，请稍后重试或改选其他方式');
        return null;
      }
      return {
        is_pack_required: true,
        estimated_packing_method: method,
        estimated_packaging: pkgId,
        estimated_packaged_weight_g: (estWeight != null && Number.isFinite(Number(estWeight))) ? Number(estWeight) : undefined,
        estimated_packaged_length_cm: (estSize?.l != null && Number.isFinite(Number(estSize.l))) ? Number(estSize.l) : undefined,
        estimated_packaged_width_cm: (estSize?.w != null && Number.isFinite(Number(estSize.w))) ? Number(estSize.w) : undefined,
        estimated_packaged_height_cm: (estSize?.h != null && Number.isFinite(Number(estSize.h))) ? Number(estSize.h) : undefined,
      };
    })();
    if (packPayload === null) return;
    // 推导项目ID：优先表单值，其次取已添加的操作对象中的项目ID
    const projectIdFromTargets = (() => {
      try {
        const first = (targets || []).find((t:any) => t && t.project_id != null);
        return first ? Number(first.project_id) : undefined;
      } catch { return undefined; }
    })();
    const projectIdFromHistory = (() => {
      try {
        if (targetSourceMode !== 'history_ioc') return undefined;
        const pidFromRows = (historyTargetRows || []).find((r:any) => r && r.project_id != null)?.project_id;
        const pid = pidFromRows ?? historyInstrRow?.project_id ?? historyInstrRow?.project?.id;
        const n = Number(pid);
        return Number.isFinite(n) && n > 0 ? n : undefined;
      } catch { return undefined; }
    })();
    const projectId = v.project_id != null ? Number(v.project_id) : (projectIdFromHistory ?? projectIdFromTargets);
    if (!Number.isFinite(projectId as any)) {
      message.error(targetSourceMode === 'history_ioc' ? '缺少项目：请先在“历史指令执行结果”的操作对象中添加IOC' : '缺少项目：请先在"添加操作对象"中选择项目');
      return;
    }
    // 推导客户ID：从项目对象取常见字段
    const projAny: any = (projects || []).find((p:any)=> Number(p?.id) === Number(projectId));
    const customerIdCandidates = [
      projAny?.customer_id,
      projAny?.customerId,
      projAny?.customer?.id,
      projAny?.owner_id,
      projAny?.owner,
    ];
    const customerIdFromProj = customerIdCandidates.find((x:any)=> Number.isFinite(Number(x)));
    const customerIdFromHistory = (() => {
      try {
        if (targetSourceMode !== 'history_ioc') return undefined;
        const pidFromRows = (historyTargetRows || []).find((r:any) => r && r.project_id != null)?.project_id;
        if (pidFromRows != null) {
          const proj = (projects || []).find((p:any)=> Number(p?.id) === Number(pidFromRows));
          const cands = [proj?.customer_id, proj?.customerId, proj?.customer?.id, proj?.owner_id, proj?.owner];
          const got = cands.find((x:any)=> Number.isFinite(Number(x)));
          if (got != null) return Number(got);
        }
        const cid = historyInstrRow?.customer_id ?? historyInstrRow?.customer?.id;
        const n = Number(cid);
        return Number.isFinite(n) && n > 0 ? n : undefined;
      } catch { return undefined; }
    })();
    const customerId = (customerIdFromProj != null ? customerIdFromProj : customerIdFromHistory);
    if (!Number.isFinite(Number(customerId))) {
      message.error('缺少客户：无法从所选项目推导客户信息');
      return;
    }
    // 仓库：库存模式由用户选择；历史模式不显示，但必须能从历史指令推导
    const warehouseIdToUse = (() => {
      try {
        const fromForm = v?.warehouse_id ?? form.getFieldValue('warehouse_id');
        const n1 = Number(fromForm);
        if (Number.isFinite(n1) && n1 > 0) return n1;
      } catch {}
      try {
        if (targetSourceMode === 'history_ioc') {
          const wid = historyInstrRow?.warehouse_id ?? historyInstrRow?.warehouse_detail?.id;
          const n2 = Number(wid);
          if (Number.isFinite(n2) && n2 > 0) {
            try { form.setFieldsValue({ warehouse_id: n2 }); } catch {}
            return n2;
          }
        }
      } catch {}
      return undefined;
    })();
    if (!warehouseIdToUse) {
      message.error(targetSourceMode === 'history_ioc' ? '缺少仓库：请先选择历史指令（将自动带出仓库）' : '请选择仓库');
      return;
    }
    const targetsToSubmit = (() => {
      if (targetSourceMode === 'inventory') return targets || [];
      const rows = Array.isArray(historyTargetRows) ? historyTargetRows : [];
      if (rows.length > 0) {
        return rows.map((row:any) => {
          const code = String(row?.output_code || row?.display_code || '').trim();
          const prevId = Number(row?.instruction_id || historyInstrId || appendFromId || 0) || 0;
          return {
          target_type: 'instruction_output',
          project_id: Number(row?.project_id || projectId),
          prev_instruction_id: prevId > 0 ? prevId : undefined,
          output_selector: { ioc_codes: code ? [code] : [] },
          use_all_available: true,
          expected_qty: undefined,
          display: `${String(row?.instruction_code || historyInstrRow?.system_code || prevId || '')} · ${code}`,
        };
        }).filter((t:any) => Array.isArray(t?.output_selector?.ioc_codes) && t.output_selector.ioc_codes.length > 0);
      }
      // 未选择 IOC：允许空 targets（用 remarks 表达需求）
      return [];
    })();

    const normalizedCustomerAnswers = (() => {
      if (!(Array.isArray(qRows) && qRows.length > 0)) return undefined;
      const base = (answers && typeof answers === 'object') ? (answers as any) : {};
      const next: any = { ...base };
      (Array.isArray(visibleQRows) ? visibleQRows : []).forEach((q: any) => {
        const key = String(q?.key || '').trim();
        if (!key) return;
        const type = String(q?.type || '').trim().toLowerCase();
        if (type !== 'boolean') return;
        if (!isQuestionRequired(q)) return;
        const cur = next[key];
        if (cur === undefined || cur === null || cur === '') {
          // 与 UI 语义保持一致：布尔开关默认关闭即“否(false)”
          next[key] = false;
        }
      });
      return next;
    })();

    const payload: any = {
      project_id: Number(projectId),
      warehouse_id: warehouseIdToUse,
      priority: (v.priority || 'normal'),
      name: (v.name != null && String(v.name).trim() !== '') ? String(v.name).trim() : undefined,
      template: (v.template && v.template !== '__NONE__') ? Number(v.template) : null,
      customer_answers: normalizedCustomerAnswers,
      customer_id: Number(customerId),
      ...(packPayload || {}),
      // 直接使用已构建的操作对象列表
      targets: targetsToSubmit.map((t:any) => ({
        target_type: t.target_type,
        // 保留每个 target 自身的项目归属，不要统一覆盖为主项目
        project_id: t.project_id,
        sorting_group_id: t.sorting_group_id,
        inventory_unit_id: t.inventory_unit_id,
        prev_instruction_id: t.prev_instruction_id,
        output_selector: t.output_selector,
        expected_qty: t.use_all_available ? undefined : t.expected_qty,
        use_all_available: !!t.use_all_available,
        expected_weight_g: t.expected_weight_g,
        expected_volume_cc: t.expected_volume_cc,
        expected_duration_min: t.expected_duration_min,
        snapshot: t.snapshot,
        fingerprint: t.fingerprint,
      })),
      // 动作由模板自动展开，不从客户端提交
      remarks: v.remarks,
    };
    try {
      const waybillSourceRaw = String((answers as any)?.waybill_source || '').trim();
      if (waybillSourceRaw) {
        const lower = waybillSourceRaw.toLowerCase();
        if (lower === 'upload' || /上传/.test(waybillSourceRaw)) {
          payload.waybill_source = 'upload';
        } else if (lower === 'warehouse' || /仓库/.test(waybillSourceRaw)) {
          payload.waybill_source = 'warehouse';
        }
      }
    } catch {}
    const buildTrackingDupCopyText = (warnings: Array<{ provider: string; tracking_number: string }>) => {
      return [
        '序号\t物流服务商\t跟踪号',
        ...warnings.map((item, index) => `${index + 1}\t${item.provider}\t${item.tracking_number}`)
      ].join('\n');
    };
    const copyTrackingDupWarnings = async (warnings: Array<{ provider: string; tracking_number: string }>) => {
      try {
        await navigator.clipboard.writeText(buildTrackingDupCopyText(warnings));
        message.success('已复制');
      } catch (err) {
        message.error('复制失败,请重试');
      }
    };
    const renderTrackingDupTitle = (warnings: Array<{ provider: string; tracking_number: string }>) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>提示</span>
        <Button size="small" onClick={() => copyTrackingDupWarnings(warnings)}>复制</Button>
      </div>
    );
    const renderTrackingDupContent = (warnings: Array<{ provider: string; tracking_number: string }>) => (
      <div>
        <div>以下记录已被使用,请确认提供的信息无误.若确认无误,可继续提交</div>
        <div style={{ marginTop: 8, border: '1px solid #d9d9d9', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ display: 'flex', fontWeight: 600, background: '#fafafa', borderBottom: '1px solid #d9d9d9' }}>
            <div style={{ width: 60, padding: '6px 8px', borderRight: '1px solid #d9d9d9' }}>序号</div>
            <div style={{ width: 160, padding: '6px 8px', borderRight: '1px solid #d9d9d9' }}>物流服务商</div>
            <div style={{ flex: 1, padding: '6px 8px' }}>跟踪号</div>
          </div>
          {warnings.map((item, index) => (
            <div key={`${item.provider}-${item.tracking_number}-${index}`} style={{ display: 'flex', borderBottom: index === warnings.length - 1 ? 'none' : '1px solid #f0f0f0' }}>
              <div style={{ width: 60, padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>{index + 1}</div>
              <div style={{ width: 160, padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>{item.provider}</div>
              <div style={{ flex: 1, padding: '6px 8px' }}>{item.tracking_number}</div>
            </div>
          ))}
        </div>
      </div>
    );
    const confirmTrackingDupWarnings = (warnings: Array<{ provider: string; tracking_number: string }>) => (
      new Promise<boolean>((resolve) => {
        Modal.confirm({
          title: renderTrackingDupTitle(warnings),
          content: renderTrackingDupContent(warnings),
          centered: true,
          width: 720,
          okText: '继续提交',
          cancelText: '返回修改',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      })
    );
    // 运单提交方式（由问卷答案映射）
    try {
      const mode = resolveWaybillSubmitMode();
      if (mode) {
        payload.waybill_submit_mode = mode;
        if (mode === 'after_pack') {
          const existing = editDetail?.pack_result_status;
          if (!isEditMode || !existing) {
            payload.pack_result_status = 'pending';
          }
        }
      }
    } catch {}
    // 校验必须提交附件是否达标
    try {
      const lacks: string[] = [];
      (visibleRuleRows || []).forEach(r => {
        const isReq = Number(r.min ?? 0) >= 1;
        const files = attachFilesMap[r.key] || [];
        if (isReq && files.length < Number(r.min || 1)) {
          lacks.push(r.label || r.key);
        }
      });
      if (lacks.length > 0) {
        message.error(`请先提交必须附件：${lacks.slice(0,3).join('、')}${lacks.length>3?' 等':''}`);
        return;
      }
      // 校验需打印附件的打印设置：仅当「是否打印」为开启时，校验打印份数、纸张、放置位置完整
      const printLacks: string[] = [];
      (visibleRuleRows || []).forEach(r => {
        if (!(r as any).printable) return;
        const files = attachFilesMap[r.key] || [];
        (files || []).forEach((f: any) => {
          const uid = String(f?.uid || '');
          const cfg = attachPrintConfigMap[r.key]?.[uid] || {};
          if (!cfg.print) return;
          const valid = typeof cfg.copies === 'number' && cfg.copies >= 1 && cfg.paper_size && cfg.placement;
          if (!valid) {
            printLacks.push(`${r.label || ''} - ${String(f?.name || '附件')}`);
          }
        });
      });
      if (printLacks.length > 0) {
        message.error(`需打印附件请填写完整打印设置（打印份数、纸张尺寸、放置位置）：${printLacks.slice(0, 2).join('、')}${printLacks.length > 2 ? ' 等' : ''}`);
        return;
      }
    } catch {}

    // 新标签 PDF 质量预审：不影响提交流程（无论预审中/是否存在风险，均允许提交）

    setLoading(true);
    try {
      // 一件代发 + 立即上传：物流服务商+运单号唯一性预检查（仅警告，可继续提交）
      if (!isEditMode && showPackingAssessment && resolveWaybillSubmitMode() === 'immediate') {
        const carrier = (answers as any)?.waybill_carrier ?? (payload as any)?.waybill_carrier ?? '';
        const tracking = (answers as any)?.tracking_number ?? (answers as any)?.waybill_tracking_no ?? (payload as any)?.waybill_tracking_no ?? '';
        if (carrier && tracking) {
          try {
            const precheckResp = await request.post('/api/v1/instruction/instructions/precheck/', {
              template: payload.template,
              customer_answers: payload.customer_answers,
              waybill_carrier: carrier,
              waybill_tracking_no: tracking,
            });
            const warnings = Array.isArray((precheckResp as any)?.data?.warnings)
              ? (precheckResp as any).data.warnings
              : (Array.isArray((precheckResp as any)?.warnings) ? (precheckResp as any).warnings : []);
            if (warnings.length > 0) {
              setLoading(false);
              const proceed = await confirmTrackingDupWarnings(warnings);
              if (!proceed) return;
              setLoading(true);
            }
          } catch (e) {
            setLoading(false);
            message.error('唯一性校验失败，请稍后重试');
            return;
          }
        }
      }
      // 编辑模式：若操作对象未变更，则不要提交 targets 字段，避免后端覆盖式重建导致"新增/移除"假变更
      if (isEditMode) {
        try {
          const compact = (arr:any[]) => {
            const norm = (x:any) => ({
              target_type: x?.target_type ?? null,
              project_id: Number.isFinite(Number(x?.project_id)) ? Number(x.project_id) : null,
              sorting_group_id: Number.isFinite(Number(x?.sorting_group_id)) ? Number(x.sorting_group_id) : null,
              inventory_unit_id: Number.isFinite(Number(x?.inventory_unit_id)) ? Number(x.inventory_unit_id) : null,
              prev_instruction_id: Number.isFinite(Number(x?.prev_instruction_id)) ? Number(x.prev_instruction_id) : null,
              output_selector: x?.output_selector ?? null,
              expected_qty: x?.use_all_available ? null : (x?.expected_qty != null ? Number(x.expected_qty) : null),
              use_all_available: !!x?.use_all_available,
              fingerprint: x?.fingerprint ?? null,
            });
            const out = (arr || []).map(norm);
            out.sort((a:any,b:any)=> {
              const ks = ['target_type','project_id','sorting_group_id','inventory_unit_id','prev_instruction_id','output_selector','expected_qty','use_all_available','fingerprint'];
              for (const k of ks) {
                const av = (a as any)[k]; const bv = (b as any)[k];
                if (av === bv) continue;
                return String(av).localeCompare(String(bv));
              }
              return 0;
            });
            return out;
          };
          const currentCompact = compact(payload.targets || []);
          const originalCompact = Array.isArray(originalTargetsRef.current) ? originalTargetsRef.current : [];
          if (JSON.stringify(currentCompact) === JSON.stringify(originalCompact)) {
            delete payload.targets;
          }
        } catch {}
      }
      // 调试输出
      try {
        console.groupCollapsed('%cCreate Instruction - Request','color:#1677ff');
        console.log('projectId', projectId, 'customerId', customerId);
        console.log('payload', payload);
        console.log('ruleRows', ruleRows);
        console.log('attachFilesMap keys', Object.keys(attachFilesMap||{}));
        console.groupEnd();
      } catch {}
      // 创建 / 修改 分支
      let instructionId: number = 0;
      let instr: any = {};
      if (isEditMode) {
        const res = await request.patch(`/api/v1/instruction/instructions/${editId}/`, payload);
        instr = (res as any)?.data || {};
        instructionId = Number(instr.id || editId);
      } else {
        const res = await request.post('/api/v1/instruction/instructions/', payload);
        instr = (res as any)?.data || {};
        instructionId = Number(instr.id);
      }
      // 编辑模式：仅删除"用户在UI中移除"的旧附件（保留未变更的旧附件），然后只上传新选择的文件
      if (isEditMode) {
        try {
          // 1) 原始旧附件ID集合
          const originalIds: number[] = Array.isArray(existingAttachments) ? existingAttachments.map((a:any)=> Number(a.id)).filter((v:number)=>Number.isFinite(v)) : [];
          // 2) 当前保留的旧附件ID集合（仍在UI文件列表中的 existing-* 项）
          const keptIds = new Set<number>();
          try {
            const entries = Object.entries(attachFilesMap || {});
            for (const [, files] of entries) {
              (files || []).forEach((f:any) => {
                const m = String(f?.uid || '').match(/^existing-(\d+)/);
                if (m && m[1]) {
                  const idNum = Number(m[1]);
                  if (Number.isFinite(idNum)) keptIds.add(idNum);
                }
              });
            }
          } catch {}
          // 如果没有任何文件列表（比如未渲染或未恢复），默认不删除任何旧附件
          const hasAnyFileList = Object.values(attachFilesMap || {}).some((arr:any)=> Array.isArray(arr) && arr.length > 0);
          const toDelete = hasAnyFileList ? originalIds.filter(id => !keptIds.has(id)) : [];
          if (toDelete.length > 0) {
            await Promise.all(toDelete.map(id => {
              try { return request.delete(`/api/v1/instruction/attachments/${id}/`); } catch { return Promise.resolve(); }
            }));
          }
        } catch {}
      }
      // 逐规则上传附件（仅上传新选择的文件；保留型旧附件不会重复上传）
      // 每个附件的结果都要告诉用户：失败的文件收集起来，最后统一提示（不再静默吞掉）
      const uploadFailures: UploadFailure[] = [];
      const entries = Object.entries(attachFilesMap || {});
      const pendingUploadTotal = entries.reduce((sum, [key, files]) => {
        const row = (ruleRows || []).find(r => r.key === key);
        const list = (files || []).filter((f:any) => !!f?.originFileObj);
        if (!row) return sum + (String(key).startsWith(CUSTOM_ATTACH_KEY) ? list.length : 0);
        return sum + Math.min(list.length, Number(row.max ?? list.length));
      }, 0);
      let uploadedCount = 0;
      const progressKey = 'attach-upload-progress';
      const postAttachment = async (fd: FormData, fileName: string) => {
        uploadedCount += 1;
        const label = `正在上传附件 ${uploadedCount}/${pendingUploadTotal}：${fileName}`;
        message.loading({ key: progressKey, content: label, duration: 0 });
        const failure = await uploadInstructionAttachment(fd, fileName, (percent) => {
          message.loading({ key: progressKey, content: `${label}（${percent}%）`, duration: 0 });
        });
        if (failure) uploadFailures.push(failure);
      };
      for (const [key, files] of entries) {
        const row = (ruleRows || []).find(r => r.key === key);
        if (!files || files.length === 0) continue;
        if (!row) {
          // 自定义附件（无模板/无规则时添加）
          if (String(key).startsWith(CUSTOM_ATTACH_KEY)) {
            for (let i=0; i<files.length; i++) {
              const f = files[i];
              if (!f.originFileObj) continue;
              const fd = new FormData();
              fd.append('instruction', String(instructionId));
              fd.append('scope', 'order');
              fd.append('label', '自定义附件');
              fd.append('file', f.originFileObj as Blob);
              try { fd.append('file_path', String(f.name || '附件')); } catch {}
              await postAttachment(fd, String(f.name || '附件'));
            }
          }
          continue;
        }
        const max = Number(row.max ?? files.length);
        for (let i=0; i<Math.min(files.length, max); i++) {
          const f = files[i];
          if (!f.originFileObj) continue;
          const fd = new FormData();
          fd.append('instruction', String(instructionId));
          fd.append('scope', row.scope);
          fd.append('label', String(row.label || '附件'));
          fd.append('file', f.originFileObj as Blob);
          try { fd.append('file_path', String(f.name || '附件')); } catch {}
          if ((row as any).printable) {
            const cfg = attachPrintConfigMap[key]?.[String(f?.uid || '')];
            if (cfg?.print === true) {
              fd.append('meta', JSON.stringify({
                print_config: {
                  print: true,
                  copies: Math.max(1, Number(cfg.copies) || 1),
                  paper_size: String(cfg.paper_size || ''),
                  placement: String(cfg.placement || ''),
                  explicit_no_print: false,
                },
              }));
            } else if (cfg?.explicit_no_print === true) {
              fd.append('meta', JSON.stringify({
                print_config: {
                  print: false,
                  explicit_no_print: true,
                },
              }));
            }
          }
          await postAttachment(fd, String(f.name || '附件'));
        }
      }
      try { message.destroy(progressKey); } catch {}
      // 有附件没上传成功：必须让用户看到（指令本身已经保存，可以到"修改指令"里重新上传）
      if (uploadFailures.length > 0) {
        await new Promise<void>((resolve) => {
          Modal.warning({
            title: `指令已${isEditMode ? '保存' : '创建'}，但有 ${uploadFailures.length} 个附件没有上传成功`,
            width: 560,
            content: (
              <div>
                <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                  {uploadFailures.map((f, i) => (
                    <div key={`${f.name}-${i}`} style={{ marginBottom: 6 }}>
                      <b>{f.name}</b>：<span style={{ color: '#cf1322' }}>{f.reason}</span>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 10, color: '#595959' }}>
                  请到“修改指令”页面重新上传这些附件。单个文件不能超过 50MB，较大的文件（尤其是视频）请上传到网盘，把链接写在备注里。
                </div>
              </div>
            ),
            okText: '知道了',
            onOk: () => resolve(),
          });
        });
      }
      // 成功提示与跳转
      try {
        const code = instr?.system_code || instr?.systemCode || instr?.code;
        const name = (instr?.name && String(instr.name).trim() !== '') ? String(instr.name).trim() : '';
        const content = isEditMode
          ? `已修改指令：系统编码 ${code || '-'}${name ? `，名称：${name}` : ''}`
          : `已创建指令：系统编码 ${code || '-'}${name ? `，名称：${name}` : ''}`;
        message.success({ content, duration: 1.5 });
      } catch {}
      try { await new Promise((r)=> setTimeout(r, 1500)); } catch {}
      try { await clearDraft(); } catch {}
      try {
        const w: any = (window as any);
        const path = String(w?.location?.pathname || '');
        const base = path.startsWith('/dashboard/staff') ? '/dashboard/staff' : (path.startsWith('/dashboard/admin') ? '/dashboard/admin' : '/dashboard/customer');
        const listUrl = base + '/instructions/list';
        try {
          if (w.tabsManager && typeof w.tabsManager.openTabByPath === 'function') {
            w.tabsManager.openTabByPath(listUrl);
            if (typeof w.tabsManager.closeCurrentTab === 'function') {
              w.tabsManager.closeCurrentTab();
            }
          } else {
            w.location.href = listUrl;
          }
        } catch {
          w.location.href = listUrl;
        }
      } catch {}
    } catch (err:any) {
      const msg = extractErrorMessage(err);
      message.error(`${isEditMode ? '修改' : '创建'}失败：${msg}`);
      // 详细弹窗
      showCreateErrorModal(err, payload);
      try {
        console.groupCollapsed('%cCreate Instruction - Error','color:#ff4d4f');
        console.error('status', err?.response?.status);
        console.error('data', err?.response?.data);
        console.error('url', err?.config?.url);
        console.groupEnd();
      } catch {}
    } finally { setLoading(false); }
  };

  const hasModeSelections = useCallback((): boolean => {
    try {
      if (targetSourceMode === 'inventory') {
        return Array.isArray(targets) && targets.length > 0;
      }
      if (targetSourceMode === 'history_ioc') {
        return Array.isArray(historyTargetRows) && historyTargetRows.length > 0;
      }
      return false;
    } catch {
      return false;
    }
  }, [historyTargetRows, targetSourceMode, targets]);
  const disableByNoTargetsGlobal = React.useMemo(() => {
    try {
      const hasTemplate = !!tplVal && String(tplVal) !== '__NONE__';
      if (!hasTemplate) return false;
      if (isEditMode && !editTargetsReady) return false;
      return !hasModeSelections();
    } catch {
      return false;
    }
  }, [tplVal, isEditMode, editTargetsReady, hasModeSelections]);
  const disableAfterTemplateCombined = disableAfterTemplate
    || disableByNoTargetsGlobal
    || (isSortAndCountTemplate && !!sortAndCountTargetValidation.blocked);
  const lastNoTargetsWarnKeyRef = React.useRef<string>('');
  useEffect(() => {
    try {
      // 一件代发/换标+装箱已有专属 warning 逻辑；此处补全“其他模板”场景
      if (requiresInventoryLiuTargets) { lastNoTargetsWarnKeyRef.current = ''; return; }
      const hasTemplate = !!tplVal && String(tplVal) !== '__NONE__';
      if (!hasTemplate) { lastNoTargetsWarnKeyRef.current = ''; return; }
      if (isEditMode && !editTargetsReady) return;
      if (hasModeSelections()) { lastNoTargetsWarnKeyRef.current = ''; return; }
      const warnKey = `${String(tplVal)}|${targetSourceMode}|${(targets || []).length}|${(historyTargetRows || []).length}`;
      if (warnKey === lastNoTargetsWarnKeyRef.current) return;
      lastNoTargetsWarnKeyRef.current = warnKey;
      Modal.warning({
        title: '提示',
        icon: null,
        centered: true,
        width: 860,
        okText: '知道了',
        content: <div style={{ whiteSpace: 'pre-line', color: '#141414' }}>请先添加操作对象</div>,
      });
    } catch {}
  }, [requiresInventoryLiuTargets, tplVal, isEditMode, editTargetsReady, hasModeSelections, targetSourceMode, targets, historyTargetRows]);

  const clearOperationObjectState = useCallback(() => {
    try { setTargetModalOpen(false); } catch {}
    try { setTargets([]); } catch {}
    try { setInvSel({}); } catch {}
    try { setHistoryIocRows([]); } catch {}
    try { setHistoryInstrRow(null); } catch {}
    try { setHistoryInstrId(undefined); } catch {}
    try { setHistoryTargetRows([]); } catch {}
    try { setHistoryAvailableRows([]); } catch {}
    try { setHistoryAvailableSelectedRowKeys([]); } catch {}
    try { historyTargetForm.resetFields(); } catch {}
    try { setHistoryTargetModalOpen(false); } catch {}
  }, [historyTargetForm]);

  const switchTargetSourceMode = useCallback((next: TargetSourceMode) => {
    if (historyIocUnsupported && next === 'history_ioc') {
      Modal.warning({
        title: '提示',
        icon: null,
        centered: true,
        width: 860,
        okText: '知道了',
        content: <div style={{ whiteSpace: 'pre-line', color: '#141414' }}>{historyIocUnsupportedText}</div>,
      });
      return;
    }
    if (next === targetSourceMode) return;
    const doSwitch = () => {
      clearOperationObjectState();
      setTargetSourceMode(next);
      // 追加入口：默认预选历史指令
      if (next === 'history_ioc' && appendFromId) {
        setHistoryInstrId(appendFromId);
      }
    };
    if (!hasModeSelections()) {
      doSwitch();
      return;
    }
    Modal.confirm({
      title: '切换操作对象来源？',
      content: '切换后会清空已选择的操作对象，以避免同一指令混用两类操作对象。',
      okText: '确认切换并清空',
      cancelText: '取消',
      centered: true,
      onOk: () => { doSwitch(); },
    });
  }, [appendFromId, clearOperationObjectState, hasModeSelections, targetSourceMode, historyIocUnsupported, historyIocUnsupportedText]);
  useEffect(() => {
    if (!historyIocUnsupported) return;
    if (targetSourceMode !== 'history_ioc') return;
    const clearedLines = (historyTargetRows || []).map((r:any, idx:number) => {
      const code = String(r?.output_code || '-');
      const proj = String(r?.project_label || (Number.isFinite(Number(r?.project_id)) ? `项目#${r?.project_id}` : '-'));
      const instr = String(r?.instruction_id || '-');
      return `#${idx + 1} ${code} · ${proj} · 来源指令#${instr}`;
    });
    clearOperationObjectState();
    setTargetSourceMode('inventory');
    Modal.warning({
      title: '提示',
      icon: null,
      centered: true,
      width: 860,
      okText: '知道了',
      content: (
        <div style={{ whiteSpace: 'pre-line', color: '#141414' }}>
          {`${historyIocUnsupportedText}已自动切换为“当前可用库存”。${clearedLines.length > 0 ? `\n\n已清除的操作对象：\n${clearedLines.join('\n')}` : ''}`}
        </div>
      ),
    });
  }, [historyIocUnsupported, historyIocUnsupportedText, targetSourceMode, historyTargetRows, clearOperationObjectState]);
  useEffect(() => {
    try {
      if (!isSortAndCountTemplate) { lastSortAndCountWarnKeyRef.current = ''; return; }
      // 仅在已存在操作对象时，对“切换到该模板”的结果做提示
      if (!Array.isArray(targets) || targets.length === 0) { lastSortAndCountWarnKeyRef.current = ''; return; }
      if (!sortAndCountTargetValidation.blocked) { lastSortAndCountWarnKeyRef.current = ''; return; }
      const warnKey = `${String(tplVal)}|${targetSourceMode}|${targets.length}|${Array.from(sortAndCountTargetProjectIds).sort((a,b)=>a-b).join(',')}|${sortAndCountTargetValidation.reason}`;
      if (warnKey === lastSortAndCountWarnKeyRef.current) return;
      lastSortAndCountWarnKeyRef.current = warnKey;
      Modal.warning({
        title: '提示',
        icon: null,
        centered: true,
        width: 860,
        okText: '知道了',
        content: renderSortAndCountValidationWarningContent(sortAndCountTargetValidation.reason),
      });
    } catch {}
  }, [isSortAndCountTemplate, tplVal, targetSourceMode, targets, sortAndCountTargetProjectIds, sortAndCountTargetValidation, renderSortAndCountValidationWarningContent]);

  return (
    <div className="p-6">
      <Card title={isEditMode ? `指令修改-${editSystemCode || editId}` : '创建指令'} bordered>
        <Form
          form={form}
          layout="vertical"
          onValuesChange={(changed, all) => {
            try {
              if ('warehouse_id' in changed) {
                const wid = all?.warehouse_id;
                urlActions.updateFilters({ ci_warehouse_id: wid ? String(wid) : '' });
              }
              if ('template' in changed) {
                const tpl = all?.template;
                urlActions.updateFilters({ ci_template: tpl ? String(tpl) : '' });
              }
              if ('priority' in changed) {
                const pr = all?.priority || 'normal';
                urlActions.updateFilters({ ci_priority: String(pr) });
              }
              if ('remarks' in changed) {
                const rk = (all?.remarks ?? '') as string;
                urlActions.updateFilters({ ci_remarks: String(rk) });
              }
            } catch {}
          }}
        >
          {/* 操作对象来源：页面级第一决策（两张大卡片） */}
          <Card
            size="small"
            style={{ marginTop: 12 }}
            title={(
              <span style={sectionTitleStyle}>
                <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>
                操作对象来源
              </span>
            )}
          >
            <div style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
              {(() => {
                const active = targetSourceMode === 'inventory';
                return (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => { switchTargetSourceMode('inventory'); }}
                    onKeyDown={() => {}}
                    style={{
                      flex: 1,
                      cursor: 'pointer',
                      borderRadius: 8,
                      border: active ? '2px solid #1677ff' : '1px solid #d9d9d9',
                      background: active ? '#e6f4ff' : '#fafafa',
                      padding: 12,
                      position: 'relative',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'stretch', gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, color: '#0958d9' }}>
                          <DatabaseOutlined />
                          当前可用库存
                          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            {active ? <Tag color="blue" style={{ margin: 0 }}>已选择</Tag> : null}
                          </span>
                        </div>
                        <div style={{ marginTop: 8, fontSize: 12, color: '#595959', lineHeight: 1.5 }}>
                          从当前可用库存中选择库存单元作为本次指令的操作目标
                        </div>
                        <div style={{ marginTop: 6, fontSize: 12, color: '#8c8c8c' }}>
                          适用于：初次新建指令，如换标/一件代发出库/中转出库/分拣等常规操作
                        </div>
                      </div>
                    </div>
                    {/* 选中态：放大“√”，靠近卡片宽度约 2/3 位置（不使用水印） */}
                    {active ? (
                      <div
                        style={{
                          position: 'absolute',
                          left: '75%',
                          top: 10,
                          transform: 'translateX(-50%)',
                          fontSize: 60,
                          fontWeight: 900,
                          lineHeight: 1,
                          color: '#1677ff',
                          pointerEvents: 'none',
                        }}
                      >
                        √
                      </div>
                    ) : null}
                  </div>
                );
              })()}
              {(() => {
                const active = targetSourceMode === 'history_ioc';
                const disabled = historyIocUnsupported;
                return (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => { if (!disabled) switchTargetSourceMode('history_ioc'); }}
                    onKeyDown={() => {}}
                    style={{
                      flex: 1,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.6 : 1,
                      borderRadius: 8,
                      border: active ? '2px solid #389e0d' : '1px solid #d9d9d9',
                      background: active ? '#f6ffed' : '#fafafa',
                      padding: 12,
                      position: 'relative',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'stretch', gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, color: '#389e0d' }}>
                          <HistoryOutlined />
                          历史指令执行结果
                          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            {active ? <Tag color="green" style={{ margin: 0 }}>已选择</Tag> : null}
                          </span>
                        </div>
                        <div style={{ marginTop: 8, fontSize: 12, color: '#595959', lineHeight: 1.5 }}>
                          选择以前已经操作完成的指令(即:历史指令)的执行结果作为本次指令的操作对象.
                        </div>
                        {disabled ? (
                          <div style={{ marginTop: 6, fontSize: 12, color: '#cf1322' }}>
                            {templatePolicy.label}不支持该来源，只能使用“当前可用库存”
                          </div>
                        ) : null}
                        <div style={{ marginTop: 6, fontSize: 12, color: '#8c8c8c', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span>适用于:追加指令,如换标装箱指令完成后出库/先申请包装数据的指令补传运单后出库/已分拣产品处理等追加操作;</span>
                          <Button
                            type="link"
                            size="small"
                            style={{ padding: 0, height: 'auto' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              Modal.info({
                                title: '更多说明',
                                width: 760,
                                content: (
                                  <div style={{ whiteSpace: 'pre-line' }}>
                                    {`历史指令执行结果指的是:完成历史指令时产生的包装容器(即指令产出容器IOC)\n举例:换标+装箱指令完成后的包装容器/先申请包装数据的代发指令或常规出库指令的包装容器/分拣指令生成的包装容器等`}
                                  </div>
                                ),
                                centered: true,
                              });
                            }}
                          >
                            更多说明
                          </Button>
                        </div>
                      </div>
                    </div>
                    {/* 选中态：放大“√”，靠近卡片宽度约 2/3 位置（不使用水印） */}
                    {active ? (
                      <div
                        style={{
                          position: 'absolute',
                          left: '75%',
                          top: 10,
                          transform: 'translateX(-50%)',
                          fontSize: 60,
                          fontWeight: 900,
                          lineHeight: 1,
                          color: '#389e0d',
                          pointerEvents: 'none',
                        }}
                      >
                        √
                      </div>
                    ) : null}
                  </div>
                );
              })()}
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
              同一指令不可同时包含两种来源的操作对象,切换会清空已选择的操作对象列表
            </div>
          </Card>

          {/* 需回答问题（依据模板问卷动态渲染） */}
          {targetSourceMode === 'inventory' ? (
          <Card size="small" style={{ marginTop: 20, border: '2px solid #1677ff', borderRadius: 8 }} title={(
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* 第1行：仓库（标题区域内） */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 80 }}>
                  <span style={{ color: '#ff4d4f' }}>*</span>
                  <span style={sectionTitleStyle}>仓库</span>
                </div>
                <Form.Item
                  name="warehouse_id"
                  rules={[{ required: true }]}
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    style={{ width: 360 }}
                    showSearch
                    loading={loadingWarehouses}
                    optionFilterProp="label"
                    options={(warehouses || []).map((w:any)=>({ value: Number(w.id), label: `${w.code || w.id} - ${w.name || ''}` }))}
                    onDropdownVisibleChange={(open)=>{
                      if (!open) return;
                      // 仅在创建模式下提供默认仓库的便捷选择；编辑模式严格使用记录中的仓库，不做覆盖
                      if (isEditMode) return;
                      try {
                        const list = Array.isArray(warehouses) ? warehouses : [];
                        const preferred = list.find((w:any) => (String(w.code) === 'US-1-NH') || /美东仓库/.test(String(w.name || '')));
                        if (preferred && !form.getFieldValue('warehouse_id')) {
                          form.setFieldsValue({ warehouse_id: Number(preferred.id) });
                        }
                      } catch {}
                    }}
                    onChange={() => {
                      // 切换仓库会影响“可用库存”，这里保持原逻辑：清空已选目标
                      try { setTargets([]); setInvSel({}); } catch {}
                    }}
                  />
                </Form.Item>
              </div>

              {/* 第2行：操作对象 + 统计 + 按钮（标题区域内） */}
              <div
                style={{
                  position: 'relative',
                  display: 'grid',
                  gridTemplateColumns: '80px auto 1fr',
                  alignItems: 'center',
                  columnGap: 10,
                }}
              >
                {/* 左侧：标题 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ color: '#ff4d4f' }}>*</span>
                  <span style={sectionTitleStyle}>操作对象</span>
                </div>

                {/* 中间：按钮（左边线与第一行仓库下拉框左边线对齐） */}
                <div>
                  <Button
                    type="primary"
                    onClick={() => { if (!form.getFieldValue('warehouse_id')) { message.warning('请先选择仓库'); return; } targetForm.resetFields(); setProjectSgCount(0); setProjectUnitCount(0); setSortingGroups([]); setInventoryUnits([]); setUpstreamOptions([]); setTargetModalOpen(true); }}
                  >
                    添加操作对象
                  </Button>
                </div>

                {/* 统计信息：整行水平居中（不受左右宽度影响） */}
                <div
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    fontSize: 12,
                    color: '#1677ff',
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                  }}
                >
                  <span>已选：</span>
                  <span>项目({targetsProjectCount})</span>
                  <span style={{ display: 'inline-block', width: 1, height: 14, background: '#d9d9d9' }} />
                  <span>库存单元种类({targetsUnitKindCount})</span>
                  <span style={{ display: 'inline-block', width: 1, height: 14, background: '#d9d9d9' }} />
                  <span>库存单元数量({targetsUnitQtyTotal})</span>
                </div>
              </div>
            </div>
          )}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <Space>
                <Button
                  size="small"
                  disabled={!hasSelectedTargets}
                  style={{
                    borderColor: hasSelectedTargets ? '#1677ff' : '#d9d9d9',
                    color: hasSelectedTargets ? '#1677ff' : '#bfbfbf',
                    cursor: hasSelectedTargets ? 'pointer' as const : 'not-allowed' as const,
                  }}
                  onClick={() => { if (!hasSelectedTargets) return; setBulkMode('all'); setBulkQty(undefined); setBulkOpen(true); }}
                >批量修改数量</Button>
                <Button
                  size="small"
                  danger
                  disabled={!hasSelectedTargets}
                  style={{
                    borderColor: hasSelectedTargets ? '#ff4d4f' : '#d9d9d9',
                    color: hasSelectedTargets ? '#ff4d4f' : '#bfbfbf',
                    cursor: hasSelectedTargets ? 'pointer' as const : 'not-allowed' as const,
                  }}
                  onClick={() => {
                  if (selectedTargetKeys.length === 0) return;
                  Modal.confirm({
                    title: <div style={{ color: '#141414' }}>确认删除选中项？</div>,
                    content: <div style={{ whiteSpace: 'pre-line', color: '#141414' }}>将删除所选的操作对象{`（涉及多单元分拣组将整组删除）`}，此操作不可恢复。</div>,
                    icon: null,
                    centered: true,
                    okText: '确认删除',
                    cancelText: '取消',
                    okButtonProps: { style: { background: '#ff4d4f', color: '#fff' } },
                    cancelButtonProps: { style: { color: '#141414' } },
                    modalRender: (node) => (<div style={{ background: '#ffffff', color: '#141414', borderRadius: 8 }}>{node}</div>),
                    onOk: () => {
                      // 计算应删除的索引集合：若属于多单元分拣组，则整组删除
                      const toDeleteIdx = new Set<number>();
                      const selectedIdx = (selectedTargetKeys || []).map(k => Number(k as any)).filter(n => Number.isFinite(n));
                      selectedIdx.forEach((idx:number) => {
                        const row = targets[idx];
                        const gid = Number((row as any)?.sorting_group_id);
                        const isMulti = (typeof multiGroupIds !== 'undefined') && (multiGroupIds as any).has(gid);
                        if (isMulti && Number.isFinite(gid)) {
                          (targets || []).forEach((t:any, i:number) => {
                            if (Number((t as any)?.sorting_group_id) === gid) toDeleteIdx.add(i);
                          });
                        } else {
                          toDeleteIdx.add(idx);
                        }
                      });
                      setTargets(prev => prev.filter((_t, i) => !toDeleteIdx.has(i)));
                      setSelectedTargetKeys([]);
                    },
                  });
                }}>批量删除</Button>
              </Space>
            </div>
            <style>{`
              .targets-added-table-wrap { position: relative; }
              .targets-added-table-wrap .ant-table-pagination,
              .targets-added-table-wrap .ant-table-pagination-right {
                display: flex !important;
                justify-content: flex-end !important;
                align-items: center !important;
                width: 100% !important;
              }
              .targets-added-table-wrap .ant-pagination-total-text {
                flex: 1 1 auto;
                min-width: 0;
                height: auto !important;
                line-height: 22px !important;
              }
            `}</style>
            <div className="targets-added-table-wrap">
            <Table
              size="small"
              rowKey={(r:any)=>String(r?.__idx ?? 0)}
              rowSelection={{
                selectedRowKeys: selectedTargetKeys,
                onChange: (keys) => setSelectedTargetKeys(keys as React.Key[]),
              }}
              dataSource={(() => {
                const start = (targetsPage - 1) * targetsPageSize;
                const end = start + targetsPageSize;
                return (targetsView || []).slice(start, end);
              })()}
              pagination={{
                current: targetsPage,
                pageSize: targetsPageSize,
                total: (targetsView || []).length,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ['10','20','50','100'],
                onChange: (p:number, ps:number) => { setTargetsPage(p); setTargetsPageSize(ps); },
                showTotal: (total: number, range: [number, number]) => (
                  // 说明在分页条最左侧，条数紧挨着页码；空间不够时说明换行，不会压到页码上
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <div className="targets-added-hint" style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#595959', minWidth: 0 }}>
                      <ExclamationCircleFilled style={{ color: '#ff7875' }} />
                      <span>
                        含多种库存单元的分拣组内库存必须同时处理，不支持选择特定库存单元或指定数量；只含一种库存单元的分拣组不受此限制
                      </span>
                    </div>
                    <span className="targets-added-total" style={{ whiteSpace: 'nowrap' }}>第 {range[0]}-{range[1]} 条，共 {total} 条记录</span>
                  </div>
                ),
              }}
              onRow={(r:any) => {
                const available = (r && r.available_qty != null) ? Number(r.available_qty) : ((r && r.warehouse_total_quantity != null) ? Number(r.warehouse_total_quantity) : NaN);
                const exp = Number(r?.expected_qty);
                const overflow = !r?.use_all_available && Number.isFinite(available) && Number.isFinite(exp) && exp > available;
                const gid = Number(r?.sorting_group_id);
                const gidx = targetsGroupIndexByGroup[gid] || 0;
                const baseBg = gidx % 2 === 0 ? '#e6f7ff' : '#fff7e6';
                const style: any = { backgroundColor: baseBg };
                if (overflow) { style.color = '#cf1322'; }
                return { style } as any;
              }}
              columns={[
                { title: '类型', dataIndex: 'target_type', key: 'target_type', width: 140, render: (v:string, r:any) => {
                  const isBad = requiresInventoryLiuTargets && !isLIUTarget(r);
                  const text = ({
                    project_all: '当前可用库存', sorting_group: '当前可用库存', inventory_unit: '当前可用库存', instruction_output: '历史指令结果'
                  } as any)[v] || v;
                  return <span style={isBad ? { color: '#cf1322', fontWeight: 600 } : undefined}>{text}</span>;
                } },
                { title: '项目', dataIndex: 'project_id', key: 'project', width: 220, render: (v:number, r:any) => {
                  const isBad = requiresInventoryLiuTargets && !isLIUTarget(r);
                  const text = (!Number.isNaN(Number(v)) && projectIdToLabel.get(Number(v))) || v;
                  return <span style={isBad ? { color: '#cf1322', fontWeight: 600 } : undefined}>{text}</span>;
                } },
                { title: '引用/标识', key: 'ref', width: 180, render: (_:any, r:any) => {
                  const isBad = requiresInventoryLiuTargets && !isLIUTarget(r);
                  const text = r.display ||
                  (r.target_type === 'project_all' ? '项目全部'
                    : r.target_type === 'sorting_group' ? `分拣组#${r.sorting_group_id}`
                    : r.target_type === 'inventory_unit' ? `库存单元#${r.inventory_unit_id}`
                    : r.target_type === 'instruction_output' ? `历史指令#${r.prev_instruction_id}`
                    : '-');
                  return <span style={isBad ? { color: '#cf1322', fontWeight: 600 } : undefined}>{text}</span>;
                }},
                { title: '内容', dataIndex: 'content', width: 260, render: (_:any, r:any) => {
                  const isBad = requiresInventoryLiuTargets && !isLIUTarget(r);
                  const items = Array.isArray(r?.content?.items) ? r.content.items : [];
                  if (!items || items.length === 0) return <span style={isBad ? { color: '#cf1322', fontWeight: 600 } : { color: '#999' }}>-</span>;
                  const lines = items.map((it:any)=>{
                    const pid2 = Number(it.product);
                    const info = Number.isFinite(pid2) ? (productInfo[pid2] || {}) : {};
                    const name = it.product_name || info?.name || (Number.isFinite(pid2) ? `产品ID:${pid2}` : '未知产品');
                    const custom = it.custom_code || info?.custom_code || '';
                    const codesArr: string[] = Array.isArray(it?.sorting_codes)
                      ? (it.sorting_codes as any[]).map((c:any)=> (typeof c === 'string' ? c : c?.code)).filter(Boolean)
                      : (Array.isArray(info?.sorting_codes) ? (info.sorting_codes as any[]).map((c:any)=>c?.code).filter(Boolean) : []);
                    const barcodePart = codesArr.length === 0 ? '' : (codesArr.length === 1 ? ` 条码: ${codesArr[0]}` : ` 条码: ${codesArr[0]} 等${codesArr.length}个`);
                    const qty = (it.quantity != null ? it.quantity : it.qty);
                    return `${name}（编码: ${custom || '-'}${barcodePart}）×${qty || 0}件`;
                  });
                  return (
                    <div style={isBad ? { color: '#cf1322', fontWeight: 600 } : undefined}>
                      {lines.map((line: string, idx: number) => (
                        <div key={`target-content-${String(r?.id || r?.key || '-')}-${idx}`}>{line}</div>
                      ))}
                    </div>
                  );
                }},
                { title: '所属分拣组', key: 'sg', width: 220, render: (_:any, r:any) => {
                  const isBad = requiresInventoryLiuTargets && !isLIUTarget(r);
                  const info = (r as any)?.sorting_group_info || sgIdToInfo[Number(r?.sorting_group_id)];
                  const code = info?.system_code || info?.systemCode || info?.code;
                  const name = info?.name;
                  if (code || name) {
                    return (
                      <div style={{ lineHeight: 1.2, ...(isBad ? { color: '#cf1322', fontWeight: 600 } : {}) }}>
                        <div style={{ fontWeight: 500 }}>{code || '-'}</div>
                        {name ? (
                          <div style={{ fontSize: 12, color: isBad ? '#cf1322' : '#666' }}>{name}</div>
                        ) : null}
                      </div>
                    );
                  }
                  return <span style={isBad ? { color: '#cf1322', fontWeight: 600 } : { color: '#999' }}>-</span>;
                }},
                { title: '可用数量', dataIndex: 'available_qty', width: 120, render: (v:any, r:any) => {
                  const isBad = requiresInventoryLiuTargets && !isLIUTarget(r);
                  const val = (v != null ? Number(v) : ((r as any)?.warehouse_total_quantity != null ? Number((r as any)?.warehouse_total_quantity) : undefined));
                  const text = Number.isFinite(val as number) ? String(val) : '-';
                  return <span style={isBad ? { color: '#cf1322', fontWeight: 600 } : undefined}>{text}</span>;
                }},
                { title: (<Tooltip title="不可超过可用数量"><span>操作数量</span></Tooltip>), key: 'exp', render: (_:any, r:any) => {
                  const available = (r && r.available_qty != null) ? Number(r.available_qty) : ((r && r.warehouse_total_quantity != null) ? Number(r.warehouse_total_quantity) : NaN);
                  const exp = Number(r?.expected_qty);
                  const overflow = !r?.use_all_available && Number.isFinite(available) && Number.isFinite(exp) && exp > available;
                  return (
                    <div style={{ fontSize: 12, color: '#555' }}>
                      {r.use_all_available ? (
                        <Tag color="blue">{`全部在库库存${(r && Number.isFinite(Number(r.available_qty))) ? `(预估:${Number(r.available_qty)}件)` : ''}`}</Tag>
                      ) : (
                        (r.expected_qty ?? '') !== '' ? (
                          <>
                            <span style={{ marginRight: 8 }}>{String(r.expected_qty)}件</span>
                            {overflow ? <Tag color="error">期望操作数量超过可用数量</Tag> : null}
                          </>
                        ) : <span style={{ color: '#999' }}>未设置</span>
                      )}
                    </div>
                  );
                } },
                { title: '操作', key: 'ops', width: 200, render: (_:any, r:any) => {
                  const isMulti = (typeof multiGroupIds !== 'undefined') && (multiGroupIds as any).has(Number(r?.sorting_group_id));
                  return (
                    <Space>
                      {isMulti ? (
                        <Tooltip title="该分拣组包含多种库存单元，必须整组全量处理，不可指定数量">
                          <span style={{ display: 'inline-block' }}>
                            <Button
                              size="small"
                              style={{ backgroundColor: '#f0f0f0', borderColor: '#d9d9d9', color: '#999' }}
                              disabled
                            >修改数量</Button>
                          </span>
                        </Tooltip>
                      ) : (
                        <Button
                          size="small"
                          style={{ borderColor: '#1677ff', color: '#1677ff' }}
                          onClick={() => {
                            setEditIdx(Number(r?.__idx));
                            const mode = r.use_all_available ? 'all' : 'number';
                            setEditMode(mode as 'all'|'number');
                            setEditQty(mode === 'number' ? Number(r.expected_qty || 0) || undefined : undefined);
                            setEditOpen(true);
                          }}
                        >修改数量</Button>
                      )}
                      <Button
                        size="small"
                        danger
                        style={{ borderColor: '#ff4d4f', color: '#ff4d4f' }}
                        onClick={() => {
                          const gid = Number(r?.sorting_group_id);
                          const confirmText = isMulti ? '该分拣组包含多种库存单元，将整组删除（该分拣组内所有目标行）。此操作不可恢复。' : '将删除该行操作对象，此操作不可恢复。';
                          Modal.confirm({
                            title: <div style={{ color: '#141414' }}>{isMulti ? '整组删除确认' : '删除该目标？'}</div>,
                            content: <div style={{ whiteSpace: 'pre-line', color: '#141414' }}>{confirmText}</div>,
                            icon: null,
                            centered: true,
                            okText: isMulti ? '整组删除' : '确认删除',
                            cancelText: '取消',
                            okButtonProps: { style: { background: '#ff4d4f', color: '#fff' } },
                            cancelButtonProps: { style: { color: '#141414' } },
                            modalRender: (node) => (<div style={{ background: '#ffffff', color: '#141414', borderRadius: 8 }}>{node}</div>),
                            onOk: () => {
                              if (isMulti) {
                                setTargets(prev => prev.filter((row:any) => Number(row?.sorting_group_id) !== gid));
                              } else {
                                const origIdx = Number(r?.__idx);
                                setTargets(prev => prev.filter((_, i) => i !== origIdx));
                              }
                            },
                          });
                        }}>删除</Button>
                    </Space>
                  );
                } },
              ]}
            />
            </div>
          </Card>
          ) : (
          <Card size="small" style={{ marginTop: 20, border: '2px solid #389e0d', borderRadius: 8 }}>
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 80 }}>
                  <span style={{ color: '#ff4d4f' }}>*</span>
                  <span style={sectionTitleStyle}>仓库</span>
                </div>
                <Form.Item
                  name="warehouse_id"
                  rules={[{ required: true, message: '请选择仓库' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    style={{ width: 360 }}
                    showSearch
                    loading={loadingWarehouses}
                    optionFilterProp="label"
                    placeholder="请选择仓库"
                    options={(warehouses || []).map((w:any)=>({ value: Number(w.id), label: `${w.code || w.id} - ${w.name || ''}` }))}
                    onChange={() => {
                      // 历史模式下切换仓库时，先清空已添加操作对象（避免跨仓混选）
                      try { setHistoryTargetRows([]); } catch {}
                      try { setHistoryTargetSelectedRowKeys([]); } catch {}
                      try { setHistoryAvailableRows([]); } catch {}
                      try { setHistoryAvailableSelectedRowKeys([]); } catch {}
                    }}
                  />
                </Form.Item>
              </div>
              <div>
                <div
                  style={{
                    position: 'relative',
                    display: 'grid',
                    gridTemplateColumns: '80px auto 1fr',
                    alignItems: 'center',
                    columnGap: 10,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ color: '#ff4d4f' }}>*</span>
                    <span style={sectionTitleStyle}>操作对象</span>
                  </div>
                  <div>
                    <Button
                      type="primary"
                      onClick={() => {
                        if (!form.getFieldValue('warehouse_id')) { message.warning('请先选择仓库'); return; }
                        historyTargetForm.resetFields();
                        setHistoryTargetModalOpen(true);
                      }}
                    >
                      添加操作对象
                    </Button>
                  </div>
                  <span style={{ justifySelf: 'center', fontSize: 12, color: '#1677ff', display: 'flex', alignItems: 'center', gap: 12, whiteSpace: 'nowrap', fontWeight: 700 }}>
                    <span>已选:</span>
                    <span>容器数量({historyAddedStats.containerCount})</span>
                    <span style={{ display: 'inline-block', width: 1, height: 14, background: '#d9d9d9' }} />
                    <span>容器内库存单元数量({historyAddedStats.inventoryUnitQty})</span>
                  </span>
                </div>
                <div style={{ marginTop: 10, marginBottom: 10, borderTop: '1px solid #f0f0f0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Space>
                    <Button
                      size="small"
                      danger
                      disabled={(historyTargetSelectedRowKeys || []).length === 0}
                      onClick={() => {
                        if ((historyTargetSelectedRowKeys || []).length === 0) return;
                        Modal.confirm({
                          title: '确认删除选中项？',
                          content: '将删除所选的历史指令执行结果操作对象（同组父/子容器将一并删除），此操作不可恢复。',
                          okText: '确认删除',
                          cancelText: '取消',
                          onOk: () => {
                            const selected = new Set(expandHistoryTargetKeysByGroup(historyTargetSelectedRowKeys || []).map((k) => String(k)));
                            setHistoryTargetRows((prev) => (prev || []).filter((x:any) => !selected.has(String(x?.__key || ''))));
                            setHistoryTargetSelectedRowKeys([]);
                          },
                        });
                      }}
                    >
                      批量删除
                    </Button>
                  </Space>
                </div>
                <div>
                <style>{`
                  .history-target-ioc-table-wrap { position: relative; }
                  .history-target-ioc-table-wrap .ant-table-pagination,
                  .history-target-ioc-table-wrap .ant-table-pagination-right {
                    display: flex !important;
                    justify-content: flex-end !important;
                    align-items: center !important;
                    width: 100% !important;
                  }
                  .history-target-ioc-table-wrap .ant-pagination-total-text {
                    margin-left: 0 !important;
                    position: absolute !important;
                    left: 0 !important;
                    transform: translateY(0);
                  }
                  .history-target-ioc-table .ant-table-tbody > tr.history-target-ioc-group-row > td {
                    border-left: 2px solid #91caff !important;
                    border-right: 2px solid #91caff !important;
                  }
                  .history-target-ioc-table .ant-table-tbody > tr.history-target-ioc-group-start > td {
                    border-top: 2px solid #91caff !important;
                  }
                  .history-target-ioc-table .ant-table-tbody > tr.history-target-ioc-group-end > td {
                    border-bottom: 2px solid #91caff !important;
                  }
                `}</style>
                <div className="history-target-ioc-table-wrap">
                <Table
                  className="history-target-ioc-table"
                  size="small"
                  rowKey={(r:any) => String(r?.__key || '')}
                  dataSource={sortedHistoryTargetRows || []}
                  pagination={{
                    current: historyTargetsPage,
                    pageSize: historyTargetsPageSize,
                    total: (historyTargetRows || []).length,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: ['10','20','50','100'],
                    onChange: (p:number, ps:number) => { setHistoryTargetsPage(p); setHistoryTargetsPageSize(ps); },
                    showTotal: () => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#595959', marginLeft: 0 }}>
                        <ExclamationCircleFilled style={{ color: '#ff7875' }} />
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                          <span>同一外边框中多个容器具有父/子层级关系</span>
                          <span>父/子容器必须作为整体删除,不可单独删除某一个容器</span>
                        </span>
                      </div>
                    ),
                  }}
                  rowSelection={{
                    columnTitle: (() => {
                      const start = (Math.max(1, Number(historyTargetsPage) || 1) - 1) * Math.max(1, Number(historyTargetsPageSize) || 10);
                      const end = start + Math.max(1, Number(historyTargetsPageSize) || 10);
                      const pageRows = (sortedHistoryTargetRows || []).slice(start, end);
                      const allKeys = pageRows.map((r:any) => String(r?.__key || '')).filter(Boolean);
                      const selectedSet = new Set((historyTargetSelectedRowKeys || []).map((k) => String(k)));
                      const checkedCount = allKeys.reduce((acc:number, k:string) => acc + (selectedSet.has(k) ? 1 : 0), 0);
                      const totalCount = allKeys.length;
                      return (
                        <Checkbox
                          indeterminate={checkedCount > 0 && checkedCount < totalCount}
                          checked={totalCount > 0 && checkedCount === totalCount}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const expanded = expandHistoryTargetKeysByGroup(allKeys as React.Key[]);
                              setHistoryTargetSelectedRowKeys(Array.from(new Set([...(historyTargetSelectedRowKeys || []), ...expanded])));
                            } else {
                              const expanded = expandHistoryTargetKeysByGroup(allKeys as React.Key[]);
                              const pageSet = new Set(expanded.map((k) => String(k)));
                              setHistoryTargetSelectedRowKeys((historyTargetSelectedRowKeys || []).filter((k) => !pageSet.has(String(k))));
                            }
                          }}
                        />
                      );
                    })(),
                    selectedRowKeys: historyTargetSelectedRowKeys,
                    onSelect: (record:any, selected:boolean) => {
                      const rowId = String(record?.__key || '');
                      if (!rowId) return;
                      const gk = historyTargetGroupingMeta.rowToGroup.get(rowId);
                      const groupRows = gk ? (historyTargetGroupingMeta.groupToRows.get(gk) || [rowId]) : [rowId];
                      const current = new Set((historyTargetSelectedRowKeys || []).map((k) => String(k)));
                      if (selected) {
                        groupRows.forEach((id) => current.add(String(id)));
                      } else {
                        groupRows.forEach((id) => current.delete(String(id)));
                      }
                      setHistoryTargetSelectedRowKeys(Array.from(current));
                    },
                    onSelectAll: (selected:boolean, selectedRows:any[], changeRows:any[]) => {
                      const current = new Set((historyTargetSelectedRowKeys || []).map((k) => String(k)));
                      const delta = [...(selectedRows || []), ...(changeRows || [])].map((r:any) => String(r?.__key || '')).filter(Boolean);
                      const expanded = expandHistoryTargetKeysByGroup(delta as React.Key[]);
                      if (selected) {
                        expanded.forEach((id) => current.add(String(id)));
                      } else {
                        expanded.forEach((id) => current.delete(String(id)));
                      }
                      setHistoryTargetSelectedRowKeys(Array.from(current));
                    },
                  }}
                  onRow={(record:any) => {
                    const rowKey = String(record?.__key || '');
                    const isBad = requiresInventoryLiuTargets && targetSourceMode === 'history_ioc';
                    const borderMeta = historyTargetVisualMeta.rowGroupBorderMap.get(rowKey) || { grouped: false, top: false, bottom: false };
                    return {
                      style: {
                        background: isBad ? '#fff1f0' : (historyTargetVisualMeta.rowBgMap.get(rowKey) || '#ffffff'),
                        color: isBad ? '#cf1322' : undefined,
                        fontWeight: isBad ? 600 : undefined,
                        borderLeft: borderMeta.grouped ? '2px solid #91caff' : undefined,
                        borderRight: borderMeta.grouped ? '2px solid #91caff' : undefined,
                        borderTop: borderMeta.top ? '2px solid #91caff' : undefined,
                        borderBottom: borderMeta.bottom ? '2px solid #91caff' : undefined,
                      },
                    };
                  }}
                  rowClassName={(record:any) => {
                    const rowKey = String(record?.__key || '');
                    const borderMeta = historyTargetVisualMeta.rowGroupBorderMap.get(rowKey) || { grouped: false, top: false, bottom: false };
                    if (!borderMeta.grouped) return '';
                    if (borderMeta.top) return 'history-target-ioc-group-row history-target-ioc-group-start';
                    if (borderMeta.bottom) return 'history-target-ioc-group-row history-target-ioc-group-end';
                    return 'history-target-ioc-group-row';
                  }}
                  locale={{ emptyText: '暂无已添加操作对象，请点击“添加操作对象”' }}
                  columns={[
                    {
                      title: '项目',
                      dataIndex: 'project_id',
                      width: 220,
                      render: (v:any, r:any) => {
                        const pid = Number(v);
                        const mappedLabel = projectIdToLabel.get(pid);
                        const rawLabel = String(r?.project_label || '').trim();
                        return mappedLabel || rawLabel || (Number.isFinite(pid) ? `项目#${pid}` : '-');
                      },
                    },
                    {
                      title: '系统编码',
                      key: 'system_code',
                      width: 200,
                      render: (_:any, r:any) => {
                        const code = String(r?.output_code || r?.display_code || '-');
                        return (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{code}</span>
                            {code && code !== '-' ? (
                              <CopyOutlined
                                style={{ color: '#1677ff', cursor: 'pointer' }}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await navigator.clipboard.writeText(code);
                                    message.success('已复制系统编码');
                                  } catch {
                                    message.error('复制失败');
                                  }
                                }}
                              />
                            ) : null}
                          </span>
                        );
                      },
                    },
                    { title: '容器类型', dataIndex: 'container_type_display', width: 120, render: (v:any) => String(v || '-') },
                    {
                      title: '容器内容统计',
                      dataIndex: 'output_contents_summary',
                      width: 140,
                      render: (_:any, r:any) => {
                        const kind = String(r?.output_content_kind || '').toLowerCase();
                        const qty = Number(r?.output_contents_total_qty || 0);
                        if (kind === 'exception') return `${Number.isFinite(qty) ? qty : 0}件`;
                        return String(r?.output_contents_summary || '');
                      },
                    },
                    {
                      title: '容器内容',
                      dataIndex: 'output_contents_detail',
                      width: 320,
                      render: (v:any, r:any) => {
                        const kind = String(r?.output_content_kind || '').toLowerCase();
                        if (kind === 'exception') {
                          return (
                            <Tooltip title="无法操作等异常产品">
                              <div style={{ color: '#595959', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                无法操作等异常产品
                              </div>
                            </Tooltip>
                          );
                        }
                        const full = String(v || '').trim();
                        if (!full) return '';
                        const lines = full.split(/[；;]/).map((x) => String(x || '').trim()).filter(Boolean);
                        if (lines.length === 0) return '';
                        const preview = lines.slice(0, 3);
                        const hasMore = lines.length > 3;
                        return (
                          <div style={{ maxWidth: 300 }}>
                            {preview.map((line, idx) => (
                              <div
                                key={`${line}-${idx}`}
                                style={{
                                  position: 'relative',
                                  minHeight: 20,
                                  marginBottom: idx === preview.length - 1 ? 0 : 2,
                                }}
                              >
                                <Tooltip title={line}>
                                  <div
                                    style={{
                                      color: '#595959',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      paddingRight: hasMore && idx === preview.length - 1 ? 56 : 0,
                                    }}
                                  >
                                    {line}
                                  </div>
                                </Tooltip>
                                {hasMore && idx === preview.length - 1 ? (
                                  <Button
                                    type="link"
                                    size="small"
                                    style={{
                                      position: 'absolute',
                                      right: 0,
                                      bottom: -2,
                                      padding: 0,
                                      height: 'auto',
                                      lineHeight: 1.2,
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const iocCode = String(r?.output_code || r?.system_code || r?.display_code || '-');
                                      const fullInfoText = [
                                        `容器系统编码: ${iocCode}`,
                                        ...lines.map((x, i) => `${i + 1}. ${x}`),
                                      ].join('\n');
                                      Modal.info({
                                        title: (
                                          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 12, paddingRight: 24 }}>
                                            <span style={{ justifySelf: 'start' }}>容器内容完整记录</span>
                                            <span style={{ justifySelf: 'center', color: '#1677ff', fontWeight: 700, fontFamily: 'monospace' }}>{iocCode}</span>
                                            <span style={{ justifySelf: 'end' }}>
                                              <Button
                                                type="link"
                                                size="small"
                                                onClick={async () => {
                                                  try {
                                                    await navigator.clipboard.writeText(fullInfoText);
                                                    message.success('已复制完整信息');
                                                  } catch {
                                                    message.error('复制失败');
                                                  }
                                                }}
                                              >
                                                复制信息
                                              </Button>
                                            </span>
                                          </div>
                                        ),
                                        width: 760,
                                        centered: true,
                                        content: (
                                          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                                            {lines.map((x, i) => (
                                              <div key={`${x}-${i}`} style={{ marginBottom: 6, wordBreak: 'break-all' }}>
                                                {i + 1}. {x}
                                              </div>
                                            ))}
                                          </div>
                                        ),
                                      });
                                    }}
                                  >
                                    [...]
                                  </Button>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        );
                      },
                    },
                    {
                      title: '容器尺寸',
                      key: 'dims',
                      width: 170,
                      render: (_:any, r:any) => {
                        const hasAll = r?.length_cm != null && r?.width_cm != null && r?.height_cm != null;
                        return hasAll ? `${r.length_cm}×${r.width_cm}×${r.height_cm} cm` : '';
                      },
                    },
                    { title: '容器重量', dataIndex: 'weight_g', width: 120, render: (v:any) => (v != null ? `${v}g` : '') },
                    {
                      title: '操作',
                      key: 'ops',
                      width: 120,
                      render: (_:any, r:any) => (
                        <Button
                          size="small"
                          danger
                          onClick={() => {
                            const rowKey = String(r?.__key || '');
                            const groupKey = historyTargetGroupingMeta.rowToGroup.get(rowKey);
                            const groupRows = groupKey ? (historyTargetGroupingMeta.groupToRows.get(groupKey) || [rowKey]) : [rowKey];
                            const groupSet = new Set(groupRows.map((k) => String(k)));
                            setHistoryTargetRows((prev) => (prev || []).filter((x:any) => !groupSet.has(String(x?.__key || ''))));
                            setHistoryTargetSelectedRowKeys((prev) => (prev || []).filter((k) => !groupSet.has(String(k))));
                          }}
                        >
                          删除
                        </Button>
                      ),
                    },
                  ]}
                />
                </div>
                </div>
              </div>
            </Space>
          </Card>
          )}
          <Space size={16} style={{ display: 'flex', marginTop: 20 }}>
            <Form.Item name="template" label={<span style={sectionTitleStyle}>指令模板</span>} rules={[{ required: true, message: '请选择指令模板（或选择不使用任何指令模板）' }]}>
              <Select
                style={{ width: 420 }}
                listHeight={520}
                dropdownStyle={{ maxHeight: 520 }}
                placeholder="请选择指令模板"
                options={(() => {
                  const all = Array.isArray(filteredTemplates) ? filteredTemplates : [];
                  // 库存非标准操作（通用指令）：没有合适的标准模板时选它，放在列表最底部
                  const isNonStdTpl = (t:any) => String(t?.meta?.engine || '') === 'nonstandard_ops';
                  const nonStd = all.find(isNonStdTpl);
                  const sys = all.filter(t => String(t.level) === 'system' && !isNonStdTpl(t));
                  const scoped = all.filter(t => (String(t.level) === 'customer' || String(t.level) === 'project') && !isNonStdTpl(t));
                  const makeOpt = (t:any) => ({ value: String(t.id), label: `${t.name} · ${t.level}` });
                  const sysOpts = sys.map(makeOpt);
                  const scopedOpts = scoped.map(makeOpt);
                  const out: Array<any> = [];
                  out.push(...sysOpts);
                  // 分隔与专属级
                  if (sysOpts.length > 0 && scopedOpts.length > 0) {
                    out.push({ value: '__DIVIDER__', label: '—— 客户/项目专属 ——', disabled: true });
                  }
                  out.push(...scopedOpts);
                  if (nonStd) {
                    if (out.length > 0) {
                      out.push({ value: '__DIVIDER_NONSTD__', label: '—— 以上没有合适的模板时 ——', disabled: true });
                    }
                    out.push(makeOpt(nonStd));
                  }
                  return out;
                })()}
                onChange={(val) => {
                  // 切换模板时，若选择自由指令，则强制备注必填；否则恢复备注的普通校验
                }}
              />
            </Form.Item>
            {tplVal && tplVal !== '__NONE__' ? (
              <Button type="primary" onClick={() => setTplDescOpen(true)}>查看指令模板说明</Button>
            ) : null}
          </Space>
          {showPackingAssessment ? (
            <Card
              size="small"
              style={{ marginTop: 12, ...(disableAfterTemplateCombined ? { opacity: 0.6, pointerEvents: 'none' } : {}) }}
              title={(
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={sectionTitleStyle}>包装预估</span>
                  <HelpHintButton onClick={() => setPackingInfoOpen(true)}>说明</HelpHintButton>
                </div>
              )}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                <span>是否需要打包：</span>
                <span
                  style={{
                    padding: '2px 8px',
                    borderRadius: 4,
                    border: `1px solid ${packingAssessmentStatus.needPacking ? '#fa541c' : '#52c41a'}`,
                    background: packingAssessmentStatus.needPacking ? '#fff2e8' : '#f6ffed',
                    color: packingAssessmentStatus.needPacking ? '#fa541c' : '#389e0d',
                    fontWeight: 600,
                  }}
                >
                  {packingAssessmentStatus.packingText}
                </span>
                {packingAssessmentStatus.needPacking ? (
                  <span style={{ color: '#8c8c8c', fontSize: 12 }}>请选择打包方式</span>
                ) : null}
              </div>
              {packingEstimateLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#1677ff', fontSize: 12, marginBottom: 8 }}>
                  <Spin size="small" />
                  <span>包装预估计算中，请稍候...</span>
                </div>
              ) : null}
              {packingAssessmentStatus.needPacking ? (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {(packingOptions || []).length === 0 ? (
                  <div style={{ color: '#999' }}>暂无可选包材</div>
                ) : (
                  packingOptions.map((opt:any) => {
                    const isSelected = String(opt.id) === String(selectedPacking?.id || '');
                    const availability = opt.availabilityKey
                      ? packingAvailability[opt.availabilityKey]
                      : (opt.id === 'special_packing' ? true : undefined);
                    const isDisabled = availability === false;
                    const est = opt.codeKey ? (packingEstimates as any)?.[opt.codeKey] : undefined;
                    const sizeValue = est
                      ? `${formatMeasure(est.l)} × ${formatMeasure(est.w)} × ${formatMeasure(est.h)} cm | ${formatMeasure(toInch(est.l))} × ${formatMeasure(toInch(est.w))} × ${formatMeasure(toInch(est.h))} in`
                      : '-';
                    const weightValue = est
                      ? `${formatMeasure(est.weight_g / 1000)} kg | ${formatMeasure(toLb(est.weight_g))} lb`
                      : '-';
                    return (
                      <div
                        key={String(opt.id)}
                        onClick={() => { if (!isDisabled) setPackingOptionId(String(opt.id)); }}
                        style={{
                          flex: '1 1 calc(20% - 12px)',
                          maxWidth: 'calc(20% - 12px)',
                          minWidth: 220,
                          border: `1px solid ${isSelected ? '#1677ff' : '#d9d9d9'}`,
                          borderRadius: 6,
                          padding: 10,
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          background: isSelected ? '#e6f4ff' : '#fff',
                          boxShadow: isSelected ? '0 0 0 2px rgba(22,119,255,0.15)' : 'none',
                          position: 'relative',
                          opacity: isDisabled ? 0.5 : 1,
                        }}
                      >
                        <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 6 }}>
                          {availability === true ? (
                            <span style={{ padding: '0 6px', borderRadius: 4, border: '1px solid #52c41a', background: '#f6ffed', color: '#389e0d', fontSize: 12 }}>
                              可用
                            </span>
                          ) : availability === false ? (
                            <span style={{ padding: '0 6px', borderRadius: 4, border: '1px solid #ff4d4f', background: '#fff1f0', color: '#cf1322', fontSize: 12 }}>
                              不可用
                            </span>
                          ) : null}
                          {isSelected ? (
                            <Button
                              size="small"
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const text = buildPackingOptionCopyText(opt, availability, sizeValue, weightValue);
                                const ok = await copyText(text);
                                if (ok) setCopiedPackingOptionId(String(opt.id));
                              }}
                              style={{
                                padding: '0 6px',
                                height: 20,
                                borderRadius: 4,
                                border: '1px solid #1677ff',
                                background: '#e6f4ff',
                                color: '#1677ff',
                                fontSize: 12,
                              }}
                            >
                              {copiedPackingOptionId === String(opt.id) ? '已复制' : '复制'}
                            </Button>
                          ) : null}
                        </div>
                        <div style={{ fontWeight: 600, marginBottom: 6, paddingRight: 72 }}>{opt.title}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 6px',
                              borderRadius: 4,
                              border: '1px solid #ff4d4f',
                              background: '#fff1f0',
                              color: '#cf1322',
                              fontSize: 12,
                            }}
                          >
                            说明
                          </span>
                          <span style={{ fontSize: 12, color: '#666' }}>{opt.desc || '-'}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 6px',
                              borderRadius: 4,
                              border: '1px solid #5cdbd3',
                              background: '#e6fffb',
                              color: '#08979c',
                              fontSize: 12,
                            }}
                          >
                            包材编码
                          </span>
                          <span style={{ fontSize: 12, color: '#666' }}>
                            {opt.codeKey ? (packingCodes as any)?.[opt.codeKey] || '-' : '-'}
                          </span>
                        </div>
                        <div style={{ marginTop: 6 }}>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 6px',
                              borderRadius: 4,
                              border: '1px solid #faad14',
                              background: '#fff7e6',
                              color: '#ad6800',
                              fontSize: 12,
                              marginRight: 6,
                            }}
                          >
                            包材费用
                          </span>
                          <span style={{ fontSize: 12, color: '#666' }}>{opt.feeText}</span>
                        </div>
                        {opt.id !== 'customer_soft' && opt.id !== 'special_packing' ? (
                          <>
                            <div style={{ marginTop: 6 }}>
                              <span
                                style={{
                                  display: 'inline-block',
                                  padding: '2px 6px',
                                  borderRadius: 4,
                                  border: '1px solid #69b1ff',
                                  background: '#e6f4ff',
                                  color: '#1677ff',
                                  fontSize: 12,
                                  marginRight: 6,
                                }}
                              >
                                预估包裹尺寸
                              </span>
                              <span style={{ fontSize: 12, color: '#666' }}>{sizeValue}</span>
                            </div>
                            <div style={{ marginTop: 6 }}>
                              <span
                                style={{
                                  display: 'inline-block',
                                  padding: '2px 6px',
                                  borderRadius: 4,
                                  border: '1px solid #b7eb8f',
                                  background: '#f6ffed',
                                  color: '#389e0d',
                                  fontSize: 12,
                                  marginRight: 6,
                                }}
                              >
                                预估包裹重量
                              </span>
                              <span style={{ fontSize: 12, color: '#666' }}>{weightValue}</span>
                            </div>
                          </>
                        ) : null}
                        {isSelected ? (
                          <span
                            style={{
                              position: 'absolute',
                              right: 8,
                              bottom: 8,
                              padding: '0 6px',
                              borderRadius: 4,
                              border: '1px solid #1677ff',
                              background: '#1677ff',
                              color: '#fff',
                              fontSize: 12,
                            }}
                          >
                            已选择
                          </span>
                        ) : null}
                      </div>
                    );
                  })
                )}
                </div>
              ) : null}
            </Card>
          ) : null}
          {/* 需回答问题（依据模板问卷动态渲染，紧跟模板选择下方） */}
          {tplVal && tplVal !== '__NONE__' && Array.isArray(visibleQRows) && visibleQRows.length > 0 ? (
            <Card size="small" style={{ marginTop: 12, ...(disableAfterTemplate ? { opacity: 0.6, pointerEvents: 'none' } : {}) }} title={(
              <span>
                {hasRequiredQuestions ? <span style={{ color: '#ff4d4f', marginRight: 6 }}>*</span> : null}
                <span style={sectionTitleStyle}>需回答问题</span>
              </span>
            )}>
              {(() => {
                const waybillSourceRaw = String((answers as any)?.waybill_source || '').trim();
                const waybillSourceLower = waybillSourceRaw.toLowerCase();
                const isWarehouseWaybillSource = waybillSourceRaw === '仓库提供' || waybillSourceLower === 'warehouse' || /仓库/.test(waybillSourceRaw);
                const useWarehouseLayout = showPackingAssessment && isWarehouseWaybillSource;
                const useRegularOutboundWarehouseLayout = isRegularOutboundTemplate && isWarehouseWaybillSource;
                const enableAddressParse = isWarehouseWaybillSource && (showPackingAssessment || isRegularOutboundTemplate);
                const qMap = new Map((visibleQRows || []).map((q:any) => [String(q.key||'').trim(), q]));
                const groupRows = [
                  ['recipient_name', 'company_name'],
                  ['address_line_1', 'address_line_2'],
                  ['city', 'state', 'postal_code', 'country'],
                  ['phone', 'email', 'shipping_notes'],
                  ['signature_required', 'insurance_required', 'insured_amount'],
                  ['preferred_carrier'],
                ];
                const groupedKeys = new Set(groupRows.flat());
                const parseText = addressParseText;
                const setParseText = setAddressParseText;
                const applyParsed = (parsed: Record<string, string>) => {
                  setAnswers((prev:any) => {
                    const next:any = { ...(prev || {}) };
                    let changed = false;
                    Object.keys(parsed).forEach((k) => {
                      if (k === 'zip_lookup_city' || k === 'zip_lookup_state') return;
                      const v = String(parsed[k] || '').trim();
                      if (!v) return;
                      if (next[k] !== v) {
                        next[k] = v;
                        changed = true;
                      }
                    });
                    return changed ? next : prev;
                  });
                };
                const scheduleZipLookup = (zipRaw: string) => {
                  const zip = String(zipRaw || '').trim();
                  if (!zip) return;
                  if (!/^\d{5}(?:-\d{4})?$/.test(zip)) return;
                  const isZipPlus4 = /^\d{5}-\d{4}$/.test(zip);
                  if (zipLookupTimerRef.current) {
                    window.clearTimeout(zipLookupTimerRef.current);
                  }
                  zipLookupTimerRef.current = window.setTimeout(async () => {
                    try {
                      const resp = await request.post('/api/v1/instruction/instructions/address-parse/', { text: zip });
                      const parsed = resp?.data?.parsed || {};
                      const hasMatch = !!(
                        parsed?.city
                        || parsed?.state
                        || parsed?.zip_lookup_city
                        || parsed?.zip_lookup_state
                      );
                      if (!hasMatch) {
                        try {
                          if (zipIgnoreWarningRef.current.has(zip)) {
                            return;
                          }
                        } catch {}
                        if (!zipInvalidModalOpenRef.current) {
                          zipInvalidModalOpenRef.current = true;
                          Modal.confirm({
                            title: `未查询到匹配的邮编信息 [已输入邮编: ${zip}],请核实邮政编码是否正确.`,
                            okText: '继续使用该邮编',
                            cancelText: '关闭',
                            maskClosable: false,
                            keyboard: false,
                            onOk: () => {
                              try { zipIgnoreWarningRef.current.add(zip); } catch {}
                              zipInvalidModalOpenRef.current = false;
                            },
                            onCancel: () => {
                              zipInvalidModalOpenRef.current = false;
                              postalCodeInputRef.current?.focus();
                            },
                          });
                        }
                        return;
                      }
                      const normalizeCityValue = (v: string) => String(v || '').trim().toLowerCase().replace(/\./g, '');
                      const normalizeStateValue = (v: string) => String(v || '').trim().toUpperCase();
                      const zipCity = String(parsed?.zip_lookup_city || parsed?.city || '').trim();
                      const zipState = String(parsed?.zip_lookup_state || parsed?.state || '').trim();
                      const currCity = String((answers as any)?.city || '').trim();
                      const currState = String((answers as any)?.state || '').trim();
                      const hasZipCheck = !!(zipCity || zipState);
                      const mismatch = hasZipCheck && (
                        (zipCity && currCity && normalizeCityValue(zipCity) !== normalizeCityValue(currCity))
                        || (zipState && currState && normalizeStateValue(zipState) !== normalizeStateValue(currState))
                      );
                      const picked: Record<string, string> = {};
                      if (parsed?.city) picked.city = parsed.city;
                      if (parsed?.state) picked.state = parsed.state;
                      if (parsed?.postal_code && !isZipPlus4) picked.postal_code = parsed.postal_code;
                      if (Object.keys(picked).length > 0) {
                      if (mismatch) {
                        try {
                          const now = Date.now();
                          const lastZip = zipMismatchModalRef.current.zip;
                          const lastTs = zipMismatchModalRef.current.ts || 0;
                          if (lastZip === zip && now - lastTs < 2000) {
                            return;
                          }
                          zipMismatchModalRef.current = { zip, ts: now };
                        } catch {}
                          Modal.confirm({
                            title: '邮编自动匹配 请确认输入了正确的城市/州/邮编信息',
                            width: 620,
                            content: (
                              <div>
                                <div>已输入: 城市 {currCity || '-'} | 州 {currState || '-'} | 邮编 {zip || '-'}</div>
                                <div>邮编匹配: 城市 {zipCity || '-'} | 州 {zipState || '-'} | 邮编 {zip || '-'}</div>
                              </div>
                            ),
                            okText: '按邮编匹配填入',
                            cancelText: '取消',
                            onOk: () => {
                              setAnswers((prev:any) => ({ ...(prev || {}), ...picked }));
                            },
                          });
                          return;
                        }
                        setAnswers((prev:any) => ({ ...(prev || {}), ...picked }));
                      }
                    } catch {}
                  }, 300);
                };
                const parseAddressText = (text: string) => {
                  const raw = String(text || '');
                  const lines = raw.split(/\r?\n/).map(l=>l.trim()).filter(Boolean);
                  const joined = lines.join(' ');
                  const emailRegex = /[^\s@]+@[^\s@]+\.[^\s@]+/;
                  const zipRegex = /\b\d{5}(?:-\d{4})?\b/g;
                  const phoneRegex = /(\+?\d[\d\-\s\(\)]{6,})/;
                  const emailMatch = joined.match(emailRegex);
                  const joinedNoZip = joined.replace(zipRegex, ' ');
                  const phoneMatch = joinedNoZip.match(phoneRegex);
                  const parsed: Record<string, string> = {};
                  if (emailMatch) parsed.email = emailMatch[0];
                  if (phoneMatch) parsed.phone = phoneMatch[0].trim();
                  const emailLine = lines.find(l => emailRegex.test(l));
                  const phoneLine = lines.find(l => {
                    const lNoZip = l.replace(zipRegex, ' ');
                    return phoneRegex.test(lNoZip);
                  });
                  const countryLine = lines.find(l => /united states|usa|us|中国|china/i.test(l));
                  if (countryLine) {
                    parsed.country = /china|中国/i.test(countryLine) ? '中国' : 'USA';
                  }
                  const usStates = new Set([
                    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','IA','ID','IL','IN','KS','KY','LA','MA','MD',
                    'ME','MI','MN','MO','MS','MT','NC','ND','NE','NH','NJ','NM','NV','NY','OH','OK','OR','PA','RI','SC',
                    'SD','TN','TX','UT','VA','VT','WA','WI','WV','WY','DC'
                  ]);
                  const cityStateZipLine = lines.find(l => {
                    const m = l.match(/([A-Za-z\.\s]+?),?\s+([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)/);
                    if (!m) return false;
                    return usStates.has(String(m[2] || '').toUpperCase());
                  });
                  if (cityStateZipLine) {
                    const m = cityStateZipLine.match(/([A-Za-z\.\s]+?),?\s+([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)/);
                    if (m) {
                      parsed.city = (m[1] || '').replace(/,$/, '').trim();
                      parsed.state = String(m[2] || '').trim().toUpperCase();
                      if (m[3]) parsed.postal_code = m[3].trim();
                    }
                  }
                  const cleanedLines = lines.filter(l => l !== countryLine && l !== cityStateZipLine && l !== emailLine && l !== phoneLine);
                  const addressScore = (line: string) => {
                    if (!line) return 0;
                    let score = 0;
                    if (/^\s*\d+\b/.test(line)) score += 3;
                    if (/\b(street|st|road|rd|ave|avenue|blvd|drive|dr|lane|ln|way|court|ct|hwy|highway)\b/i.test(line)) score += 3;
                    if (/\b(suite|ste|apt|unit|#)\b/i.test(line)) score += 1;
                    if (/,/.test(line)) score += 1;
                    if (/\d/.test(line)) score += 1;
                    return score;
                  };
                  const scored = cleanedLines
                    .map((l, idx) => ({ l, idx, score: addressScore(l) }))
                    .filter(item => item.score > 0)
                    .sort((a, b) => (b.score - a.score) || (a.idx - b.idx));
                  const addrLines = scored.map(item => item.l);
                  if (addrLines.length > 0) {
                    const firstAddr = addrLines[0];
                    const m = firstAddr.match(/(.+?)(?:,|\s)\s*(suite|ste|apt|unit|#)\s*([A-Za-z0-9\-]+.*)$/i);
                    if (m) {
                      parsed.address_line_1 = String(m[1] || '').trim().replace(/,$/, '');
                      parsed.address_line_2 = `${m[2]} ${m[3]}`.trim();
                    } else {
                      parsed.address_line_1 = firstAddr;
                      if (addrLines.length > 1) parsed.address_line_2 = addrLines[1];
                    }
                  }
                  const nameLine = cleanedLines.find(l => !addrLines.includes(l));
                  if (nameLine) parsed.recipient_name = nameLine;
                  return parsed;
                };
                const normalizeCity = (v: string) => String(v || '').trim().toLowerCase().replace(/\./g, '');
                const normalizeState = (v: string) => String(v || '').trim().toUpperCase();
                const cleanParsedForApply = (parsed: Record<string, string>) => {
                  const { zip_lookup_city, zip_lookup_state, ...rest } = parsed as any;
                  return rest as Record<string, string>;
                };
                const addressParseBlock = (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 120, paddingTop: 6 }}>
                      <span>地址解析</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 520 }}>
                      <Input.TextArea
                        value={parseText}
                        onChange={(e)=> setParseText(e.target.value)}
                        rows={5}
                        placeholder="为提高地址解析正确率,请按照以下格式调整后再执行解析: 第1行-收件人姓名,第2行-具体地址(多行地址可用逗号隔开),第3行-城市 州 邮编, 第4行-电话 email"
                      />
                      <div style={{ marginTop: 6 }}>
                        <Button size="small" onClick={async () => {
                          try {
                            const resp = await request.post('/api/v1/instruction/instructions/address-parse/', { text: parseText });
                            const parsed = resp?.data?.parsed || {};
                            if (parsed && Object.keys(parsed).length > 0) {
                              const pCity = String(parsed?.city || '').trim();
                              const pState = String(parsed?.state || '').trim();
                              const pZip = String(parsed?.postal_code || '').trim();
                              let zipCity = String(parsed?.zip_lookup_city || '').trim();
                              let zipState = String(parsed?.zip_lookup_state || '').trim();
                              if (pZip) {
                                try {
                                  const zipResp = await request.post('/api/v1/instruction/instructions/address-parse/', { text: pZip });
                                  const zipParsed = zipResp?.data?.parsed || {};
                                  zipCity = String(zipParsed?.zip_lookup_city || '').trim();
                                  zipState = String(zipParsed?.zip_lookup_state || '').trim();
                                } catch {}
                                if (!zipCity && !zipState) {
                                  Modal.warning({
                                    title: `未查询到匹配的邮编信息 [已输入邮编: ${pZip}],请核实邮政编码是否正确.`,
                                    okText: '关闭',
                                    maskClosable: false,
                                    keyboard: false,
                                  });
                                  return;
                                }
                              }
                              const hasZipCheck = !!(zipCity || zipState);
                              const mismatch = hasZipCheck && (
                                (zipCity && normalizeCity(zipCity) !== normalizeCity(pCity))
                                || (zipState && normalizeState(zipState) !== normalizeState(pState))
                              );
                              if (mismatch) {
                                const modal = Modal.confirm({
                                  title: '地址自动解析 请确认输入了正确的城市/州/邮编信息',
                                  width: 620,
                                  content: (
                                    <div>
                                      <div>地址解析结果: 城市 {pCity || '-'} | 州 {pState || '-'} | 邮编 {pZip || '-'}</div>
                                      <div>邮编自动匹配: 城市 {zipCity || '-'} | 州 {zipState || '-'} | 邮编 {pZip || '-'}</div>
                                    </div>
                                  ),
                                  footer: (
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                      <Button onClick={() => modal.destroy()}>
                                        返回修改
                                      </Button>
                                      <Button type="primary" onClick={() => {
                                        applyParsed(cleanParsedForApply(parsed));
                                        message.success('已解析并填充（不会覆盖已有值）');
                                        modal.destroy();
                                      }}>
                                        按解析结果填入
                                      </Button>
                                      <Button type="primary" onClick={() => {
                                        const zipPicked: Record<string, string> = {};
                                        if (zipCity) zipPicked.city = zipCity;
                                        if (zipState) zipPicked.state = zipState;
                                        if (pZip) zipPicked.postal_code = pZip;
                                        applyParsed(zipPicked);
                                        message.success('已按邮编匹配填充（不会覆盖已有值）');
                                        modal.destroy();
                                      }}>
                                        按邮编匹配填入
                                      </Button>
                                    </div>
                                  ),
                                });
                              } else {
                                applyParsed(cleanParsedForApply(parsed));
                                message.success('已解析并填充（不会覆盖已有值）');
                              }
                              return;
                            }
                          } catch {}
                          const parsed = parseAddressText(parseText);
                          applyParsed(parsed);
                          message.success('已解析并填充（不会覆盖已有值）');
                        }}>
                          解析
                        </Button>
                      </div>
                    </div>
                  </div>
                );
                const renderField = (q:any, idx:number, opts?: { inputWidth?: number | string }) => {
                  const key = String(q.key||'').trim();
                  const label = String(q.label||key||`问题${idx+1}`);
                  const keyLower = key.toLowerCase();
                  const isWaybillSourceQuestion = keyLower === 'waybill_source' || label === '运单提供方式';
                  const showRegularOutboundWaybillDivider = isRegularOutboundTemplate && isWaybillSourceQuestion;
                  const type = String(q.type||'text');
                  const isSortingStrategyQuestion = keyLower === 'sorting_strategy' || label === '分拣策略';
                  const showSortingStrategyHelp = isSortAndCountTemplate && isSortingStrategyQuestion && type === 'select';
                  const isSortDemandQuestion = keyLower === 'sorting_requirement' || label === '分拣需求';
                  const showSortDemandHelp = isSortAndCountTemplate && isSortDemandQuestion && type === 'select';
                  const isSortMethodQuestion = keyLower === 'sorting_method' || keyLower === 'sort_method' || label === '分拣方式';
                  const required = isQuestionRequired(q);
                  const tips = q.tips;
                  const tipsText = String(tips || '').trim();
                  const isMappingQuestion = keyLower === 'shipping_label_to_target_mapping' || label === '运输标签与出库对象是否存在对应关系';
                  const mappingShortTips = '指的是上传的箱标/托盘标/运单等运输标签,和出库对象之间是否存在指定对应关系';
                  const mappingFullTipsText = isMappingQuestion
                    ? tipsText.replace(/出库对象只有1件[，,]默认对应上传的所有运输标签/g, '出库对象只有1件,则所有上传的运输标签都应用于此出库对象')
                    : tipsText;
                  const showMappingExpandBtn = isMappingQuestion && !!tipsText && tipsText !== mappingShortTips;
                  const optionSource = q?.options_source || q?.optionsSource;
                  const optionSourceKey = (optionSource && optionSource.type === 'model') ? getOptionSourceKey(optionSource) : '';
                  const dynamicOptions = optionSourceKey ? (questionOptionsBySource[optionSourceKey] || []) : [];
                  const dynamicLoading = optionSourceKey ? !!questionOptionsLoading[optionSourceKey] : false;
                  const selectOptions = optionSourceKey
                    ? dynamicOptions
                    : (q.options||[]).map((o:any)=>({ value: o.value, label: o.label||o.value }));
                  const lockWaybillTimingSelect = lockImmediateWaybillSubmitTiming && isWaybillSubmitTimingQuestion(q);
                  const selectOptionsWithConstraint = lockWaybillTimingSelect
                    ? (selectOptions || []).map((opt: any) => ({
                      ...opt,
                      disabled: !isImmediateWaybillValue(opt?.value) && !isImmediateWaybillValue(opt?.label),
                    }))
                    : selectOptions;
                  const selectOptionsForSortAndCountDemand = (
                    isSortAndCountTemplate && isSortMethodQuestion && (sortAndCountDemandMode === 'need1' || sortAndCountDemandMode === 'need2')
                  )
                    ? (selectOptionsWithConstraint || []).filter((opt: any) => {
                      const labelText = String(opt?.label || opt?.value || '').trim();
                      const valueText = String(opt?.value || '').trim().toLowerCase();
                      return labelText.includes('扫码分拣') || labelText.includes('条码分拣') || valueText.includes('scan') || valueText.includes('barcode');
                    })
                    : selectOptionsWithConstraint;
                  const val = answers[key];
                  const isCarrierQuestion = key === 'waybill_carrier' || key === 'preferred_carrier';
                  const inputWidth = showSortDemandHelp ? 700 : (isCarrierQuestion ? 360 : (opts?.inputWidth ?? 260));
                  const isEmailField = key === 'email';
                  const emailInvalid = isEmailField
                    && val
                    && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(val).trim());
                  const onVal = (nv:any) => setAnswers(prev => ({ ...prev, [key]: nv }));
                  const clearVal = () => setAnswers((prev:any) => {
                    const next:any = { ...(prev || {}) };
                    delete next[key];
                    return next;
                  });
                  const fieldContent = (
                    <div key={key || idx} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1, flexWrap: 'wrap' }}>
                      <div style={{ minWidth: 120, display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 4 }}>
                        {required ? <span style={{ color: '#ff4d4f' }}>*</span> : null}
                        <span>{label}</span>
                        {isMappingQuestion ? <span style={{ marginLeft: 8, color: '#8c8c8c' }}>{mappingShortTips}</span> : null}
                        {!isMappingQuestion && tips ? <span style={{ marginLeft: 8, color: '#8c8c8c' }}>{tips}</span> : null}
                        {showMappingExpandBtn ? (
                          <Button
                            type="primary"
                            size="small"
                            style={{ marginLeft: 8, background: '#ffffff', borderColor: '#1677ff', color: '#1677ff' }}
                            onClick={() => {
                              Modal.info({
                                title: '详细说明',
                                width: 760,
                                okText: '关闭',
                                content: (
                                  <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, color: '#595959' }}>
                                    {mappingFullTipsText}
                                  </div>
                                ),
                              });
                            }}
                          >
                            详细说明
                          </Button>
                        ) : null}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        {type === 'boolean' ? (
                          <Switch checked={!!val} onChange={(ck)=> onVal(ck)} checkedChildren="是" unCheckedChildren="否" />
                        ) : type === 'select' ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {showSortingStrategyHelp ? (
                              <HelpHintButton
                                style={{ background: '#ffffff', borderColor: '#1677ff', color: '#1677ff' }}
                                onClick={() => {
                                  const dataSource = [
                                    {
                                      key: 'overview',
                                      item: '概述',
                                      standard: '分拣细化程度高，每款产品单独作为一个分拣组。',
                                      grouped: '分拣细化程度低，可以指定多款产品组成一个分拣组。',
                                    },
                                    {
                                      key: 'attachment',
                                      item: '附件要求',
                                      standard: '无需上传附件。',
                                      grouped: '需上传“分组列表”附件。',
                                    },
                                    {
                                      key: 'flexibility',
                                      item: '分拣后操作灵活度',
                                      standard: '分拣后的操作灵活度高，可以单独指定任意产品的任意数量执行后续操作。',
                                      grouped: '分拣后的操作灵活度低，必须整组的所有产品的所有数量一起操作。',
                                    },
                                    {
                                      key: 'example',
                                      item: '举例',
                                      standard: '将某些库存按照“扫码分拣”方式分拣，分拣策略选择“标准模式”，若这些库存共包括10种不同的条形码，最终分拣结果会分为10组，每个条形码的产品归为一组，后续操作可以指定任意产品的任意数量。',
                                      grouped: '同样这些库存，分拣策略选择“分组模式”，10种条码指定按照3组分拣，最终结果会分为3组，后续操作必须以某一组的所有种类和数量一起操作。',
                                    },
                                    {
                                      key: 'billing',
                                      item: '收费',
                                      standard: '收费较高。',
                                      grouped: '收费较低。',
                                    },
                                  ];
                                  Modal.info({
                                    title: '详细说明',
                                    width: 1320,
                                    okText: '关闭',
                                    content: (
                                      <div style={{ lineHeight: 1.8 }}>
                                        <div style={{ marginBottom: 12, color: '#595959' }}>
                                          分拣策略是指仓库进行分拣时的细化程度，分为“标准模式”和“分组模式”，请根据后续操作灵活度需求选择适合的分拣策略，以节省成本。详细对比请看下面表格。
                                        </div>
                                        <Table
                                          size="small"
                                          bordered
                                          pagination={false}
                                          rowKey="key"
                                          dataSource={dataSource}
                                          columns={[
                                            {
                                              title: '对比项目',
                                              dataIndex: 'item',
                                              key: 'item',
                                              width: 160,
                                              render: (v:any) => <span style={{ fontWeight: 700 }}>{String(v || '')}</span>,
                                            },
                                            {
                                              title: '标准模式',
                                              dataIndex: 'standard',
                                              key: 'standard',
                                              width: 470,
                                              render: (v:any) => <span style={{ whiteSpace: 'pre-wrap' }}>{String(v || '')}</span>,
                                            },
                                            {
                                              title: '分组模式',
                                              dataIndex: 'grouped',
                                              key: 'grouped',
                                              width: 470,
                                              render: (v:any) => <span style={{ whiteSpace: 'pre-wrap' }}>{String(v || '')}</span>,
                                            },
                                          ]}
                                          scroll={{ x: 1100 }}
                                        />
                                      </div>
                                    ),
                                  });
                                }}
                              >
                                详细说明
                              </HelpHintButton>
                            ) : null}
                            <Select
                              style={{ width: inputWidth }}
                              allowClear={!lockWaybillTimingSelect}
                              value={(val === undefined || val === null || val === '') ? undefined : val}
                              onChange={(v)=> { if (v === undefined || v === null || v === '') clearVal(); else onVal(v); }}
                              options={selectOptionsForSortAndCountDemand}
                              loading={dynamicLoading}
                              dropdownMatchSelectWidth={showSortDemandHelp ? false : !isCarrierQuestion}
                              dropdownStyle={showSortDemandHelp ? { minWidth: 760 } : (isCarrierQuestion ? { minWidth: 400 } : undefined)}
                            />
                            {showSortDemandHelp ? (
                              <Button
                                type="primary"
                                size="small"
                                style={{ background: '#ffffff', borderColor: '#1677ff', color: '#1677ff' }}
                                onClick={() => {
                                  const demandBlocks = [
                                    {
                                      title: '需求1',
                                      text: '将多款入库时混合在一起的散货库存单元(LIU)或者箱库存单元(BIU)分拣开,将按照指令选定的分拣策略(标准模式/分组模式)重新为每种库存单元设定入库分拣组,以后这些库存单元再有入库时将按照新的入库分拣组设定执行入库;',
                                    },
                                    {
                                      title: '需求2',
                                      text: '将某款箱库存单元(BIU)或者托盘库存单元(PIU)拆开后执行分拣,分拣出的不同产品将转换为散货库存单元(LIU),将按照指令选定的分拣策略(标准模式/分组模式)为每种散货库存单元设定入库分拣组;此场景仅支持条码分拣;此场景限每个指令只能处理一款箱库存单元或者托盘库存单元,处理数量不限;',
                                    },
                                    {
                                      title: '需求3',
                                      text: '将某款散货库存单元(LIU)再按照特定规则分拣开,如:同一款产品按照不同的分拣条码分开(每个分拣条码可能适用于不同的平台店铺),或者按照产品不同特征分开等(如包装方式/产品颜色/型号等);将生成全新的变体产品和变体散货库存单元,并将按照指令选定的分拣策略(标准模式/分组模式)为每种变体散货库存单元设定入库分拣组;此场景支持条码分拣/目视分拣/其他分拣方式;此场景限每个指令只能处理一款散货库存单元,处理数量不限;',
                                    },
                                  ];
                                  const splitBySemicolon = (text: string): string[] => {
                                    return (String(text || '').match(/[^;；]+[;；]?/g) || [])
                                      .map((x) => String(x || '').trim())
                                      .filter(Boolean);
                                  };
                                  Modal.info({
                                    title: '详细说明',
                                    width: 980,
                                    okText: '关闭',
                                    content: (
                                      <div style={{ lineHeight: 1.8, color: '#595959' }}>
                                        {demandBlocks.map((blk, idx) => (
                                          <div key={blk.title} style={{ marginBottom: idx < demandBlocks.length - 1 ? 12 : 0 }}>
                                            <span
                                              style={{
                                                display: 'inline-block',
                                                padding: '1px 8px',
                                                borderRadius: 4,
                                                border: '1px solid #1677ff',
                                                color: '#1677ff',
                                                background: '#f0f5ff',
                                                fontWeight: 600,
                                              }}
                                            >
                                              {blk.title}
                                            </span>
                                            <div style={{ marginTop: 6 }}>
                                              {splitBySemicolon(blk.text).map((line, lineIdx) => (
                                                <div key={`${blk.title}-${lineIdx}`}>{line}</div>
                                              ))}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ),
                                  });
                                }}
                              >
                                详细说明
                              </Button>
                            ) : null}
                          </div>
                        ) : type === 'multiselect' ? (
                          <Select
                            mode="multiple"
                            allowClear
                            style={{ width: inputWidth }}
                            value={Array.isArray(val)?val:[]}
                            onChange={(arr)=> {
                              const a = Array.isArray(arr) ? arr : [];
                              if (a.length === 0) clearVal();
                              else onVal(a);
                            }}
                            options={selectOptions}
                            loading={dynamicLoading}
                          />
                        ) : type === 'number' ? (
                          <InputNumber
                            style={{ width: inputWidth }}
                            value={val}
                            min={q.min}
                            max={q.max}
                            step={q.step}
                            onChange={(v)=> { if (v === null || v === undefined) clearVal(); else onVal(v); }}
                            placeholder={q.placeholder}
                            addonAfter={q.unit}
                          />
                        ) : type === 'textarea' ? (
                          <Input.TextArea style={{ width: inputWidth }} value={val} onChange={(e)=> onVal(e.target.value)} maxLength={q.max_length} placeholder={q.placeholder} rows={2} />
                        ) : (
                          <Input
                            style={{ width: inputWidth }}
                            value={val}
                            ref={key === 'postal_code' ? postalCodeInputRef : undefined}
                            onChange={(e)=> {
                              const v = e.target.value;
                              onVal(v);
                              if (key === 'postal_code') scheduleZipLookup(v);
                            }}
                            onBlur={(e)=> {
                              if (key === 'postal_code') scheduleZipLookup(e.target.value);
                            }}
                            maxLength={q.max_length}
                            placeholder={q.placeholder}
                            status={emailInvalid ? 'error' : undefined}
                          />
                        )}
                        {emailInvalid ? (
                          <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>邮箱格式不正确</div>
                        ) : null}
                      </div>
                    </div>
                  );
                  if (!showRegularOutboundWaybillDivider) return fieldContent;
                  return (
                    <div key={`with-divider-${key || idx}`} style={{ width: '100%' }}>
                      <div style={{ width: '100%', borderTop: '1px solid #f0f0f0', margin: '30px 0' }} />
                      {fieldContent}
                    </div>
                  );
                };
                if (!useWarehouseLayout && !useRegularOutboundWarehouseLayout) {
                  return (
                    <Space direction="vertical" style={{ width: '100%' }} size={8}>
                      {visibleQRows.map((q:any, i:number) => renderField(q, i, { inputWidth: 260 }))}
                    </Space>
                  );
                }
                if (useRegularOutboundWarehouseLayout) {
                  const rows = Array.isArray(visibleQRows) ? visibleQRows : [];
                  const getKey = (q: any) => String(q?.key || '').trim();
                  let insertedAddressParse = false;
                  const rowSpecs: Array<{ keys: string[]; columns: string; key: string }> = [
                    { key: 'recipient_name', keys: ['recipient_name', 'company_name'], columns: 'repeat(2, minmax(0, 1fr))' },
                    { key: 'address_line_1', keys: ['address_line_1', 'address_line_2'], columns: 'repeat(2, minmax(0, 1fr))' },
                    { key: 'city', keys: ['city', 'state', 'postal_code', 'country'], columns: 'repeat(4, minmax(0, 1fr))' },
                    { key: 'phone', keys: ['phone', 'email'], columns: 'repeat(2, minmax(0, 1fr))' },
                    { key: 'signature_required', keys: ['signature_required', 'insurance_required', 'insured_amount'], columns: 'repeat(3, minmax(0, 1fr))' },
                  ];
                  const specByStartKey = new Map(rowSpecs.map((s) => [s.key, s]));
                  const rendered: React.ReactNode[] = [];
                  let i = 0;
                  while (i < rows.length) {
                    const q = rows[i];
                    const key = getKey(q);
                    const spec = specByStartKey.get(key);
                    if (!spec) {
                      rendered.push(
                        <div key={`ro-wb-single-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                          {renderField(q, i, { inputWidth: 260 })}
                        </div>
                      );
                      const qLabel = String(q?.label || '').trim();
                      const kLower = key.toLowerCase();
                      const isWaybillSourceQ = kLower === 'waybill_source' || qLabel === '运单提供方式';
                      if (!insertedAddressParse && enableAddressParse && isWaybillSourceQ) {
                        rendered.push(<React.Fragment key={`ro-address-parse-${i}`}>{addressParseBlock}</React.Fragment>);
                        insertedAddressParse = true;
                      }
                      i += 1;
                      continue;
                    }
                    const picked: any[] = [];
                    let j = i;
                    for (const expectedKey of spec.keys) {
                      if (j >= rows.length) break;
                      const qj = rows[j];
                      const kj = getKey(qj);
                      if (kj !== expectedKey) break;
                      picked.push(qj);
                      j += 1;
                    }
                    if (picked.length <= 0) {
                      rendered.push(
                        <div key={`ro-wb-single-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                          {renderField(q, i, { inputWidth: 260 })}
                        </div>
                      );
                      i += 1;
                      continue;
                    }
                    const actualCols = spec.columns;
                    rendered.push(
                      <div key={`ro-wb-row-${i}`} style={{ display: 'grid', gridTemplateColumns: actualCols, gap: 12 }}>
                        {spec.key === 'signature_required'
                          ? spec.keys.map((expectedKey, idx) => {
                            const hit = picked.find((item) => getKey(item) === expectedKey);
                            if (!hit) return <div key={`ro-wb-empty-${i}-${expectedKey}`} />;
                            return <React.Fragment key={`ro-wb-fixed-${i}-${expectedKey}`}>{renderField(hit, i + idx, { inputWidth: '100%' })}</React.Fragment>;
                          })
                          : picked.map((item, idx) => renderField(item, i + idx, { inputWidth: '100%' }))}
                      </div>
                    );
                    i = j;
                  }
                  if (!insertedAddressParse && enableAddressParse) {
                    rendered.unshift(<React.Fragment key="ro-address-parse-fallback">{addressParseBlock}</React.Fragment>);
                  }
                  return (
                    <Space direction="vertical" style={{ width: '100%' }} size={8}>
                      {rendered}
                    </Space>
                  );
                }
                const groupedRows = groupRows.map((row, rIdx) => {
                  const items = row.map((k) => qMap.get(k)).filter(Boolean) as any[];
                  if (items.length === 0) return null;
                  // 行布局定制
                  if (rIdx === 2) {
                    // 城市/州省/邮编/国家：四等份
                    return (
                      <div key={`wb-row-${rIdx}`} style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
                        {items.map((q, i) => renderField(q, i, { inputWidth: '100%' }))}
                      </div>
                    );
                  }
                  if (rIdx === 3) {
                    // 电话+email 占半行，备注占半行
                    const phone = qMap.get('phone');
                    const email = qMap.get('email');
                    const notes = qMap.get('shipping_notes');
                    return (
                      <div key={`wb-row-${rIdx}`} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          {phone ? renderField(phone, 0, { inputWidth: '100%' }) : null}
                          {email ? renderField(email, 1, { inputWidth: '100%' }) : null}
                        </div>
                        {notes ? renderField(notes, 2, { inputWidth: 520 }) : null}
                      </div>
                    );
                  }
                  if (rIdx === 4) {
                    // 签名/保险/保险金额：三个并排，开关更靠近
                    return (
                      <div key={`wb-row-${rIdx}`} style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
                        {items.map((q, i) => renderField(q, i, { inputWidth: '100%' }))}
                      </div>
                    );
                  }
                  // 默认：两列或三列自适应
                  const widerKeys = new Set(['address_line_1', 'address_line_2']);
                  return (
                    <div key={`wb-row-${rIdx}`} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      {items.map((q, i) => {
                        const k = String(q?.key || '').trim();
                        const w = widerKeys.has(k) ? 520 : 260;
                        return renderField(q, i, { inputWidth: w });
                      })}
                    </div>
                  );
                }).filter(Boolean);
                const others = (visibleQRows || []).filter((q:any) => {
                  const k = String(q.key||'').trim();
                  if (k === 'waybill_source') return false;
                  return !groupedKeys.has(k);
                });
                const waybillQ = qMap.get('waybill_source');
                return (
                  <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    {waybillQ ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        {renderField(waybillQ, 0, { inputWidth: 260 })}
                      </div>
                    ) : null}
                    {enableAddressParse ? addressParseBlock : null}
                    {groupedRows}
                    {others.length > 0 ? <div style={{ height: 4 }} /> : null}
                    {others.map((q:any, i:number) => renderField(q, i, { inputWidth: 260 }))}
                  </Space>
                );
              })()}
            </Card>
          ) : null}
          {/* 模板说明弹窗 */}
          <Modal
            open={tplDescOpen}
            onCancel={() => setTplDescOpen(false)}
            footer={null}
            title={(() => {
              try {
                const tpl = templates.find(t => String(t.id) === String(tplVal));
                const name = tpl?.name;
                return (
                  <span>
                    模板说明 - {name ? <span style={{ color: '#1677ff' }}>{name}</span> : '-'}
                  </span>
                );
              } catch {
                return '模板说明';
              }
            })()}
            width={900}
          >
            {(() => {
              try {
                const tpl = templates.find(t => String(t.id) === String(tplVal));
                const rawText = String(tpl?.brief_description || '-');
                const text = rawText.replace(
                  '明细要求：需指定包装方式,如"只装箱/装袋 直接打托盘 装箱+打托盘"等',
                  '明细要求：需指定是否需要重新包装及包装方式,如"只装箱 直接打托盘 装箱后打托盘"等',
                );
                const lines = text.split('\n').map((x) => String(x || '').trim());
                const nonEmpty = lines.filter(Boolean);
                if (nonEmpty.length <= 1) {
                  return <div style={{ whiteSpace: 'pre-wrap', color: '#444' }}>{text}</div>;
                }
                return (
                  <div style={{ color: '#444' }}>
                    {nonEmpty.map((line, idx) => (
                      <div key={`tpl-desc-line-${idx}`} style={{ whiteSpace: 'pre-wrap', marginBottom: idx === nonEmpty.length - 1 ? 0 : 12 }}>
                        {line}
                      </div>
                    ))}
                  </div>
                );
              } catch { return <div>-</div>; }
            })()}
          </Modal>
          <Modal
            open={packingInfoOpen}
            onCancel={() => setPackingInfoOpen(false)}
            footer={[
              <Button key="close" onClick={() => setPackingInfoOpen(false)}>关闭</Button>
            ]}
            title="包装预估说明"
            width={720}
          >
            <div style={{ color: '#444', lineHeight: 1.7 }}>
              1. 包装预估用于提交一件代发指令前，对于需要打包的订单(一单一件无物流包装 或 一单多件)，预估仓库将会使用的包材及打包后的重量与尺寸。<br />
              2. 根据添加到指令中的库存单元信息，系统将调用推荐算法挑选最合适的包材，并展示预估的包装尺寸与重量。<br />
              3. 默认的推荐逻辑是:能够装下所有产品 且 体积最小的包材。<br />
              4. 客户可使用此预估结果(重量及尺寸)自行购买物流运单并在提交&quot;一件代发&quot;指令时上传此运单。<br />
              5. 此预估结果仅供参考,因为算法限制,仓库最终打包结果和显示的预估结果可能不一致。<br />
              6. 若在提交&quot;一件代发&quot;指令前需要精确的打包数据,请联系本司客服。<br />
              7. 仅显示使用&quot;箱子&quot;作为发货物流包装的预估结果,若选择使用其他软包装发货,如塑胶袋/气泡信封等,无法提供预估结果,仓库会默认使用最小可用包材。<br />
              8. 对于需打包的指令,在提交指令前必须选定打包方式,若无可用打包方式则无需选定,系统将自动记录&quot;特殊打包操作&quot;,见下一条。<br />
              9. 当无可用标准包装方式或不希望使用标准包装方式时,可选择特殊包装操作,比如改箱(将多个空箱合并成一个大箱)/特殊加固(如需使用伸缩膜防水袋等),请在下方指令备注栏标明包装要求,特殊包装操作可能产生额外费用,请联系本司客服确认.
            </div>
          </Modal>
          {/* 附件（按规则逐行上传） */}
          <div style={{ marginTop: 20, ...(disableAfterTemplateCombined ? { opacity: 0.6, pointerEvents: 'none' } : {}) }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
              {hasRequiredAttachments ? <span style={{ color: '#ff4d4f' }}>*</span> : null}
              <span style={sectionTitleStyle}>附件</span>
            </div>
            {/* 限制上传列表中文件名过长导致的布局撑开 */}
            <style>{`
              .attach-filename-limit .ant-upload-list-item-name {
                max-width: 160px;
                display: inline-block;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                vertical-align: bottom;
              }
              .attach-filename-limit .ant-upload-list-item {
                align-items: center;
              }
              /* 附件chip：名称裁剪 + 悬浮显示删除图标 */
              .attach-chip { display: flex; align-items: center; gap: 6px; position: relative; }
              .attach-chip .name-clip { max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
              .attach-chip .del-icon { opacity: 0; transition: opacity .15s ease; color: #ff4d4f; cursor: pointer; }
              .attach-chip:hover .del-icon { opacity: 1; }
            `}</style>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              {/* 规则按"必须提交/可选提交"分组显示，每行三段：左信息/中模板/右上传 */}
              {(() => {
                if (!tplVal || tplVal === '__NONE__') {
                  const customFiles = attachFilesMap[CUSTOM_ATTACH_KEY] || [];
                  return (
                    <div style={{ fontSize: 12, color: '#666' }}>
                      <div>无强制附件要求</div>
                      <div style={{ marginTop: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Upload className="attach-filename-limit" multiple beforeUpload={() => false} showUploadList={false} fileList={customFiles} onChange={({ fileList }) => setFilesForRule(CUSTOM_ATTACH_KEY, fileList)}>
                            <Button type="primary" size="small">添加附件</Button>
                          </Upload>
                          <span style={{ fontSize: 12, color: '#595959' }}>{`已提交 ${customFiles.length}`}</span>
                          {customFiles.length > 0 ? (
                            <Button type="link" danger size="small" onClick={() => setFilesForRule(CUSTOM_ATTACH_KEY, [])}>删除所有附件</Button>
                          ) : null}
                        </div>
                        {/* 两列展示，最多50个，其余折叠为 +X个附件 */}
                        {customFiles.length > 0 ? (
                          <div className="attach-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', marginTop: 8 }}>
                            {customFiles.slice(0, 50).map((f:any, i:number) => (
                              <div key={`cfile-${i}`} className="attach-chip">
                                <span className="name-clip" style={{ fontSize: 12, color: '#595959' }}>{String(f?.name || '附件')}</span>
                                {renderLocalPreviewLink(f)}
                                <DeleteOutlined className="del-icon" onClick={() => {
                                  try {
                                    const list = (attachFilesMap[CUSTOM_ATTACH_KEY] || []) as any[];
                                    const next = list.filter((ff:any, j:number) => (f?.uid != null ? ff?.uid !== f.uid : j !== i));
                                    setFilesForRule(CUSTOM_ATTACH_KEY, next);
                                  } catch {}
                                }} />
                              </div>
                            ))}
                            {customFiles.length > 50 ? (
                              <div className="attach-chip" style={{ fontSize: 12, color: '#595959' }}>{`+${customFiles.length - 50}个附件`}</div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                }
                const rows = ruleRows || [];
                if (rows.length === 0) {
                  const customFiles = attachFilesMap[CUSTOM_ATTACH_KEY] || [];
                  return (
                    <div style={{ fontSize: 12, color: '#666' }}>
                      <div>无强制附件要求</div>
                      <div style={{ marginTop: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Upload className="attach-filename-limit" multiple beforeUpload={() => false} showUploadList={false} fileList={customFiles} onChange={({ fileList }) => setFilesForRule(CUSTOM_ATTACH_KEY, fileList)}>
                            <Button type="primary" size="small">添加附件</Button>
                          </Upload>
                          <span style={{ fontSize: 12, color: '#595959' }}>{`已提交 ${customFiles.length}`}</span>
                          {customFiles.length > 0 ? (
                            <Button type="link" danger size="small" onClick={() => setFilesForRule(CUSTOM_ATTACH_KEY, [])}>删除所有附件</Button>
                          ) : null}
                        </div>
                        {customFiles.length > 0 ? (
                          <div className="attach-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', marginTop: 8 }}>
                            {customFiles.slice(0, 50).map((f:any, i:number) => (
                              <div key={`cfile2-${i}`} className="attach-chip">
                                <span className="name-clip" style={{ fontSize: 12, color: '#595959' }}>{String(f?.name || '附件')}</span>
                                {renderLocalPreviewLink(f)}
                                <DeleteOutlined className="del-icon" onClick={() => {
                                  try {
                                    const list = (attachFilesMap[CUSTOM_ATTACH_KEY] || []) as any[];
                                    const next = list.filter((ff:any, j:number) => (f?.uid != null ? ff?.uid !== f.uid : j !== i));
                                    setFilesForRule(CUSTOM_ATTACH_KEY, next);
                                  } catch {}
                                }} />
                              </div>
                            ))}
                            {customFiles.length > 50 ? (
                              <div className="attach-chip" style={{ fontSize: 12, color: '#595959' }}>{`+${customFiles.length - 50}个附件`}</div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                }
                const visibleRows = rows.filter(isAttachmentVisible);
                const required = visibleRows.filter(r => Number(r.min ?? 0) >= 1);
                const optional = visibleRows.filter(r => !required.includes(r));
                return (
                  <div>
                    {/* 必须提交 */}
                    {required.length > 0 && (
                      <div style={{ background: '#fff1f0', border: '1px solid #ffa39e', padding: 8, borderRadius: 4, marginBottom: 8 }}>
                        <div style={{ fontWeight: 600, marginBottom: 4, color: '#cf1322' }}>必须提交</div>
                        {required.map((r, idx) => {
                          const files = attachFilesMap[r.key] || [];
                          const types = Array.isArray(r.types) ? r.types : [];
                          const accept = types.map(t => (t && !t.startsWith('.') ? `.${t}` : t)).join(',');
                          const max = Number(r.max ?? undefined);
                          return (
                            <div key={`req-row-${r.key}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '6px 0' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div><strong>{r.label || '未命名'}</strong></div>
                                <div style={{ fontSize: 12, color: '#595959' }}>
                                  {`数量要求：${(r.min!=null)?`最小${r.min}`:'最小0'} ${(r.max!=null)?`最大${r.max}`:''}`}；{` 文件类型选项：${(types && types.length>0)? types.join('  ') : '-'}`}
                                </div>
                                {r.tips ? <div style={{ fontSize: 12, color: '#8c8c8c' }}>{`附件提示信息：${r.tips}`}</div> : null}
                              </div>
                              <div style={{ width: 240, textAlign: 'center', paddingRight: 6 }}>
                                {(() => {
                                  const ids = Array.isArray((r as any).template_ids)
                                    ? (r as any).template_ids
                                    : (r.template_id != null ? [Number(r.template_id)] : []);
                                  if (!ids || ids.length === 0) {
                                    return <span style={{ fontSize: 12, color: '#999' }}>无附件模板</span>;
                                  }
                                  return (
                                    <Space size={4} direction="vertical">
                                      {ids.map((tid:number, tIdx:number) => {
                                        const meta = (attachTplMetaById || {})[Number(tid)] || {};
                                        const label = meta.file_name || meta.name || r.label || '附件模板';
                                        const btnText = `下载附件模板-${label}${ids.length > 1 ? `-${tIdx + 1}` : ''}`;
                                        return (
                                          <Tooltip key={`tpl-${r.key}-${tid}`} title={btnText}>
                                            <Button
                                              type="link"
                                              onClick={() => downloadTemplate(Number(tid), label)}
                                              style={{ maxWidth: 200, padding: 0 }}
                                            >
                                              <span
                                                style={{
                                                  display: 'inline-block',
                                                  maxWidth: 200,
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                                  whiteSpace: 'nowrap',
                                                  verticalAlign: 'bottom',
                                                }}
                                              >
                                                {btnText}
                                              </span>
                                            </Button>
                                          </Tooltip>
                                        );
                                      })}
                                    </Space>
                                  );
                                })()}
                              </div>
                              <div style={{ flex: 1, minWidth: 420, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <Upload
                                  className="attach-filename-limit"
                                  multiple
                                  accept={accept || undefined}
                                  beforeUpload={(file) => {
                                    if (isWaybillAttachmentRow(r) && shouldValidateWaybillUpload()) {
                                      return guardWaybillBeforeUpload(file as any);
                                    }
                                    return false;
                                  }}
                                  showUploadList={false}
                                  fileList={files}
                                  onChange={async ({ fileList }) => {
                                    let next = fileList;
                                    try {
                                      console.info('[WaybillValidate][UploadChange]', {
                                        label: r?.label,
                                        ruleKey: r?.key,
                                        fileCount: Array.isArray(next) ? next.length : 0,
                                        showPackingAssessment,
                                        submitMode: resolveWaybillSubmitMode(),
                                        waybillSource: (answers as any)?.waybill_source,
                                        shouldValidate: shouldValidateWaybillUpload(),
                                        isWaybillRow: isWaybillAttachmentRow(r),
                                      });
                                    } catch {}
                                    // 类型校验（与规则 types 对齐）
                                    const allowed = (types || []).map((t:any)=>String(t).replace(/^\./,'').toLowerCase());
                                    if (Array.isArray(allowed) && allowed.length > 0) {
                                      const invalid = next.filter((f:any) => {
                                        const name = String(f?.name || '');
                                        const ext = name.includes('.') ? name.split('.').pop()?.toLowerCase() : '';
                                        return !allowed.includes(String(ext || ''));
                                      });
                                      if (invalid.length > 0) {
                                        message.error(`文件类型不符合要求：${invalid.map((f:any)=>String(f.name||'')).join('，')}（允许：${allowed.join('  ')}）`);
                                        next = next.filter((f:any) => !invalid.includes(f));
                                      }
                                    }
                                    if (Number.isFinite(max) && next.length > Number(max)) {
                                      message.warning(`最多允许上传 ${Number(max)} 个附件，已忽略多余文件`);
                                      next = next.slice(0, Number(max));
                                    }
                                    next = await applyWaybillValidationIfNeeded(r, next);
                                    setFilesForRule(r.key, next);
                                    try {
                                      const binding = findBindingByRuleKey(r.key);
                                      if (binding && isBarcodeMappingBinding(binding)) {
                                        const f: File | undefined = (next && next[0] && (next[0] as any).originFileObj) as any;
                                        if (f) { await checkBarcodeMappingDuplicates(f, binding); }
                                      }
                                    } catch {}
                                    try {
                                      const binding = findBindingByRuleKey(r.key);
                                      if (binding && isShippingLabelTargetMappingBinding(binding)) {
                                        const f: File | undefined = (next && next[0] && (next[0] as any).originFileObj) as any;
                                        if (f) { await checkShippingLabelTargetMappingWarnings(f, binding); }
                                      }
                                    } catch {}
                                    try {
                                      const binding = findBindingByRuleKey(r.key);
                                      const canCheckGroupList = !!isSortAndCountTemplate && (
                                        (binding && isGroupListBinding(binding))
                                        || String(r?.label || '').includes('分组列表')
                                      );
                                      if (canCheckGroupList) {
                                        const f: File | undefined = (next && next[0] && (next[0] as any).originFileObj) as any;
                                        if (f) { await checkGroupListWarnings(f, binding); }
                                      }
                                    } catch {}
                                    // 新标签 PDF 质量预审（弱提示）：仅对 role_code=NEW_BARCODE（或包含 NEW_BARCODE）的规则执行
                                    try {
                                      const binding = findBindingByRuleKey(r.key);
                                      if (binding && isNewBarcodeLabelBinding(binding)) {
                                        const prevUids = new Set((files || []).map((f:any)=> String(f?.uid || '')));
                                        const nextUids = new Set((next || []).map((f:any)=> String(f?.uid || '')));
                                        const removedUids = Array.from(prevUids).filter(uid => uid && !nextUids.has(uid));
                                        if (removedUids.length > 0) {
                                          setPdfQaResultByUid(prev => {
                                            const m = { ...(prev || {}) } as any;
                                            removedUids.forEach(uid => { try { delete m[String(uid)]; } catch {} });
                                            return m;
                                          });
                                          setPdfQaLoadingByUid(prev => {
                                            const m = { ...(prev || {}) } as any;
                                            removedUids.forEach(uid => { try { delete m[String(uid)]; } catch {} });
                                            return m;
                                          });
                                        }
                                        const added = (next || []).filter((f:any) => {
                                          const uid = String(f?.uid || '');
                                          if (!uid || prevUids.has(uid)) return false;
                                          if (!f?.originFileObj) return false;
                                          if (!isPdfAuditCandidateFile(String(f?.name || ''))) return false;
                                          return true;
                                        });
                                        for (const ff of (added || [])) {
                                          const uid = String(ff?.uid || '');
                                          const real: File | undefined = (ff as any)?.originFileObj as any;
                                          if (!uid || !real) continue;
                                          setPdfQaLoadingByUid(prev => ({ ...(prev || {}), [uid]: true }));
                                          try {
                                            const data = await runPdfLabelQualityAudit(real);
                                            setPdfQaResultByUid(prev => ({ ...(prev || {}), [uid]: data }));
                                            // 预审结束且存在风险：自动打开风险详情弹窗（不影响提交流程）
                                            try {
                                              const sum = (data as any)?.summary || {};
                                              const err = Number(sum?.error ?? 0) || 0;
                                              if (err > 0) {
                                                setPdfQaModalRuleKey(String(r.key));
                                                setPdfQaModalTitle(String(r.label || '新标签PDF'));
                                                // 避免重复打断：弹窗未打开时才自动打开
                                                setPdfQaModalOpen((prevOpen) => prevOpen ? prevOpen : true);
                                              }
                                            } catch {}
                                          } catch (e:any) {
                                            try { console.error('[PDFQa][Create] audit failed', e); } catch {}
                                            message.warning(`新标签PDF预审失败：${String(real?.name || ff?.name || '')}`);
                                          } finally {
                                            setPdfQaLoadingByUid(prev => ({ ...(prev || {}), [uid]: false }));
                                          }
                                        }
                                      }
                                    } catch {}
                                  }}
                                >
                                  <Button type="primary" size="small" onClick={(e) => guardWaybillUploadClick(r, e)}>添加附件</Button>
                                </Upload>
                                <span style={{ fontSize: 12, color: '#595959' }}>{`已提交 ${files.length}`}</span>
                                {files.length > 0 ? (
                                  <Button type="link" danger size="small" onClick={() => setFilesForRule(r.key, [])}>删除所有附件</Button>
                                ) : null}
                                {isWaybillAttachmentRow(r) && (waybillAuditIssues || []).length > 0 ? (
                                  <Button
                                    size="small"
                                    type="link"
                                    style={{ padding: 0, height: 18 }}
                                    onClick={() => {
                                      setWaybillAuditModalRuleKey(String(r.key));
                                      setWaybillAuditModalTitle(String(r.label || '物流运单'));
                                      setWaybillAuditModalOpen(true);
                                    }}
                                  >
                                    查看风险项
                                  </Button>
                                ) : null}
                                {(() => {
                                  try {
                                    const binding = findBindingByRuleKey(r.key);
                                    if (!binding || !isNewBarcodeLabelBinding(binding)) return null;
                                    const q = String(nbFileQueryByRuleKey[r.key] || '');
                                    return (
                                      <Input.Search
                                        allowClear
                                        value={q}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          setNbFileQueryByRuleKey(prev => ({ ...(prev || {}), [r.key]: v }));
                                          setNbFilePageByRuleKey(prev => ({ ...(prev || {}), [r.key]: 1 }));
                                        }}
                                        placeholder="搜索文件名 模糊匹配"
                                        style={{ width: 200 }}
                                      />
                                    );
                                  } catch { return null; }
                                })()}
                                {/* 已上传附件展示：
                                    - NEW_BARCODE：启用 搜索+分页（两列chip网格）
                                    - 其它：保持旧逻辑（最多50个） */}
                                {(() => {
                                  if (!files || files.length === 0) return null;
                                  let isNB = false;
                                  try {
                                    const binding = findBindingByRuleKey(r.key);
                                    isNB = !!binding && isNewBarcodeLabelBinding(binding);
                                  } catch { isNB = false; }
                                  if (!isNB) {
                                    return (
                                      <div className="attach-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', width: '100%' }}>
                                        {files.slice(0, 50).map((f:any, i:number) => (
                                          <div key={`rfile-${r.key}-${i}`} className="attach-chip">
                                            <span className="name-clip" style={{ fontSize: 12, color: '#595959' }}>{String(f?.name || '附件')}</span>
                                            {renderLocalPreviewLink(f)}
                                            <DeleteOutlined className="del-icon" onClick={() => {
                                              try {
                                                const list = (attachFilesMap[r.key] || []) as any[];
                                                const next = list.filter((ff:any, j:number) => (f?.uid != null ? ff?.uid !== f.uid : j !== i));
                                                setFilesForRule(r.key, next);
                                              } catch {}
                                            }} />
                                          </div>
                                        ))}
                                        {files.length > 50 ? (
                                          <div className="attach-chip" style={{ fontSize: 12, color: '#595959' }}>{`+${files.length - 50}个附件`}</div>
                                        ) : null}
                                      </div>
                                    );
                                  }
                                  const q = String(nbFileQueryByRuleKey[r.key] || '');
                                  const norm = (s:any) => String(s ?? '').toLowerCase();
                                  const filtered = (files || []).filter((f:any) => {
                                    if (!q) return true;
                                    return norm(f?.name).includes(norm(q));
                                  });
                                  const pageSize = Math.max(10, Number(nbFilePageSizeByRuleKey[r.key] || 50) || 50);
                                  const total = filtered.length;
                                  const maxPage = Math.max(1, Math.ceil(total / pageSize));
                                  const pageRaw = Number(nbFilePageByRuleKey[r.key] || 1) || 1;
                                  const page = Math.min(Math.max(1, pageRaw), maxPage);
                                  const start = (page - 1) * pageSize;
                                  const pageItems = filtered.slice(start, start + pageSize);
                                  return (
                                    <div style={{ width: '100%' }}>
                                      <div className="attach-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', width: '100%', marginTop: 8 }}>
                                        {pageItems.map((f:any, i:number) => (
                                          <div key={`rfile-nb-${r.key}-${String(f?.uid || i)}`} className="attach-chip">
                                            <span className="name-clip" style={{ fontSize: 12, color: '#595959' }}>{String(f?.name || '附件')}</span>
                                            {renderLocalPreviewLink(f)}
                                            <DeleteOutlined className="del-icon" onClick={() => {
                                              try {
                                                const list = (attachFilesMap[r.key] || []) as any[];
                                                const next = list.filter((ff:any, j:number) => (f?.uid != null ? ff?.uid !== f.uid : ff?.name !== f?.name));
                                                setFilesForRule(r.key, next);
                                              } catch {}
                                            }} />
                                          </div>
                                        ))}
                                      </div>
                                      <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                                        <Pagination
                                          size="small"
                                          current={page}
                                          pageSize={pageSize}
                                          total={total}
                                          showSizeChanger
                                          pageSizeOptions={['10', '20', '50', '100', '200']}
                                          showQuickJumper
                                          showTotal={(t, range) => `共${t}条记录，显示${range[0]}-${range[1]}条`}
                                          onChange={(p, ps) => {
                                            setNbFilePageByRuleKey(prev => ({ ...(prev || {}), [r.key]: p }));
                                            setNbFilePageSizeByRuleKey(prev => ({ ...(prev || {}), [r.key]: ps }));
                                          }}
                                        />
                                      </div>
                                    </div>
                                  );
                                })()}
                                {(r as any).printable && files.length > 0 ? (
                                  <div style={{ marginTop: 8, padding: 8, background: '#fafafa', borderRadius: 4 }}>
                                    <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 12 }}>打印设置（每个附件需填写）</div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                      {(files || []).slice(0, 50).map((f: any, i: number) => {
                                        const uid = String(f?.uid || i);
                                        const cfg = attachPrintConfigMap[r.key]?.[uid] || {};
                                        return (
                                          <div key={`print-${r.key}-${uid}`} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: i < files.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                            <span style={{ width: 140, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(f?.name || '附件')}</span>
                                            <span style={{ fontSize: 12 }}>是否打印</span>
                                            <Switch
                                              checked={!!cfg.print}
                                              size="small"
                                              onChange={(v) => setAttachPrintConfig(
                                                r.key,
                                                uid,
                                                v
                                                  ? { print: true, explicit_no_print: false }
                                                  : { print: false, explicit_no_print: true, copies: undefined, paper_size: undefined, placement: undefined },
                                              )}
                                            />
                                            <Tooltip title="该附件需打印的份数">
                                              <InputNumber size="small" min={1} value={cfg.print ? cfg.copies : undefined} disabled={!cfg.print} onChange={(v) => setAttachPrintConfig(r.key, uid, { copies: Math.max(1, Number(v) || 1) })} style={{ width: 90 }} placeholder="打印份数" />
                                            </Tooltip>
                                            <Tooltip title="选择打印使用的纸张尺寸">
                                              <Select size="small" value={cfg.print ? (cfg.paper_size || undefined) : undefined} disabled={!cfg.print} onChange={(v) => setAttachPrintConfig(r.key, uid, { paper_size: v })} options={PRINT_PAPER_OPTIONS} style={{ width: 160 }} placeholder="选择打印纸" allowClear />
                                            </Tooltip>
                                            <Tooltip title="打印后放置在包裹内或包裹外">
                                              <Select size="small" value={cfg.print ? (cfg.placement || undefined) : undefined} disabled={!cfg.print} onChange={(v) => setAttachPrintConfig(r.key, uid, { placement: v })} options={PRINT_PLACEMENT_OPTIONS} style={{ width: 120 }} placeholder="放置位置" allowClear />
                                            </Tooltip>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ) : null}
                                {(() => {
                                  try {
                                    const binding = findBindingByRuleKey(r.key);
                                    if (!binding || !isNewBarcodeLabelBinding(binding)) return null;
                                    const uids = (files || []).map((f:any)=> String(f?.uid || '')).filter(Boolean);
                                    const pending = uids.some((uid)=> !!pdfQaLoadingByUid[uid]);
                                    const sums = uids.map((uid)=> ({ uid, sum: getPdfQaSummaryForUid(uid) })).filter((x:any)=> !!x?.sum);
                                    const hasRisk = sums.some((x:any)=> !!x?.sum?.hasRisk);
                                    if (uids.length === 0 && sums.length === 0) return null;
                                    const submitted = uids.length;
                                    const passed = sums.filter((x:any)=> !x?.sum?.hasRisk).length;
                                    const riskCnt = sums.filter((x:any)=> !!x?.sum?.hasRisk).length;
                                    return (
                                      <div style={{ width: '100%', marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                                      {null}
                                        {!pending ? (
                                          <span style={{ fontSize: 12, color: '#595959' }}>
                                            预审结果：已提交 {submitted} ｜ 通过 {passed} ｜ 存在风险 {riskCnt}
                                          </span>
                                        ) : null}
                                        {riskCnt > 0 ? (
                                          <Button
                                            size="small"
                                            type="link"
                                            style={{ padding: 0, height: 18 }}
                                            onClick={() => {
                                              setPdfQaModalRuleKey(String(r.key));
                                              setPdfQaModalTitle(String(r.label || '新标签PDF'));
                                              setPdfQaModalOpen(true);
                                            }}
                                          >
                                            查看风险项
                                          </Button>
                                        ) : null}
                                      </div>
                                    );
                                  } catch { return null; }
                                })()}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* 可选提交 */}
                    {optional.length > 0 && (
                      <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', padding: 8, borderRadius: 4 }}>
                        <div style={{ fontWeight: 600, marginBottom: 4, color: '#389e0d' }}>可选提交</div>
                        {optional.map((r, idx) => {
                          const files = attachFilesMap[r.key] || [];
                          const types = Array.isArray(r.types) ? r.types : [];
                          const accept = types.map(t => (t && !t.startsWith('.') ? `.${t}` : t)).join(',');
                          const max = Number(r.max ?? undefined);
                          return (
                            <div key={`opt-row-${r.key}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '6px 0' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div><strong>{r.label || '未命名'}</strong></div>
                                <div style={{ fontSize: 12, color: '#595959' }}>
                                  {`数量要求：${(r.min!=null)?`最小${r.min}`:'最小0'} ${(r.max!=null)?`最大${r.max}`:''}`}；{` 文件类型选项：${(types && types.length>0)? types.join('  ') : '-'}`}
                                </div>
                                {r.tips ? <div style={{ fontSize: 12, color: '#8c8c8c' }}>{`附件提示信息：${r.tips}`}</div> : null}
                              </div>
                              <div style={{ width: 240, textAlign: 'center', paddingRight: 6 }}>
                                {(() => {
                                  const ids = Array.isArray((r as any).template_ids)
                                    ? (r as any).template_ids
                                    : (r.template_id != null ? [Number(r.template_id)] : []);
                                  if (!ids || ids.length === 0) {
                                    return <span style={{ fontSize: 12, color: '#999' }}>无附件模板</span>;
                                  }
                                  return (
                                    <Space size={4} direction="vertical">
                                      {ids.map((tid:number, tIdx:number) => {
                                        const meta = (attachTplMetaById || {})[Number(tid)] || {};
                                        const label = meta.file_name || meta.name || r.label || '附件模板';
                                        const btnText = `下载附件模板-${label}${ids.length > 1 ? `-${tIdx + 1}` : ''}`;
                                        return (
                                          <Tooltip key={`tpl-opt-${r.key}-${tid}`} title={btnText}>
                                            <Button
                                              type="link"
                                              onClick={() => downloadTemplate(Number(tid), label)}
                                              style={{ maxWidth: 200, padding: 0 }}
                                            >
                                              <span
                                                style={{
                                                  display: 'inline-block',
                                                  maxWidth: 200,
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                                  whiteSpace: 'nowrap',
                                                  verticalAlign: 'bottom',
                                                }}
                                              >
                                                {btnText}
                                              </span>
                                            </Button>
                                          </Tooltip>
                                        );
                                      })}
                                    </Space>
                                  );
                                })()}
                              </div>
                              <div style={{ flex: 1, minWidth: 420, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <Upload
                                  className="attach-filename-limit"
                                  multiple
                                  accept={accept || undefined}
                                  beforeUpload={(file) => {
                                    if (isWaybillAttachmentRow(r) && shouldValidateWaybillUpload()) {
                                      return guardWaybillBeforeUpload(file as any);
                                    }
                                    return false;
                                  }}
                                  showUploadList={false}
                                  fileList={files}
                                  onChange={async ({ fileList }) => {
                                    let next = fileList;
                                    try {
                                      console.info('[WaybillValidate][UploadChange]', {
                                        label: r?.label,
                                        ruleKey: r?.key,
                                        fileCount: Array.isArray(next) ? next.length : 0,
                                        showPackingAssessment,
                                        submitMode: resolveWaybillSubmitMode(),
                                        waybillSource: (answers as any)?.waybill_source,
                                        shouldValidate: shouldValidateWaybillUpload(),
                                        isWaybillRow: isWaybillAttachmentRow(r),
                                      });
                                    } catch {}
                                    // 类型校验（与规则 types 对齐）
                                    const allowed = (types || []).map((t:any)=>String(t).replace(/^\./,'').toLowerCase());
                                    if (Array.isArray(allowed) && allowed.length > 0) {
                                      const invalid = next.filter((f:any) => {
                                        const name = String(f?.name || '');
                                        const ext = name.includes('.') ? name.split('.').pop()?.toLowerCase() : '';
                                        return !allowed.includes(String(ext || ''));
                                      });
                                      if (invalid.length > 0) {
                                        message.error(`文件类型不符合要求：${invalid.map((f:any)=>String(f.name||'')).join('，')}（允许：${allowed.join('  ')}）`);
                                        next = next.filter((f:any) => !invalid.includes(f));
                                      }
                                    }
                                    if (Number.isFinite(max) && next.length > Number(max)) {
                                      message.warning(`最多允许上传 ${Number(max)} 个附件，已忽略多余文件`);
                                      next = next.slice(0, Number(max));
                                    }
                                    next = await applyWaybillValidationIfNeeded(r, next);
                                    setFilesForRule(r.key, next);
                                    try {
                                      const binding = findBindingByRuleKey(r.key);
                                      if (binding && isBarcodeMappingBinding(binding)) {
                                        const f: File | undefined = (next && next[0] && (next[0] as any).originFileObj) as any;
                                        if (f) { await checkBarcodeMappingDuplicates(f, binding); }
                                      }
                                    } catch {}
                                    try {
                                      const binding = findBindingByRuleKey(r.key);
                                      if (binding && isShippingLabelTargetMappingBinding(binding)) {
                                        const f: File | undefined = (next && next[0] && (next[0] as any).originFileObj) as any;
                                        if (f) { await checkShippingLabelTargetMappingWarnings(f, binding); }
                                      }
                                    } catch {}
                                    try {
                                      const binding = findBindingByRuleKey(r.key);
                                      const canCheckGroupList = !!isSortAndCountTemplate && (
                                        (binding && isGroupListBinding(binding))
                                        || String(r?.label || '').includes('分组列表')
                                      );
                                      if (canCheckGroupList) {
                                        const f: File | undefined = (next && next[0] && (next[0] as any).originFileObj) as any;
                                        if (f) { await checkGroupListWarnings(f, binding); }
                                      }
                                    } catch {}
                                    // 新标签 PDF 质量预审（弱提示）
                                    try {
                                      const binding = findBindingByRuleKey(r.key);
                                      if (binding && isNewBarcodeLabelBinding(binding)) {
                                        const prevUids = new Set((files || []).map((f:any)=> String(f?.uid || '')));
                                        const nextUids = new Set((next || []).map((f:any)=> String(f?.uid || '')));
                                        const removedUids = Array.from(prevUids).filter(uid => uid && !nextUids.has(uid));
                                        if (removedUids.length > 0) {
                                          setPdfQaResultByUid(prev => {
                                            const m = { ...(prev || {}) } as any;
                                            removedUids.forEach(uid => { try { delete m[String(uid)]; } catch {} });
                                            return m;
                                          });
                                          setPdfQaLoadingByUid(prev => {
                                            const m = { ...(prev || {}) } as any;
                                            removedUids.forEach(uid => { try { delete m[String(uid)]; } catch {} });
                                            return m;
                                          });
                                        }
                                        const added = (next || []).filter((f:any) => {
                                          const uid = String(f?.uid || '');
                                          if (!uid || prevUids.has(uid)) return false;
                                          if (!f?.originFileObj) return false;
                                          if (!isPdfAuditCandidateFile(String(f?.name || ''))) return false;
                                          return true;
                                        });
                                        for (const ff of (added || [])) {
                                          const uid = String(ff?.uid || '');
                                          const real: File | undefined = (ff as any)?.originFileObj as any;
                                          if (!uid || !real) continue;
                                          setPdfQaLoadingByUid(prev => ({ ...(prev || {}), [uid]: true }));
                                          try {
                                            const data = await runPdfLabelQualityAudit(real);
                                            setPdfQaResultByUid(prev => ({ ...(prev || {}), [uid]: data }));
                                            try {
                                              const sum = (data as any)?.summary || {};
                                              const err = Number(sum?.error ?? 0) || 0;
                                              if (err > 0) {
                                                setPdfQaModalRuleKey(String(r.key));
                                                setPdfQaModalTitle(String(r.label || '新标签PDF'));
                                                setPdfQaModalOpen((prevOpen) => prevOpen ? prevOpen : true);
                                              }
                                            } catch {}
                                          } catch (e:any) {
                                            try { console.error('[PDFQa][Create] audit failed', e); } catch {}
                                            message.warning(`新标签PDF预审失败：${String(real?.name || ff?.name || '')}`);
                                          } finally {
                                            setPdfQaLoadingByUid(prev => ({ ...(prev || {}), [uid]: false }));
                                          }
                                        }
                                      }
                                    } catch {}
                                  }}
                                >
                                  <Button type="primary" size="small" onClick={(e) => guardWaybillUploadClick(r, e)}>添加附件</Button>
                                </Upload>
                                <span style={{ fontSize: 12, color: '#595959' }}>{`已提交 ${files.length}`}</span>
                                {files.length > 0 ? (
                                  <Button type="link" danger size="small" onClick={() => setFilesForRule(r.key, [])}>删除所有附件</Button>
                                ) : null}
                                {(() => {
                                  try {
                                    const binding = findBindingByRuleKey(r.key);
                                    if (!binding || !isNewBarcodeLabelBinding(binding)) return null;
                                    const q = String(nbFileQueryByRuleKey[r.key] || '');
                                    return (
                                      <Input.Search
                                        allowClear
                                        value={q}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          setNbFileQueryByRuleKey(prev => ({ ...(prev || {}), [r.key]: v }));
                                          setNbFilePageByRuleKey(prev => ({ ...(prev || {}), [r.key]: 1 }));
                                        }}
                                        placeholder="搜索文件名 模糊匹配"
                                        style={{ width: 200 }}
                                      />
                                    );
                                  } catch { return null; }
                                })()}
                                {(() => {
                                  if (!files || files.length === 0) return null;
                                  let isNB = false;
                                  try {
                                    const binding = findBindingByRuleKey(r.key);
                                    isNB = !!binding && isNewBarcodeLabelBinding(binding);
                                  } catch { isNB = false; }
                                  if (!isNB) {
                                    return (
                                      <div className="attach-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', width: '100%' }}>
                                        {files.slice(0, 50).map((f:any, i:number) => (
                                          <div key={`ofile-${r.key}-${i}`} className="attach-chip">
                                            <span className="name-clip" style={{ fontSize: 12, color: '#595959' }}>{String(f?.name || '附件')}</span>
                                            {renderLocalPreviewLink(f)}
                                            <DeleteOutlined className="del-icon" onClick={() => {
                                              try {
                                                const list = (attachFilesMap[r.key] || []) as any[];
                                                const next = list.filter((ff:any, j:number) => (f?.uid != null ? ff?.uid !== f.uid : j !== i));
                                                setFilesForRule(r.key, next);
                                              } catch {}
                                            }} />
                                          </div>
                                        ))}
                                        {files.length > 50 ? (
                                          <div className="attach-chip" style={{ fontSize: 12, color: '#595959' }}>{`+${files.length - 50}个附件`}</div>
                                        ) : null}
                                      </div>
                                    );
                                  }
                                  const q = String(nbFileQueryByRuleKey[r.key] || '');
                                  const norm = (s:any) => String(s ?? '').toLowerCase();
                                  const filtered = (files || []).filter((f:any) => {
                                    if (!q) return true;
                                    return norm(f?.name).includes(norm(q));
                                  });
                                  const pageSize = Math.max(10, Number(nbFilePageSizeByRuleKey[r.key] || 50) || 50);
                                  const total = filtered.length;
                                  const maxPage = Math.max(1, Math.ceil(total / pageSize));
                                  const pageRaw = Number(nbFilePageByRuleKey[r.key] || 1) || 1;
                                  const page = Math.min(Math.max(1, pageRaw), maxPage);
                                  const start = (page - 1) * pageSize;
                                  const pageItems = filtered.slice(start, start + pageSize);
                                  return (
                                    <div style={{ width: '100%' }}>
                                      <div className="attach-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', width: '100%', marginTop: 8 }}>
                                        {pageItems.map((f:any, i:number) => (
                                          <div key={`ofile-nb-${r.key}-${String(f?.uid || i)}`} className="attach-chip">
                                            <span className="name-clip" style={{ fontSize: 12, color: '#595959' }}>{String(f?.name || '附件')}</span>
                                            {renderLocalPreviewLink(f)}
                                            <DeleteOutlined className="del-icon" onClick={() => {
                                              try {
                                                const list = (attachFilesMap[r.key] || []) as any[];
                                                const next = list.filter((ff:any, j:number) => (f?.uid != null ? ff?.uid !== f.uid : ff?.name !== f?.name));
                                                setFilesForRule(r.key, next);
                                              } catch {}
                                            }} />
                                          </div>
                                        ))}
                                      </div>
                                      <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                                        <Pagination
                                          size="small"
                                          current={page}
                                          pageSize={pageSize}
                                          total={total}
                                          showSizeChanger
                                          pageSizeOptions={['10', '20', '50', '100', '200']}
                                          showQuickJumper
                                          showTotal={(t, range) => `共${t}条记录，显示${range[0]}-${range[1]}条`}
                                          onChange={(p, ps) => {
                                            setNbFilePageByRuleKey(prev => ({ ...(prev || {}), [r.key]: p }));
                                            setNbFilePageSizeByRuleKey(prev => ({ ...(prev || {}), [r.key]: ps }));
                                          }}
                                        />
                                      </div>
                                    </div>
                                  );
                                })()}
                                {(r as any).printable && files.length > 0 ? (
                                  <div style={{ marginTop: 8, padding: 8, background: '#fafafa', borderRadius: 4 }}>
                                    <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 12 }}>打印设置（每个附件需填写）</div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                      {(files || []).slice(0, 50).map((f: any, i: number) => {
                                        const uid = String(f?.uid || i);
                                        const cfg = attachPrintConfigMap[r.key]?.[uid] || {};
                                        return (
                                          <div key={`print-${r.key}-${uid}`} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: i < files.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                            <span style={{ width: 140, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(f?.name || '附件')}</span>
                                            <span style={{ fontSize: 12 }}>是否打印</span>
                                            <Switch
                                              checked={!!cfg.print}
                                              size="small"
                                              onChange={(v) => setAttachPrintConfig(
                                                r.key,
                                                uid,
                                                v
                                                  ? { print: true, explicit_no_print: false }
                                                  : { print: false, explicit_no_print: true, copies: undefined, paper_size: undefined, placement: undefined },
                                              )}
                                            />
                                            <Tooltip title="该附件需打印的份数">
                                              <InputNumber size="small" min={1} value={cfg.print ? cfg.copies : undefined} disabled={!cfg.print} onChange={(v) => setAttachPrintConfig(r.key, uid, { copies: Math.max(1, Number(v) || 1) })} style={{ width: 90 }} placeholder="打印份数" />
                                            </Tooltip>
                                            <Tooltip title="选择打印使用的纸张尺寸">
                                              <Select size="small" value={cfg.print ? (cfg.paper_size || undefined) : undefined} disabled={!cfg.print} onChange={(v) => setAttachPrintConfig(r.key, uid, { paper_size: v })} options={PRINT_PAPER_OPTIONS} style={{ width: 160 }} placeholder="选择打印纸" allowClear />
                                            </Tooltip>
                                            <Tooltip title="打印后放置在包裹内或包裹外">
                                              <Select size="small" value={cfg.print ? (cfg.placement || undefined) : undefined} disabled={!cfg.print} onChange={(v) => setAttachPrintConfig(r.key, uid, { placement: v })} options={PRINT_PLACEMENT_OPTIONS} style={{ width: 120 }} placeholder="放置位置" allowClear />
                                            </Tooltip>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ) : null}
                                {(() => {
                                  try {
                                    const binding = findBindingByRuleKey(r.key);
                                    if (!binding || !isNewBarcodeLabelBinding(binding)) return null;
                                    const uids = (files || []).map((f:any)=> String(f?.uid || '')).filter(Boolean);
                                    const pending = uids.some((uid)=> !!pdfQaLoadingByUid[uid]);
                                    const sums = uids.map((uid)=> ({ uid, sum: getPdfQaSummaryForUid(uid) })).filter((x:any)=> !!x?.sum);
                                    const hasRisk = sums.some((x:any)=> !!x?.sum?.hasRisk);
                                    if (uids.length === 0 && sums.length === 0) return null;
                                    const submitted = uids.length;
                                    const passed = sums.filter((x:any)=> !x?.sum?.hasRisk).length;
                                    const riskCnt = sums.filter((x:any)=> !!x?.sum?.hasRisk).length;
                                    return (
                                      <div style={{ width: '100%', marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                                        {null}
                                        {!pending ? (
                                          <span style={{ fontSize: 12, color: '#595959' }}>
                                            预审结果：已提交 {submitted} ｜ 通过 {passed} ｜ 存在风险 {riskCnt}
                                          </span>
                                        ) : null}
                                        {riskCnt > 0 ? (
                                          <Button
                                            size="small"
                                            type="link"
                                            style={{ padding: 0, height: 18 }}
                                            onClick={() => {
                                              setPdfQaModalRuleKey(String(r.key));
                                              setPdfQaModalTitle(String(r.label || '新标签PDF'));
                                              setPdfQaModalOpen(true);
                                            }}
                                          >
                                            查看风险项
                                          </Button>
                                        ) : null}
                                      </div>
                                    );
                                  } catch { return null; }
                                })()}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
            </Space>
          </div>
          {/* 备注（移动到末尾） */}
          <Form.Item name="priority" label={<span style={sectionTitleStyle}>指令优先级</span>} initialValue={'normal'} style={{ marginTop: 20 }}>
            <Select style={{ width: 180 }} disabled={disableAfterTemplateCombined} options={[
              { value: 'normal', label: '普通' },
              { value: 'high', label: '一般优先' },
              { value: 'super', label: '超级优先' },
            ]} />
          </Form.Item>
          <Form.Item
            shouldUpdate
            noStyle
          >
            {() => {
              const tplVal = form.getFieldValue('template');
              const isFree = !tplVal || tplVal === '__NONE__';
              return (
                <Form.Item
                  name="name"
                  label={<span style={sectionTitleStyle}>指令命名（可选）</span>}
                  style={{ marginTop: 20 }}
                >
                  <Input maxLength={128} placeholder="给本次指令起一个名字，便于后期查询和管理(可选)" disabled={disableAfterTemplateCombined} />
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item
            shouldUpdate
            noStyle
          >
            {() => {
              const tplVal = form.getFieldValue('template');
              const ADHOC_CODE = 'InstructionTemplate-SYS-14';
              // 选中预设"无模板"模板时，强制备注必填
              let isAdhoc = false;
              try {
                const t = (templates || []).find((x:any) => String(x.system_code || '').trim() === ADHOC_CODE);
                if (t && tplVal) { isAdhoc = String(tplVal) === String(t.id); }
              } catch {}
              // 库存非标准操作模板：同样必须在备注里写清要求（仓库靠备注和附件判断要做什么）
              let isNonStd = false;
              try {
                const sel = (templates || []).find((x:any) => String(x.id) === String(tplVal));
                isNonStd = String(sel?.meta?.engine || '') === 'nonstandard_ops';
              } catch {}
              return (
                <Form.Item
                  name="remarks"
                  label={<span style={sectionTitleStyle}>指令备注</span>}
                  style={{ marginTop: 20 }}
                  rules={isAdhoc ? [{ required: true, message: '选择"不使用任何指令模板"预设时，备注为必填' }] : (isNonStd ? [{ required: true, message: '库存非标准操作需要在备注里写清楚具体要求，备注为必填' }] : [])}
                >
                  <Input.TextArea rows={2} placeholder="请添加指令备注信息，当不使用任何指令模板时，必须在此处提交指令的具体要求" disabled={disableAfterTemplateCombined} />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Form>
      </Card>

      <MediaPreviewModal target={localPreview} onClose={closeLocalPreview} />
      <Modal open={previewOpen} footer={null} onCancel={() => setPreviewOpen(false)} width={960}>
        {previewUrl && (
          (/\.png$|\.jpg$|\.jpeg$|\.gif$|^blob:/.test(previewUrl))
            ? <img alt="预览" style={{ maxWidth: '100%' }} src={previewUrl} />
            : <iframe title="预览" src={previewUrl} style={{ width: '100%', height: 600, border: 0 }} />
        )}
      </Modal>

      {/* BARCODE_MAPPING 内容审核错误弹窗（聚合） */}
      <Modal
        open={bmAuditOpen}
        title="新旧条码对应表附件内容审核 错误提示"
        width={900}
        onCancel={() => setBmAuditOpen(false)}
        footer={(
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12, width:'100%' }}>
            <span style={{ fontWeight: 700, color: '#8c8c8c' }}>请修改后再次提交附件</span>
            <div style={{ display:'flex', alignItems:'center', gap: 8 }}>
              <Button
                onClick={downloadBmAuditPdf}
                style={{ background: '#fa541c', borderColor: '#fa541c', color: '#fff' }}
              >
                下载错误信息
              </Button>
              <Button type="primary" onClick={() => setBmAuditOpen(false)}>关闭</Button>
            </div>
          </div>
        )}
      >
        {(() => {
          const d = bmAuditData || {};
          const sameRows: any[] = Array.isArray(d?.sameRows) ? d.sameRows : [];
          const dupPairs: any[] = Array.isArray(d?.dupPairs) ? d.dupPairs : [];
          const third = d?.third || null;
          const groupOrder: string[] = Array.isArray(third?.groupOrder) ? third.groupOrder : [];
          const groupMap: Record<string, any[]> = (third?.groupMap && typeof third.groupMap === 'object') ? third.groupMap : {};
          const hasFirst = sameRows.length > 0;
          const hasSecond = dupPairs.length > 0;
          const hasThird = !!third && groupOrder.length > 0;
          const divider = <div style={{ borderTop: '2px solid #f0f0f0', margin: '12px 0' }} />;
          return (
            <div>
              {hasFirst ? (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, color: '#ff7875' }}>
                    审核内容项：新旧条码相同（已移除首尾空格，忽略大小写）
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>行号</th>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>旧条码(A列)</th>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>新条码(B列)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sameRows.slice(0, 200).map((r:any, idx:number) => (
                        <tr key={`same-${r.rowNo}-${idx}`} style={{ backgroundColor: '#fff1f0' }}>
                          <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{r.rowNo}</td>
                          <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{r.old}</td>
                          <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{r.newer}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {sameRows.length > 200 ? <div style={{ marginTop: 6, color:'#8c8c8c' }}>仅显示前 200 行，剩余已省略。</div> : null}
                </div>
              ) : null}
              {hasFirst && (hasSecond || hasThird) ? divider : null}
              {hasSecond ? (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, color: '#ff7875' }}>
                    审核内容项：新旧条码组合相同（已移除首尾空格，忽略大小写）
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>组合</th>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>出现次数</th>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>行号</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dupPairs.slice(0, 200).map((it:any, gi:number) => {
                        const bg = gi % 2 === 0 ? '#e6f4ff' : '#fff7e6';
                        const rowNos = Array.isArray(it?.rowNos) ? it.rowNos.join('、') : '';
                        return (
                          <tr key={`dup-${it.key}`} style={{ backgroundColor: bg }}>
                            <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{it.key}</td>
                            <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{it.count}</td>
                            <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{rowNos}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {dupPairs.length > 200 ? <div style={{ marginTop: 6, color:'#8c8c8c' }}>仅显示前 200 组，剩余已省略。</div> : null}
                </div>
              ) : null}
              {hasSecond && hasThird ? divider : null}
              {hasThird ? (
                <div style={{ marginTop: 4 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, color: '#ff7875' }}>
                    审核内容项：一个旧条码对应多个新条码（且数量列无效）
                  </div>
                  <div>
                    <div style={{ marginBottom: 8, color: '#8c8c8c' }}>
                      若一个旧条码需要更换为多个新条码，请在D列“数量分配”中指定每个新条码的数量，请参考模板说明第8条。
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>行号</th>
                          <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>A列-旧条码</th>
                          <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>B列-新条码</th>
                          <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>C列-新条码文本</th>
                          <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>D列-数量分配</th>
                          <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>E列-备注</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupOrder.map((gk:string, gi:number) => {
                          const rowsInGroup = groupMap[gk] || [];
                          const bg = gi % 2 === 0 ? '#e6f7ff' : '#fff7e6';
                          return rowsInGroup.map((r:any, i:number) => (
                            <tr key={`${gk}-${r.rowNo}-${i}`} style={{ backgroundColor: bg }}>
                              <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{r.rowNo}</td>
                              <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{String(r.a ?? '')}</td>
                              <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{String(r.b ?? '')}</td>
                              <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{String(r.c ?? '')}</td>
                              <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{String(r.d ?? '')}</td>
                              <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{String(r.e ?? '')}</td>
                            </tr>
                          ));
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })()}
      </Modal>

      {/* SHIPPING_LABEL_TARGET_MAPPING 内容审核告警弹窗（不阻断提交） */}
      <Modal
        open={sltmAuditOpen}
        title={(
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12, paddingRight: 44 }}>
            <span>运输标签与出库对象对应表格附件内容审核 警告信息</span>
            <Button size="small" style={{ marginRight: 8 }} onClick={copySltmAuditWarnText}>复制信息</Button>
          </div>
        )}
        width={900}
        onCancel={() => setSltmAuditOpen(false)}
        footer={(
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12, width:'100%' }}>
            <span style={{ fontWeight: 700, color: '#8c8c8c' }}>该警告信息不影响创建指令，仅建议修正附件内容</span>
            <Button type="primary" onClick={() => setSltmAuditOpen(false)}>关闭</Button>
          </div>
        )}
      >
        {(() => {
          const d = sltmAuditData || {};
          const incompleteRows: any[] = Array.isArray(d?.incompleteRows) ? d.incompleteRows : [];
          const invalidBRows: any[] = Array.isArray(d?.invalidBRows) ? d.invalidBRows : [];
          const dupC: any[] = Array.isArray(d?.dupC) ? d.dupC : [];
          const dupD: any[] = Array.isArray(d?.dupD) ? d.dupD : [];
          const has1 = incompleteRows.length > 0;
          const has2 = invalidBRows.length > 0;
          const has3 = dupC.length > 0;
          const has4 = dupD.length > 0;
          const divider = <div style={{ borderTop: '2px solid #f0f0f0', margin: '12px 0' }} />;
          return (
            <div>
              {has1 ? (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, color: '#fa541c' }}>
                    审核内容项1：记录不完整（A列/B列必须有值，且C列/D列至少一列有值）
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>行号</th>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>A列</th>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>B列</th>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>C列</th>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>D列</th>
                      </tr>
                    </thead>
                    <tbody>
                      {incompleteRows.slice(0, 200).map((r:any, idx:number) => (
                        <tr key={`incomplete-${r.rowNo}-${idx}`} style={{ backgroundColor: '#fffbe6' }}>
                          <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{r.rowNo}</td>
                          <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{r.a || '-'}</td>
                          <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{r.b || '-'}</td>
                          <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{r.c || '-'}</td>
                          <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{r.d || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {incompleteRows.length > 200 ? <div style={{ marginTop: 6, color:'#8c8c8c' }}>仅显示前 200 行，剩余已省略。</div> : null}
                </div>
              ) : null}
              {has1 && (has2 || has3 || has4) ? divider : null}
              {has2 ? (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, color: '#fa541c' }}>
                    审核内容项2：B列值必须为 1
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>行号</th>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>B列值</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invalidBRows.slice(0, 200).map((r:any, idx:number) => (
                        <tr key={`b-invalid-${r.rowNo}-${idx}`} style={{ backgroundColor: '#fffbe6' }}>
                          <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{r.rowNo}</td>
                          <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{r.b || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {invalidBRows.length > 200 ? <div style={{ marginTop: 6, color:'#8c8c8c' }}>仅显示前 200 行，剩余已省略。</div> : null}
                </div>
              ) : null}
              {has2 && (has3 || has4) ? divider : null}
              {has3 ? (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, color: '#fa541c' }}>
                    审核内容项3：C列值重复（已移除首尾空格，忽略大小写）
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>C列值</th>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>出现次数</th>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>行号</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dupC.slice(0, 200).map((it:any, idx:number) => (
                        <tr key={`dup-c-${it.value}-${idx}`} style={{ backgroundColor: idx % 2 === 0 ? '#fffbe6' : '#fff7e6' }}>
                          <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{String(it.value || '-')}</td>
                          <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{Number(it.count || 0)}</td>
                          <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{Array.isArray(it.rowNos) ? it.rowNos.join('、') : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {dupC.length > 200 ? <div style={{ marginTop: 6, color:'#8c8c8c' }}>仅显示前 200 组，剩余已省略。</div> : null}
                </div>
              ) : null}
              {has3 && has4 ? divider : null}
              {has4 ? (
                <div style={{ marginTop: 4 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, color: '#fa541c' }}>
                    审核内容项4：D列值重复（已移除首尾空格，忽略大小写）
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>D列值</th>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>出现次数</th>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>行号</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dupD.slice(0, 200).map((it:any, idx:number) => (
                        <tr key={`dup-d-${it.value}-${idx}`} style={{ backgroundColor: idx % 2 === 0 ? '#fffbe6' : '#fff7e6' }}>
                          <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{String(it.value || '-')}</td>
                          <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{Number(it.count || 0)}</td>
                          <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{Array.isArray(it.rowNos) ? it.rowNos.join('、') : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {dupD.length > 200 ? <div style={{ marginTop: 6, color:'#8c8c8c' }}>仅显示前 200 组，剩余已省略。</div> : null}
                </div>
              ) : null}
            </div>
          );
        })()}
      </Modal>

      {/* GROUP_LIST 内容审核告警弹窗（不阻断提交） */}
      <Modal
        open={groupListAuditOpen}
        title={(
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12, paddingRight: 44 }}>
            <span>分组列表附件内容审核 警告信息</span>
            <Button size="small" style={{ marginRight: 8 }} onClick={copyGroupListAuditWarnText}>复制信息</Button>
          </div>
        )}
        width={900}
        onCancel={() => setGroupListAuditOpen(false)}
        footer={(
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12, width:'100%' }}>
            <span style={{ fontWeight: 700, color: '#8c8c8c' }}>该警告信息不影响创建指令，仅建议修正附件内容</span>
            <Button type="primary" onClick={() => setGroupListAuditOpen(false)}>关闭</Button>
          </div>
        )}
      >
        {(() => {
          const d = groupListAuditData || {};
          const missingABRows: any[] = Array.isArray(d?.missingABRows) ? d.missingABRows : [];
          const invalidARows: any[] = Array.isArray(d?.invalidARows) ? d.invalidARows : [];
          const duplicateABGroups: any[] = Array.isArray(d?.duplicateABGroups) ? d.duplicateABGroups : [];
          const duplicateBGroups: any[] = Array.isArray(d?.duplicateBGroups) ? d.duplicateBGroups : [];
          const has1 = missingABRows.length > 0;
          const has2 = invalidARows.length > 0;
          const has3 = duplicateABGroups.length > 0;
          const has4 = duplicateBGroups.length > 0;
          const divider = <div style={{ borderTop: '2px solid #f0f0f0', margin: '12px 0' }} />;
          return (
            <div>
              {has1 ? (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, color: '#fa541c' }}>
                    审核内容项1：有数据的行必须 A 列和 B 列都有值
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>行号</th>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>A列</th>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>B列</th>
                      </tr>
                    </thead>
                    <tbody>
                      {missingABRows.slice(0, 200).map((r:any, idx:number) => (
                        <tr key={`group-list-miss-${r.rowNo}-${idx}`} style={{ backgroundColor: '#fffbe6' }}>
                          <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{r.rowNo}</td>
                          <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{r.a || '-'}</td>
                          <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{r.b || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {missingABRows.length > 200 ? <div style={{ marginTop: 6, color:'#8c8c8c' }}>仅显示前 200 行，剩余已省略。</div> : null}
                </div>
              ) : null}
              {has1 && has2 ? divider : null}
              {has2 ? (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, color: '#fa541c' }}>
                    审核内容项2：有效行 A 列必须是非0整数（从第2行开始）
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>行号</th>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>A列值</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invalidARows.slice(0, 200).map((r:any, idx:number) => (
                        <tr key={`group-list-a-invalid-${r.rowNo}-${idx}`} style={{ backgroundColor: '#fffbe6' }}>
                          <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{r.rowNo}</td>
                          <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{r.a || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {invalidARows.length > 200 ? <div style={{ marginTop: 6, color:'#8c8c8c' }}>仅显示前 200 行，剩余已省略。</div> : null}
                </div>
              ) : null}
              {(has1 || has2) && has3 ? divider : null}
              {has3 ? (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, color: '#fa541c' }}>
                    审核内容项3：分组编号+分拣标识组合不能重复（比较时已去除首尾空格且不区分大小写）
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>重复行号</th>
                      </tr>
                    </thead>
                    <tbody>
                      {duplicateABGroups.slice(0, 200).map((it:any, idx:number) => (
                        <tr key={`group-list-ab-dup-${idx}`} style={{ backgroundColor: '#fffbe6' }}>
                          <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{Array.isArray(it?.rowNos) ? it.rowNos.join('、') : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {duplicateABGroups.length > 200 ? <div style={{ marginTop: 6, color:'#8c8c8c' }}>仅显示前 200 组，剩余已省略。</div> : null}
                </div>
              ) : null}
              {(has1 || has2 || has3) && has4 ? divider : null}
              {has4 ? (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, color: '#fa541c' }}>
                    审核内容项4：分拣标识不能重复（一个分拣标识只能属于一个分组编号；比较时已去除首尾空格且不区分大小写）
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>重复行号</th>
                      </tr>
                    </thead>
                    <tbody>
                      {duplicateBGroups.slice(0, 200).map((it:any, idx:number) => (
                        <tr key={`group-list-b-dup-${idx}`} style={{ backgroundColor: '#fffbe6' }}>
                          <td style={{ border: '1px solid #ddd', padding: 4, textAlign: 'center' }}>{Array.isArray(it?.rowNos) ? it.rowNos.join('、') : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {duplicateBGroups.length > 200 ? <div style={{ marginTop: 6, color:'#8c8c8c' }}>仅显示前 200 组，剩余已省略。</div> : null}
                </div>
              ) : null}
            </div>
          );
        })()}
      </Modal>

      {/* 历史指令执行结果：添加操作对象弹窗（骨架） */}
      <Modal
        title="添加操作对象-历史指令执行结果"
        open={historyTargetModalOpen}
        onCancel={() => {
          setHistoryTargetModalOpen(false);
          setHistoryAvailableSelectedRowKeys([]);
          setHistoryAvailablePage(1);
          setHistoryIocFilterKeyword('');
        }}
        onOk={async () => {
          try {
            const vals = await historyTargetForm.validateFields();
            const pid = Number(vals?.modal_project_id);
            if (!Number.isFinite(pid) || pid <= 0) { message.warning('请选择项目'); return; }
            const projectLabel = projectIdToLabel.get(pid) || `项目#${pid}`;
            const selectedSet = new Set((historyAvailableSelectedRowKeys || []).map((k) => String(k)));
            const selectedRows = (historyAvailableRows || []).filter((r:any) => selectedSet.has(String(r?.id)));
            if (selectedRows.length === 0) { message.warning('请先在列表中选择至少一个操作对象'); return; }
            setHistoryTargetRows((prev) => {
              const arr = Array.isArray(prev) ? [...prev] : [];
              const exists = new Set(arr.map((x:any) => String(x?.__key || '')));
              selectedRows.forEach((r:any) => {
                const key = `ioc-${String(r?.id || '')}`;
                if (!key || exists.has(key)) return;
                arr.push({
                  __key: key,
                  project_id: pid,
                  project_label: projectLabel,
                  output_container_id: r?.id,
                  instruction_id: r?.instruction_id,
                  output_code: r?.system_code || r?.display_code || '',
                  warehouse_label: r?.warehouse_code || r?.warehouse_name || '-',
                  container_type_display: r?.container_type_display || '-',
                  output_content_kind: r?.output_content_kind || '',
                  output_content_kind_display: r?.output_content_kind_display || '-',
                  output_contents_summary: r?.output_contents_summary || '-',
                  output_contents_detail: r?.output_contents_detail || '',
                  output_contents_direct_total_qty: r?.output_contents_direct_total_qty,
                  output_contents_total_qty: r?.output_contents_total_qty,
                  length_cm: r?.length_cm,
                  width_cm: r?.width_cm,
                  height_cm: r?.height_cm,
                  weight_g: r?.weight_g,
                  created_at: r?.created_at,
                });
                exists.add(key);
              });
              return arr;
            });
            setHistoryTargetModalOpen(false);
            setHistoryAvailableSelectedRowKeys([]);
          } catch {}
        }}
        okText="添加"
        cancelText="取消"
        width={1280}
      >
        <Form
          form={historyTargetForm}
          layout="vertical"
          initialValues={{ modal_project_id: undefined }}
        >
          <Form.Item
            name="modal_project_id"
            label="项目"
            rules={[{ required: true, message: '请选择项目' }]}
          >
            <Select
              style={{ width: 640 }}
              showSearch
              loading={loadingProjects}
              optionFilterProp="label"
              placeholder="请选择项目"
              options={(projects || []).map((p:any) => ({
                value: Number(p.id),
                label: `${p.project_code || p.id} - ${p.name || ''}`,
              }))}
              onChange={(pid:number) => { void loadHistoryProjectOutputs(Number(pid)); }}
            />
          </Form.Item>
          <Card
            size="small"
            title={(
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ whiteSpace: 'nowrap' }}>项目历史指令执行结果：将在库的项目历史指令执行结果添加到本次指令</span>
                <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                  <Input
                    allowClear
                    size="small"
                    value={historyIocFilterKeyword}
                    onChange={(e) => { setHistoryIocFilterKeyword(e.target.value); setHistoryAvailablePage(1); }}
                    placeholder="筛选IOC（系统编码/容器内容）"
                    style={{ width: 360 }}
                  />
                </div>
                <span style={{ fontSize: 12, color: '#1677ff', display: 'flex', alignItems: 'center', gap: 12, whiteSpace: 'nowrap' }}>
                  <span>已选:</span>
                  <span>容器数量({historySelectedStats.containerCount})</span>
                  <span style={{ display: 'inline-block', width: 1, height: 14, background: '#d9d9d9' }} />
                  <span>容器内库存单元数量({historySelectedStats.inventoryUnitQty})</span>
                </span>
              </div>
            )}
          >
            {historyModalProjectId ? (
              <>
              <style>{`
                .history-ioc-table-wrap { position: relative; }
                .history-ioc-table-wrap .ant-table-pagination,
                .history-ioc-table-wrap .ant-table-pagination-right {
                  display: flex !important;
                  justify-content: flex-end !important;
                  align-items: center !important;
                  width: 100% !important;
                }
                .history-ioc-table-wrap .ant-pagination-total-text {
                  margin-left: 0 !important;
                  position: absolute !important;
                  left: 0 !important;
                  transform: translateY(0);
                }
                .history-ioc-table .ant-table-tbody > tr.history-ioc-group-row > td {
                  border-left: 2px solid #91caff !important;
                  border-right: 2px solid #91caff !important;
                }
                .history-ioc-table .ant-table-tbody > tr.history-ioc-group-start > td {
                  border-top: 2px solid #91caff !important;
                }
                .history-ioc-table .ant-table-tbody > tr.history-ioc-group-end > td {
                  border-bottom: 2px solid #91caff !important;
                }
              `}</style>
              <div className="history-ioc-table-wrap">
              <Table
                className="history-ioc-table"
                size="small"
                rowKey={(r:any) => String(r?.id)}
                loading={historyAvailableLoading}
                dataSource={sortedFilteredHistoryAvailableRows || []}
                rowSelection={{
                  selectedRowKeys: historyAvailableSelectedRowKeys,
                  onSelect: (record:any, selected:boolean) => {
                    const rowId = String(record?.id || '');
                    if (!rowId || historyDisabledRowKeySet.has(rowId)) return;
                    const groupKey = historyIocGroupingMeta.rowToGroup.get(rowId);
                    const groupRows = groupKey ? (historyIocGroupingMeta.groupToRows.get(groupKey) || [rowId]) : [rowId];
                    const current = new Set((historyAvailableSelectedRowKeys || []).map((k) => String(k)));
                    if (selected) {
                      groupRows.forEach((id) => { if (!historyDisabledRowKeySet.has(String(id))) current.add(String(id)); });
                    } else {
                      groupRows.forEach((id) => current.delete(String(id)));
                    }
                    setHistoryAvailableSelectedRowKeys(Array.from(current));
                  },
                  onSelectAll: (selected:boolean, selectedRows:any[], changeRows:any[]) => {
                    const current = new Set((historyAvailableSelectedRowKeys || []).map((k) => String(k)));
                    if (selected) {
                      const delta = [...(selectedRows || []), ...(changeRows || [])].map((r:any) => String(r?.id || '')).filter(Boolean);
                      const expanded = expandHistorySelectedKeysByGroup(delta as React.Key[]);
                      expanded.forEach((id) => { if (!historyDisabledRowKeySet.has(String(id))) current.add(String(id)); });
                    } else {
                      const delta = [...(changeRows || []), ...(selectedRows || [])].map((r:any) => String(r?.id || '')).filter(Boolean);
                      const expanded = expandHistorySelectedKeysByGroup(delta as React.Key[]);
                      expanded.forEach((id) => current.delete(String(id)));
                    }
                    setHistoryAvailableSelectedRowKeys(Array.from(current));
                  },
                  getCheckboxProps: (record:any) => ({
                    disabled: historyDisabledRowKeySet.has(String(record?.id || '')),
                  }),
                }}
                onRow={(record:any) => {
                  const rowKey = String(record?.id || '');
                  const isDisabled = historyDisabledRowKeySet.has(rowKey);
                  const borderMeta = historyIocVisualMeta.rowGroupBorderMap.get(rowKey) || { grouped: false, top: false, bottom: false };
                  if (isDisabled) {
                    return {
                      style: {
                        background: '#f5f5f5',
                        color: '#8c8c8c',
                        cursor: 'not-allowed',
                        borderLeft: borderMeta.grouped ? '2px solid #91caff' : undefined,
                        borderRight: borderMeta.grouped ? '2px solid #91caff' : undefined,
                        borderTop: borderMeta.top ? '2px solid #91caff' : undefined,
                        borderBottom: borderMeta.bottom ? '2px solid #91caff' : undefined,
                      },
                    };
                  }
                  return {
                    style: {
                      background: historyIocVisualMeta.rowBgMap.get(rowKey) || '#ffffff',
                      borderLeft: borderMeta.grouped ? '2px solid #91caff' : undefined,
                      borderRight: borderMeta.grouped ? '2px solid #91caff' : undefined,
                      borderTop: borderMeta.top ? '2px solid #91caff' : undefined,
                      borderBottom: borderMeta.bottom ? '2px solid #91caff' : undefined,
                    },
                  };
                }}
                rowClassName={(record:any) => {
                  const rowKey = String(record?.id || '');
                  const borderMeta = historyIocVisualMeta.rowGroupBorderMap.get(rowKey) || { grouped: false, top: false, bottom: false };
                  if (!borderMeta.grouped) return '';
                  if (borderMeta.top) return 'history-ioc-group-row history-ioc-group-start';
                  if (borderMeta.bottom) return 'history-ioc-group-row history-ioc-group-end';
                  return 'history-ioc-group-row';
                }}
                pagination={{
                  current: historyAvailablePage,
                  pageSize: historyAvailablePageSize,
                  total: (sortedFilteredHistoryAvailableRows || []).length,
                  showSizeChanger: true,
                  onChange: (p:number, ps:number) => {
                    setHistoryAvailablePage(p);
                    setHistoryAvailablePageSize(ps);
                  },
                  showTotal: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#595959', marginLeft: 0 }}>
                      <ExclamationCircleFilled style={{ color: '#ff7875' }} />
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <span>同一外边框中多个容器具有父/子层级关系</span>
                        <span>父/子容器必须作为整体添加,不可单独添加某一个容器</span>
                        <span>独立容器可单独添加</span>
                      </span>
                    </div>
                  ),
                }}
                locale={{ emptyText: '暂无可用的项目历史指令执行结果' }}
                columns={[
                  {
                    title: '层级标识',
                    dataIndex: '__hierarchy_role__',
                    width: 120,
                    render: (_:any, r:any) => {
                      const rowId = String(r?.id || '');
                      const role = historyIocGroupingMeta.roleByRow.get(rowId)
                        || (r?.is_child_container ? 'child' : (r?.is_parent_container ? 'parent' : 'standalone'));
                      const roleText = role === 'parent' ? '父容器' : (role === 'child' ? '子容器' : '独立容器');
                      const roleStyle = role === 'parent'
                        ? { color: '#1677ff', border: '#91caff', bg: '#e6f4ff' }
                        : (role === 'child'
                          ? { color: '#d4380d', border: '#ffbb96', bg: '#fff2e8' }
                          : { color: '#389e0d', border: '#b7eb8f', bg: '#f6ffed' });
                      return (
                        <span
                          style={{
                            display: 'inline-block',
                            minWidth: 76,
                            textAlign: 'center',
                            padding: '2px 10px',
                            borderRadius: 6,
                            border: `1px solid ${roleStyle.border}`,
                            color: roleStyle.color,
                            background: roleStyle.bg,
                            lineHeight: 1.4,
                          }}
                        >
                          {roleText}
                        </span>
                      );
                    },
                  },
                  {
                    title: '系统编码',
                    dataIndex: 'system_code',
                    width: 220,
                    render: (v:any, r:any) => {
                      const code = String(v || r?.display_code || '-');
                      const instructionId = Number(r?.instruction_id || r?.prev_instruction_id || 0);
                      const openInstructionDetail = () => {
                        try {
                          if (!Number.isFinite(instructionId) || instructionId <= 0) return;
                          const w: any = window as any;
                          const path = String(w?.location?.pathname || '');
                          const m = path.match(/^\/dashboard\/(admin|staff|customer)(?:\/|$)/);
                          const role = m?.[1] || 'customer';
                          const detailUrl = `/dashboard/${role}/instructions/i-${instructionId}`;
                          if (w.tabsManager && typeof w.tabsManager.openTabByPath === 'function') {
                            w.tabsManager.openTabByPath(detailUrl);
                            return;
                          }
                          w.open(detailUrl, '_blank');
                        } catch {}
                      };
                      const isDisabled = alreadyAddedHistoryIocIds.has(String(r?.id || ''));
                      return (
                        <div style={{ lineHeight: 1.2 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <Button
                              type="link"
                              size="small"
                              disabled={!Number.isFinite(instructionId) || instructionId <= 0}
                              style={{ padding: 0, height: 'auto', fontFamily: 'monospace', fontWeight: 600 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                openInstructionDetail();
                              }}
                            >
                              {code}
                            </Button>
                            {code && code !== '-' ? (
                              <CopyOutlined
                                style={{ color: '#1677ff', cursor: 'pointer' }}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await navigator.clipboard.writeText(code);
                                    message.success('已复制系统编码');
                                  } catch {
                                    message.error('复制失败');
                                  }
                                }}
                              />
                            ) : null}
                          </span>
                          {isDisabled ? (
                            <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                              已被添加到操作对象列表
                            </div>
                          ) : null}
                        </div>
                      );
                    },
                  },
                  { title: '容器类型', dataIndex: 'container_type_display', width: 120, render: (v:any) => String(v || '-') },
                  {
                    title: '容器内容统计',
                    dataIndex: 'output_contents_summary',
                    width: 150,
                    render: (_:any, r:any) => {
                      const kind = String(r?.output_content_kind || '').toLowerCase();
                      const qty = Number(r?.output_contents_total_qty || 0);
                      if (kind === 'exception') return `${Number.isFinite(qty) ? qty : 0}件`;
                      return String(r?.output_contents_summary || '');
                    },
                  },
                  {
                    title: '容器内容',
                    dataIndex: 'output_contents_detail',
                    width: 320,
                    render: (v:any, r:any) => {
                      const kind = String(r?.output_content_kind || '').toLowerCase();
                      if (kind === 'exception') {
                        return (
                          <Tooltip title="无法操作等异常产品">
                            <div style={{ color: '#595959', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              无法操作等异常产品
                            </div>
                          </Tooltip>
                        );
                      }
                      const full = String(v || '').trim();
                      if (!full) return '';
                      const lines = full.split(/[；;]/).map((x) => String(x || '').trim()).filter(Boolean);
                      if (lines.length === 0) return '';
                      const preview = lines.slice(0, 3);
                      const hasMore = lines.length > 3;
                      return (
                        <div style={{ maxWidth: 300 }}>
                          {preview.map((line, idx) => (
                            <div
                              key={`${line}-${idx}`}
                              style={{
                                position: 'relative',
                                minHeight: 20,
                                marginBottom: idx === preview.length - 1 ? 0 : 2,
                              }}
                            >
                              <Tooltip title={line}>
                                <div
                                  style={{
                                    color: '#595959',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    paddingRight: hasMore && idx === preview.length - 1 ? 56 : 0,
                                  }}
                                >
                                  {line}
                                </div>
                              </Tooltip>
                              {hasMore && idx === preview.length - 1 ? (
                                <Button
                                  type="link"
                                  size="small"
                                  style={{
                                    position: 'absolute',
                                    right: 0,
                                    bottom: -2,
                                    padding: 0,
                                    height: 'auto',
                                    lineHeight: 1.2,
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const iocCode = String(r?.output_code || r?.system_code || r?.display_code || '-');
                                    const fullInfoText = [
                                      `容器系统编码: ${iocCode}`,
                                      ...lines.map((x, i) => `${i + 1}. ${x}`),
                                    ].join('\n');
                                    Modal.info({
                                      title: (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 12, paddingRight: 24 }}>
                                          <span style={{ justifySelf: 'start' }}>容器内容完整记录</span>
                                          <span style={{ justifySelf: 'center', color: '#1677ff', fontWeight: 700, fontFamily: 'monospace' }}>{iocCode}</span>
                                          <span style={{ justifySelf: 'end' }}>
                                            <Button
                                              type="link"
                                              size="small"
                                              onClick={async () => {
                                                try {
                                                  await navigator.clipboard.writeText(fullInfoText);
                                                  message.success('已复制完整信息');
                                                } catch {
                                                  message.error('复制失败');
                                                }
                                              }}
                                            >
                                              复制信息
                                            </Button>
                                          </span>
                                        </div>
                                      ),
                                      width: 760,
                                      centered: true,
                                      content: (
                                        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                                          {lines.map((x, i) => (
                                            <div key={`${x}-${i}`} style={{ marginBottom: 6, wordBreak: 'break-all' }}>
                                              {i + 1}. {x}
                                            </div>
                                          ))}
                                        </div>
                                      ),
                                    });
                                  }}
                                >
                                  [...]
                                </Button>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      );
                    },
                  },
                  {
                    title: '容器尺寸',
                    key: 'dims',
                    width: 180,
                    render: (_:any, r:any) => {
                      const hasAll = r?.length_cm != null && r?.width_cm != null && r?.height_cm != null;
                      return hasAll ? `${r.length_cm}×${r.width_cm}×${r.height_cm} cm` : '';
                    },
                  },
                  { title: '容器重量', dataIndex: 'weight_g', width: 140, render: (v:any) => (v != null ? `${v}g` : '') },
                  {
                    title: '来源历史指令',
                    key: 'source_instruction_code',
                    width: 180,
                    render: (_: any, r: any) => {
                      const code = String(
                        r?.instruction_code
                        || r?.prev_instruction_code
                        || r?.source_instruction_code
                        || ''
                      ).trim();
                      const id = Number(r?.instruction_id || r?.prev_instruction_id || 0);
                      const openDetail = (instructionId: number) => {
                        try {
                          if (!Number.isFinite(instructionId) || instructionId <= 0) return;
                          const w: any = window as any;
                          const path = String(w?.location?.pathname || '');
                          const m = path.match(/^\/dashboard\/(admin|staff|customer)(?:\/|$)/);
                          const role = m?.[1] || 'customer';
                          const base = `/dashboard/${role}`;
                          const detailUrl = `${base}/instructions/${instructionId}`;
                          if (w.tabsManager && typeof w.tabsManager.openTabByPath === 'function') {
                            w.tabsManager.openTabByPath(detailUrl);
                            return;
                          }
                          w.open(detailUrl, '_blank');
                        } catch {}
                      };
                      const instId = Number.isFinite(id) && id > 0 ? id : 0;
                      const brief = instId > 0 ? historyInstructionBriefById[instId] : undefined;
                      const text = code || (instId > 0 ? `#${instId}` : '');
                      if (!text) return '';
                      const createdDateText = (() => {
                        const raw = brief?.created_at || r?.instruction_created_at || r?.created_at;
                        if (!raw) return '-';
                        try {
                          const d = new Date(raw);
                          if (!Number.isFinite(d.getTime())) return String(raw);
                          const y = d.getFullYear();
                          const m = String(d.getMonth() + 1).padStart(2, '0');
                          const day = String(d.getDate()).padStart(2, '0');
                          return `${y}-${m}-${day}`;
                        } catch {
                          return String(raw);
                        }
                      })();
                      const summary = (
                        <div style={{ maxWidth: 360, lineHeight: 1.6 }}>
                          <div><b>来源历史指令摘要</b></div>
                          <div>系统编码：{String(code || brief?.system_code || '-')}</div>
                          <div>指令模板：{String(r?.instruction_template_name || r?.template_name || r?.template_detail?.name || brief?.template_name || '-')}</div>
                          <div>指令名称：{String(r?.instruction_name || r?.name || brief?.name || '-')}</div>
                          <div>备注：{String(r?.instruction_remarks || r?.remarks || brief?.remarks || '-')}</div>
                          <div>创建时间：{createdDateText}</div>
                        </div>
                      );
                      return (
                        <Tooltip title={summary}>
                          <Button
                            type="link"
                            size="small"
                            style={{ padding: 0, fontFamily: 'monospace' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (instId > 0) openDetail(instId);
                            }}
                          >
                            {text}
                          </Button>
                        </Tooltip>
                      );
                    },
                  },
                ]}
              />
              </div>
              </>
            ) : (
              <div style={{ color: '#8c8c8c' }}>
                请先选择项目
              </div>
            )}
          </Card>
        </Form>
      </Modal>

      <div style={{ marginTop: 16 }}>
        <Button type="primary" loading={loading} onClick={submit} disabled={disableAfterTemplateCombined}>提交指令</Button>
      </div>

      {/* 添加操作对象弹窗 */}
      <Modal
        title={(
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingRight: 32 }}>
            <span style={{ whiteSpace: 'nowrap' }}>添加操作对象-当前可用库存</span>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <span className="targets-group-hint" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 10px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 4, color: '#ad6800', fontSize: 13, fontWeight: 'normal' }}>
                <ExclamationCircleFilled style={{ color: '#faad14' }} />
                <span>含多种库存单元的分拣组内库存必须同时处理，不支持选择特定库存单元或指定数量；只含一种库存单元的分拣组不受此限制</span>
              </span>
            </div>
          </div>
        )}
        open={targetModalOpen}
        onCancel={() => setTargetModalOpen(false)}
        okButtonProps={{ disabled: !hasAnySelection }}
        width={1280}
        onOk={async () => {
          try {
            // 要求先选项目
            const pid = targetForm.getFieldValue('modal_project_id');
            if (!pid) { message.warning('请选择项目'); return; }
            if (isSortAndCountTemplate && sortAndCountTargetProjectIds.size > 0 && !sortAndCountTargetProjectIds.has(Number(pid))) {
              Modal.warning({
                title: '提示',
                icon: null,
                centered: true,
                width: 860,
                okText: '知道了',
                content: renderSortAndCountValidationWarningContent('库存分拣与清点指令的操作对象必须归属于同一个项目，请选择与已添加操作对象一致的项目。'),
              });
              return;
            }
            if (isSortAndCountTemplate) {
              const selectedHasAIU = Object.keys(invSel).some((k) => {
                const id = Number(k);
                const s = invSel[id];
                if (!s || !s.checked) return false;
                const unit = (inventoryUnits || []).find((u:any) => Number(u.id) === id);
                if (!unit) return false;
                return isAIUInventoryTarget({
                  target_type: 'inventory_unit',
                  unit_type: unit?.unit_type,
                  unit_type_display: unit?.unit_type_display,
                  inventory_unit_system_barcode: unit?.system_barcode,
                  display: unit?.system_barcode || '',
                });
              });
              if (selectedHasAIU) {
                Modal.warning({
                  title: '提示',
                  icon: null,
                  centered: true,
                  width: 860,
                  okText: '知道了',
                  content: renderSortAndCountValidationWarningContent(sortAndCountAIUWarningText),
                });
                return;
              }
            }
            // 校验指定数量必须填写
            try {
              const invalidIds: number[] = [];
              Object.keys(invSel).forEach((k) => {
                const id = Number(k);
                const s = invSel[id];
                if (!s || !s.checked) return;
                if (s.mode === 'number' && !(Number(s.qty) > 0)) invalidIds.push(id);
              });
              if (invalidIds.length > 0) {
                Modal.warning({
                  title: '提示',
                  content: '存在选择“指定数量”但未填写数量的库存单元，请先填写数量',
                  centered: true,
                });
                return;
              }
            } catch {}
            // 从双列表 selections 生成 targets
            const next: any[] = [];
            // 库存单元所选
            Object.keys(invSel).forEach((k) => {
              const id = Number(k);
              const s = invSel[id];
              if (!s || !s.checked) return;
              // 已添加过的库存单元不允许再次添加（理论上前端已禁用，这里做二次兜底）
              if (alreadyAddedUnitIds.has(id)) return;
              const unit = (inventoryUnits || []).find((u:any)=> Number(u.id)===id);
              const gid = (() => {
                try {
                  const v = Number((unit as any)?.sorting_group || (unit as any)?.sorting_group_info?.id);
                  return Number.isFinite(v) ? v : undefined;
                } catch { return undefined; }
              })();
              const sgInfo = (() => {
                try {
                  const info = (unit as any)?.sorting_group_info;
                  if (info && (info.system_code || info.name)) return info;
                  return undefined;
                } catch { return undefined; }
              })();
              next.push({
                target_type: 'inventory_unit',
                project_id: pid,
                sorting_group_id: gid,
                sorting_group_info: sgInfo,
                inventory_unit_id: id,
                unit_type: unit?.unit_type,
                unit_type_display: unit?.unit_type_display,
                actual_length: unit?.actual_length,
                actual_width: unit?.actual_width,
                actual_height: unit?.actual_height,
                actual_weight: unit?.actual_weight,
                use_all_available: s.mode === 'all',
                expected_qty: s.mode === 'number' ? s.qty : undefined,
                display: unit ? `${unit.system_barcode || id} · ${unit.unit_type_display || ''}` : `库存单元#${id}`,
                content: unit ? unit.content : undefined,
                // 与弹窗列表口径一致：可用数量 = 后端可用 + 本指令已锁定
                available_qty: unit ? getDisplayAvailableQtyForUnit(unit) : undefined,
              });
            });
            if (next.length === 0) { message.warning('未选择任何目标'); return; }
            setTargets(prev => [...prev, ...next]);
            // 重置选择
            setInvSel({});
            setTargetModalOpen(false);
          } catch {}
        }}
        okText="添加"
        cancelText="取消"
      >
        <Form form={targetForm} layout="vertical" initialValues={{ source_type: 'inventory', inventory_scope: 'project_all', quantity_mode: 'all' }}>
          {/* 管理员：客户筛选（优先） */}
          {isAdminContainer ? (
            <Form.Item name="modal_customer_id" label="客户" rules={[{ required: true, message: '请选择客户' }]}>
              <Select
                style={{ width: 640 }}
                showSearch
                allowClear
                loading={adminCustomerLoading}
                onFocus={ensureAdminCustomers}
                options={adminCustomerOptions}
                optionFilterProp="label"
                onChange={(cid:number) => { setBaseCustomerId(Number(cid)); targetForm.setFieldsValue({ modal_project_id: undefined }); setAdminProjectOptions([]); loadAdminProjects(cid); setProjectSgCount(0); setProjectUnitCount(0); setInvSel({}); }}
              />
            </Form.Item>
          ) : null}
          {/* 项目选择（必填，驱动分拣组/库存单元/历史指令结果） */}
          <Form.Item name="modal_project_id" label="项目" rules={[{ required: true, message: '请选择项目' }]}>
            {isAdminContainer ? (
              <Select
                style={{ width: 640 }}
                showSearch
                loading={adminProjectLoading}
                optionFilterProp="label"
                options={(adminProjectOptions || []).map((o:any)=> ({
                  value: o.value,
                  label: o.label,
                }))}
                onFocus={() => { const cid = targetForm.getFieldValue('modal_customer_id'); if (cid) loadAdminProjects(Number(cid)); }}
                onChange={(pid:number) => { loadSortingGroups(pid); loadInventoryUnits({ project_id: pid }); loadUpstream(pid); setProjectSgCount(0); setProjectUnitCount(0); setInvSel({}); }}
              />
            ) : (
              <Select style={{ width: 640 }} showSearch loading={loadingProjects} optionFilterProp="label"
                      options={(projects || []).map((p:any)=>(
                        {
                          value: Number(p.id),
                          label: `${p.project_code || p.id} - ${p.name || ''}`,
                        }
                      ))}
                      onChange={(pid:number) => { loadSortingGroups(pid); loadInventoryUnits({ project_id: pid }); loadUpstream(pid); setProjectSgCount(0); setProjectUnitCount(0); setInvSel({}); setUnitFilterKeyword(''); }}
              />
            )}
          </Form.Item>
          {/* 列表1：项目在库 */}
          <Card size="small" title={( 
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>项目在库：将项目当前可用库存添加到本次指令</span>
              <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                <Input
                  allowClear
                  size="small"
                  value={unitFilterKeyword}
                  onChange={(e) => setUnitFilterKeyword(e.target.value)}
                  placeholder="筛选库存单元（系统编码/产品名/客户编码/分拣条码/备注）"
                  style={{ width: 440 }}
                />
              </div>
              <span style={{ fontSize: 12, color: '#1677ff', display: 'flex', alignItems: 'center', gap: 12 }}>
                <span>已选：</span>
                <span>分拣组种类({selectedGroupCount})</span>
                <span style={{ display: 'inline-block', width: 1, height: 14, background: '#d9d9d9' }} />
                <span>库存单元种类({selectedUnitCount})</span>
                <span style={{ display: 'inline-block', width: 1, height: 14, background: '#d9d9d9' }} />
                <span>库存单元数量({selectedUnitQtyTotal})</span>
                {selectedOutsideFilterCount > 0 && (
                  <span className="targets-selected-outside-filter" style={{ color: '#d46b08' }}>其中 {selectedOutsideFilterCount} 个不在当前筛选结果中</span>
                )}
              </span>
            </div>
          )} style={{ marginBottom: 12 }}>
            {(() => {
              const pid = targetForm.getFieldValue('modal_project_id');
              if (!pid) return null;
              // 正在加载时展示"正在加载库存单元"
              if (loadingUnits) {
                return (
                  <div style={{ marginBottom: 8, padding: '6px 8px', background: '#e6f4ff', border: '1px solid #91caff', color: '#0958d9', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="waybill-validate-spinner is-sm">
                      <span />
                      <span />
                      <span />
                      <span />
                    </span>
                    <span>{unitsLoadProgress ? `正在加载库存单元 ${unitsLoadProgress.loaded}/${unitsLoadProgress.total}...` : '正在加载库存单元...'}</span>
                  </div>
                );
              }
              if (unitsLoadError) {
                return (
                  <div className="inventory-units-load-error" style={{ marginBottom: 8, padding: '6px 8px', background: '#fff2f0', border: '1px solid #ffccc7', color: '#cf1322', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>{unitsLoadError}</span>
                    <Button size="small" onClick={() => loadInventoryUnits(unitsLoadParamsRef.current)}>重新加载</Button>
                  </div>
                );
              }
              // 加载完成后且无可用库存再提示无数据
              if (Array.isArray(displayUnits) && displayUnits.length === 0) {
                return (
                  <div style={{ marginBottom: 8, padding: '6px 8px', background: '#fffbe6', border: '1px solid #ffe58f', color: '#ad6800', borderRadius: 4 }}>
                    此项目无可用库存，请重新选择项目或仓库
                  </div>
                );
              }
              return null;
            })()}
            {/* 覆盖分页条样式：总计紧挨着页码、整体靠右；保持单行不换行并压缩分页项间距 */}
            <style>{`
              .targets-table-wrap { position: relative; }
              .targets-table-wrap .ant-table-pagination,
              .targets-table-wrap .ant-table-pagination-right {
                display: flex !important;
                justify-content: flex-end !important;
                align-items: center !important;
                width: 100% !important;
              }
              .targets-table-wrap .ant-pagination { 
                margin-left: auto !important;
                display: flex !important;
                flex-wrap: nowrap !important;
                align-items: center !important;
                gap: 4px !important;
              }
              .targets-table-wrap .ant-pagination-total-text {
                flex: 1 1 auto;
                min-width: 0;
              }
              .targets-table-wrap .ant-pagination-item,
              .targets-table-wrap .ant-pagination-prev,
              .targets-table-wrap .ant-pagination-next,
              .targets-table-wrap .ant-pagination-options {
                margin-inline: 2px !important;
              }
            `}</style>
            <div className="targets-table-wrap">
            <Table
              size="small"
              rowKey={(r:any)=>String(r.id)}
              dataSource={filteredUnits}
              pagination={{
                current: unitsCurrentPage,
                pageSize: unitsPageSize,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50, 100],
                showQuickJumper: false,
                showTotal: (total: number, range: [number, number]) => (
                  // 跨页全选开关在分页条最左侧，条数紧挨着页码
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <span className="targets-select-across-pages" style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', color: '#595959' }}>
                      <Switch size="small" checked={selectAllAcrossPages} onChange={setSelectAllAcrossPages} />
                      <span>跨页全选</span>
                    </span>
                    <span className="targets-units-total" style={{ whiteSpace: 'nowrap' }}>第 {range[0]}-{range[1]} 条，共 {total} 条记录</span>
                  </div>
                ),
                onChange: (page: number, size: number) => {
                  setUnitsPage(size !== unitsPageSize ? 1 : page);
                  setUnitsPageSize(size);
                },
              }}
              columns={[
                { title: (
                    <span>
                      <Checkbox
                        indeterminate={headerCheckedCount > 0 && headerCheckedCount < headerEnabledCount}
                        checked={headerEnabledCount > 0 && headerCheckedCount === headerEnabledCount}
                        onChange={(e) => {
                          const nextChecked = e.target.checked;
                          setInvSel(prev => {
                            const next = { ...prev } as typeof prev;
                            (headerScopeUnits || []).forEach((u:any) => {
                              const id = Number(u.id);
                              if (alreadyAddedUnitIds.has(id)) {
                                const cur0 = next[id] || { mode: 'all' as const };
                                next[id] = { ...cur0, checked: false, mode: 'all', qty: undefined };
                                return;
                              }
                              const gid = Number(u.sorting_group || (u.sorting_group_info && u.sorting_group_info.id));
                              const isMulti = (groupUnitCountById[gid] || 0) > 1;
                              const cur = next[id] || { mode: 'all' as const };
                              next[id] = {
                                ...cur,
                                checked: nextChecked,
                                // 多单元分拣组强制全部数量
                                mode: nextChecked ? (isMulti ? 'all' : (cur.mode || 'all')) : 'all',
                                qty: nextChecked ? (isMulti ? undefined : cur.qty) : undefined,
                              };
                            });
                            return next;
                          });
                        }}
                      /> 全选
                      <div className="targets-select-all-scope" style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 'normal' }}>
                        {selectAllAcrossPages ? `（全部 ${(filteredUnits || []).length} 条）` : '（本页）'}
                      </div>
                    </span>
                  ), dataIndex: 'select', width: 90, render: (_:any, r:any) => {
                  const s = invSel[r.id] || { checked: false, mode: 'all' as const };
                  const isAlready = alreadyAddedUnitIds.has(Number(r.id));
                  const gid = Number(r.sorting_group || (r.sorting_group_info && r.sorting_group_info.id));
                  const isMulti = (groupUnitCountById[gid] || 0) > 1;
                  return (
                    <Checkbox
                      checked={s.checked}
                      disabled={isAlready}
                      onChange={(e)=> setInvSel(prev=>{
                        if (isAlready) return prev;
                        const next = { ...prev } as typeof prev;
                        // 对于多单元分拣组，联动同组全部行；单一分拣组按原行处理
                        const affected = (displayUnits || []).filter((u:any)=> {
                          const gid2 = Number(u.sorting_group || (u.sorting_group_info && u.sorting_group_info.id));
                          return isMulti ? gid2 === gid : Number(u.id) === Number(r.id);
                        });
                        affected.forEach((u:any) => {
                          const id2 = Number(u.id);
                          if (alreadyAddedUnitIds.has(id2)) {
                            const cur0 = next[id2] || { mode: 'all' as const };
                            next[id2] = { ...cur0, checked: false, mode: 'all', qty: undefined };
                            return;
                          }
                          const cur = next[id2] || { mode: 'all' as const };
                          next[id2] = {
                            ...cur,
                            checked: e.target.checked,
                            mode: e.target.checked ? (isMulti ? 'all' : (cur.mode || 'all')) : 'all',
                            qty: e.target.checked ? (isMulti ? undefined : cur.qty) : undefined,
                          };
                        });
                        return next;
                      })}
                    />
                  );
                }},
                { title: '分拣组', dataIndex: 'sorting_group_info', width: 220, render: (v:any)=> (
                  <div style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any, overflow: 'hidden' }}>
                    {v ? v.name : '-'}
                  </div>
                ) },
                { title: '库存单元系统编码', dataIndex: 'system_barcode', width: 230, render: (v:any, r:any) => {
                  const code = (v != null && String(v).trim() !== '') ? String(v) : (r?.id != null ? String(r.id) : '-');
                  const isAlready = alreadyAddedUnitIds.has(Number(r.id));
                  return (
                    <div style={{ lineHeight: 1.2 }}>
                      <div>{code}</div>
                      {isAlready ? (
                        <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                          已被添加到操作对象列表
                        </div>
                      ) : null}
                    </div>
                  );
                }},
                { title: '库存单元内容', dataIndex: 'content', width: 260, render: (_:any, r:any)=> {
                  const items = Array.isArray(r?.content?.items) ? r.content.items : [];
                  const lines = items.map((it:any)=>{
                    const pid2 = Number(it.product);
                    const info = productInfo[pid2] || {};
                    const name = info?.name || `产品ID:${pid2}`;
                    const custom = info?.custom_code || '';
                    const codesArr: string[] = Array.isArray(info?.sorting_codes) ? (info.sorting_codes as any[]).map((c:any)=>c?.code).filter((v:any)=>!!v) : [];
                    const barcodePart = codesArr.length === 0 ? '' : (codesArr.length === 1 ? ` 条码: ${codesArr[0]}` : ` 条码: ${codesArr[0]} 等${codesArr.length}个`);
                    return `${name}（编码: ${custom || '-'}${barcodePart}）×${it.quantity}件`;
                  });
                  return lines.length > 0 ? (
                    <div>
                      {lines.map((line: string, idx: number) => (
                        <div key={`inv-content-${String(r?.id || '-')}-${idx}`}>{line}</div>
                      ))}
                    </div>
                  ) : '-';
                }},
                { title: '备注', dataIndex: 'remarks', width: 200, render: (v:any) => {
                  // 原箱/原托盘的备注是"跟踪号-X；第三方参考号-Y"，每段一行更好认
                  const parts = String(v || '').split('；').map((part: string) => part.trim()).filter(Boolean);
                  return parts.length > 0 ? (
                    <div className="inventory-unit-remarks" style={{ wordBreak: 'break-all' }}>
                      {parts.map((part: string, idx: number) => <div key={idx}>{part}</div>)}
                    </div>
                  ) : '-';
                }},
                { title: '可用数量', dataIndex: 'available_qty', width: 120, render: (v:any, r:any) => {
                  const val = getDisplayAvailableQtyForUnit(r);
                  return Number.isFinite(val as number) ? String(val) : '-';
                }},
                { title: (<Tooltip title="不可超过可用数量"><span>操作数量</span></Tooltip>), dataIndex: 'qty', width: 260, render: (_:any, r:any) => {
                  const s = invSel[r.id] || { checked: false, mode: 'all' as const };
                  const isAlready = alreadyAddedUnitIds.has(Number(r.id));
                  const gid = Number(r.sorting_group || (r.sorting_group_info && r.sorting_group_info.id));
                  const isMulti = (groupUnitCountById[gid] || 0) > 1;
                  const maxAvail = (() => {
                    const v = getDisplayAvailableQtyForUnit(r);
                    return (Number.isFinite(v) && v > 0) ? Math.floor(v) : undefined;
                  })();
                  return (
                    <Space>
                      <Radio.Group
                        value={s.mode}
                        disabled={!s.checked || isMulti || isAlready}
                        onChange={(e)=> setInvSel(prev=>({
                          ...prev,
                          [r.id]: {
                            ...(prev[r.id]||{ checked: false }),
                            mode: e.target.value,
                            // 选择"全部"时清空指定数量
                            qty: e.target.value === 'all' ? undefined : prev[r.id]?.qty,
                          }
                        }))}
                      >
                        <Radio value={'all'} disabled={!s.checked || isMulti}>全部</Radio>
                        <Radio value={'number'} disabled={!s.checked || isMulti}>指定</Radio>
                      </Radio.Group>
                      <InputNumber
                        disabled={!s.checked || s.mode!=='number' || isMulti || isAlready}
                        min={1}
                        max={maxAvail}
                        value={s.qty}
                        onChange={(val)=> setInvSel(prev=>({ ...prev, [r.id]: { ...(prev[r.id]||{checked:false, mode:'number'}), qty: Number(val||0) || undefined } }))}
                      />
                    </Space>
                  );
                }},
              ]}
              rowClassName={(record:any) => {
                const s = invSel[record.id];
                return s && s.checked === false ? 'row-deselected' : '';
              }}
              onRow={(record:any) => {
                const s = invSel[record.id];
                const isOff = s && s.checked === false;
                const isAlready = alreadyAddedUnitIds.has(Number(record.id));
                const gid = Number(record.sorting_group || (record.sorting_group_info && record.sorting_group_info.id));
                const gidx = groupIndexById[gid] || 0;
                // 更明显的分组底色
                const bg = gidx % 2 === 0 ? '#e6f7ff' : '#fff7e6';
                const baseStyle: any = isOff ? { backgroundColor: '#f5f5f5', color: '#999' } : { backgroundColor: bg, borderLeft: '4px solid ' + (gidx % 2 === 0 ? '#91d5ff' : '#ffd591') };
                const style: any = isAlready ? { ...baseStyle, backgroundColor: '#fafafa', color: '#8c8c8c', cursor: 'not-allowed' } : baseStyle;
                return {
                  style,
                  onClick: (e: any) => {
                    const target = e?.target as HTMLElement | null;
                    if (target && (target.closest('input') || target.closest('button') || target.closest('a') || target.closest('.ant-checkbox'))) {
                      return;
                    }
                    const cur = invSel[record.id] || { checked: false, mode: 'all' as const };
                    if (isAlready || cur.checked) return;
                    const isMulti = (groupUnitCountById[gid] || 0) > 1;
                    setInvSel(prev => {
                      const next = { ...prev } as typeof prev;
                      const affected = (displayUnits || []).filter((u:any)=> {
                        const gid2 = Number(u.sorting_group || (u.sorting_group_info && u.sorting_group_info.id));
                        return isMulti ? gid2 === gid : Number(u.id) === Number(record.id);
                      });
                      affected.forEach((u:any) => {
                        const id2 = Number(u.id);
                        if (alreadyAddedUnitIds.has(id2)) {
                          const cur0 = next[id2] || { mode: 'all' as const };
                          next[id2] = { ...cur0, checked: false, mode: 'all', qty: undefined };
                          return;
                        }
                        const cur2 = next[id2] || { mode: 'all' as const };
                        next[id2] = {
                          ...cur2,
                          checked: true,
                          mode: isMulti ? 'all' : (cur2.mode || 'all'),
                          qty: isMulti ? undefined : cur2.qty,
                        };
                      });
                      return next;
                    });
                  }
                } as any;
              }}
            />
            </div>
          </Card>
        </Form>
      </Modal>
      {/* 编辑操作对象（数量） */}
      <Modal
        title={(
          <span>
            编辑操作对象数量
            <span style={{ marginLeft: 10, fontSize: 12, color: '#8c8c8c', fontWeight: 400 }}>指定数量不可超过可用数量</span>
          </span>
        )}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={() => {
          if (editIdx == null) { setEditOpen(false); return; }
          const row = targets[editIdx] as any;
          const gid = Number(row?.sorting_group_id);
          const isMulti = (typeof multiGroupIds !== 'undefined') && (multiGroupIds as any).has(gid);
          const maxAvail = (() => {
            const v = (row?.available_qty != null ? Number(row.available_qty) : (row?.warehouse_total_quantity != null ? Number(row.warehouse_total_quantity) : NaN));
            return (Number.isFinite(v) && v > 0) ? Math.floor(v) : undefined;
          })();
          setTargets(prev => prev.map((t, i) => {
            if (i !== editIdx) return t;
            const forceAll = isMulti ? true : (editMode === 'all');
            const picked = (editQty && editQty > 0) ? Math.floor(Number(editQty)) : undefined;
            const clamped = (picked != null && maxAvail != null) ? Math.min(picked, maxAvail) : picked;
            return {
              ...t,
              use_all_available: forceAll,
              expected_qty: forceAll ? undefined : clamped,
            };
          }));
          setEditOpen(false);
        }}
      >
        <Space align="end">
          {(() => {
            const row = (editIdx != null) ? targets[editIdx] : null;
            const gid = Number((row as any)?.sorting_group_id);
            const isMulti = (typeof multiGroupIds !== 'undefined') && (multiGroupIds as any).has(gid);
            const available = (row && (row as any)?.available_qty != null)
              ? Number((row as any).available_qty)
              : (row && (row as any)?.warehouse_total_quantity != null ? Number((row as any).warehouse_total_quantity) : NaN);
            const maxAvail = (Number.isFinite(available) && available > 0) ? Math.floor(available) : undefined;
            const availText = (maxAvail != null) ? String(maxAvail) : '-';
            return (
              <>
                <Radio.Group value={isMulti ? 'all' : editMode} onChange={(e)=> setEditMode(e.target.value)} disabled={isMulti}>
                  <Radio value={'all'} disabled={isMulti}>
                    全部库存
                    <span style={{ marginLeft: 8, fontSize: 12, color: '#8c8c8c', fontWeight: 400 }}>{`可用数量 ${availText}`}</span>
                  </Radio>
                  <Radio value={'number'} disabled={isMulti}>指定数量</Radio>
                </Radio.Group>
                <InputNumber
                  disabled={isMulti || editMode!=='number'}
                  min={1}
                  max={maxAvail}
                  value={editQty}
                  onChange={(v)=> setEditQty(Number(v||0) || undefined)}
                />
              </>
            );
          })()}
        </Space>
      </Modal>
      {/* 新标签PDF预审详情（A1） */}
      <Modal
        title="新标签pdf文件解析审核 风险项提示"
        open={pdfQaModalOpen}
        onCancel={() => { setPdfQaModalOpen(false); }}
        width={900}
        footer={(
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12, width:'100%' }}>
            <span style={{ fontWeight: 700, color: '#8c8c8c' }}>提示：该弹窗仅用于风险告警，不影响指令提交</span>
            <div style={{ display:'flex', alignItems:'center', gap: 8 }}>
              <Button onClick={copyPdfQaWarnText}>复制</Button>
              <Button type="primary" onClick={() => setPdfQaModalOpen(false)}>关闭</Button>
            </div>
          </div>
        )}
      >
        {(() => {
          try {
            const ruleKey = String(pdfQaModalRuleKey || '');
            if (!ruleKey) return <div style={{ color: '#8c8c8c' }}>未选择风险项</div>;
            const files = (attachFilesMap[ruleKey] || []) as any[];
            const uids = files
              .filter((f:any) => !!f?.originFileObj && isPdfAuditCandidateFile(String(f?.name || '')))
              .map((f:any)=> String(f?.uid || ''))
              .filter(Boolean);
            const raws = uids.map((uid)=> ({ uid, raw: pdfQaResultByUid[uid] })).filter((x:any)=> !!x?.raw);
            if (raws.length === 0) return <div style={{ color: '#8c8c8c' }}>暂无预审结果（可能仍在预审中或未产生结果）</div>;

            const issueList: Array<{ uid: string; src: string; file_name: string; loc: string; decoded: string; reason: string }> = [];
            raws.forEach((x:any) => {
              const raw = x.raw;
              const sourceName = String(raw?.source_name || '');
              const items = Array.isArray(raw?.items) ? raw.items : [];
              items.forEach((it:any) => {
                const issues = Array.isArray(it?.issues) ? it.issues : [];
                if (!issues || issues.length === 0) return;
                const fn = String(it?.file_name || '');
                const kind = String(it?.source?.kind || '');
                const arc = String(it?.source?.archive_name || '');
                const ep = String(it?.source?.entry_path || '');
                const loc = (kind === 'zip' || kind === 'rar')
                  ? `${arc || '-'} :: ${ep || fn || '-'}`
                  : (fn || sourceName || '-');
                const decodedArr = Array.isArray(it?.decoded) ? it.decoded : [];
                const decodedText = decodedArr.length > 0 ? decodedArr.join('，') : '-';
                // 原因文案（仅输出两类中文提示，不展示 pdf.xxx code）
                const codes = issues.map((iss:any)=> String(iss?.code || '').trim()).filter(Boolean);
                const hasMismatch = codes.includes('pdf.decoded_filename_mismatch');
                const msg = hasMismatch ? '条码解析结果与文件名不一致' : '整页模拟打印后未识别到可解析条码';
                issueList.push({
                  uid: String(x.uid),
                  src: String(sourceName || '-'),
                  file_name: fn,
                  loc,
                  decoded: decodedText,
                  reason: msg || '-',
                });
              });
            });

            if (issueList.length === 0) {
              return <div style={{ color: '#389e0d' }}>当前无风险项（预审通过）</div>;
            }

            const dataSource = issueList.map((it:any, idx:number)=> ({ ...it, _k: `${ruleKey}-${idx}` , idx: idx + 1 }));
            return (
              <div>
                <div style={{ marginBottom: 10, padding: '10px 12px', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                    <Tag style={{ background: '#fa541c', borderColor: '#fa541c', color: '#fff', fontWeight: 700 }}>
                      {`存在风险 ${issueList.length} 项`}
                    </Tag>
                  </div>
                  <div style={{ fontSize: 12, color: '#614700', lineHeight: 1.6 }}>
                    <div><strong>审核规则：</strong>每个文件仅允许 1 个可解析条码，且条码解析结果需与文件名一致；将整页(不截图)模拟打印在 2.25×1.25 英寸（5.7×3.2厘米 203dpi）标签上后验证标签是否可扫描。</div>
                  </div>
                </div>
                <Table
                  size="small"
                  rowKey={(r:any)=> r._k}
                  dataSource={dataSource}
                  pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10','20','50'], showQuickJumper: true, showTotal: (t, range) => `共${t}条风险项，显示${range[0]}-${range[1]}条` }}
                  columns={[
                    { title: '序号', dataIndex: 'idx', width: 70 },
                    { title: '文件/条目', dataIndex: 'loc', width: 260, ellipsis: true },
                    { title: '解码结果', dataIndex: 'decoded', width: 180, ellipsis: true, render: (v:any) => String(v || '-') },
                    { title: '原因(警告)', dataIndex: 'reason', width: 360, ellipsis: true },
                  ]}
                />
              </div>
            );
          } catch {
            return <div style={{ color: '#8c8c8c' }}>预审结果渲染失败</div>;
          }
        })()}
      </Modal>
      {/* 物流运单审核运行中 */}
      <Modal
        open={waybillAuditRunning}
        footer={null}
        closable={false}
        centered
        maskClosable={false}
        width={360}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '8px 0' }}>
          <div className="waybill-validate-spinner">
            <span />
            <span />
            <span />
            <span />
          </div>
          <div style={{ color: '#595959', fontWeight: 600 }}>运单审核运行中...</div>
        </div>
      </Modal>
      <style>{`
        .waybill-validate-spinner {
          width: 56px;
          height: 56px;
          position: relative;
          animation: waybill-rotate 1.1s linear infinite;
        }
        .waybill-validate-spinner.is-sm {
          width: 18px;
          height: 18px;
        }
        .waybill-validate-spinner span {
          position: absolute;
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: #1677ff;
        }
        .waybill-validate-spinner.is-sm span {
          width: 4px;
          height: 4px;
        }
        .waybill-validate-spinner span:nth-child(1) { top: 0; left: 50%; transform: translate(-50%, 0); }
        .waybill-validate-spinner span:nth-child(2) { right: 0; top: 50%; transform: translate(0, -50%); }
        .waybill-validate-spinner span:nth-child(3) { bottom: 0; left: 50%; transform: translate(-50%, 0); }
        .waybill-validate-spinner span:nth-child(4) { left: 0; top: 50%; transform: translate(0, -50%); }
        @keyframes waybill-rotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      {/* 新标签PDF预审运行中 */}
      <Modal
        open={pdfQaRunning}
        footer={null}
        closable={false}
        centered
        maskClosable={false}
        width={360}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '8px 0' }}>
          <div className="waybill-validate-spinner">
            <span />
            <span />
            <span />
            <span />
          </div>
          <div style={{ color: '#595959', fontWeight: 600 }}>新标签PDF预审运行中...</div>
        </div>
      </Modal>
      {/* 物流运单解析审核结果 */}
      <Modal
        title="物流运单解析审核 风险项提示"
        open={waybillAuditModalOpen}
        onCancel={() => setWaybillAuditModalOpen(false)}
        width={760}
        footer={(
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12, width:'100%' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, textAlign: 'left' }}>
              <span style={{ fontWeight: 700, color: '#8c8c8c' }}>说明:</span>
              <span style={{ fontWeight: 700, color: '#8c8c8c' }}>1 此警告信息仅用于风险提示,不影响指令提交</span>
              <span style={{ fontWeight: 700, color: '#8c8c8c' }}>2 请确认提交了正确的物流跟踪号和运单附件,可参考附件说明信息修改后再次提交</span>
              <span style={{ fontWeight: 700, color: '#8c8c8c' }}>3 若确认所有信息无误,可继续提交指令.</span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap: 8 }}>
              <Button onClick={copyWaybillAuditWarnText}>复制</Button>
              <Button type="primary" onClick={() => setWaybillAuditModalOpen(false)}>关闭</Button>
            </div>
          </div>
        )}
      >
        {(() => {
          try {
            const issues = Array.isArray(waybillAuditIssues) ? waybillAuditIssues : [];
            if (issues.length === 0) return <div style={{ color: '#8c8c8c' }}>暂无审核结果</div>;
            return (
              <div>
                <div style={{ marginBottom: 10, padding: '10px 12px', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                    <Tag style={{ background: '#fa541c', borderColor: '#fa541c', color: '#fff', fontWeight: 700 }}>
                      {`存在风险 ${issues.length} 项`}
                    </Tag>
                    <span style={{ fontSize: 12, color: '#614700' }}>
                      {waybillAuditFileName ? `文件：${waybillAuditFileName}` : ''}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#614700', lineHeight: 1.6 }}>
                    <div><strong>审核规则：</strong>文件格式、大小、页数、有效区域占比（≥0.6）、4×6英寸模拟打印条码可读性、跟踪号匹配。</div>
                  </div>
                </div>
                <Table
                  size="small"
                  rowKey={(r:any, idx?: number)=> `${r.code}-${idx ?? 0}`}
                  dataSource={issues}
                  pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10','20','50'], showQuickJumper: true, showTotal: (t, range) => `共${t}条风险项，显示${range[0]}-${range[1]}条` }}
                  columns={[
                    { title: '规则类别', dataIndex: 'label', width: 180 },
                    { title: '原因', dataIndex: 'message', width: 520, render: (v:any) => <div style={{ whiteSpace: 'pre-line' }}>{String(v || '-')}</div> },
                  ]}
                />
              </div>
            );
          } catch {
            return <div style={{ color: '#8c8c8c' }}>审核结果渲染失败</div>;
          }
        })()}
      </Modal>
      {/* 批量修改数量 */}
      <Modal
        title="批量编辑操作对象数量"
        open={bulkOpen}
        width={860}
        onCancel={() => setBulkOpen(false)}
        onOk={() => {
          if (selectedTargetKeys.length === 0) { setBulkOpen(false); return; }
          // 若选择中包含"多单元分拣组"且尝试指定数量，阻止并提示
          const hasMultiInSelection = selectedTargetKeys.some(k => {
            const idx = Number(k as any);
            const row = targets[idx];
            const gid = Number((row as any)?.sorting_group_id);
            return (typeof multiGroupIds !== 'undefined') && (multiGroupIds as any).has(gid);
          });
          if (hasMultiInSelection && bulkMode === 'number') {
            message.warning('所选包含多单元分拣组的目标，必须整组全量处理，不能指定数量');
            return;
          }
          // 指定数量：不可超过所选集合中的最小可用数量；并列出超限记录
          if (bulkMode === 'number') {
            const qty = Math.floor(Number(bulkQty || 0));
            if (!Number.isFinite(qty) || qty <= 0) {
              message.warning('请输入有效的指定数量（最小1）');
              return;
            }
            const getAvail = (t:any): number | undefined => {
              try {
                const v = (t?.available_qty != null ? Number(t.available_qty) : (t?.warehouse_total_quantity != null ? Number(t.warehouse_total_quantity) : NaN));
                return (Number.isFinite(v) && v > 0) ? Math.floor(v) : undefined;
              } catch { return undefined; }
            };
            const selectedRows = (selectedTargetKeys || []).map(k => targets[Math.floor(Number(k as any))]).filter(Boolean);
            const offenders = selectedRows
              .map((t:any) => ({ t, avail: getAvail(t) }))
              .filter((x:any) => x.avail != null && Number.isFinite(Number(x.avail)) && Number(x.avail) < qty);
            if (offenders.length > 0) {
              const show = offenders.slice(0, 10).map((x:any) => {
                const label = String(x?.t?.display || x?.t?.inventory_unit_system_barcode || x?.t?.inventory_unit_id || '-');
                return `${label}(可用${x.avail})`;
              });
              message.error(`指定数量 ${qty} 超过以下对象的可用数量：${show.join('，')}${offenders.length > 10 ? ` 等${offenders.length}条` : ''}`);
              return;
            }
          }
          setTargets(prev => prev.map((t, i) => {
            if (!selectedTargetKeys.includes(String(i))) return t;
            const gid = Number((t as any)?.sorting_group_id);
            const isMulti = (typeof multiGroupIds !== 'undefined') && (multiGroupIds as any).has(gid);
            const useAll = isMulti ? true : (bulkMode === 'all');
            return {
              ...t,
              use_all_available: useAll,
              expected_qty: useAll ? undefined : (bulkQty && bulkQty > 0 ? bulkQty : undefined),
            };
          }));
          // 批量调整后清空已选，恢复未勾选状态
          setSelectedTargetKeys([]);
          setBulkOpen(false);
        }}
      >
        <div style={{ marginBottom: 10, color: '#8c8c8c', fontSize: 12, lineHeight: 1.6 }}>
          将所选对象的操作数量统一修改为“全部库存”或指定数量，指定数量不可超过当前所选对象集合中的最小可用数量，或者可使用列表的“修改数量”按钮单独修改特定对象的操作数量。
        </div>
        <Space>
          {(() => {
            const getAvail = (t:any): number | undefined => {
              try {
                const v = (t?.available_qty != null ? Number(t.available_qty) : (t?.warehouse_total_quantity != null ? Number(t.warehouse_total_quantity) : NaN));
                return (Number.isFinite(v) && v > 0) ? Math.floor(v) : undefined;
              } catch { return undefined; }
            };
            const selectedRows = (selectedTargetKeys || []).map(k => targets[Math.floor(Number(k as any))]).filter(Boolean);
            const avails = selectedRows.map(getAvail).filter((v:any) => v != null && Number.isFinite(Number(v)) && Number(v) > 0).map((v:any)=>Number(v));
            const minAvail = avails.length ? Math.min(...avails) : undefined;
            const nText = (minAvail != null && Number.isFinite(minAvail)) ? String(minAvail) : '-';
            return (
              <>
                <Radio.Group
                  value={bulkMode}
                  onChange={(e)=> setBulkMode(e.target.value)}
                  style={{ display:'flex', flexDirection:'column', gap: 10 }}
                >
                  <Radio value={'all'}>全部库存</Radio>
                  <Radio value={'number'}>
                    <span style={{ display:'inline-flex', alignItems:'flex-end' }}>
                      <span>指定数量</span>
                      <span style={{ marginLeft: 8, fontSize: 12, color: '#8c8c8c', fontWeight: 400, lineHeight: '22px', whiteSpace: 'nowrap' }}>
                        {`当前所选对象集合中最小可用数量为${nText}，指定数量不可超过${nText}`}
                      </span>
                    </span>
                  </Radio>
                </Radio.Group>
                <InputNumber
                  disabled={bulkMode!=='number'}
                  min={1}
                  max={minAvail}
                  value={bulkQty}
                  onChange={(v)=> setBulkQty(Number(v||0) || undefined)}
                />
              </>
            );
          })()}
        </Space>
      </Modal>
    </div>
  );
};

export default CreateInstructionPage;

